// eslint-disable-next-line no-unused-vars
import { LayoutDashboard, Terminal, BookOpen, TrendingUp, Users, Building2, UserCircle, CalendarDays, Mail, Phone, FileText, Video, CheckSquare, Sparkles, ArrowRight, CalendarPlus, Clock, Send, PenLine, Zap, Copy, Pencil, Trash2, X, Maximize2, Download, Search, Filter, RefreshCw, Eye, EyeOff, XCircle, Flame, AlertCircle, UserMinus, MessageSquare, Bell, CalendarCheck, Settings, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Plus, BarChart2, Home, GraduationCap, Heart, Hotel, Menu, Megaphone, Pin, Check, Accessibility, Star, Wallet } from "lucide-react";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import CalendarPage from "./CalendarPage";
import ChairsScreen from "./Chairs";
import FinancialsScreen from "./Financials";

// ─── BRAND ───────────────────────────────────────────────────────────────────
// DrewOS palette: Ink/Bone/Signal/Slate/Steel.
// Operator-facing chrome only — customer-facing artifacts (email signatures,
// PDF exports, booking widget, chair tracking module) remain Outpost-branded.
// Variable names kept (green/orange/etc.) so the rest of the 15K-line app
// re-skins with zero downstream edits.
const COLORS = {
  green:        "#13182a",   // Sidebar Ink — softer than pure black, slight blue undertone
  greenDark:    "#13182a",   // Sidebar Ink — same
  greenDeep:    "#E8E2D5",   // Page bg — warm linen, ~6% darker than Bone for tonal depth
  orange:       "#FF6B1A",   // Signal — primary accent / CTAs
  orangeLight:  "#FF8240",   // Signal cool — hover/active state
  white:        "#FFFFFF",
  gray:         "#E8E2D5",   // Linen — matches page bg
  grayMid:      "#8C97A6",   // Steel — secondary text
  grayDark:     "#3A4452",   // Slate — body text on light
  cardBg:       "#FFFFFF",   // white cards lift visibly on linen page
  cardBorder:   "#D6D0C2",   // hairline border, darker for definition on linen bg
  textPrimary:  "#0A0E1A",   // Ink — primary text on light bg (still pure for readability)
  textSecondary:"#8C97A6",   // Steel — muted text
  rowHover:     "#FFF1E8",   // faint Signal tint — table row hover
  success:      "#4CAF50",
  warning:      "#FFC107",
  danger:       "#F44336",
};

const CRM_URL = "https://outpost-crm-production.up.railway.app";
// Draft images are served from the Railway volume. Relative paths from the API
// (e.g. "/marketing/images/42") get the CRM_URL prefix; absolute URLs and
// data: URIs are returned as-is so legacy values still render.
const resolveImg = (u) => !u ? null : (u.startsWith("/") ? `${CRM_URL}${u}` : u);
const CALENDLY = "https://build-black-tau.vercel.app/#booking";
const SB435_DEADLINE = new Date("2027-01-01");

const VERTICALS = ["RCFE","Hotel","Hospital","Campus","PropertyMgmt"];
const VERTICAL_ICONS = { RCFE: <Home size={14} />, Hotel: <Hotel size={14} />, Hospital: <Heart size={14} />, Campus: <GraduationCap size={14} />, PropertyMgmt: <Building2 size={14} /> };

// Particle colors for the Dashboard funnel — one color per vertical.
const VERTICAL_COLORS = {
  RCFE:         "#F59E0B", // amber
  Hotel:        "#38BDF8", // sky blue
  Hospital:     "#10B981", // emerald
  Campus:       "#7C3AED", // violet
  PropertyMgmt: "#64748B", // slate
};

// Outreach sources feeding into the funnel top. x is the x-coord in
// the 460px-wide funnel container; all tributaries converge at center.
const OUTREACH_SOURCES = [
  { key: "linkedin", label: "LinkedIn DMs",   icon: "in", color: "#0077B5", x: 55  },
  { key: "email",    label: "Email Sequence", icon: "✉",  color: "#D66F27", x: 175 },
  { key: "meta",     label: "Meta Ads",       icon: "f",  color: "#1877F2", x: 295, comingSoon: true },
  { key: "cold",     label: "Cold Call",      icon: "☎",  color: "#494C43", x: 410 },
];

const LEAD_STATUSES = ["New","Contacted","Replied","Meeting Booked","Quote Sent","Closed Won","Closed Lost","Follow Up Later"];
const STATUS_COLORS = {
  "New":            "#6B7280",
  "Contacted":      "#3B82F6",
  "Replied":        "#8B5CF6",
  "Meeting Booked": "#D66F27",
  "Quote Sent":     "#F59E0B",
  "Closed Won":     "#10B981",
  "Closed Lost":    "#EF4444",
  "Follow Up Later":"#6366F1",
};

const DEAL_STAGES = ["Lead In","Qualified","Demo Scheduled","Quote Sent","Negotiation","Closed Won","Closed Lost"];
const DEAL_STAGE_COLORS = {
  "Lead In":        "#94A3B8",
  "Qualified":      "#3B82F6",
  "Demo Scheduled": "#8B5CF6",
  "Quote Sent":     "#F59E0B",
  "Negotiation":    "#D66F27",
  "Closed Won":     "#10B981",
  "Closed Lost":    "#EF4444",
};

function titleCase(s) {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}
// Agent 6 LinkedIn DM templates — hydrated from Railway /agent6/linkedin-dm.
// Local fallback used only if Railway is unreachable.
const LI_DM_FALLBACK = {
  RCFE: (fn) => `Hi ${fn}, I'm a retired Marine who helps RCFEs replace scattered evac chairs and 72-hour kits with one simple staff-ready system. Worth a 15-min call?`,
  Hotel: (fn) => `Hi ${fn}, I'm a retired Marine who helps hotels replace scattered evac equipment with one simple stairwell system their staff can actually run. Worth a 15-min call?`,
  Hospital: (fn) => `Hi ${fn}, I'm a retired Marine who helps hospitals standardize stairwell evacuation and patient transfers campus-wide. Worth a 15-min call?`,
  Campus: (fn) => `Hi ${fn}, I'm a retired Marine who helps campuses standardize stairwell evacuation so BERT/CERT staff know exactly what to do. Worth a 15-min call?`,
  PropertyMgmt: (fn) => `Hi ${fn}, I'm a retired Marine who helps property managers give buildings a simple repeatable stairwell evac system. Worth a 15-min call?`,
};
function getLiDm(vertical, firstName) {
  const fn = titleCase(firstName) || "there";
  return (LI_DM_FALLBACK[vertical] || LI_DM_FALLBACK.RCFE)(fn);
}
async function fetchAgent6Dm(vertical, firstName) {
  const fn = titleCase(firstName) || "there";
  const res = await api(`/agent6/linkedin-dm?vertical=${encodeURIComponent(vertical || "RCFE")}&first_name=${encodeURIComponent(fn)}`);
  if (res && res.message) return res.message;
  return getLiDm(vertical, firstName);
}
const PIPELINES = [
  { id: "ALL", label: "All Deals" },
  { id: "BTC", label: "BTC" },
  { id: "BTB", label: "BTB" },
  { id: "BTG", label: "BTG" },
  { id: "HALO", label: "HALO Leads" },
  { id: "DREWOS", label: "DrewOS" },
];
const PIPELINE_FULL = { ALL: "All Deals", BTC: "Business to Consumer", BTB: "Business to Business", BTG: "Business to Government", HALO: "HALO Intelligence Leads", DREWOS: "DrewOS" };
const PIPELINE_COLORS = { BTC: "#3B82F6", BTB: "#8B5CF6", BTG: "#10B981", HALO: "#D66F27", DREWOS: "#FF6B1A" };

// Book-of-business chips for the Contacts view. 'outpost' (default) covers
// everything pre-existing; 'drewos' isolates drewos.ai contact-form traffic.
const CONTACT_BOOKS = [
  { id: "all", label: "All" },
  { id: "outpost", label: "Outpost" },
  { id: "drewos", label: "DrewOS" },
];
const CONTACT_BOOK_COLORS = { outpost: "#D66F27", drewos: "#FF6B1A" };

// DrewOS — HALO pipeline stages (matches outpost_crm/api.py HALO_PIPELINE_STAGES).
// Includes legacy stages (Enrolled, Hot-Manual) that exist in the live data.
const HALO_STAGES = [
  "HALO-Discovered", "HALO-Enriched", "HALO-Enrolled", "HALO-Hot-Manual",
  "HALO-Engaged", "HALO-Demo", "HALO-Proposal",
  "HALO-Closed-Won", "HALO-Closed-Lost",
];
const HALO_STAGE_COLORS = {
  "HALO-Discovered":  "#94A3B8",
  "HALO-Enriched":    "#3B82F6",
  "HALO-Enrolled":    "#6366F1",
  "HALO-Hot-Manual":  "#EC4899",
  "HALO-Engaged":     "#8B5CF6",
  "HALO-Demo":        "#F59E0B",
  "HALO-Proposal":    "#D66F27",
  "HALO-Closed-Won":  "#10B981",
  "HALO-Closed-Lost": "#EF4444",
};
const HALO_TIERS = ["HOT","WARM","NURTURE","WATCH"];
const HALO_TIER_COLORS = { HOT: "#EF4444", WARM: "#F59E0B", NURTURE: "#3B82F6", WATCH: "#94A3B8" };

// ─── API ─────────────────────────────────────────────────────────────────────
async function api(path, method = "GET", body = null) {
  try {
    const opts = { method, headers: { "Content-Type": "application/json" } };
    if (body) opts.body = JSON.stringify(body);
    const r = await fetch(`${CRM_URL}${path}`, opts);
    if (!r.ok) throw new Error(`${r.status}`);
    return await r.json();
  } catch (e) {
    console.error("API error:", path, e.message);
    return null;
  }
}

// Click-to-copy CDSS license badge (RCFE only). Omitted when license is falsy.
function CdssLicenseBadge({ license, vertical, compact }) {
  const [copied, setCopied] = useState(false);
  if (!license) return null;
  if (vertical && vertical !== "RCFE") return null;
  const onCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(String(license)).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  if (compact) {
    return (
      <span onClick={onCopy} title="Click to copy CDSS license number"
        style={{ cursor: "pointer", fontSize: 10, color: "#8A9B8E" }}>
        CDSS #{license}{copied ? " \u2713" : ""}
      </span>
    );
  }
  return (
    <span onClick={onCopy} title="Click to copy CDSS license number"
      style={{ display: "inline-block", cursor: "pointer", fontSize: 11,
        fontWeight: 600, background: "#0002", border: "1px solid #ffffff22",
        color: "#D0D3CA", padding: "3px 8px", borderRadius: 4, letterSpacing: 0.3 }}>
      CDSS License #: {license}{copied ? " \u2713" : ""}
    </span>
  );
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function normalizeLead(lead) {
  if (!lead) return lead;
  return {
    ...lead,
    id: lead.id || lead.lead_id,
    title: lead.lead_title || lead.title,
    status: lead.lead_status || lead.status,
    contact_name: lead.contact?.full_name || lead.contact_name,
    email_work: lead.contact?.email_work || lead.email_work,
    phone_work: lead.contact?.phone_work || lead.phone_work,
    contact_title: lead.contact?.job_title || lead.contact_title,
    linkedin_url: lead.contact?.linkedin_url || lead.linkedin_url,
    // Vertical-specific fields (top-level in /command-center/:vertical response)
    facility_capacity: lead.facility_capacity,
    facility_status: lead.facility_status,
    citation_count: lead.citation_count,
    room_count: lead.room_count,
    floor_count: lead.floor_count,
    star_rating: lead.star_rating,
    brand_flag: lead.brand_flag,
    patient_capacity: lead.patient_capacity,
    cms_rating: lead.cms_rating,
    is_va: lead.is_va,
    enrollment: lead.enrollment,
    ncaa_division: lead.ncaa_division,
    campus_type: lead.campus_type,
    multi_story_buildings: lead.multi_story_buildings,
    portfolio_size: lead.portfolio_size,
    unit_count: lead.unit_count,
    service_area: lead.service_area,
    pm_score: lead.pm_score,
    has_hoa: lead.has_hoa,
  };
}

function daysUntil(date) {
  return Math.ceil((date - new Date()) / 86400000);
}

function fmt$$(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
}

function fmtNum(n) {
  return new Intl.NumberFormat("en-US").format(n || 0);
}

// ─── STYLES ──────────────────────────────────────────────────────────────────
// ─── DREWOS STYLES ───────────────────────────────────────────────────────────
// Cohesive operator-console aesthetic per the DrewOS brand guide:
// Inter for body/headings, JetBrains Mono for caption/nav/label/button
// labels, hairline borders, deliberate spacing, Signal-orange accent.
const FONT_SANS = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const FONT_MONO = "'JetBrains Mono', ui-monospace, Menlo, monospace";
const SIDEBAR_BORDER = "#1f2538";   // hairline on softened Ink
const SIDEBAR_HOVER = "#0e1322";    // subtle active/hover dip (slightly darker than sidebar bg)

const S = {
  app: {
    display: "flex", height: "100vh", width: "100vw",
    background: COLORS.greenDeep, color: COLORS.textPrimary,
    fontFamily: FONT_SANS,
    overflow: "hidden",
  },
  sidebar: {
    width: 232, minWidth: 232, background: COLORS.greenDark,
    display: "flex", flexDirection: "column",
    borderRight: `1px solid ${SIDEBAR_BORDER}`,
    transition: "width 0.2s",
    zIndex: 100,
  },
  sidebarCollapsed: { width: 64, minWidth: 64 },
  logoArea: {
    padding: "20px 16px", borderBottom: `1px solid ${SIDEBAR_BORDER}`,
    background: COLORS.greenDark,
    display: "flex", alignItems: "center", gap: 10, minHeight: 76,
  },
  logoImg: { width: 36, height: 36, objectFit: "contain", flexShrink: 0 },
  logoText: {
    fontFamily: FONT_MONO, fontSize: 11, fontWeight: 600,
    color: COLORS.orange, letterSpacing: "0.16em", lineHeight: 1.2,
    textTransform: "uppercase",
  },
  nav: { flex: 1, padding: "12px 0", overflowY: "auto" },
  navItem: (active, collapsed) => ({
    display: "flex", alignItems: "center", gap: collapsed ? 0 : 12,
    padding: collapsed ? "13px 0" : "11px 18px",
    justifyContent: collapsed ? "center" : "flex-start",
    cursor: "pointer", transition: "background 0.12s, color 0.12s",
    background: active ? SIDEBAR_HOVER : "transparent",
    borderLeft: active ? `2px solid ${COLORS.orange}` : "2px solid transparent",
    color: active ? COLORS.orange : "#A8B0BC",
    fontFamily: FONT_MONO,
    fontSize: 11, fontWeight: active ? 700 : 500,
    letterSpacing: "0.12em", textTransform: "uppercase",
    userSelect: "none",
  }),
  navIcon: { fontSize: 16, flexShrink: 0 },
  sidebarBottom: {
    padding: "14px 18px", borderTop: `1px solid ${SIDEBAR_BORDER}`,
    fontFamily: FONT_MONO, fontSize: 10, color: "#5a6478",
    letterSpacing: "0.12em", textTransform: "uppercase",
    display: "flex", alignItems: "center", gap: 8,
  },
  main: {
    flex: 1, display: "flex", flexDirection: "column",
    overflow: "hidden",
    background: COLORS.greenDeep,
  },
  topbar: {
    background: COLORS.white, borderBottom: `1px solid ${COLORS.cardBorder}`,
    padding: "0 24px", height: 60, display: "flex", alignItems: "center",
    justifyContent: "space-between", flexShrink: 0,
    color: COLORS.textPrimary,
  },
  pageTitle: {
    fontFamily: FONT_SANS, fontSize: 22, fontWeight: 800,
    letterSpacing: "-0.01em", color: COLORS.textPrimary,
  },
  content: {
    flex: 1, overflowY: "auto", padding: 24,
    background: COLORS.greenDeep,
    color: COLORS.textPrimary,
  },
  card: {
    background: COLORS.cardBg, border: `1px solid ${COLORS.cardBorder}`,
    borderRadius: 6, padding: 20,
  },
  kpiGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: 14, marginBottom: 24,
  },
  kpiCard: {
    background: COLORS.cardBg, border: `1px solid ${COLORS.cardBorder}`,
    borderRadius: 6, padding: "18px 20px",
    transition: "border-color 0.12s",
  },
  kpiLabel: {
    fontFamily: FONT_MONO, fontSize: 10, color: COLORS.orange,
    letterSpacing: "0.16em", textTransform: "uppercase",
    fontWeight: 600, marginBottom: 8,
  },
  kpiValue: {
    fontFamily: FONT_SANS, fontSize: 32, fontWeight: 800,
    color: COLORS.textPrimary, lineHeight: 1,
    letterSpacing: "-0.02em",
  },
  kpiSub: {
    fontFamily: FONT_MONO, fontSize: 10, color: COLORS.grayMid,
    letterSpacing: "0.06em", marginTop: 6,
  },
  btn: (variant = "primary") => {
    const base = {
      padding: "10px 18px", borderRadius: 4, cursor: "pointer",
      fontFamily: FONT_MONO, fontWeight: 600, fontSize: 11,
      letterSpacing: "0.12em", textTransform: "uppercase",
      transition: "background 0.12s, opacity 0.12s, color 0.12s",
    };
    if (variant === "primary") {
      return { ...base, background: COLORS.orange, color: COLORS.textPrimary, border: "none" };
    }
    if (variant === "ghost") {
      return { ...base, background: "transparent", color: COLORS.textSecondary, border: `1px solid ${COLORS.cardBorder}` };
    }
    // default / secondary — outline on light bg
    return { ...base, background: COLORS.white, color: COLORS.textPrimary, border: `1px solid ${COLORS.cardBorder}` };
  },
  badge: (color) => ({
    display: "inline-block", padding: "3px 10px", borderRadius: 2,
    fontFamily: FONT_MONO,
    fontSize: 9, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase",
    background: `${color}1a`, color: color, border: `1px solid ${color}55`,
  }),
  input: {
    background: COLORS.white, border: `1px solid ${COLORS.cardBorder}`,
    borderRadius: 4, padding: "9px 12px", color: COLORS.textPrimary,
    fontFamily: FONT_SANS, fontSize: 13, outline: "none", width: "100%",
    boxSizing: "border-box",
    transition: "border-color 0.12s",
  },
  select: {
    background: COLORS.white, border: `1px solid ${COLORS.cardBorder}`,
    borderRadius: 4, padding: "9px 12px", color: COLORS.textPrimary,
    fontFamily: FONT_SANS, fontSize: 13, outline: "none",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: FONT_SANS },
  th: {
    padding: "12px 14px", textAlign: "left",
    fontFamily: FONT_MONO, fontSize: 10,
    color: COLORS.grayMid, letterSpacing: "0.14em", textTransform: "uppercase",
    borderBottom: `1px solid ${COLORS.cardBorder}`, fontWeight: 600,
    background: COLORS.white,
  },
  td: {
    padding: "12px 14px", borderBottom: `1px solid ${COLORS.cardBorder}`,
    color: COLORS.textPrimary, verticalAlign: "middle",
    background: COLORS.white,
    fontFamily: FONT_SANS, fontSize: 13,
  },
  modal: {
    position: "fixed", inset: 0, background: "#0a0e1ad9", zIndex: 1000,
    display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    backdropFilter: "blur(2px)",
  },
  modalBox: {
    background: COLORS.white, border: `1px solid ${COLORS.cardBorder}`,
    borderRadius: 6, padding: 28, width: "100%", maxWidth: 560,
    maxHeight: "85vh", overflowY: "auto",
    color: COLORS.textPrimary,
    fontFamily: FONT_SANS,
    boxShadow: "0 20px 60px rgba(10,14,26,0.3)",
  },
};

// ─── COMPONENTS ──────────────────────────────────────────────────────────────

function Badge({ status }) {
  const color = STATUS_COLORS[status] || COLORS.grayMid;
  return <span style={S.badge(color)}>{status}</span>;
}

function Spinner() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
      <div style={{
        width: 32, height: 32, border: `3px solid ${COLORS.cardBorder}`,
        borderTop: `3px solid ${COLORS.orange}`, borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── COMMAND CENTER ───────────────────────────────────────────────────────────
const REPLY_STATES = ["No Reply", "Positive Reply", "Objection", "Not Interested"];
const REPLY_STATE_COLORS = {
  "No Reply":        "#6B7280",
  "Positive Reply":  "#10B981",
  "Objection":       "#F59E0B",
  "Not Interested":  "#EF4444",
};

// Per-vertical primary size metric (used for Min filter + card chips)
// eslint-disable-next-line no-unused-vars
const VERTICAL_SIZE = {
  RCFE:         { field: "facility_capacity", label: "Beds" },
  Hotel:        { field: "room_count",        label: "Rooms" },
  Hospital:     { field: "patient_capacity",  label: "Patients" },
  Campus:       { field: "enrollment",        label: "Enrollment" },
  PropertyMgmt: { field: "portfolio_size",    label: "Portfolio" },
};

// Per-vertical field chips shown on lead cards
function verticalFields(lead, vertical) {
  switch (vertical) {
    case "RCFE":
      return [
        lead.facility_capacity ? `${lead.facility_capacity} beds` : null,
        lead.facility_status ? `Status: ${lead.facility_status}` : null,
        lead.citation_count > 0 ? `${lead.citation_count} citation${lead.citation_count === 1 ? "" : "s"}` : null,
        lead.license_number ? `Lic #${lead.license_number}` : null,
      ];
    case "Hotel":
      return [
        lead.room_count ? `${lead.room_count} rooms` : null,
        lead.floor_count ? `${lead.floor_count} floors` : null,
        lead.star_rating ? `${lead.star_rating}★` : null,
        lead.brand_flag ? `Brand: ${lead.brand_flag}` : null,
      ];
    case "Hospital":
      return [
        lead.patient_capacity ? `${lead.patient_capacity} patients` : null,
        lead.floor_count ? `${lead.floor_count} floors` : null,
        lead.cms_rating ? `CMS ${lead.cms_rating}` : null,
        lead.is_va ? "🇺🇸 VA" : null,
      ];
    case "Campus":
      return [
        lead.enrollment ? `${fmtNum(lead.enrollment)} students` : null,
        lead.ncaa_division ? `NCAA ${lead.ncaa_division}` : null,
        lead.campus_type ? lead.campus_type : null,
        lead.multi_story_buildings ? `${lead.multi_story_buildings} multi-story` : null,
      ];
    case "PropertyMgmt":
      return [
        lead.portfolio_size ? `${fmtNum(lead.portfolio_size)} properties` : null,
        lead.unit_count ? `${fmtNum(lead.unit_count)} units` : null,
        lead.service_area ? `Area: ${lead.service_area}` : null,
        lead.pm_score ? `PM ${lead.pm_score}` : null,
        lead.has_hoa ? "HOA" : null,
      ];
    default:
      return [];
  }
}

function RepliesPanel({ onBadgeUpdate }) {
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewModal, setViewModal] = useState(null);

  useEffect(() => {
    api("/activities/replies?limit=50").then(r => { setReplies(Array.isArray(r) ? r : []); setLoading(false); });
  }, []);

  async function markRead(r) {
    await api(`/activities/replies/read?activity_id=${r.id}`, "POST");
    setReplies(prev => prev.map(x => x.id === r.id ? { ...x, read_at: new Date().toISOString() } : x));
    if (r.org_id) {
      setViewModal({ type: "org", data: { id: r.org_id, name: r.org_name } });
    } else if (r.contact_id) {
      setViewModal({ type: "contact", data: { id: r.contact_id, full_name: r.contact_name } });
    }
    onBadgeUpdate && onBadgeUpdate();
  }

  async function clearReply(r) {
    await api(`/activities/replies/clear?activity_id=${r.id}`, "POST");
    setReplies(prev => prev.filter(x => x.id !== r.id));
    onBadgeUpdate && onBadgeUpdate();
  }

  if (loading) return <Spinner />;
  if (replies.length === 0) return <div style={{ ...S.card, textAlign: "center", color: COLORS.grayMid, padding: 40 }}>No replies yet. When leads reply to Instantly sequences, they appear here.</div>;
  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {replies.map(r => {
          const isUnread = !r.read_at;
          return (
            <div key={r.id} style={{
              ...S.card,
              borderLeft: `4px solid ${isUnread ? COLORS.danger : COLORS.success}`,
              opacity: isUnread ? 1 : 0.85,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>
                    {isUnread && <span style={{ color: COLORS.danger, marginRight: 6 }}>NEW</span>}
                    {r.contact_name || r.subject || "Reply"}
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.grayMid }}>{r.org_name || "Unknown org"} · {r.contact_email || ""}</div>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0, alignItems: "center" }}>
                  <span style={{ fontSize: 11, color: COLORS.grayDark }}>{(r.created_at || "").replace("T", " ").slice(0, 16)}</span>
                </div>
              </div>
              <div style={{ marginTop: 8, fontSize: 13, background: COLORS.greenDeep, borderRadius: 5, padding: "10px 12px", whiteSpace: "pre-wrap" }}>
                {r.note || r.body || r.content || "(empty)"}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button onClick={() => markRead(r)}
                  style={{ ...S.btn("primary"), fontSize: 11, padding: "5px 14px" }}>
                  View Record
                </button>
                <button onClick={() => clearReply(r)}
                  style={{ ...S.btn("ghost"), fontSize: 11, padding: "5px 14px" }}>
                  Clear
                </button>
                {r.lead_id && <span style={{ fontSize: 11, color: COLORS.orange, marginLeft: "auto", alignSelf: "center" }}>Lead #{r.lead_id}</span>}
              </div>
            </div>
          );
        })}
      </div>
      {viewModal?.type === "org" && <OrgDetailModal org={viewModal.data} onClose={() => setViewModal(null)} />}
      {viewModal?.type === "contact" && <ContactDetailModal contact={viewModal.data} onClose={() => setViewModal(null)} />}
    </>
  );
}

function BookingsPanel() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewModal, setViewModal] = useState(null);

  useEffect(() => {
    api("/booking/upcoming?admin_email=drew@outpostprep.com").then(r => { setBookings(Array.isArray(r) ? r : []); setLoading(false); });
  }, []);

  async function viewBooking(b) {
    const search = await api(`/search?q=${encodeURIComponent(b.email)}`);
    if (search?.contacts?.length > 0) {
      setViewModal({ type: "contact", data: search.contacts[0] });
    } else if (search?.organizations?.length > 0) {
      setViewModal({ type: "org", data: search.organizations[0] });
    } else {
      setViewModal({ type: "create", data: b });
    }
  }

  const statusColor = { confirmed: COLORS.success, cancelled: COLORS.danger, rescheduled: "#F59E0B" };
  if (loading) return <Spinner />;
  if (bookings.length === 0) return <div style={{ ...S.card, textAlign: "center", color: COLORS.grayMid, padding: 40 }}>No upcoming bookings.</div>;
  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {bookings.map(b => {
          let timeStr = b.start_time || "";
          try { timeStr = new Date(b.start_time).toLocaleString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short" }); } catch {}
          return (
            <div key={b.id} style={{ ...S.card, borderLeft: `4px solid ${statusColor[b.status] || COLORS.cardBorder}` }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{b.name}</div>
                  <div style={{ fontSize: 12, color: COLORS.grayMid }}>{b.company || ""} · {b.email}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: statusColor[b.status] || COLORS.grayMid, textTransform: "uppercase" }}>{b.status}</span>
              </div>
              <div style={{ marginTop: 6, fontSize: 13, color: COLORS.orange }}>{timeStr}</div>
              {b.meet_url && <a href={b.meet_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: COLORS.orange, marginTop: 4, display: "block" }}>Join Google Meet</a>}
              {b.notes && <div style={{ fontSize: 12, color: COLORS.grayMid, marginTop: 4 }}>{b.notes}</div>}
              <div style={{ marginTop: 8 }}>
                <button onClick={() => viewBooking(b)} style={{ ...S.btn("primary"), fontSize: 11, padding: "5px 14px" }}>View Record</button>
              </div>
            </div>
          );
        })}
      </div>
      {viewModal?.type === "org" && <OrgDetailModal org={viewModal.data} onClose={() => setViewModal(null)} />}
      {viewModal?.type === "contact" && <ContactDetailModal contact={viewModal.data} onClose={() => setViewModal(null)} />}
      {viewModal?.type === "create" && (
        <CCModalShell title="Create Contact" onClose={() => setViewModal(null)}>
          <CreateContactFromBooking booking={viewModal.data} onClose={() => setViewModal(null)} />
        </CCModalShell>
      )}
    </>
  );
}

function CreateContactFromBooking({ booking, onClose }) {
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    const org = await api("/orgs", "POST", { name: booking.company || `${booking.name} (Company)`, vertical: "RCFE", source: "Booking" });
    if (org?.id) {
      await api("/contacts", "POST", {
        org_id: org.id, full_name: booking.name, first_name: booking.name.split(" ")[0],
        last_name: booking.name.split(" ").slice(1).join(" "),
        email_work: booking.email, source: "Booking",
      });
    }
    setSaving(false);
    onClose();
  }
  return (
    <div>
      <DetailGrid rows={[
        ["Name", booking.name], ["Email", booking.email],
        ["Company", booking.company || "—"], ["Meeting", booking.start_time || "—"],
      ]} />
      <div style={{ marginTop: 12, fontSize: 12, color: COLORS.grayMid }}>
        No CRM record found for this email. Create one?
      </div>
      <button onClick={save} disabled={saving} style={{ ...S.btn("primary"), marginTop: 10, width: "100%" }}>
        {saving ? "Creating..." : "Create Contact + Org"}
      </button>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// DREW — AI CHIEF OF STAFF (April 18, 2026)
// ═════════════════════════════════════════════════════════════════════════════

// Maps priority-card kind → default deploy action (Drew can switch via tabs)
function defaultActionForCard(card) {
  switch (card?.kind) {
    case "reply": return "email";
    case "overdue_task": return "followup";
    case "cold_deal": return "email";
    case "hot_lead": return "strategy";
    case "meeting_prep": return "followup";
    default: return "email";
  }
}

// Unique key for a priority card (used for the "deployed" set)
function cardKey(card) {
  return `${card?.kind}:${card?.contact_id || card?.deal_id || card?.lead_id || card?.task_id || card?.booking_id || card?.title || ""}`;
}

// eslint-disable-next-line no-unused-vars
function RuleOf100Block() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [starting, setStarting] = useState(false);

  const reload = useCallback(() => {
    api("/rule-of-100/today").then(r => { if (r) setData(r); });
  }, []);

  useEffect(() => {
    reload();
    // Refresh once per minute while the panel is open so logged actions reflect in real time.
    const iv = setInterval(reload, 60000);
    return () => clearInterval(iv);
  }, [reload]);

  if (!data) return null;

  const {
    actions_today = 0, daily_target = 100, streak_day = 0,
    pct_complete = 0, actions_breakdown = {}, days_remaining = 100,
    days_complete = 0,
  } = data;

  async function startRun() {
    if (!window.confirm("Start a new 100-day run from today?")) return;
    setStarting(true);
    await api("/rule-of-100/start", "POST", { daily_target: 100 });
    setStarting(false);
    reload();
  }

  const pct = Math.min(100, Math.round(pct_complete));
  const barColor = actions_today >= daily_target ? COLORS.success
                 : actions_today >= daily_target / 2 ? COLORS.orange
                 : COLORS.danger;

  return (
    <div style={{
      background: "#fff", border: `1px solid ${COLORS.cardBorder}`,
      borderLeft: `3px solid ${barColor}`, borderRadius: 5,
      padding: "10px 12px", marginBottom: 10, cursor: "pointer",
    }} onClick={() => setExpanded(e => !e)}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.grayMid, letterSpacing: 1, textTransform: "uppercase" }}>
          Rule of 100
        </div>
        <div style={{ fontSize: 10, color: COLORS.grayMid }}>
          {streak_day > 0 ? `Day ${streak_day} of 100` : "Not started"}
        </div>
      </div>
      <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ flex: 1, height: 10, background: `${barColor}22`, borderRadius: 5, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: barColor, transition: "width .3s" }} />
        </div>
        <div style={{ fontSize: 12, fontWeight: 800, color: barColor, minWidth: 64, textAlign: "right" }}>
          {actions_today}/{daily_target}
        </div>
      </div>
      <div style={{ marginTop: 6, display: "flex", gap: 10, fontSize: 11, color: COLORS.grayDark, flexWrap: "wrap" }}>
        <span>{"\uD83D\uDCE7"} {actions_breakdown.email || 0}</span>
        <span>{"\uD83D\uDCBC"} {actions_breakdown.linkedin || 0}</span>
        <span>{"\uD83D\uDCDE"} {actions_breakdown.call || 0}</span>
        <span>{"\uD83D\uDCC5"} {actions_breakdown.meeting || 0}</span>
        <span>{"\u2705"} {actions_breakdown.followup || 0}</span>
      </div>
      <div style={{ marginTop: 4, fontSize: 10, color: COLORS.grayMid }}>
        {days_complete} day{days_complete === 1 ? "" : "s"} complete {"\u00B7"} {days_remaining} day{days_remaining === 1 ? "" : "s"} remaining
      </div>
      {expanded && (
        <div style={{ marginTop: 8, padding: "8px 0 0 0", borderTop: `1px solid ${COLORS.cardBorder}22` }}
             onClick={e => e.stopPropagation()}>
          <div style={{ fontSize: 10, color: COLORS.grayMid, marginBottom: 6 }}>
            Primary actions that count: email sent, LinkedIn DM logged, call logged,
            meeting scheduled, follow-up task completed, DrewOS deploy sent.
          </div>
          {streak_day === 0 && (
            <button onClick={startRun} disabled={starting}
              style={{ ...S.btn("primary"), fontSize: 11, padding: "4px 10px" }}>
              {starting ? "Starting\u2026" : "Start 100-Day Run"}
            </button>
          )}
          {streak_day > 0 && (
            <button onClick={startRun} disabled={starting}
              style={{ ...S.btn("ghost"), fontSize: 11, padding: "4px 10px" }}>
              {starting ? "Restarting\u2026" : "Restart Run \u2192 Day 1"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function DrewPanel({ vertical, onOpenContact, onOpenLead, onOpenDeal }) {
  const [collapsed, setCollapsed] = useState(false);
  const [brief, setBrief] = useState(null);
  const [priorityQueue, setPriorityQueue] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatHistory, setChatHistory] = useState([]);    // [{role:'user'|'assistant', content}]
  const [chatInput, setChatInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [deployCard, setDeployCard] = useState(null);    // priority card passed into DeployModal
  const [deployedKeys, setDeployedKeys] = useState(() => new Set());  // cardKey set
  const [deployCount, setDeployCount] = useState(0);
  const [huntOpen, setHuntOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardSeed, setWizardSeed] = useState(null);
  const [modeOpen, setModeOpen] = useState(false);
  const [drewMode, setDrewMode] = useState(() => {
    try { return localStorage.getItem("drew_mode") || "review"; }
    catch { return "review"; }
  });
  const [rule100, setRule100] = useState(null);
  const [r100Settings, setR100Settings] = useState(null);
  const [openDeals, setOpenDeals] = useState([]);
  const [seasonAlert, setSeasonAlert] = useState(null);
  const [showAllPriority, setShowAllPriority] = useState(false);
  const chatEndRef = useRef(null);

  function updateMode(m) {
    setDrewMode(m);
    try { localStorage.setItem("drew_mode", m); } catch {}
    setModeOpen(false);
  }

  const loadBrief = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${CRM_URL}/drew/brief`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: "{}",
      });
      const j = await r.json();
      if (r.ok) {
        setBrief(j.brief || "");
        setPriorityQueue(j.priority_queue || []);
        setSummary(j.summary || null);
      } else {
        setBrief(`DrewOS is offline: ${j.detail || r.status}`);
      }
    } catch (e) {
      setBrief(`DrewOS is offline: ${String(e).slice(0, 100)}`);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadBrief(); }, [loadBrief]);

  useEffect(() => {
    const loadRule100 = () => api("/rule-of-100/today").then(r => { if (r) setRule100(r); });
    loadRule100();
    api("/rule-of-100/settings").then(r => { if (r) setR100Settings(r); });
    api("/deals?limit=30").then(r => {
      if (Array.isArray(r)) {
        const open = r.filter(d => d.stage !== "Closed Won" && d.stage !== "Closed Lost");
        setOpenDeals(open.slice(0, 8));
      }
    });
    (async () => {
      try {
        const res = await fetch(`${CRM_URL}/disasters/seasons?upcoming_window_days=30`);
        const j = await res.json();
        const active = (j.active || []);
        const upcoming = (j.upcoming || []);
        let alert = null;
        if (upcoming.length > 0 && upcoming[0].days_to_start <= 30) {
          alert = { ...upcoming[0], days: upcoming[0].days_to_start, isActive: false };
        } else if (active.length > 0) {
          alert = { ...active[0], days: 0, isActive: true };
        }
        if (alert) {
          const states = (alert.region_states || []).join(",");
          const verticals = (alert.verticals || []).join(",");
          const p = new URLSearchParams({ states, verticals });
          const lr = await fetch(`${CRM_URL}/disasters/leads-in-region?${p}`);
          const lj = await lr.json();
          alert.leads_count = lj.count || 0;
          setSeasonAlert(alert);
        }
      } catch {}
    })();
    const iv = setInterval(loadRule100, 60000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory, streaming]);

  async function sendChat(overrideMsg) {
    const msg = ((typeof overrideMsg === "string" ? overrideMsg : chatInput) || "").trim();
    if (!msg || streaming) return;
    // Trigger phrase: launch the Offer Wizard directly instead of chatting.
    const lower = msg.toLowerCase();
    if (/\bbuild (me )?an? offer\b|\bnew offer\b|\boffer wizard\b/.test(lower)) {
      setWizardSeed(msg);
      setWizardOpen(true);
      setChatInput("");
      return;
    }
    const newHistory = [...chatHistory, { role: "user", content: msg }];
    setChatHistory([...newHistory, { role: "assistant", content: "" }]);
    setChatInput("");
    setStreaming(true);
    try {
      // DrewOS Chief of Staff endpoint — falls back to /drew/chat if DrewOS unavailable.
      let resp = await fetch(`${CRM_URL}/drewos/ask-stream`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, history: newHistory.slice(0, -1) }),
      });
      if (resp.status === 404 || resp.status === 405) {
        resp = await fetch(`${CRM_URL}/drew/chat`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: msg, history: newHistory.slice(0, -1) }),
        });
      }
      if (!resp.ok || !resp.body) {
        const err = await resp.text().catch(() => "");
        setChatHistory(h => {
          const copy = [...h];
          copy[copy.length - 1] = { role: "assistant", content: `Error: ${err.slice(0, 200) || resp.status}` };
          return copy;
        });
        setStreaming(false);
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const frames = buf.split("\n\n");
        buf = frames.pop();
        for (const f of frames) {
          const line = f.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const d = JSON.parse(line.slice(5).trim());
            if (d.type === "text" && d.text) {
              setChatHistory(h => {
                const copy = [...h];
                const last = copy[copy.length - 1];
                copy[copy.length - 1] = { role: "assistant", content: (last?.content || "") + d.text };
                return copy;
              });
            } else if (d.type === "error") {
              setChatHistory(h => {
                const copy = [...h];
                copy[copy.length - 1] = { role: "assistant", content: `Error: ${d.error}` };
                return copy;
              });
            }
          } catch { /* ignore partial frames */ }
        }
      }
    } catch (e) {
      setChatHistory(h => {
        const copy = [...h];
        copy[copy.length - 1] = { role: "assistant", content: `Stream error: ${String(e).slice(0, 100)}` };
        return copy;
      });
    }
    setStreaming(false);
  }

  function handleDeploy(card) {
    // All cards open the unified DeployModal. Modal picks the right form from card.kind.
    setDeployCard(card);
  }

  function markDeployed(card) {
    const key = cardKey(card);
    setDeployedKeys(prev => { const s = new Set(prev); s.add(key); return s; });
    setDeployCount(n => n + 1);
  }

  async function snoozeCard(card) {
    if (card.task_id) {
      const newDue = new Date();
      newDue.setDate(newDue.getDate() + 3);
      await api(`/tasks/${card.task_id}`, "PATCH", { due_date: newDue.toISOString().slice(0, 10) });
    }
    setPriorityQueue(q => q.filter(c => c !== card));
  }

  async function doneCard(card) {
    if (card.task_id) {
      await api(`/tasks/${card.task_id}`, "PATCH", { status: "done" });
    }
    setPriorityQueue(q => q.filter(c => c !== card));
  }

  const rankColor = (r) =>
    r === 1 ? COLORS.danger :
    r === 2 ? "#F97316" :
    r === 3 ? COLORS.orange :
    COLORS.grayMid;

  const r100 = rule100 || {};
  const actionsToday = r100.actions_today || 0;
  const dailyTarget  = r100.daily_target || 100;
  const streakDay    = r100.streak_day || 0;
  const daysComplete = r100.days_complete || 0;
  const breakdown    = r100.actions_breakdown || {};
  const remaining    = Math.max(0, dailyTarget - actionsToday);
  const pctToday     = Math.min(100, Math.round((actionsToday / dailyTarget) * 100));
  // Hormozi framework (B-007): sends bar thresholds — green at/over 100,
  // yellow 70-99, red under 70.
  const r100Color = actionsToday >= dailyTarget ? COLORS.success
                  : actionsToday >= Math.round(dailyTarget * 0.7) ? COLORS.orange
                  : COLORS.danger;

  // Warm-actions counter (B-007): target band 20-30.
  const warmToday     = r100.warm_actions_today || 0;
  const warmBreakdown = r100.warm_breakdown || {};
  const warmBand      = r100.warm_target_band || [20, 30];
  const warmBandLow   = warmBand[0] ?? 20;
  const warmBandHigh  = warmBand[1] ?? 30;
  const warmPct       = Math.min(100, Math.round((warmToday / 25) * 100));
  // Green in the band (20-30), yellow below 20 OR above 30 (too many warms =
  // not enough prospecting).
  const warmColor =
    warmToday >= warmBandLow && warmToday <= warmBandHigh ? COLORS.success :
    COLORS.orange;

  const totalPipeline = openDeals.reduce((s, d) => s + (d.value || 0), 0);
  const visiblePriority = showAllPriority ? priorityQueue : priorityQueue.slice(0, 3);
  const rankIcon = (r) => r === 1 ? "\uD83D\uDEA8" : r === 2 ? "\u26A1" : "\uD83D\uDCCB";

  const quickActions = [
    { label: "Find Leads",        prompt: "Find me the top 20 uncontacted RCFE leads in California." },
    { label: "Write Post",        prompt: "Write a Facebook post tied to today's trending topic from my trends feed." },
    { label: "Research Contact",  prompt: "Research our most recent inbound contact and give me a quick briefing." },
    { label: "Build Sequence",    prompt: "Build a 4-email sequence for RCFE administrators." },
    { label: "Creative Outreach", prompt: "Give me 3 creative ways to reach new customers this week." },
  ];

  return (
    <>
    <div style={{
      background: "#fff", border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8,
      padding: 0, marginBottom: 14,
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      overflow: "hidden",
    }}>
      {/* Header — Drew full width */}
      <div style={{ padding: "16px 18px", borderBottom: `1px solid ${COLORS.cardBorder}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
            {/* DrewOS brand mark — highlighted-s on Signal square. Same shape as the
                favicon. Used as the avatar for the AI Chief of Staff (orchestrator). */}
            <div
              aria-label="DrewOS"
              style={{
                width: 44, height: 44, borderRadius: 6,
                background: COLORS.orange,
                color: COLORS.textPrimary,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "'JetBrains Mono', ui-monospace, Menlo, monospace",
                fontWeight: 700, fontSize: 26, letterSpacing: 0,
                flexShrink: 0, lineHeight: 1,
              }}
            >
              s
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 17, letterSpacing: 1 }}>
                DREWOS &mdash; AI CHIEF OF STAFF
              </div>
              <div style={{ fontSize: 11, color: COLORS.grayMid, marginTop: 2 }}>
                {streakDay > 0 ? `Day ${streakDay} of 100` : "Not started"}
                {" \u00B7 "}
                <span style={{ color: r100Color, fontWeight: 700 }}>
                  {actionsToday}/{dailyTarget} today
                </span>
                {" \u00B7 "}
                {daysComplete} day{daysComplete === 1 ? "" : "s"} complete
                {deployCount > 0 && (
                  <>
                    {" \u00B7 "}
                    <span style={{ color: COLORS.success, fontWeight: 700 }}>
                      {deployCount} deployed today
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0, flexWrap: "wrap" }}>
            <button onClick={() => setHuntOpen(true)} style={{ ...S.btn("ghost"), fontSize: 11, padding: "4px 10px", borderColor: COLORS.orange, color: COLORS.orange }}>
              Hunt
            </button>
            <button onClick={() => { setWizardSeed(null); setWizardOpen(true); }}
              style={{ ...S.btn("ghost"), fontSize: 11, padding: "4px 10px", borderColor: COLORS.orange, color: COLORS.orange }}>
              🎯 Offer Wizard
            </button>
            <div style={{ position: "relative" }}>
              <button onClick={() => setModeOpen(o => !o)}
                title={`DrewOS Mode: ${drewMode}`}
                style={{ ...S.btn("ghost"), fontSize: 11, padding: "4px 10px" }}>
                <Settings size={12} style={{ verticalAlign: "middle" }} />
                <span style={{ marginLeft: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  {drewMode === "review" ? "REVIEW" : drewMode === "semi_auto" ? "SEMI" : "AUTO"}
                </span>
              </button>
              {modeOpen && (
                <div style={{
                  position: "absolute", top: "100%", right: 0, marginTop: 4,
                  background: "#fff", border: `1px solid ${COLORS.cardBorder}`,
                  borderRadius: 6, padding: 10, zIndex: 1200, minWidth: 240,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.grayMid,
                                textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
                    DrewOS Mode
                  </div>
                  {[
                    { id: "review",    label: "Review",    hint: "Approve everything" },
                    { id: "semi_auto", label: "Semi-Auto", hint: "Auto tasks, approve emails" },
                    { id: "full_auto", label: "Full Auto", hint: "Deploy everything" },
                  ].map(m => (
                    <label key={m.id} style={{
                      display: "flex", alignItems: "flex-start", gap: 8, padding: "6px 4px",
                      cursor: "pointer", borderRadius: 4,
                      background: drewMode === m.id ? `${COLORS.orange}11` : "transparent",
                    }}>
                      <input type="radio" checked={drewMode === m.id}
                        onChange={() => updateMode(m.id)}
                        style={{ marginTop: 2 }} />
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>{m.label}</div>
                        <div style={{ fontSize: 10, color: COLORS.grayMid }}>{m.hint}</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setCollapsed(c => !c)} style={{ ...S.btn("ghost"), fontSize: 11, padding: "4px 10px" }}>
              {collapsed ? "Expand" : "Collapse"}
            </button>
          </div>
        </div>

        {/* Rule of 100 — dual counter (Hormozi framework, B-007) */}
        <div style={{ marginTop: 10 }} title={
          "Hormozi framework: two daily numbers.\n" +
          "• SENDS (100): outbound touches to targeted contacts (automation allowed).\n" +
          "• WARM ACTIONS (20-30): manual replies, calls, DMs on contacts with recent opens/clicks/replies.\n" +
          "Earn the 100 by burning through the warm queue each morning."
        }>
          {/* Sends row */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: COLORS.grayMid,
                          letterSpacing: 1, textTransform: "uppercase", minWidth: 48 }}>
              Sends
            </div>
            <div style={{ flex: 1, height: 10, background: `${r100Color}22`, borderRadius: 5, overflow: "hidden" }}>
              <div style={{ width: `${pctToday}%`, height: "100%", background: r100Color, transition: "width .3s" }} />
            </div>
            <div style={{ fontSize: 11, fontWeight: 800, color: r100Color, minWidth: 72, textAlign: "right" }}>
              {actionsToday}/{dailyTarget}
            </div>
          </div>
          {/* Warm-actions row */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6 }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: COLORS.grayMid,
                          letterSpacing: 1, textTransform: "uppercase", minWidth: 48 }}>
              Warm
            </div>
            <div style={{ flex: 1, height: 7, background: `${warmColor}22`, borderRadius: 4, overflow: "hidden", position: "relative" }}>
              <div style={{ width: `${warmPct}%`, height: "100%", background: warmColor, transition: "width .3s" }} />
              {/* band markers */}
              <div style={{ position: "absolute", left: `${(warmBandLow / 30) * 100}%`, top: 0, bottom: 0, width: 1, background: `${COLORS.grayMid}88` }} />
              <div style={{ position: "absolute", left: `${(warmBandHigh / 30) * 100}%`, top: 0, bottom: 0, width: 1, background: `${COLORS.grayMid}88` }} />
            </div>
            <div style={{ fontSize: 11, fontWeight: 800, color: warmColor, minWidth: 72, textAlign: "right" }}>
              {warmToday}/{warmBandLow}-{warmBandHigh}
            </div>
          </div>
        </div>
      </div>

      {!collapsed && (
        <>
        {/* Morning brief */}
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${COLORS.cardBorder}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: COLORS.grayMid, letterSpacing: 1.2, textTransform: "uppercase" }}>
              Morning Brief
            </div>
            <button onClick={loadBrief} disabled={loading}
              style={{ ...S.btn("ghost"), fontSize: 11, padding: "4px 10px", opacity: loading ? 0.5 : 1 }}>
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
          {brief ? (
            <div style={{
              background: `${COLORS.greenDark}0A`, borderLeft: `3px solid ${COLORS.orange}`,
              padding: "10px 14px", borderRadius: 4, fontSize: 12.5, lineHeight: 1.6,
              whiteSpace: "pre-wrap", color: COLORS.greenDark,
              maxHeight: 220, overflowY: "auto",
            }}>{brief}</div>
          ) : (
            <div style={{ fontSize: 12, color: COLORS.grayMid }}>No brief yet.</div>
          )}
        </div>

        {/* Priority Queue + Chat */}
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0,
          borderBottom: `1px solid ${COLORS.cardBorder}`,
        }}>
          {/* Priority Queue — left */}
          <div style={{ padding: "14px 18px", borderRight: `1px solid ${COLORS.cardBorder}` }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: COLORS.grayMid, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 8 }}>
              Priority Queue ({priorityQueue.length})
            </div>
            {priorityQueue.length === 0 ? (
              <div style={{ fontSize: 12, color: COLORS.grayMid }}>No priorities — you're caught up.</div>
            ) : (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {[...visiblePriority]
                    .sort((a, b) => (deployedKeys.has(cardKey(a)) ? 1 : 0) - (deployedKeys.has(cardKey(b)) ? 1 : 0))
                    .map((card, i) => {
                      const deployed = deployedKeys.has(cardKey(card));
                      return (
                    <div key={i} style={{
                      border: `1px solid ${deployed ? COLORS.success : COLORS.cardBorder}`,
                      borderLeft: `4px solid ${deployed ? COLORS.success : rankColor(card.rank)}`,
                      borderRadius: 6, padding: 10,
                      background: deployed ? `${COLORS.success}0A` : "#fff",
                      opacity: deployed ? 0.7 : 1,
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 800,
                                       color: deployed ? COLORS.success : rankColor(card.rank),
                                       letterSpacing: 0.5 }}>
                          {deployed ? "DEPLOYED \u2713" : `${rankIcon(card.rank)} P${card.rank}`}
                        </span>
                        <span style={{ fontSize: 9, color: COLORS.grayMid, textTransform: "uppercase" }}>{card.kind}</span>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>
                        {card.title}
                      </div>
                      <div style={{ fontSize: 11, color: COLORS.grayMid, marginBottom: 4 }}>{card.action}</div>
                      <div style={{ fontSize: 11, color: COLORS.grayDark, marginBottom: 6, lineHeight: 1.4 }}>
                        {card.reason}
                      </div>
                      {card.value_at_stake != null && (
                        <div style={{ fontSize: 11, color: COLORS.success, fontWeight: 700, marginBottom: 6 }}>
                          ${Math.round(card.value_at_stake).toLocaleString()} at stake
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 4 }}>
                        {card.kind === "warm_followup" ? (
                          <>
                            <button
                              onClick={async () => {
                                await api("/rule-of-100/log-warm", "POST", {
                                  action_type: "reply_handled",
                                  contact_id: card.contact_id,
                                  lead_id: card.lead_id,
                                  source: "priority_queue",
                                });
                                markDeployed(card);
                                api("/rule-of-100/today").then(r => { if (r) setRule100(r); });
                              }}
                              disabled={deployed}
                              style={{ ...S.btn("primary"), fontSize: 10, padding: "4px 10px", flex: 1, opacity: deployed ? 0.5 : 1 }}>
                              Reply
                            </button>
                            {card.warm_channels && card.warm_channels.includes("Call") && (
                              <button
                                onClick={async () => {
                                  await api("/rule-of-100/log-warm", "POST", {
                                    action_type: "warm_phone_call",
                                    contact_id: card.contact_id,
                                    lead_id: card.lead_id,
                                    source: "priority_queue",
                                  });
                                  markDeployed(card);
                                  api("/rule-of-100/today").then(r => { if (r) setRule100(r); });
                                }}
                                disabled={deployed}
                                style={{ ...S.btn("ghost"), fontSize: 10, padding: "4px 8px", opacity: deployed ? 0.5 : 1 }}>
                                Call
                              </button>
                            )}
                            <button
                              onClick={async () => {
                                await api("/rule-of-100/log-warm", "POST", {
                                  action_type: "high_priority_linkedin_dm",
                                  contact_id: card.contact_id,
                                  lead_id: card.lead_id,
                                  source: "priority_queue",
                                });
                                markDeployed(card);
                                api("/rule-of-100/today").then(r => { if (r) setRule100(r); });
                              }}
                              disabled={deployed}
                              style={{ ...S.btn("ghost"), fontSize: 10, padding: "4px 8px", opacity: deployed ? 0.5 : 1 }}>
                              DM
                            </button>
                            <button onClick={() => snoozeCard(card)} style={{ ...S.btn("ghost"), fontSize: 10, padding: "4px 8px" }}>Snooze</button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => handleDeploy(card)} disabled={deployed}
                              style={{ ...S.btn("primary"), fontSize: 10, padding: "4px 10px", flex: 1, opacity: deployed ? 0.5 : 1 }}>
                              {deployed ? "Deployed" : "Deploy"}
                            </button>
                            <button onClick={() => snoozeCard(card)} style={{ ...S.btn("ghost"), fontSize: 10, padding: "4px 8px" }}>Snooze</button>
                            <button onClick={() => doneCard(card)} style={{ ...S.btn("ghost"), fontSize: 10, padding: "4px 8px" }}>Done</button>
                          </>
                        )}
                      </div>
                    </div>
                      );
                    })}
                </div>
                {priorityQueue.length > 3 && (
                  <button onClick={() => setShowAllPriority(v => !v)}
                    style={{ ...S.btn("ghost"), fontSize: 11, padding: "5px 10px", marginTop: 8, width: "100%" }}>
                    {showAllPriority ? "Show Less" : `View All (${priorityQueue.length})`}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Chat — right */}
          <div style={{ padding: "14px 18px", display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: COLORS.grayMid, letterSpacing: 1.2, textTransform: "uppercase" }}>
              DrewOS Chat
            </div>
            <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.greenDark, letterSpacing: 0.8, textTransform: "uppercase", marginTop: -4, marginBottom: 2 }}>
              Chief of Staff
            </div>
            {chatHistory.length > 0 ? (
              <div style={{
                background: `${COLORS.cardBorder}22`, borderRadius: 6, padding: 10,
                maxHeight: 240, overflowY: "auto", minHeight: 80,
              }}>
                {chatHistory.map((m, i) => (
                  <div key={i} style={{
                    marginBottom: 8, padding: "6px 10px", borderRadius: 5,
                    background: m.role === "user" ? `${COLORS.orange}18` : "#fff",
                    borderLeft: `3px solid ${m.role === "user" ? COLORS.orange : COLORS.greenDark}`,
                    fontSize: 12, lineHeight: 1.5, whiteSpace: "pre-wrap",
                  }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.grayMid, textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 }}>
                      {m.role === "user" ? "You" : "DrewOS"}
                      {i === chatHistory.length - 1 && m.role === "assistant" && streaming && (
                        <span style={{ marginLeft: 6, opacity: 0.6 }}>▊</span>
                      )}
                    </div>
                    {m.content}
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
            ) : (
              <div style={{
                background: `${COLORS.cardBorder}11`, borderRadius: 6, padding: 12,
                fontSize: 12, color: COLORS.grayMid, minHeight: 80,
                border: `1px dashed ${COLORS.cardBorder}`,
              }}>
                Ask DrewOS anything — drafting, research, strategy, outreach.
              </div>
            )}
            <div style={{ display: "flex", gap: 6 }}>
              <input
                type="text"
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                placeholder={streaming ? "DrewOS is typing..." : "Ask DrewOS anything"}
                disabled={streaming}
                style={{ ...S.input, flex: 1, fontSize: 12 }}
              />
              <button onClick={() => sendChat()} disabled={streaming || !chatInput.trim()}
                style={{ ...S.btn("primary"), fontSize: 12, opacity: streaming || !chatInput.trim() ? 0.5 : 1 }}>
                {streaming ? "..." : "Send"}
              </button>
            </div>
            <div style={{ fontSize: 10, fontWeight: 800, color: COLORS.grayMid, letterSpacing: 1.2, textTransform: "uppercase", marginTop: 4 }}>
              Quick Actions
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {quickActions.map((qa, i) => (
                <button key={i} onClick={() => sendChat(qa.prompt)} disabled={streaming}
                  style={{ ...S.btn("ghost"), fontSize: 11, padding: "5px 10px",
                           opacity: streaming ? 0.5 : 1 }}>
                  {qa.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Metrics Row */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0,
          borderBottom: `1px solid ${COLORS.cardBorder}`,
        }}>
          {[
            { label: "Pipeline",    value: `$${Math.round(summary?.open_pipeline_value || totalPipeline).toLocaleString()}`, sub: `${summary?.open_deal_count || openDeals.length} open` },
            { label: "Replies",     value: `${summary?.reply_count_7d || 0}`,           sub: "last 7 days" },
            { label: "Meetings",    value: `${summary?.upcoming_meeting_count || 0}`,   sub: "this week" },
            { label: "Rule of 100", value: `${actionsToday}/${dailyTarget}`,            sub: `Day ${streakDay || 0}` },
          ].map((m, i) => (
            <div key={i} style={{
              padding: "12px 16px",
              borderRight: i < 3 ? `1px solid ${COLORS.cardBorder}` : "none",
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.grayMid, textTransform: "uppercase", letterSpacing: 1 }}>
                {m.label}
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: COLORS.textPrimary, marginTop: 4 }}>
                {m.value}
              </div>
              <div style={{ fontSize: 11, color: COLORS.grayMid, marginTop: 2 }}>
                {m.sub}
              </div>
            </div>
          ))}
        </div>

        {/* Pipeline at a glance */}
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${COLORS.cardBorder}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: COLORS.grayMid, letterSpacing: 1.2, textTransform: "uppercase" }}>
              Pipeline at a Glance
            </div>
            <div style={{ fontSize: 11, color: COLORS.grayMid }}>
              ${Math.round(summary?.open_pipeline_value || totalPipeline).toLocaleString()}
              {" \u00B7 "}{summary?.open_deal_count || openDeals.length} active
              {" \u00B7 "}{summary?.overdue_task_count || 0} overdue
              {" \u00B7 "}{summary?.reply_count_7d || 0} reply
            </div>
          </div>
          {openDeals.length > 0 ? (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {openDeals.map(d => {
                const stageColor = DEAL_STAGE_COLORS[d.stage] || COLORS.orange;
                return (
                  <div key={d.id} onClick={() => onOpenDeal && onOpenDeal(d.id)}
                    style={{
                      border: `1px solid ${COLORS.cardBorder}`,
                      borderLeft: `4px solid ${stageColor}`,
                      borderRadius: 6, padding: "6px 10px",
                      background: "#fff", cursor: onOpenDeal ? "pointer" : "default",
                      minWidth: 160, flex: "0 0 auto",
                    }}>
                    <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 2,
                                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                  maxWidth: 220 }}>
                      {d.title || d.org_name || `Deal #${d.id}`}
                    </div>
                    <div style={{ fontSize: 11, color: COLORS.grayMid, display: "flex", gap: 6, alignItems: "center" }}>
                      <span style={{ color: COLORS.success, fontWeight: 700 }}>
                        ${Math.round(d.value || 0).toLocaleString()}
                      </span>
                      <span>&middot;</span>
                      <span style={{ color: stageColor, fontWeight: 600 }}>{d.stage || "—"}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: COLORS.grayMid }}>No open deals.</div>
          )}

          {seasonAlert && (
            <div style={{
              marginTop: 10, padding: "10px 12px",
              background: "#FFF7ED", border: `1px solid ${COLORS.orange}55`,
              borderLeft: `4px solid ${COLORS.orange}`, borderRadius: 6,
              display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
              flexWrap: "wrap",
            }}>
              <div style={{ fontSize: 12, color: COLORS.greenDark, lineHeight: 1.5 }}>
                <span style={{ fontSize: 15, marginRight: 6 }}>{"\uD83C\uDF00"}</span>
                <strong>{seasonAlert.label}</strong>
                {seasonAlert.isActive
                  ? " — active now"
                  : ` in ${seasonAlert.days} day${seasonAlert.days === 1 ? "" : "s"}`}
                {seasonAlert.leads_count != null && (
                  <> — {seasonAlert.leads_count.toLocaleString()} leads in {seasonAlert.regions}</>
                )}
              </div>
              <button
                onClick={() => sendChat(`Deploy a regional outreach sequence for ${seasonAlert.label} targeting ${(seasonAlert.verticals || []).join(", ")} leads in ${seasonAlert.regions}.`)}
                disabled={streaming}
                style={{ ...S.btn("primary"), fontSize: 11, padding: "5px 10px", flexShrink: 0 }}>
                Deploy Regional
              </button>
            </div>
          )}
        </div>

        {/* Rule of 100 detail row — Hormozi dual-counter breakdown (B-007) */}
        <div style={{ padding: "12px 18px", display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <div style={{ display: "flex", gap: 14, fontSize: 12, color: COLORS.grayDark, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.grayMid, letterSpacing: 1, textTransform: "uppercase" }}>
                Sends ({actionsToday})
              </span>
              <span>{"\uD83D\uDCE7"} Email: <strong>{breakdown.email || 0}</strong></span>
              <span>{"\uD83D\uDCBC"} LinkedIn: <strong>{breakdown.linkedin || 0}</strong></span>
              <span>{"\uD83D\uDCDE"} Call: <strong>{breakdown.call || 0}</strong></span>
              <span>{"\uD83D\uDCC5"} Meeting: <strong>{breakdown.meeting || 0}</strong></span>
              <span>{"\u2705"} Follow-up: <strong>{breakdown.followup || 0}</strong></span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {r100Settings && (
                <label title="When OFF, automated Instantly sends still log activities but don't tick the 100 counter."
                  style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11,
                           color: COLORS.grayMid, cursor: "pointer" }}>
                  <input type="checkbox"
                    checked={!!r100Settings.count_automated_sends}
                    onChange={async (e) => {
                      const next = e.target.checked;
                      setR100Settings(s => ({ ...(s || {}), count_automated_sends: next }));
                      const r = await api("/rule-of-100/settings", "POST", {
                        count_automated_sends: next,
                      });
                      if (r) setR100Settings(r);
                    }} />
                  Count automated sends
                </label>
              )}
              <div style={{ fontSize: 12, color: remaining === 0 ? COLORS.success : COLORS.grayMid, fontWeight: 700 }}>
                {remaining === 0 ? "100 sends complete \u2713" : `${remaining} sends remaining today`}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <div style={{ display: "flex", gap: 14, fontSize: 12, color: COLORS.grayDark, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.grayMid, letterSpacing: 1, textTransform: "uppercase" }}>
                Warm ({warmToday})
              </span>
              <span>{"\u21A9"} Replies handled: <strong>{warmBreakdown.reply_handled || 0}</strong></span>
              <span>{"\uD83D\uDCDE"} Warm calls: <strong>{warmBreakdown.warm_phone_call || 0}</strong></span>
              <span>{"\uD83D\uDD17"} HP LinkedIn DM: <strong>{warmBreakdown.high_priority_linkedin_dm || 0}</strong></span>
              <span>{"\u270F"} Sequence edits: <strong>{warmBreakdown.sequence_copy_edit || 0}</strong></span>
            </div>
            <div style={{
              fontSize: 12, fontWeight: 700,
              color: warmToday >= warmBandLow && warmToday <= warmBandHigh ? COLORS.success : COLORS.grayMid,
            }}>
              {warmToday < warmBandLow
                ? `${warmBandLow - warmToday} more to hit band`
                : warmToday > warmBandHigh
                  ? "over band — shift to prospecting"
                  : "in band \u2713"}
            </div>
          </div>
        </div>
        </>
      )}
    </div>

    {/* Unified Deploy modal — slides in from right, routes by action type */}
    {deployCard && (
      <DeployModal
        card={deployCard}
        drewMode={drewMode}
        onClose={() => setDeployCard(null)}
        onDeployed={(card) => { markDeployed(card); setDeployCard(null); }}
      />
    )}

    {/* Hunt modal */}
    {huntOpen && (
      <DrewHuntModal vertical={vertical} onClose={() => setHuntOpen(false)} />
    )}

    {/* Offer Wizard modal */}
    {wizardOpen && (
      <OfferWizardModal
        initialMessage={wizardSeed}
        onClose={() => { setWizardOpen(false); setWizardSeed(null); }}
      />
    )}
    </>
  );
}

// ── DEPLOY MODAL ─────────────────────────────────────────────────────────────
// Slide-in-from-right panel. Picks initial form from card.kind; Drew can switch
// action via the 5 tabs at the top. Handles email / linkedin / followup / call / strategy.
function DeployModal({ card, drewMode, onClose, onDeployed }) {
  const initial = defaultActionForCard(card);
  const [action, setAction] = useState(initial);
  const [visible, setVisible] = useState(false);
  const [contact, setContact] = useState(null);
  const [conflicts, setConflicts] = useState(null);  // {conflicts: [], blocked: bool} or null (loading)
  // Per-tab content cache. First open of a tab generates; switching back reuses.
  const [cache, setCache] = useState({
    email: null, linkedin: null, followup: null, call: null, strategy: null, champion: null,
  });
  // Bumping regenKeys[action] re-triggers the form's generator for that tab only.
  const [regenKeys, setRegenKeys] = useState({
    email: 0, linkedin: 0, followup: 0, call: 0, strategy: 0, champion: 0,
  });
  const handleRegenerate = () => {
    setCache(prev => ({ ...prev, [action]: null }));
    setRegenKeys(prev => ({ ...prev, [action]: (prev[action] || 0) + 1 }));
  };
  const setCacheFor = (key, value) => setCache(prev => ({ ...prev, [key]: value }));

  // Drew's morning-brief recommendation for this card. Tracked separately so
  // the user can dismiss the banner ("generate fresh"), which clears the
  // recommendation context + re-fetches the email with no steering text.
  const initialRec = [card.action, card.reason].filter(Boolean).join(" — ");
  const [recommendation, setRecommendation] = useState(initialRec || "");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const dismissRecommendation = () => {
    setRecommendation("");
    setDetailsOpen(false);
    // Blow away the email cache + bump regen so the form re-fetches generic
    setCache(prev => ({ ...prev, email: null }));
    setRegenKeys(prev => ({ ...prev, email: (prev.email || 0) + 1 }));
  };

  useEffect(() => { const t = setTimeout(() => setVisible(true), 10); return () => clearTimeout(t); }, []);

  function handleClose() {
    setVisible(false);
    setTimeout(onClose, 260);
  }

  // Load contact + recent activity → compute client-side conflict flags
  useEffect(() => {
    const cid = card.contact_id || (card.record_type === "contact" ? card.record_id : null);
    if (!cid) { setConflicts({ conflicts: [], blocked: false }); return; }
    (async () => {
      const c = await api(`/contacts/${cid}`);
      if (c) setContact(c);
      const acts = await api(`/activities?contact_id=${cid}&limit=10`);
      const list = Array.isArray(acts) ? acts : [];
      const out = []; let blocked = false;
      if (c?.unsubscribed) { blocked = true; out.push("Contact has unsubscribed — hard block."); }
      const now = Date.now();
      for (const a of list) {
        const ts = a.created_at ? Date.parse(a.created_at) : 0;
        const days = ts ? (now - ts) / 86400000 : 999;
        if (days < 7 && (a.type === "Email" || a.type === "email")) {
          out.push(`Email sent ${a.created_at} — let engagement cycle finish.`); break;
        }
        if (days < 7 && a.type === "Reply") {
          out.push(`Contact replied ${a.created_at} — respond to that thread.`); break;
        }
      }
      setConflicts({ conflicts: out, blocked });
    })();
  }, [card.contact_id, card.record_id, card.record_type]);

  // Champion tab only shows when: deal kind + stage in Qualified/Demo Scheduled/Quote Sent
  // + inactive >7 days + has a primary contact
  const dealStage = card.stage || card.deal_stage || "";
  const champStage = ["Qualified", "Demo Scheduled", "Quote Sent"].includes(dealStage);
  const daysInactive = card.days_inactive ?? card.inactive_days ?? 0;
  const hasPrimaryContact = !!(card.contact_id || contact?.id);
  const showChampion = (card.kind === "deal" || card.deal_id)
    && champStage && daysInactive > 7 && hasPrimaryContact;

  const TABS = [
    { id: "email",    label: "Email",     icon: <Mail size={13} /> },
    { id: "linkedin", label: "LinkedIn",  icon: <Send size={13} /> },
    { id: "followup", label: "Follow Up", icon: <CalendarDays size={13} /> },
    { id: "call",     label: "Call",      icon: <Phone size={13} /> },
    { id: "strategy", label: "Strategy",  icon: <Sparkles size={13} /> },
    ...(showChampion ? [{ id: "champion", label: "Champion", icon: <Flame size={13} /> }] : []),
  ];

  const panelStyle = {
    position: "fixed", top: 0, right: 0, bottom: 0,
    width: "calc(100vw - 60px)", maxWidth: 720,
    background: "#fff", boxShadow: "-4px 0 16px rgba(0,0,0,0.15)",
    zIndex: 1100, overflowY: "auto",
    transform: visible ? "translateX(0)" : "translateX(100%)",
    transition: "transform 300ms cubic-bezier(0.4, 0.0, 0.2, 1)",
    willChange: "transform",
    display: "flex", flexDirection: "column",
  };
  const backdrop = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)",
    zIndex: 1050, opacity: visible ? 1 : 0,
    transition: "opacity 260ms ease-out", cursor: "pointer",
  };

  return (
    <>
      <div style={backdrop} onClick={handleClose} />
      <div style={panelStyle} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${COLORS.cardBorder}`,
                      display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.grayMid,
                          textTransform: "uppercase", letterSpacing: 1 }}>
              Deploy · P{card.rank}
            </div>
            <div style={{ fontSize: 15, fontWeight: 800, marginTop: 2 }}>{card.title}</div>
            <div style={{ fontSize: 11, color: COLORS.grayMid, marginTop: 2 }}>{card.reason}</div>
          </div>
          <button onClick={handleClose} style={{ ...S.btn("ghost"), padding: "4px 10px" }}>✕</button>
        </div>

        {/* Drew Mode badge */}
        <div style={{ padding: "6px 18px", borderBottom: `1px solid ${COLORS.cardBorder}`,
                      fontSize: 10, color: COLORS.grayMid, display: "flex", justifyContent: "space-between" }}>
          <span>DrewOS Mode: <strong style={{ color: COLORS.greenDark }}>{drewMode.replace("_", "-").toUpperCase()}</strong></span>
          <ConflictBadge conflicts={conflicts} />
        </div>

        {/* Drew's recommendation banner — only shows when deploy launched
            from a priority card with a specific action/reason. Dismiss clears
            the steering and regenerates the email generically. */}
        {recommendation && (
          <div style={{
            borderBottom: `1px solid ${COLORS.cardBorder}`,
            background: "#FEF3C7", color: "#78350F",
            borderLeft: `4px solid #F59E0B`,
          }}>
            {/* Clickable header — toggles the details panel */}
            <div
              onClick={() => setDetailsOpen(o => !o)}
              style={{
                padding: "10px 14px", display: "flex", gap: 10,
                alignItems: "flex-start", cursor: Object.keys(card.details || {}).length ? "pointer" : "default",
                userSelect: "none",
              }}
            >
              <div style={{ fontSize: 14, flexShrink: 0, lineHeight: 1.4 }}>⚡</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
                              textTransform: "uppercase", marginBottom: 2 }}>
                  DrewOS recommends
                </div>
                <div style={{ fontSize: 12, lineHeight: 1.45 }}>{recommendation}</div>
                {Object.keys(card.details || {}).length > 0 && (
                  <div style={{ fontSize: 10, color: "#92400E", marginTop: 4, fontWeight: 600 }}>
                    {detailsOpen ? "▲ Hide context" : "▼ See full context"}
                  </div>
                )}
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); dismissRecommendation(); }}
                title="Dismiss — generate fresh"
                style={{ ...S.btn("ghost"), padding: "2px 8px", color: "#78350F",
                         borderColor: "transparent", flexShrink: 0 }}>
                ✕
              </button>
            </div>

            {/* Expandable details panel */}
            {detailsOpen && Object.keys(card.details || {}).length > 0 && (
              <div style={{
                padding: "8px 14px 12px 40px",
                borderTop: "1px solid #F59E0B66",
                fontSize: 11, color: "#78350F",
                background: "#FFF8E6",
              }}>
                <table style={{ borderCollapse: "collapse", width: "100%" }}>
                  <tbody>
                    {Object.entries(card.details).map(([k, v]) => (
                      <tr key={k}>
                        <td style={{ padding: "3px 10px 3px 0", fontWeight: 700,
                                     color: "#92400E", verticalAlign: "top",
                                     whiteSpace: "nowrap", width: 110 }}>{k}:</td>
                        <td style={{ padding: "3px 0", wordBreak: "break-word" }}>
                          {typeof v === "string" && v.startsWith("http")
                            ? <a href={v} target="_blank" rel="noreferrer" style={{ color: "#C2410C" }}>{v}</a>
                            : (v || "—")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: `1px solid ${COLORS.cardBorder}`, background: `${COLORS.cardBorder}11` }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setAction(t.id)}
              style={{
                flex: 1, padding: "10px 6px", background: "transparent",
                border: "none", cursor: "pointer",
                borderBottom: action === t.id ? `2px solid ${COLORS.orange}` : "2px solid transparent",
                color: action === t.id ? COLORS.orange : COLORS.grayMid,
                fontSize: 11, fontWeight: action === t.id ? 700 : 500,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
              }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Form body (scrollable) */}
        <div style={{ flex: 1, overflowY: "auto", padding: 18, position: "relative" }}>
          <div style={{ position: "sticky", top: -18, marginTop: -18, marginBottom: 8,
                        padding: "6px 0", background: "#fff", zIndex: 5,
                        display: "flex", justifyContent: "flex-end" }}>
            <button onClick={handleRegenerate}
              title="Regenerate content for this tab (current tab only)"
              style={{ ...S.btn("ghost"), fontSize: 11, padding: "4px 10px" }}>
              {"\u21BA"} Regenerate
            </button>
          </div>
          {action === "email" && (
            <EmailDeployForm card={card} contact={contact} conflicts={conflicts}
              cached={cache.email} onCache={v => setCacheFor("email", v)}
              regenKey={regenKeys.email}
              recommendation={recommendation}
              onDeployed={() => onDeployed(card)} onCancel={handleClose} />
          )}
          {action === "linkedin" && (
            <LinkedInDeployForm card={card} contact={contact}
              cached={cache.linkedin} onCache={v => setCacheFor("linkedin", v)}
              regenKey={regenKeys.linkedin}
              onDeployed={() => onDeployed(card)} onCancel={handleClose} />
          )}
          {action === "followup" && (
            <FollowUpDeployForm card={card} contact={contact}
              cached={cache.followup} onCache={v => setCacheFor("followup", v)}
              regenKey={regenKeys.followup}
              onDeployed={() => onDeployed(card)} onCancel={handleClose} />
          )}
          {action === "call" && (
            <CallDeployForm card={card} contact={contact}
              cached={cache.call} onCache={v => setCacheFor("call", v)}
              regenKey={regenKeys.call}
              onDeployed={() => onDeployed(card)} onCancel={handleClose} />
          )}
          {action === "strategy" && (
            <StrategyDeployForm card={card} contact={contact}
              cached={cache.strategy} onCache={v => setCacheFor("strategy", v)}
              regenKey={regenKeys.strategy}
              onDeployed={() => onDeployed(card)} onCancel={handleClose} />
          )}
          {action === "champion" && (
            <ChampionDeployForm card={card} contact={contact}
              cached={cache.champion} onCache={v => setCacheFor("champion", v)}
              regenKey={regenKeys.champion}
              onDeployed={() => onDeployed(card)} onCancel={handleClose} />
          )}
        </div>
      </div>
    </>
  );
}

// ── Champion Email deploy form ──────────────────────────────────────────────
function ChampionDeployForm({ card, contact, cached, onCache, regenKey, onDeployed, onCancel }) {
  const [draft, setDraft] = useState(cached);
  const [loading, setLoading] = useState(!cached);
  const [sending, setSending] = useState(false);
  const [sentOk, setSentOk] = useState(false);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [editingOnePager, setEditingOnePager] = useState(false);

  const dealId = card.deal_id || (card.kind === "deal" ? card.record_id : null);
  const contactId = card.contact_id || contact?.id;
  const firstName = contact?.first_name || (contact?.full_name || card.title || "there").split(" ")[0];

  useEffect(() => {
    if (!dealId) { setErr("No deal_id on this card."); setLoading(false); return; }
    if (cached) { setDraft(cached); setLoading(false); return; }
    (async () => {
      setLoading(true); setErr(null);
      try {
        const r = await fetch(`${CRM_URL}/drew/champion-email`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ deal_id: dealId, contact_id: contactId }),
        });
        const j = await r.json();
        if (r.ok) { setDraft(j); if (onCache) onCache(j); }
        else setErr(j.detail || `HTTP ${r.status}`);
      } catch (e) { setErr(String(e)); }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, regenKey]);

  async function sendIt() {
    if (!draft?.contact_email) { setErr("No champion email on file."); return; }
    setSending(true); setErr(null);
    try {
      const r = await fetch(`${CRM_URL}/send-email`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: draft.contact_email, subject: draft.email_subject,
          body: draft.email_body, is_html: true,
          contact_id: draft.contact_id,
        }),
      });
      if (r.ok || r.status === 202) {
        setSentOk(true);
        setTimeout(() => onDeployed(), 900);
      } else {
        const j = await r.json().catch(() => ({}));
        setErr(j.detail || `HTTP ${r.status}`);
      }
    } catch (e) { setErr(String(e)); }
    setSending(false);
  }

  function copyOnePager() {
    try { navigator.clipboard?.writeText(draft?.one_pager || ""); } catch {}
    setCopied(true); setTimeout(() => setCopied(false), 1800);
  }

  function downloadPdf() {
    // Browser-side: open a clean window with the rendered markdown and
    // trigger print-to-PDF. Zero new dependencies; user chooses "Save as PDF"
    // in the native print dialog.
    const w = window.open("", "_blank");
    if (!w) return;
    const company = draft?.company || "Outpost";
    const safe = (draft?.one_pager || "")
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    // Tiny markdown → HTML rendering (H1/H2 + bullets + hr)
    const html = safe
      .replace(/^# (.+)$/gm, "<h1>$1</h1>")
      .replace(/^## (.+)$/gm, "<h2>$1</h2>")
      .replace(/^- (.+)$/gm, "<li>$1</li>")
      .replace(/(<li>[\s\S]+?<\/li>)(?!\s*<li>)/g, "<ul>$1</ul>")
      .replace(/^---$/gm, "<hr/>")
      .replace(/\n\n/g, "</p><p>");
    w.document.title = `${company}_Outpost_OnePager`;
    w.document.body.innerHTML = `
      <style>
        body { font-family: -apple-system, Arial, sans-serif; max-width: 720px;
               margin: 40px auto; padding: 0 24px; line-height: 1.55; color: #1f2937; }
        h1 { color: #c26a1a; border-bottom: 2px solid #c26a1a; padding-bottom: 6px; }
        h2 { color: #1f2937; margin-top: 24px; }
        hr { border: none; border-top: 1px solid #e5e7eb; margin: 20px 0; }
        ul { padding-left: 24px; } li { margin-bottom: 4px; }
      </style>
      <p>${html}</p>
    `;
    w.document.close();
    setTimeout(() => w.print(), 200);
  }

  if (!dealId) return <div style={{ color: COLORS.danger }}>No deal linked to this card.</div>;
  if (loading) return <div style={{ textAlign: "center", color: COLORS.grayMid, padding: 30 }}>DrewOS is building the champion package…</div>;
  if (err && !draft) return <div style={{ background: `${COLORS.danger}22`, color: COLORS.danger, padding: 8, borderRadius: 5, fontSize: 12 }}>{err}</div>;
  if (!draft) return null;

  return (
    <>
      {sentOk && <div style={{ background: `${COLORS.success}22`, color: COLORS.success, padding: 10, borderRadius: 5, fontSize: 12, marginBottom: 10, fontWeight: 700 }}>✅ Champion email sent to {draft.contact_email}</div>}
      {err && <div style={{ background: `${COLORS.danger}22`, color: COLORS.danger, padding: 8, borderRadius: 5, fontSize: 12, marginBottom: 10 }}>{err}</div>}
      <div style={{
        background: "#FFF8F3", border: `1px solid ${COLORS.orange}55`,
        borderLeft: `4px solid ${COLORS.orange}`, borderRadius: 6,
        padding: "10px 12px", fontSize: 12, marginBottom: 12,
      }}>
        <div style={{ fontWeight: 700, marginBottom: 2 }}>
          🏆 Help {draft.first_name || firstName} sell internally
        </div>
        DrewOS generated two things:
        <ol style={{ margin: "4px 0 0 18px", padding: 0 }}>
          <li>A one-pager {draft.first_name || firstName} can forward to the VP / GM / Finance team.</li>
          <li>An email to {draft.first_name || firstName} offering a 15-minute 3-way call.</li>
        </ol>
      </div>

      {/* ONE-PAGER */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase", letterSpacing: 0.5 }}>
            One-Pager {editingOnePager ? "(editing)" : "(preview)"}
          </div>
          <button onClick={() => setEditingOnePager(e => !e)}
            style={{ ...S.btn("ghost"), fontSize: 10, padding: "2px 8px" }}>
            {editingOnePager ? "Preview" : "Edit"}
          </button>
        </div>
        {editingOnePager ? (
          <textarea
            value={draft.one_pager || ""}
            onChange={e => setDraft(d => ({ ...d, one_pager: e.target.value }))}
            style={{ ...S.input, width: "100%", height: 280, fontFamily: "monospace", fontSize: 11 }}
          />
        ) : (
          <div style={{
            background: COLORS.white, border: `1px solid ${COLORS.cardBorder}`,
            borderRadius: 6, padding: 14, fontSize: 12, whiteSpace: "pre-wrap",
            maxHeight: 320, overflowY: "auto",
          }}>{draft.one_pager || "(empty)"}</div>
        )}
        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
          <button onClick={copyOnePager} style={{ ...S.btn("ghost"), fontSize: 11, padding: "3px 10px" }}>
            {copied ? "Copied!" : "📋 Copy One-Pager"}
          </button>
          <button onClick={downloadPdf} style={{ ...S.btn("ghost"), fontSize: 11, padding: "3px 10px" }}>
            📄 Download PDF
          </button>
        </div>
      </div>

      {/* EMAIL */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
          Email to {draft.first_name || firstName} ({draft.contact_email})
        </div>
        <input value={draft.email_subject || ""}
          onChange={e => setDraft(d => ({ ...d, email_subject: e.target.value }))}
          placeholder="Subject"
          style={{ ...S.input, fontSize: 13, marginBottom: 6 }} />
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
          <button onClick={() => setEditingEmail(e => !e)}
            style={{ ...S.btn("ghost"), fontSize: 10, padding: "2px 8px" }}>
            {editingEmail ? "Show Preview" : "Edit Text"}
          </button>
        </div>
        {editingEmail ? (
          <textarea
            value={draft.email_body_plain || ""}
            onChange={e => setDraft(d => ({ ...d, email_body_plain: e.target.value }))}
            onBlur={async () => {
              // Re-wrap with Drew HTML shell via champion endpoint? No — just
              // leave text edits for now; user can send plaintext. Simpler:
              // send whatever they typed.
              setEditingEmail(false);
            }}
            style={{ ...S.input, height: 220, fontFamily: "inherit", resize: "vertical" }}
          />
        ) : (
          <iframe title="Champion email preview"
            srcDoc={draft.email_body || ""}
            sandbox=""
            style={{ width: "100%", height: 320, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 6, background: "#fff" }}
          />
        )}
      </div>

      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={sendIt} disabled={sending || sentOk || !draft.contact_email}
          style={{ ...S.btn("primary"), fontSize: 12, flex: 1,
                   opacity: (sending || sentOk || !draft.contact_email) ? 0.5 : 1 }}>
          {sending ? "Sending…" : sentOk ? "Sent ✓" : `Send to ${draft.first_name || firstName}`}
        </button>
        <button onClick={onCancel} style={{ ...S.btn("ghost"), fontSize: 12 }}>Discard</button>
      </div>
    </>
  );
}

function ConflictBadge({ conflicts }) {
  if (!conflicts) return <span style={{ color: COLORS.grayMid }}>Checking conflicts…</span>;
  if (conflicts.blocked) {
    return <span style={{ color: COLORS.danger, fontWeight: 700 }}>🚫 Blocked — unsubscribed</span>;
  }
  if (conflicts.conflicts.length > 0) {
    return <span style={{ color: "#92400E", fontWeight: 700 }}>⚠ {conflicts.conflicts.length} conflict{conflicts.conflicts.length > 1 ? "s" : ""}</span>;
  }
  return <span style={{ color: COLORS.success, fontWeight: 700 }}>✅ Clear to contact</span>;
}

// ── Email deploy form ───────────────────────────────────────────────────────
function EmailDeployForm({ card, contact, conflicts, cached, onCache, regenKey, recommendation, onDeployed, onCancel }) {
  const [draft, setDraft] = useState(cached);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentOk, setSentOk] = useState(false);
  const [err, setErr] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  // User can override the body plaintext; backend reassembles the HTML with
  // the signature+logo each regenerate. Local edits re-render preview live.
  const [editingBody, setEditingBody] = useState(false);
  // Attachments: [{filename, content_base64, content_type, size}]
  const [attachments, setAttachments] = useState([]);
  const attachFileRef = useRef(null);

  const contactId = card.contact_id || (card.record_type === "contact" ? card.record_id : null);
  const hasRec = !!(recommendation && recommendation.trim());

  // Detect the conflict indicating a recent reply → flip to reply_mode
  const wantsReplyMode = (conflicts?.conflicts || []).some(c => /replied/i.test(c));

  // Explicit generator — callable from the effect (no-rec path) OR from the
  // "Draft Recommended Email" / "Generate Fresh Email" buttons (rec path).
  const generate = useCallback(async (useRec) => {
    if (!contactId) { setErr("No contact_id on this priority card."); return; }
    setLoading(true); setErr(null);
    try {
      const r = await fetch(`${CRM_URL}/drew/deploy-email`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contact_id: contactId,
          auto_generate: true,
          reply_mode: wantsReplyMode,
          recommendation: useRec ? (recommendation || "").trim() : "",
        }),
      });
      const j = await r.json();
      if (r.ok) { setDraft(j); if (onCache) onCache(j); }
      else setErr(j.detail || `HTTP ${r.status}`);
    } catch (e) { setErr(String(e)); }
    setLoading(false);
  }, [contactId, wantsReplyMode, recommendation, onCache]);

  useEffect(() => {
    if (!contactId) { setErr("No contact_id on this priority card."); return; }
    if (cached) { setDraft(cached); setLoading(false); return; }
    // When Drew has a specific recommendation, DO NOT auto-generate. Wait
    // for the user to pick "Draft Recommended Email" or "Generate Fresh".
    // When no recommendation, auto-generate immediately as before.
    if (hasRec) { setLoading(false); return; }
    generate(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, regenKey, wantsReplyMode, hasRec]);

  function onAttachPick(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    files.forEach(f => {
      if (f.size > 10 * 1024 * 1024) {
        setErr(`${f.name} exceeds the 10MB attachment limit.`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result || "";
        const b64 = typeof result === "string" && result.startsWith("data:")
          ? result.split(",", 2)[1] : "";
        setAttachments(prev => [...prev, {
          filename: f.name, content_base64: b64,
          content_type: f.type || null, size: f.size,
        }]);
      };
      reader.readAsDataURL(f);
    });
  }

  function removeAttachment(i) {
    setAttachments(prev => prev.filter((_, idx) => idx !== i));
  }

  async function sendIt() {
    if (!draft) return;
    if (draft.blocked || conflicts?.blocked) { setErr("Blocked — contact unsubscribed."); return; }
    setSending(true); setErr(null);
    try {
      const r = await fetch(`${CRM_URL}/send-email`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: draft.to,
          subject: draft.subject,
          body: draft.body,
          is_html: true,
          contact_id: draft.contact_id,
          org_id: draft.org_id,
          thread_id: draft.thread_id || null,
          reply_to_message_id: draft.reply_to_message_id || null,
          attachments: attachments.length ? attachments.map(a => ({
            filename: a.filename, content_base64: a.content_base64,
            content_type: a.content_type,
          })) : undefined,
        }),
      });
      if (r.ok || r.status === 202) {
        setSentOk(true);
        setTimeout(() => onDeployed(), 900);
      } else {
        const j = await r.json().catch(() => ({}));
        setErr(j.detail || `HTTP ${r.status}`);
      }
    } catch (e) { setErr(String(e)); }
    setSending(false);
  }

  if (!contactId) return <div style={{ color: COLORS.danger }}>No contact linked to this card.</div>;
  if (loading) return <div style={{ textAlign: "center", color: COLORS.grayMid, padding: 30 }}>DrewOS is composing...</div>;
  if (err && !draft) return <div style={{ background: `${COLORS.danger}22`, color: COLORS.danger, padding: 8, borderRadius: 5, fontSize: 12 }}>{err}</div>;

  // Recommendation-present, no draft yet → show explicit two-button choice
  // instead of auto-generating. This is the PRIMARY path when the deploy
  // was launched from a priority card with specific morning-brief guidance.
  if (!draft && hasRec) {
    return (
      <div style={{ maxWidth: 520, margin: "30px auto 0", textAlign: "center" }}>
        <div style={{
          background: "#FFF8E6", border: "1px solid #F59E0B",
          borderLeft: "4px solid #F59E0B", borderRadius: 8,
          padding: "18px 20px", textAlign: "left",
        }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#78350F", marginBottom: 8 }}>
            ⚡ DrewOS has a recommendation
          </div>
          <div style={{
            fontSize: 13, color: "#78350F", fontStyle: "italic",
            background: "#FEF3C7", padding: "10px 12px", borderRadius: 6,
            lineHeight: 1.5, marginBottom: 16,
          }}>
            "{recommendation}"
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <button onClick={() => generate(true)}
              style={{
                ...S.btn("primary"), fontSize: 13, padding: "10px 14px",
                fontWeight: 700,
              }}>
              ⚡ Draft Recommended Email
            </button>
            <div style={{ textAlign: "center", fontSize: 10, color: COLORS.grayMid }}>or</div>
            <button onClick={() => generate(false)}
              style={{ ...S.btn("ghost"), fontSize: 12, padding: "8px 14px" }}>
              Generate Fresh Email instead
            </button>
          </div>
          <div style={{ fontSize: 10, color: "#92400E", marginTop: 12, lineHeight: 1.4 }}>
            <strong>Draft Recommended</strong> uses DrewOS's guidance as the primary prompt —
            targeted follow-up, not a generic cold intro.<br/>
            <strong>Generate Fresh</strong> ignores the recommendation and uses Agent 6
            knowledge as if this were a new outreach.
          </div>
        </div>
      </div>
    );
  }

  if (!draft) return null;

  const totalBlocked = draft.blocked || conflicts?.blocked;
  const replyMode = !!draft.reply_mode;
  // When in reply mode, the "replied — respond to that thread" note is the
  // primary action signal, not a conflict. Demote it from the warning list.
  const allConflicts = [...(conflicts?.conflicts || []), ...(draft.conflicts || [])]
    .filter(c => !replyMode || !/replied/i.test(c));
  const uniq = [...new Set(allConflicts)];
  const sendLabel = sending ? "Sending…" : sentOk ? (replyMode ? "Reply Sent ✓" : "Sent ✓")
                    : totalBlocked ? "Blocked" : replyMode ? "Send Reply" : "Send Now";

  return (
    <>
      {sentOk && <div style={{ background: `${COLORS.success}22`, color: COLORS.success, padding: 10, borderRadius: 5, fontSize: 12, marginBottom: 10, fontWeight: 700 }}>✅ Sent. Tracking active. Activity logged.</div>}
      {err && <div style={{ background: `${COLORS.danger}22`, color: COLORS.danger, padding: 8, borderRadius: 5, fontSize: 12, marginBottom: 10 }}>{err}</div>}
      {replyMode && (
        <div style={{ background: "#ECFDF5", color: "#065F46", border: "1px solid #10B981",
                      padding: 10, borderRadius: 5, fontSize: 12, marginBottom: 10 }}>
          <div style={{ fontWeight: 700, marginBottom: 2 }}>↩️ Reply mode</div>
          Responding on the existing Gmail thread. No new cold intro — acknowledging their
          reply and moving toward a call.
        </div>
      )}
      {uniq.length > 0 && (
        <div style={{ background: totalBlocked ? `${COLORS.danger}22` : "#FEF3C7",
                      color: totalBlocked ? COLORS.danger : "#92400E",
                      padding: 10, borderRadius: 5, fontSize: 12, marginBottom: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            {totalBlocked ? "🛑 BLOCKED" : "⚠ Conflicts detected"}
          </div>
          {uniq.map((c, i) => <div key={i}>• {c}</div>)}
        </div>
      )}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>To</div>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{draft.to}</div>
      </div>
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>
          Subject {replyMode && <span style={{ color: COLORS.grayMid, textTransform: "none", fontWeight: 400 }}>(thread reply — inherited)</span>}
        </div>
        <input value={draft.subject || ""} onChange={e => setDraft(d => ({ ...d, subject: e.target.value }))}
          disabled={replyMode}
          style={{ ...S.input, fontSize: 13, opacity: replyMode ? 0.7 : 1 }} />
      </div>
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>
            Body {editingBody ? "(plaintext — preview rebuilds on save)" : "(rendered preview)"}
          </div>
          <button onClick={() => setEditingBody(e => !e)}
            style={{ ...S.btn("ghost"), fontSize: 10, padding: "2px 8px" }}>
            {editingBody ? "Show Preview" : "Edit Text"}
          </button>
        </div>
        {editingBody ? (
          <textarea
            value={draft.body_plain || ""}
            onChange={e => setDraft(d => ({ ...d, body_plain: e.target.value }))}
            onBlur={async () => {
              // Re-render HTML via backend so signature/logo/footer stay correct.
              try {
                const r = await fetch(`${CRM_URL}/drew/deploy-email`, {
                  method: "POST", headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    contact_id: contactId, auto_generate: false,
                    subject: draft.subject, body: draft.body_plain,
                    reply_mode: replyMode,
                  }),
                });
                const j = await r.json();
                if (r.ok) setDraft(d => ({ ...d, body: j.body, body_plain: j.body_plain }));
              } catch (_) {}
              setEditingBody(false);
            }}
            style={{ ...S.input, height: 260, fontFamily: "inherit", resize: "vertical" }}
          />
        ) : (
          <iframe
            title="Email preview"
            srcDoc={draft.body || ""}
            sandbox=""
            style={{
              width: "100%", height: 340, border: `1px solid ${COLORS.cardBorder}`,
              borderRadius: 6, background: "#FFFFFF",
            }}
          />
        )}
      </div>
      <div style={{ fontSize: 10, color: COLORS.grayMid, marginBottom: 10 }}>
        ✅ Tracking pixel injected on send &nbsp;·&nbsp; ✅ Signature + logo rendered inline &nbsp;·&nbsp; ✅ Unsubscribe blurb appended
        {replyMode && draft.thread_id && <> &nbsp;·&nbsp; 🔗 Thread: <code style={{ fontSize: 10 }}>{draft.thread_id.slice(0, 10)}…</code></>}
      </div>

      {/* Attachments — files ride along with /send-email as base64 parts */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <button onClick={() => attachFileRef.current?.click()}
            style={{ ...S.btn("ghost"), fontSize: 11, padding: "4px 10px" }}>
            {"\ud83d\udcce"} Attach File
          </button>
          <input ref={attachFileRef} type="file" multiple hidden onChange={onAttachPick} />
          <span style={{ fontSize: 10, color: COLORS.grayMid }}>
            PDF / DOC / image — up to 10MB each
          </span>
        </div>
        {attachments.length > 0 && (
          <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
            {attachments.map((a, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6,
                fontSize: 11, background: "#F5F5F0", padding: "4px 8px", borderRadius: 4 }}>
                <span>{"\ud83d\udcce"}</span>
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {a.filename}
                </span>
                <span style={{ fontSize: 10, color: COLORS.grayMid }}>
                  {(a.size / 1024).toFixed(0)} KB
                </span>
                <button onClick={() => removeAttachment(i)}
                  style={{ background: "none", border: "none", cursor: "pointer",
                    fontSize: 11, color: COLORS.danger, padding: 0 }}>{"\u00d7"}</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={sendIt} disabled={sending || sentOk || totalBlocked}
          style={{ ...S.btn("primary"), fontSize: 12, flex: 1,
                   opacity: (sending || sentOk || totalBlocked) ? 0.5 : 1 }}>
          {sendLabel}
        </button>
        <button onClick={() => setPreviewOpen(true)} disabled={!draft || sentOk}
          title="Preview as recipient sees it (does not send)"
          style={{ ...S.btn("ghost"), fontSize: 12, opacity: (!draft || sentOk) ? 0.5 : 1 }}>
          👁 Preview
        </button>
        <button onClick={onCancel} style={{ ...S.btn("ghost"), fontSize: 12 }}>Discard</button>
      </div>
      {previewOpen && (
        <EmailPreviewModal
          to={draft.to}
          subject={draft.subject}
          body={draft.body}
          sending={sending}
          sendLabel={sendLabel}
          sendDisabled={sending || sentOk || totalBlocked}
          onSend={() => { setPreviewOpen(false); sendIt(); }}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </>
  );
}

// ── Email Preview Modal — shows what the recipient will see ─────────────────
// Pure visual; does not send. Renders body with line breaks, image embeds
// data: URIs inline, appends Outpost signature block + logo.
function EmailPreviewModal({ to, subject, body, sending, sendLabel, sendDisabled, onSend, onClose }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), 10); return () => clearTimeout(t); }, []);
  function handleClose() { setVisible(false); setTimeout(onClose, 200); }

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") handleClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detect whether the body is already HTML (Drew deploy-email may return HTML
  // strings embedding <img>/<p>). Otherwise render plaintext with line breaks.
  const looksHtml = typeof body === "string" && /<\w+[^>]*>/.test(body);

  const backdrop = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)",
    zIndex: 1300, opacity: visible ? 1 : 0,
    transition: "opacity 200ms ease-out",
    display: "flex", alignItems: "flex-start", justifyContent: "center",
    padding: "40px 20px", overflowY: "auto",
  };
  const paper = {
    background: "#fff", borderRadius: 8, maxWidth: 680, width: "100%",
    boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
    transform: visible ? "translateY(0)" : "translateY(-8px)",
    opacity: visible ? 1 : 0,
    transition: "transform 220ms ease-out, opacity 220ms ease-out",
    display: "flex", flexDirection: "column", overflow: "hidden",
  };
  const topbar = {
    background: COLORS.greenDark, color: "#fff",
    padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center",
  };
  const headerRow = {
    borderBottom: `1px solid ${COLORS.cardBorder}`,
    padding: "10px 24px", fontSize: 12, color: COLORS.greenDark,
    display: "grid", gridTemplateColumns: "60px 1fr", gap: 6, rowGap: 4,
  };
  const labelStyle = { color: COLORS.grayMid, fontWeight: 600, textTransform: "uppercase", fontSize: 10, letterSpacing: 0.5 };

  return (
    <div style={backdrop} onClick={handleClose}>
      <div style={paper} onClick={e => e.stopPropagation()}>
        <div style={topbar}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>
            👁 Email Preview · As recipient sees it
          </div>
          <button onClick={handleClose}
            style={{ background: "transparent", color: "#fff", border: "none", fontSize: 18, cursor: "pointer", padding: "0 4px" }}>
            ✕
          </button>
        </div>

        {/* Email headers */}
        <div style={headerRow}>
          <span style={labelStyle}>From</span>
          <span>Drew Jennings &lt;drew@outpostprep.com&gt;</span>
          <span style={labelStyle}>To</span>
          <span>{to || "—"}</span>
          <span style={labelStyle}>Subject</span>
          <span style={{ fontWeight: 700 }}>{subject || "(no subject)"}</span>
        </div>

        {/* Email body — scrollable */}
        <div style={{ padding: "22px 24px", fontSize: 14, lineHeight: 1.6,
                      color: "#1E2019", maxHeight: "55vh", overflowY: "auto",
                      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
          {looksHtml ? (
            <div dangerouslySetInnerHTML={{ __html: body || "" }} />
          ) : (
            <div style={{ whiteSpace: "pre-wrap" }}>{body || ""}</div>
          )}
          {/* Signature block — only append if body didn't already include it */}
          {!/Drew Jennings/i.test(body || "") && (
            <div style={{ marginTop: 24, paddingTop: 12, borderTop: `1px solid ${COLORS.cardBorder}`, fontSize: 13 }}>
              <div style={{ fontWeight: 700 }}>Drew Jennings</div>
              <div style={{ color: COLORS.grayMid, fontSize: 12 }}>
                Founder & CEO · USMC Ret. · Outpost Preparedness · SDVOSB
              </div>
              <div style={{ color: COLORS.orange, fontSize: 12, marginTop: 2 }}>
                drew@outpostprep.com · outpostprep.com
              </div>
              <img src={`${CRM_URL}/marketing/assets/signature_logo.png`}
                alt="Outpost Preparedness"
                onError={e => { e.currentTarget.style.display = "none"; }}
                style={{ maxWidth: 180, marginTop: 10, display: "block" }} />
            </div>
          )}
        </div>

        {/* Notice bar */}
        <div style={{ background: `${COLORS.cardBorder}22`, padding: "8px 24px",
                      fontSize: 10, color: COLORS.grayMid, textAlign: "center", fontStyle: "italic" }}>
          Preview only — tracking pixel injected on actual send. No email sent yet.
        </div>

        {/* Footer buttons */}
        <div style={{ padding: "12px 18px", borderTop: `1px solid ${COLORS.cardBorder}`,
                      display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={handleClose}
            style={{ ...S.btn("ghost"), fontSize: 12 }}>
            Close Preview
          </button>
          <button onClick={onSend} disabled={sendDisabled}
            style={{ ...S.btn("primary"), fontSize: 12, minWidth: 110,
                     opacity: sendDisabled ? 0.5 : 1 }}>
            {sending ? "Sending…" : sendLabel.replace("Sent ✓", "Send Now")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── LinkedIn deploy form ────────────────────────────────────────────────────
function LinkedInDeployForm({ card, contact, cached, onCache, regenKey, onDeployed, onCancel }) {
  const [message, setMessage] = useState(cached || "");
  const [loading, setLoading] = useState(!cached);
  const [sent, setSent] = useState(false);

  const fullName = contact?.full_name || card.title || "";
  const firstName = titleCase((contact?.first_name || fullName.split(" ")[0] || "there").trim());

  useEffect(() => {
    if (cached) { setMessage(cached); setLoading(false); return; }
    (async () => {
      setLoading(true);
      const msg = await fetchAgent6Dm(contact?.vertical || card.vertical || "RCFE", firstName);
      setMessage(msg || "");
      if (onCache) onCache(msg || "");
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact?.vertical, card.vertical, firstName, regenKey]);

  async function copyAndOpen() {
    try { await navigator.clipboard.writeText(message); } catch {}
    const q = encodeURIComponent(fullName);
    window.open(`https://www.linkedin.com/search/results/people/?keywords=${q}`, "_blank", "noopener");
    // Log LinkedIn outreach activity
    if (contact?.id) {
      await api("/activities", "POST", {
        type: "linkedin_outreach",
        description: `LinkedIn DM queued for ${fullName}`,
        subject: message,
        contact_id: contact.id,
      });
    }
    setSent(true);
    setTimeout(() => onDeployed(), 700);
  }

  if (loading) return <div style={{ textAlign: "center", color: COLORS.grayMid, padding: 30 }}>Loading DM…</div>;
  return (
    <>
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>To</div>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{fullName || "?"}</div>
      </div>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Message</div>
        <textarea value={message} onChange={e => setMessage(e.target.value.slice(0, 200))}
          style={{ ...S.input, height: 140, fontFamily: "inherit" }} />
        <div style={{ fontSize: 10, color: message.length > 200 ? COLORS.danger : COLORS.grayMid, textAlign: "right", marginTop: 2 }}>
          {message.length}/200
        </div>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={copyAndOpen} disabled={sent || !message}
          style={{ ...S.btn("primary"), fontSize: 12, flex: 1, opacity: sent ? 0.5 : 1 }}>
          {sent ? "Opened ✓" : "Copy & Open LinkedIn"}
        </button>
        <button onClick={onCancel} style={{ ...S.btn("ghost"), fontSize: 12 }}>Discard</button>
      </div>
    </>
  );
}

// ── Follow Up deploy form ───────────────────────────────────────────────────
function FollowUpDeployForm({ card, contact, cached, onCache, regenKey, onDeployed, onCancel }) {
  const defaultDesc = card.kind === "overdue_task" ? (card.title || "Follow up") :
                      `Follow up with ${contact?.full_name || card.title}`;
  const defaultDue = () => { const d = new Date(); d.setDate(d.getDate() + 3); return d.toISOString().slice(0, 10); };
  const [desc, setDesc] = useState(cached?.desc ?? defaultDesc);
  const [due, setDue] = useState(cached?.due ?? defaultDue());
  const [priority, setPriority] = useState(cached?.priority ?? "Medium");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState(null);

  // Cache current form values so switching tabs doesn't reset them
  useEffect(() => {
    if (onCache) onCache({ desc, due, priority });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desc, due, priority]);

  // Regenerate → reset to defaults
  useEffect(() => {
    if (regenKey === undefined || regenKey === 0) return;
    setDesc(defaultDesc);
    setDue(defaultDue());
    setPriority("Medium");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regenKey]);

  async function createTask(alsoGcal = false) {
    setSaving(true); setErr(null);
    try {
      const recordType = card.kind === "overdue_task" ? card.record_type
                        : contact?.id ? "contact"
                        : card.deal_id ? "deal"
                        : card.lead_id ? "lead" : null;
      const recordId = card.kind === "overdue_task" ? card.record_id
                      : recordType === "contact" ? contact?.id
                      : card.deal_id || card.lead_id;
      const body = {
        description: desc, due_date: due, priority,
        record_type: recordType, record_id: recordId, type: "task",
      };
      // If this was an existing overdue task, update it instead of creating a new one
      let r;
      if (card.task_id) {
        r = await api(`/tasks/${card.task_id}`, "PATCH", { description: desc, due_date: due, priority, status: "open" });
      } else {
        r = await api("/tasks", "POST", body);
      }
      if (!r) throw new Error("Task save failed");
      if (alsoGcal) {
        // Best-effort GCal event — silently continue if endpoint missing
        await fetch(`${CRM_URL}/calendar/events`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            summary: desc, start: `${due}T09:00:00`, end: `${due}T09:30:00`,
            description: `Follow-up queued by DrewOS for ${contact?.full_name || card.title}`,
          }),
        }).catch(() => {});
      }
      setSaved(true);
      setTimeout(() => onDeployed(), 700);
    } catch (e) { setErr(String(e).slice(0, 200)); }
    setSaving(false);
  }

  return (
    <>
      {err && <div style={{ background: `${COLORS.danger}22`, color: COLORS.danger, padding: 8, borderRadius: 5, fontSize: 12, marginBottom: 10 }}>{err}</div>}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Task</div>
        <input value={desc} onChange={e => setDesc(e.target.value)} style={{ ...S.input, fontSize: 13 }} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Due date</div>
          <input type="date" value={due} onChange={e => setDue(e.target.value)} style={{ ...S.input, fontSize: 13 }} />
        </div>
        <div>
          <div style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Priority</div>
          <select value={priority} onChange={e => setPriority(e.target.value)} style={{ ...S.select, fontSize: 13 }}>
            <option>High</option><option>Medium</option><option>Low</option>
          </select>
        </div>
      </div>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Contact</div>
        <div style={{ fontSize: 13 }}>{contact?.full_name || card.title || "—"}</div>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={() => createTask(false)} disabled={saving || saved}
          style={{ ...S.btn("primary"), fontSize: 12, opacity: (saving || saved) ? 0.5 : 1 }}>
          {saved ? "Saved ✓" : saving ? "Saving..." : "Create Task"}
        </button>
        <button onClick={() => createTask(true)} disabled={saving || saved}
          style={{ ...S.btn("ghost"), fontSize: 12 }}>
          Add to GCal
        </button>
        <button onClick={onCancel} style={{ ...S.btn("ghost"), fontSize: 12 }}>Discard</button>
      </div>
    </>
  );
}

// ── Call deploy form (talk track generated via /drew/chat stream) ───────────
function CallDeployForm({ card, contact, cached, onCache, regenKey, onDeployed, onCancel }) {
  const [script, setScript] = useState(cached || "");
  const [loading, setLoading] = useState(!cached);
  const [outcome, setOutcome] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const phone = contact?.phone_mobile || contact?.phone_work || contact?.phone_home;
  const name = contact?.full_name || card.title;

  useEffect(() => {
    if (cached) { setScript(cached); setLoading(false); return; }
    (async () => {
      setLoading(true); setScript("");
      try {
        const query = `Write a concise 60-second call talk track for calling ${name} ${contact?.org_name ? `at ${contact.org_name}` : ""}. Context: ${card.reason || card.action}. Include: opener, one question to qualify, and a direct CTA. No filler.`;
        const resp = await fetch(`${CRM_URL}/drew/chat`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: query, history: [] }),
        });
        if (!resp.ok || !resp.body) { setScript("DrewOS couldn't generate a script."); setLoading(false); return; }
        const reader = resp.body.getReader();
        const decoder = new TextDecoder(); let buf = "";
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const frames = buf.split("\n\n"); buf = frames.pop();
          for (const f of frames) {
            const l = f.trim();
            if (!l.startsWith("data:")) continue;
            try {
              const d = JSON.parse(l.slice(5).trim());
              if (d.type === "text" && d.text) setScript(s => s + d.text);
            } catch {}
          }
        }
      } catch (e) { setScript(`Error: ${String(e).slice(0, 100)}`); }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card, name, contact?.org_name, regenKey]);

  // Cache script into parent whenever it changes (so tab switches don't lose it)
  useEffect(() => {
    if (script && onCache) onCache(script);
  }, [script, onCache]);

  async function logOutcome() {
    if (!contact?.id) return;
    setSaving(true);
    await api("/activities", "POST", {
      type: "Call",
      subject: `Call: ${name}`,
      note: `Outcome: ${outcome || "(none)"}\n${notes}`,
      contact_id: contact.id,
    });
    setSaving(false); setSaved(true);
    setTimeout(() => onDeployed(), 700);
  }

  return (
    <>
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Contact</div>
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          {name}{phone && <> — <a href={`tel:${phone}`} style={{ color: COLORS.orange }}>{phone}</a></>}
        </div>
      </div>
      <div style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
        Talk track {loading && "· generating…"}
      </div>
      <div style={{
        background: `${COLORS.cardBorder}22`, borderLeft: `3px solid ${COLORS.greenDark}`,
        padding: 12, borderRadius: 4, fontSize: 12, lineHeight: 1.55, whiteSpace: "pre-wrap",
        marginBottom: 12, minHeight: 120, maxHeight: 260, overflowY: "auto",
      }}>
        {script || (loading ? "DrewOS is writing…" : "No script generated.")}
      </div>
      <div style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase", marginBottom: 4 }}>Log outcome</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 8 }}>
        <select value={outcome} onChange={e => setOutcome(e.target.value)} style={{ ...S.select, fontSize: 12 }}>
          <option value="">— outcome —</option>
          <option>Connected</option><option>Voicemail</option>
          <option>No answer</option><option>Meeting booked</option>
          <option>Not interested</option>
        </select>
        <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)"
          style={{ ...S.input, fontSize: 12 }} />
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={logOutcome} disabled={saving || saved}
          style={{ ...S.btn("primary"), fontSize: 12, flex: 1, opacity: (saving || saved) ? 0.5 : 1 }}>
          {saved ? "Logged ✓" : saving ? "Saving..." : "Log Call Outcome"}
        </button>
        <button onClick={onCancel} style={{ ...S.btn("ghost"), fontSize: 12 }}>Discard</button>
      </div>
    </>
  );
}

// ── Strategy deploy form (/drew/strategy) ───────────────────────────────────
function StrategyDeployForm({ card, contact, cached, onCache, regenKey, onDeployed, onCancel }) {
  const [strategy, setStrategy] = useState(cached?.strategy || "");
  const [blocked, setBlocked] = useState(cached?.blocked || false);
  const [loading, setLoading] = useState(!cached);
  const [err, setErr] = useState(null);
  const [question, setQuestion] = useState("");
  const [qAnswer, setQAnswer] = useState("");
  const [asking, setAsking] = useState(false);

  const recordType = card.kind === "hot_lead" ? "lead"
                    : card.deal_id ? "deal"
                    : card.contact_id ? "contact"
                    : "contact";
  const recordId = card.lead_id || card.deal_id || card.contact_id || (card.record_type === "contact" ? card.record_id : null);

  useEffect(() => {
    if (!recordId) { setErr("No record linked to this card."); setLoading(false); return; }
    if (cached) { setStrategy(cached.strategy); setBlocked(cached.blocked); setLoading(false); return; }
    (async () => {
      setLoading(true);
      const r = await api("/drew/strategy", "POST", { record_type: recordType, record_id: recordId });
      if (r?.strategy) {
        setStrategy(r.strategy); setBlocked(!!r.blocked);
        if (onCache) onCache({ strategy: r.strategy, blocked: !!r.blocked });
      }
      else setErr(r?.detail || "No strategy returned");
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordType, recordId, regenKey]);

  async function askFollowUp() {
    if (!question.trim()) return;
    setAsking(true); setQAnswer("");
    try {
      const resp = await fetch(`${CRM_URL}/drew/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: `Context: strategy for ${card.title}.\nQuestion: ${question}`, history: [] }),
      });
      if (!resp.ok || !resp.body) { setQAnswer("DrewOS offline"); setAsking(false); return; }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder(); let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const frames = buf.split("\n\n"); buf = frames.pop();
        for (const f of frames) {
          const l = f.trim();
          if (!l.startsWith("data:")) continue;
          try {
            const d = JSON.parse(l.slice(5).trim());
            if (d.type === "text" && d.text) setQAnswer(s => s + d.text);
          } catch {}
        }
      }
    } catch (e) { setQAnswer(`Error: ${String(e).slice(0, 80)}`); }
    setAsking(false);
  }

  return (
    <>
      {err && <div style={{ background: `${COLORS.danger}22`, color: COLORS.danger, padding: 8, borderRadius: 5, fontSize: 12, marginBottom: 10 }}>{err}</div>}
      {loading && <div style={{ textAlign: "center", color: COLORS.grayMid, padding: 20 }}>DrewOS is thinking…</div>}
      {blocked && (
        <div style={{ background: `${COLORS.danger}22`, color: COLORS.danger, padding: 10,
                      borderRadius: 5, fontSize: 12, marginBottom: 10, fontWeight: 700 }}>
          🛑 BLOCKED — unsubscribed contact. No outreach permitted.
        </div>
      )}
      {!loading && strategy && (
        <>
          <div style={{
            background: `${COLORS.cardBorder}22`, borderLeft: `3px solid ${COLORS.greenDark}`,
            padding: 12, borderRadius: 4, fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap",
            marginBottom: 12, maxHeight: 320, overflowY: "auto",
          }}>{strategy}</div>
          <div style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase", marginBottom: 4 }}>
            Ask DrewOS a question about this
          </div>
          <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
            <input value={question} onChange={e => setQuestion(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") askFollowUp(); }}
              placeholder="e.g. What if they say not this quarter?"
              style={{ ...S.input, flex: 1, fontSize: 12 }} />
            <button onClick={askFollowUp} disabled={asking || !question.trim()}
              style={{ ...S.btn("primary"), fontSize: 12, opacity: (asking || !question.trim()) ? 0.5 : 1 }}>
              {asking ? "..." : "Ask"}
            </button>
          </div>
          {qAnswer && (
            <div style={{ background: "#fff", border: `1px solid ${COLORS.cardBorder}`,
                          borderLeft: `3px solid ${COLORS.orange}`,
                          padding: 10, borderRadius: 4, fontSize: 12, whiteSpace: "pre-wrap", marginBottom: 12 }}>
              {qAnswer}
            </div>
          )}
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={onDeployed} style={{ ...S.btn("primary"), fontSize: 12, flex: 1 }}>
              Mark Deployed
            </button>
            <button onClick={onCancel} style={{ ...S.btn("ghost"), fontSize: 12 }}>Close</button>
          </div>
        </>
      )}
    </>
  );
}

// Legacy email modal — superseded by DeployModal, kept for reference callers.
// eslint-disable-next-line no-unused-vars
function DrewEmailDeployModal({ contactId, onClose }) {
  const [draft, setDraft] = useState(null);    // { to, subject, body, conflicts, blocked }
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sentOk, setSentOk] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true); setErr(null);
      try {
        const r = await fetch(`${CRM_URL}/drew/deploy-email`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ contact_id: contactId, auto_generate: true }),
        });
        const j = await r.json();
        if (r.ok) setDraft(j);
        else setErr(j.detail || `HTTP ${r.status}`);
      } catch (e) { setErr(String(e)); }
      setLoading(false);
    })();
  }, [contactId]);

  async function sendIt() {
    if (!draft || draft.blocked) return;
    setSending(true); setErr(null);
    try {
      const r = await fetch(`${CRM_URL}/send-email`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: draft.to, subject: draft.subject, body: draft.body,
          contact_id: draft.contact_id, org_id: draft.org_id,
        }),
      });
      if (r.ok || r.status === 202) setSentOk(true);
      else { const j = await r.json().catch(() => ({})); setErr(j.detail || `HTTP ${r.status}`); }
    } catch (e) { setErr(String(e)); }
    setSending(false);
  }

  return (
    <ModalShell title="Deploy Email" subtitle="DrewOS drafted this. Review and send." onClose={onClose} wide>
      {loading && <div style={{ padding: 30, textAlign: "center", color: COLORS.grayMid }}>DrewOS is composing...</div>}
      {err && <div style={{ background: `${COLORS.danger}22`, color: COLORS.danger, padding: 8, borderRadius: 5, fontSize: 12, marginBottom: 10 }}>{err}</div>}
      {sentOk && <div style={{ background: `${COLORS.success}22`, color: COLORS.success, padding: 8, borderRadius: 5, fontSize: 12, marginBottom: 10 }}>✓ Sent. Tracking active. Activity logged.</div>}
      {draft && !sentOk && (
        <>
          {draft.conflicts && draft.conflicts.length > 0 && (
            <div style={{ background: draft.blocked ? `${COLORS.danger}22` : "#FEF3C7",
                          color: draft.blocked ? COLORS.danger : "#92400E",
                          padding: 10, borderRadius: 5, fontSize: 12, marginBottom: 10 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                {draft.blocked ? "🛑 BLOCKED" : "⚠ Conflicts detected"}
              </div>
              {draft.conflicts.map((c, i) => <div key={i}>• {c}</div>)}
            </div>
          )}
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>To</div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{draft.to}</div>
          </div>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Subject</div>
            <input value={draft.subject} onChange={e => setDraft(d => ({ ...d, subject: e.target.value }))}
              style={{ ...S.input, fontSize: 13 }} />
          </div>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Body</div>
            <textarea value={draft.body} onChange={e => setDraft(d => ({ ...d, body: e.target.value }))}
              style={{ ...S.input, height: 260, fontFamily: "inherit", resize: "vertical" }} />
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={sendIt} disabled={sending || draft.blocked}
              style={{ ...S.btn("primary"), fontSize: 12, flex: 1, opacity: (sending || draft.blocked) ? 0.5 : 1 }}>
              {sending ? "Sending..." : draft.blocked ? "Blocked" : "Send"}
            </button>
            <button onClick={onClose} style={{ ...S.btn("ghost"), fontSize: 12 }}>Discard</button>
          </div>
        </>
      )}
    </ModalShell>
  );
}

// ─── OFFER WIZARD ────────────────────────────────────────────────────────────
// 6 questions → GTM package (4 emails, 20 outreach, one-pager, 3 social posts).
// Hormozi framework enforced backend-side via /drew/build-offer-gtm stream.

const OFFER_WHO_CHOICES = [
  { id: "RCFE",         label: "RCFE Administrators" },
  { id: "Hotel",        label: "Hotel Safety Directors" },
  { id: "Hospital",     label: "Hospital Emergency Managers" },
  { id: "Campus",       label: "Campus Safety Directors" },
  { id: "PropertyMgmt", label: "Property Managers" },
  { id: "BTC",          label: "Veterans & First Responders (BTC)" },
  { id: "Custom",       label: "Custom — I'll describe" },
];

// Per-vertical pre-fills Drew suggests for each wizard step.
const OFFER_SUGGESTIONS = {
  RCFE: {
    problem: "Title 22 inspection failure risk — scattered evac chairs, 72-hour kits, and drill documentation across vendors.",
    promise: "Inspection-ready in 30 days with one staff-runnable system.",
    price: "$8,500 full install + training + documentation binder.",
    proof: "3 RCFE installs, zero inspection failures, LIC 610E compliant documentation.",
    guarantee: "30-day satisfaction — replace or refund.",
  },
  Hotel: {
    problem: "Stairwell evacuation gap — guest mobility emergency with no trained staff or equipment plan.",
    promise: "One trained-staff system per stairwell, ready for Title 19 / ADA / NFPA expectations in 30 days.",
    price: "$7,200 per stairwell install + training + ProMove slings.",
    proof: "Installs across select-service and luxury brands, documented training records.",
    guarantee: "30-day satisfaction.",
  },
  Hospital: {
    problem: "Joint Commission documentation gap on stairwell evacuation and patient transfers across shifts.",
    promise: "Campus-wide system with documented training for every floor in 45 days.",
    price: "Custom campus quote — scales by floor count and shift coverage.",
    proof: "Hospital deployments with full training sign-offs and inspection binders.",
    guarantee: "30-day satisfaction.",
  },
  Campus: {
    problem: "BERT/CERT turnover leaves buildings uncovered; ADA Title II exposure on equal evacuation access.",
    promise: "Standardized system across every building so any staff member can execute the plan.",
    price: "Per-building install starting at $4,800 + BERT/CERT training included.",
    proof: "District-wide deployments with documented training records.",
    guarantee: "30-day satisfaction.",
  },
  PropertyMgmt: {
    problem: "No equipment, no trained staff, no documentation — ADA Title III exposure on access during emergencies.",
    promise: "Simple repeatable system per building with inspection records maintained.",
    price: "$5,400 per stairwell install + quarterly inspection support.",
    proof: "Residential and commercial portfolios with documented records.",
    guarantee: "30-day satisfaction.",
  },
  BTC: {
    problem: "Emergency preparedness gap at home — no evacuation plan, no 72-hour kit, no family drill.",
    promise: "72-hour family preparedness in one kit, delivered to your door.",
    price: "$185 Go-Bag with 15% veteran & first responder discount.",
    proof: "SDVOSB certified, Marine founder, military-grade equipment.",
    guarantee: "30-day satisfaction guarantee.",
  },
  Custom: { problem: "", promise: "", price: "", proof: "", guarantee: "" },
};

function OfferWizardModal({ onClose, initialMessage }) {
  const [step, setStep] = useState(0); // 0..5 questions, 6 summary, 7 generating, 8 done
  const [answers, setAnswers] = useState({
    who: "",
    vertical: "RCFE",
    problem: "",
    promise: "",
    price: "",
    proof: "",
    guarantee: "",
  });
  const [customWho, setCustomWho] = useState("");
  const [streamText, setStreamText] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [offerId, setOfferId] = useState(null);
  const [offerData, setOfferData] = useState(null);
  const [err, setErr] = useState(null);
  const [viewTab, setViewTab] = useState("emails"); // emails | outreach | onepager | social
  const [launching, setLaunching] = useState(false);
  const [launchResult, setLaunchResult] = useState(null);

  // Prefill suggestions when vertical changes
  function applySuggestions(vertical) {
    const s = OFFER_SUGGESTIONS[vertical] || OFFER_SUGGESTIONS.Custom;
    setAnswers(a => ({
      ...a,
      problem: a.problem || s.problem,
      promise: a.promise || s.promise,
      price: a.price || s.price,
      proof: a.proof || s.proof,
      guarantee: a.guarantee || s.guarantee,
    }));
  }

  function pickWho(choice) {
    const label = OFFER_WHO_CHOICES.find(c => c.id === choice)?.label || choice;
    setAnswers(a => ({ ...a, who: choice === "Custom" ? "" : label, vertical: choice }));
    applySuggestions(choice);
  }

  const TOTAL_Q = 6;
  const questions = [
    { key: "who",       label: "Who is this offer for?" },
    { key: "problem",   label: "What specific problem does it solve?" },
    { key: "promise",   label: "What's the outcome you're promising?" },
    { key: "price",     label: "What's the offer price/structure?" },
    { key: "proof",     label: "What proof do you have?" },
    { key: "guarantee", label: "What's the guarantee?" },
  ];

  function next() {
    if (step === 0 && answers.vertical === "Custom" && !customWho.trim()) return;
    if (step === 0 && answers.vertical === "Custom") {
      setAnswers(a => ({ ...a, who: customWho.trim() }));
    }
    setStep(s => Math.min(s + 1, 6));
  }
  function back() { setStep(s => Math.max(0, s - 1)); }

  async function buildGTM() {
    setStep(7);
    setStreaming(true);
    setStreamText("");
    setErr(null);
    try {
      const resp = await fetch(`${CRM_URL}/drew/build-offer-gtm`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          who: answers.who, problem: answers.problem, promise: answers.promise,
          price: answers.price, proof: answers.proof, guarantee: answers.guarantee,
          vertical: answers.vertical,
        }),
      });
      if (!resp.ok || !resp.body) {
        const t = await resp.text().catch(() => "");
        setErr(`Build failed: ${t.slice(0, 200) || resp.status}`);
        setStreaming(false); return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let finalId = null;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const frames = buf.split("\n\n"); buf = frames.pop();
        for (const f of frames) {
          const line = f.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const d = JSON.parse(line.slice(5).trim());
            if (d.type === "text") setStreamText(t => t + d.text);
            else if (d.type === "done") finalId = d.offer_id;
            else if (d.type === "error") setErr(d.error);
          } catch {}
        }
      }
      if (finalId) {
        setOfferId(finalId);
        const full = await api(`/offers/${finalId}`);
        if (full) setOfferData(full);
        setStep(8);
      }
    } catch (e) { setErr(String(e).slice(0, 200)); }
    setStreaming(false);
  }

  async function launchOffer() {
    if (!offerId) return;
    setLaunching(true);
    const r = await api(`/offers/${offerId}/launch`, "POST", {});
    setLaunching(false);
    if (r) {
      setLaunchResult(r);
      setOfferData(o => o ? { ...o, status: "active" } : o);
    }
  }

  function copyText(text) {
    try { navigator.clipboard.writeText(text || ""); } catch {}
  }

  // Parse initial chat message hint into a vertical if possible
  useEffect(() => {
    if (!initialMessage) return;
    const m = initialMessage.toLowerCase();
    const match = OFFER_WHO_CHOICES.find(c => m.includes(c.id.toLowerCase()) || m.includes(c.label.toLowerCase()));
    if (match) pickWho(match.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMessage]);

  const currentQ = questions[step];
  const progress = Math.min(step + 1, TOTAL_Q);

  return (
    <ModalShell
      title={offerId ? (offerData?.name || "Offer Generated") : "🎯 Offer Wizard"}
      subtitle={step < 6 ? `Question ${progress} of ${TOTAL_Q}` :
                step === 6 ? "Review your offer" :
                step === 7 ? "DrewOS is building your GTM package..." :
                "GTM package ready"}
      onClose={onClose}
      wide
    >
      {/* Progress bar */}
      {step < 7 && (
        <div style={{ height: 4, background: COLORS.cardBorder, borderRadius: 2, marginBottom: 14 }}>
          <div style={{
            height: "100%", width: `${(progress / TOTAL_Q) * 100}%`,
            background: COLORS.orange, borderRadius: 2, transition: "width 0.2s",
          }} />
        </div>
      )}

      {/* Step 0: Who */}
      {step === 0 && (
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{currentQ.label}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {OFFER_WHO_CHOICES.map(c => (
              <label key={c.id} style={{
                display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
                border: `1px solid ${answers.vertical === c.id ? COLORS.orange : COLORS.cardBorder}`,
                background: answers.vertical === c.id ? `${COLORS.orange}11` : "#fff",
                borderRadius: 5, cursor: "pointer", fontSize: 13,
              }}>
                <input type="radio" checked={answers.vertical === c.id}
                  onChange={() => pickWho(c.id)} />
                {c.label}
              </label>
            ))}
          </div>
          {answers.vertical === "Custom" && (
            <input
              value={customWho} onChange={e => setCustomWho(e.target.value)}
              placeholder="Describe your audience..."
              style={{ ...S.input, marginTop: 10 }}
            />
          )}
        </div>
      )}

      {/* Steps 1-5: text answer */}
      {step >= 1 && step <= 5 && (
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>{currentQ.label}</div>
          <div style={{ fontSize: 10, color: COLORS.grayMid, marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>
            DrewOS's suggestion (edit or accept)
          </div>
          <textarea
            value={answers[currentQ.key]}
            onChange={e => setAnswers(a => ({ ...a, [currentQ.key]: e.target.value }))}
            rows={5}
            style={{ ...S.input, resize: "vertical", fontFamily: "inherit" }}
          />
        </div>
      )}

      {/* Step 6: Summary */}
      {step === 6 && (
        <div style={{
          border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, padding: 16,
          background: `${COLORS.orange}08`,
        }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: COLORS.orange, letterSpacing: 1,
                        textTransform: "uppercase", marginBottom: 10 }}>
            🎯 Your Offer
          </div>
          {[
            ["FOR", answers.who],
            ["PROBLEM", answers.problem],
            ["PROMISE", answers.promise],
            ["PRICE", answers.price],
            ["PROOF", answers.proof],
            ["GUARANTEE", answers.guarantee],
          ].map(([k, v]) => (
            <div key={k} style={{ marginBottom: 8, fontSize: 12.5, lineHeight: 1.5 }}>
              <span style={{ fontWeight: 800, color: COLORS.greenDark, marginRight: 6 }}>{k}:</span>
              {v || <em style={{ color: COLORS.grayMid }}>(empty)</em>}
            </div>
          ))}
        </div>
      )}

      {/* Step 7: Generating */}
      {step === 7 && (
        <div>
          <div style={{ padding: 10, fontSize: 12, color: COLORS.grayMid }}>
            {streaming ? "Streaming response from Claude Sonnet..." : err ? "" : "Complete."}
          </div>
          <pre style={{
            background: COLORS.greenDeep, padding: 12, borderRadius: 6,
            maxHeight: 400, overflowY: "auto", fontSize: 11, lineHeight: 1.4,
            whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0,
            fontFamily: "monospace", color: COLORS.greenDark,
          }}>{streamText || "Waiting for DrewOS..."}</pre>
          {err && (
            <div style={{ background: `${COLORS.danger}22`, color: COLORS.danger,
                          padding: 8, borderRadius: 5, fontSize: 12, marginTop: 10 }}>
              {err}
            </div>
          )}
        </div>
      )}

      {/* Step 8: Done — offer dashboard card */}
      {step === 8 && offerData && (
        <div>
          {/* Offer card header */}
          <div style={{
            border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, padding: 14,
            marginBottom: 14, background: "#fff",
          }}>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>
              🎯 {offerData.name}
            </div>
            <div style={{ fontSize: 11, color: COLORS.grayMid, marginBottom: 10 }}>
              Status: <strong style={{
                color: offerData.status === "active" ? COLORS.success : COLORS.orange,
                textTransform: "capitalize",
              }}>{offerData.status}</strong>
              {" · "}Created: {new Date(offerData.created_at || Date.now()).toLocaleDateString()}
              {" · "}Target: {offerData.vertical}
              {" · "}Qualified leads: {fmtNum(offerData.qualified_leads || 0)}
            </div>

            {/* Tab selector */}
            <div style={{ display: "flex", gap: 6, borderBottom: `1px solid ${COLORS.cardBorder}`,
                          marginBottom: 10, paddingBottom: 0 }}>
              {[
                { id: "emails",   label: `📧 ${(offerData.email_sequence || []).length}-email sequence` },
                { id: "outreach", label: `💬 ${(offerData.outreach_messages || []).length} outreach messages` },
                { id: "onepager", label: "📄 One-pager" },
                { id: "social",   label: `📱 ${(offerData.social_posts || []).length} social posts` },
              ].map(t => (
                <button key={t.id} onClick={() => setViewTab(t.id)}
                  style={{
                    background: "transparent", border: "none", cursor: "pointer",
                    padding: "6px 10px",
                    borderBottom: viewTab === t.id ? `2px solid ${COLORS.orange}` : "2px solid transparent",
                    color: viewTab === t.id ? COLORS.orange : COLORS.grayMid,
                    fontSize: 11, fontWeight: viewTab === t.id ? 700 : 500,
                  }}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* Email sequence */}
            {viewTab === "emails" && (
              <div>
                {(offerData.email_sequence || []).map((e, i) => (
                  <div key={i} style={{
                    border: `1px solid ${COLORS.cardBorder}`, borderRadius: 5,
                    padding: 10, marginBottom: 8, background: COLORS.greenDeep,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <div style={{ fontWeight: 700, fontSize: 12 }}>
                        Email {e.step || i + 1}: {e.subject}
                      </div>
                      <button onClick={() => copyText(`Subject: ${e.subject}\n\n${e.body}`)}
                        style={{ ...S.btn("ghost"), fontSize: 10, padding: "2px 8px" }}>Copy</button>
                    </div>
                    <div style={{ fontSize: 11, lineHeight: 1.5, whiteSpace: "pre-wrap", color: COLORS.greenDark }}>
                      {e.body}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Outreach messages */}
            {viewTab === "outreach" && (
              <div>
                <button onClick={() => copyText((offerData.outreach_messages || [])
                    .map(m => `[${m.channel}]${m.subject ? ` ${m.subject}` : ""}\n${m.message}`).join("\n\n---\n\n"))}
                  style={{ ...S.btn("ghost"), fontSize: 10, marginBottom: 8 }}>
                  Copy all 20
                </button>
                {(offerData.outreach_messages || []).map((m, i) => (
                  <div key={i} style={{
                    border: `1px solid ${COLORS.cardBorder}`, borderRadius: 5,
                    padding: 8, marginBottom: 6, background: "#fff", fontSize: 11,
                  }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.orange,
                                  textTransform: "uppercase", letterSpacing: 1, marginBottom: 3 }}>
                      #{i + 1} · {m.channel}{m.subject ? ` · ${m.subject}` : ""}
                    </div>
                    <div style={{ lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{m.message}</div>
                  </div>
                ))}
              </div>
            )}

            {/* One-pager */}
            {viewTab === "onepager" && (
              <div>
                <button onClick={() => copyText(offerData.one_pager || "")}
                  style={{ ...S.btn("ghost"), fontSize: 10, marginBottom: 8 }}>
                  Copy markdown
                </button>
                <pre style={{
                  background: COLORS.greenDeep, padding: 12, borderRadius: 6,
                  maxHeight: 360, overflowY: "auto", fontSize: 11, lineHeight: 1.5,
                  whiteSpace: "pre-wrap", wordBreak: "break-word",
                  fontFamily: "inherit", color: COLORS.greenDark, margin: 0,
                }}>{offerData.one_pager}</pre>
              </div>
            )}

            {/* Social posts */}
            {viewTab === "social" && (
              <div>
                {(offerData.social_posts || []).map((p, i) => (
                  <div key={i} style={{
                    border: `1px solid ${COLORS.cardBorder}`, borderRadius: 5,
                    padding: 10, marginBottom: 8, background: "#fff",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.orange,
                                    textTransform: "uppercase", letterSpacing: 1 }}>
                        Post {i + 1} · {p.platform}
                      </div>
                      <button onClick={() => copyText(`${p.hook}\n\n${p.body}`)}
                        style={{ ...S.btn("ghost"), fontSize: 10, padding: "2px 8px" }}>Copy</button>
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{p.hook}</div>
                    <div style={{ fontSize: 11, lineHeight: 1.5, whiteSpace: "pre-wrap", marginBottom: 6 }}>{p.body}</div>
                    {p.image_prompt && (
                      <div style={{ fontSize: 10, color: COLORS.grayMid, fontStyle: "italic",
                                    borderTop: `1px dashed ${COLORS.cardBorder}`, paddingTop: 4 }}>
                        🎨 Image prompt: {p.image_prompt}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Launch confirmation */}
          {launchResult && (
            <div style={{
              background: `${COLORS.success}14`, border: `1px solid ${COLORS.success}`,
              borderRadius: 6, padding: 12, marginBottom: 10, fontSize: 12, lineHeight: 1.6,
            }}>
              <div style={{ fontWeight: 800, color: COLORS.success, marginBottom: 4 }}>
                ✓ Offer launched
              </div>
              {launchResult.social_posts_scheduled} social posts scheduled ·{" "}
              {fmtNum(launchResult.leads_tagged)} leads tagged ·{" "}
              {launchResult.priority_cards_created} priority cards created for top leads.
            </div>
          )}
        </div>
      )}

      {/* Footer buttons */}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16, gap: 8 }}>
        <div>
          {step > 0 && step < 7 && (
            <button onClick={back} style={{ ...S.btn("ghost"), fontSize: 12 }}>← Back</button>
          )}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {step < 5 && (
            <button onClick={next}
              disabled={step === 0 && (answers.vertical === "Custom" && !customWho.trim())}
              style={{ ...S.btn("primary"), fontSize: 12 }}>Next →</button>
          )}
          {step === 5 && (
            <button onClick={next} style={{ ...S.btn("primary"), fontSize: 12 }}>Review →</button>
          )}
          {step === 6 && (
            <>
              <button onClick={() => setStep(0)} style={{ ...S.btn("ghost"), fontSize: 12 }}>Edit Answers</button>
              <button onClick={buildGTM} style={{ ...S.btn("primary"), fontSize: 12 }}>Build GTM Package</button>
            </>
          )}
          {step === 8 && offerData && (
            <>
              {offerData.status !== "active" && (
                <button onClick={launchOffer} disabled={launching}
                  style={{ ...S.btn("primary"), fontSize: 12, opacity: launching ? 0.5 : 1 }}>
                  {launching ? "Launching..." : "🚀 Launch Offer"}
                </button>
              )}
              <button onClick={onClose} style={{ ...S.btn("ghost"), fontSize: 12 }}>Done</button>
            </>
          )}
        </div>
      </div>
    </ModalShell>
  );
}

function DrewHuntModal({ vertical, onClose }) {
  const [query, setQuery] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  async function run() {
    if (!query.trim()) return;
    setLoading(true); setErr(null); setReport(null);
    try {
      const r = await fetch(`${CRM_URL}/drew/hunt`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query, vertical }),
      });
      const j = await r.json();
      if (r.ok) setReport(j.report);
      else setErr(j.detail || `HTTP ${r.status}`);
    } catch (e) { setErr(String(e)); }
    setLoading(false);
  }

  return (
    <ModalShell title="Corporate Partner Hunter" subtitle="DrewOS searches the web for decision makers and partners." onClose={onClose} wide>
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        <input value={query} onChange={e => setQuery(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") run(); }}
          placeholder="Hunt Marriott corporate for safety decision makers..."
          style={{ ...S.input, flex: 1 }} />
        <button onClick={run} disabled={loading || !query.trim()}
          style={{ ...S.btn("primary"), fontSize: 12, opacity: (loading || !query.trim()) ? 0.5 : 1 }}>
          {loading ? "Hunting..." : "Hunt"}
        </button>
      </div>
      {err && <div style={{ background: `${COLORS.danger}22`, color: COLORS.danger, padding: 8, borderRadius: 5, fontSize: 12, marginBottom: 10 }}>{err}</div>}
      {loading && <div style={{ padding: 20, textAlign: "center", color: COLORS.grayMid }}>DrewOS is searching...</div>}
      {report && (
        <div style={{ background: `${COLORS.cardBorder}22`, borderRadius: 6, padding: 12,
                      fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap", maxHeight: 500, overflowY: "auto" }}>
          {report}
        </div>
      )}
    </ModalShell>
  );
}

// ─── DrewOS — Command Center P1 stale queue + quick-toggle chips ────────────
function QuickToggle({ label, icon, active, onToggle }) {
  return (
    <button type="button" onClick={onToggle}
      style={{
        fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
        padding: "5px 10px", borderRadius: 14,
        border: `1px solid ${active ? COLORS.orange : COLORS.cardBorder}`,
        background: active ? COLORS.orange : COLORS.cardBg,
        color: active ? COLORS.white : COLORS.textPrimary,
        cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4,
        transition: "background 0.12s",
      }}>
      <span style={{ fontSize: 12 }}>{icon}</span>{label}
    </button>
  );
}

function staleBand(daysOverdue) {
  if (daysOverdue >= 15) return { bg: "#FEE2E2", border: "#FCA5A5", text: "#991B1B", kind: "red" };
  if (daysOverdue >= 4)  return { bg: "#FFEDD5", border: "#FDBA74", text: "#9A3412", kind: "orange" };
  return { bg: "#FEF9C3", border: "#FDE68A", text: "#854D0E", kind: "yellow" };
}

function StaleLeadsPanel({ leads, loading, onChange, onOpen }) {
  const [collapsed, setCollapsed] = useState(false);
  const [snoozing, setSnoozing] = useState(null);

  async function snooze(contactId, days) {
    setSnoozing(contactId);
    const r = await api(`/contacts/${contactId}/snooze`, "POST", { days });
    setSnoozing(null);
    if (r && r.ok) onChange && onChange();
  }
  async function logTouch(contact) {
    await api("/activities", "POST", {
      type: "Call",
      subject: `Stale-lead touch — ${contact.full_name || contact.org_name}`,
      contact_id: contact.id, org_id: contact.org_id,
    });
    onChange && onChange();
  }

  const total = leads.length;
  const red = leads.filter(l => (l.days_overdue ?? 0) >= 15).length;
  const orange = leads.filter(l => (l.days_overdue ?? 0) >= 4 && (l.days_overdue ?? 0) < 15).length;
  const yellow = leads.filter(l => (l.days_overdue ?? 0) < 4).length;

  if (!loading && total === 0) return null;

  return (
    <div style={{
      ...S.card, marginBottom: 12, borderLeft: `4px solid ${COLORS.orange}`,
      padding: 12, background: COLORS.cardBg,
    }}>
      <div onClick={() => setCollapsed(c => !c)}
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", marginBottom: collapsed ? 0 : 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: 0.5, color: COLORS.orange }}>
            ⏰ STALE LEADS — P1 PRIORITY QUEUE
          </span>
          <span style={{ fontSize: 11, color: COLORS.grayMid }}>
            {loading ? "loading…" : `${total} overdue`}
          </span>
          {!loading && total > 0 && (
            <span style={{ fontSize: 10, color: COLORS.grayMid, display: "flex", gap: 6 }}>
              {red > 0 && <span style={{ color: "#991B1B", fontWeight: 700 }}>{red} red</span>}
              {orange > 0 && <span style={{ color: "#9A3412", fontWeight: 700 }}>{orange} orange</span>}
              {yellow > 0 && <span style={{ color: "#854D0E", fontWeight: 700 }}>{yellow} yellow</span>}
            </span>
          )}
        </div>
        <span style={{ fontSize: 12, color: COLORS.grayMid }}>{collapsed ? "▶ show" : "▼ hide"}</span>
      </div>

      {!collapsed && !loading && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 8 }}>
          {leads.slice(0, 20).map(l => {
            const d = l.days_overdue ?? 0;
            const band = staleBand(d);
            return (
              <div key={l.id}
                style={{
                  background: band.bg, border: `1px solid ${band.border}`, borderRadius: 6,
                  padding: 10, display: "flex", flexDirection: "column", gap: 6,
                }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: band.text, cursor: "pointer" }}
                    onClick={() => onOpen && onOpen(l)}>
                    {l.full_name || "(no name)"}
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 800, color: band.text }}>
                    {d}d overdue
                  </span>
                </div>
                {l.org_name && <div style={{ fontSize: 11, color: band.text, opacity: 0.85 }}>🏢 {l.org_name}</div>}
                <div style={{ fontSize: 10, color: band.text, opacity: 0.85, display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {l.pipeline_stage && <span>stage: <b>{l.pipeline_stage}</b></span>}
                  {l.halo_tier && <span>· tier: <b>{l.halo_tier}</b></span>}
                </div>
                <div style={{ fontSize: 10, color: band.text, opacity: 0.8 }}>
                  last: {l.last_activity_kind || "—"} · {l.last_action_date ? l.last_action_date.slice(0, 10) : "never"}
                </div>
                {band.kind === "red" && (
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#991B1B" }}>
                    → consider reassign or close-lost
                  </div>
                )}
                <div style={{ display: "flex", gap: 4, marginTop: 2 }}>
                  <button type="button" onClick={() => logTouch(l)}
                    style={{ ...S.btn("primary"), fontSize: 10, padding: "3px 8px" }}>
                    Log Activity
                  </button>
                  <button type="button" onClick={() => snooze(l.id, 7)}
                    disabled={snoozing === l.id}
                    style={{ ...S.btn("ghost"), fontSize: 10, padding: "3px 8px" }}>
                    {snoozing === l.id ? "…" : "Snooze 7d"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CommandCenter() {
  const [vertical, setVertical] = useState("RCFE");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [replyCount, setReplyCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 100;

  // Open the lead/contact modal when a notification dispatches crm:open-record.
  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {};
      if (d.entity_type === "lead" && d.entity_id) {
        setSelected({ id: d.entity_id });
      } else if (d.entity_type === "contact" && d.entity_id) {
        // LeadDetailModal loads full record; if no matching lead, it's OK — modal
        // will show error and user can close.
        api(`/contacts/${d.entity_id}`).then(c => {
          if (c) {
            setSelected({
              id: c.id, full_name: c.full_name, org_name: c.org_name,
              email_work: c.email_work, org_id: c.org_id, vertical: c.vertical || "RCFE",
            });
          }
        });
      }
    }
    window.addEventListener("crm:open-record", onOpen);
    return () => window.removeEventListener("crm:open-record", onOpen);
  }, []);

  // All filters — server-side
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [label, setLabel] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sort, setSort] = useState("");
  const [allLabels, setAllLabels] = useState([]);
  // DrewOS — pipeline visibility + stale filters
  const [pipelineStage, setPipelineStage] = useState("");
  const [haloLeadSource, setHaloLeadSource] = useState("");
  const [sourceBatch, setSourceBatch] = useState("");
  const [tier, setTier] = useState("");
  const [showHaloOnly, setShowHaloOnly] = useState(false);
  const [showStaleOnly, setShowStaleOnly] = useState(false);
  const [hideClosedLost, setHideClosedLost] = useState(false);
  const [filterValues, setFilterValues] = useState({ halo_lead_source: [], source_batch: [], tiers: [] });
  const [pipelineCfg, setPipelineCfg] = useState(null);
  // Stale leads (P1 queue) fetched for the current vertical.
  const [staleLeads, setStaleLeads] = useState([]);
  const [staleLoading, setStaleLoading] = useState(false);

  const isSpecialTab = vertical === "__replies__" || vertical === "__bookings__";

  // Load filter dropdown values + pipeline stages config once.
  useEffect(() => {
    api("/pipelines/filter-values").then(r => { if (r) setFilterValues(r); });
    api("/pipelines/stages").then(r => { if (r) setPipelineCfg(r); });
  }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Load labels list for the filter dropdown
  useEffect(() => { api("/labels").then(r => { if (Array.isArray(r)) setAllLabels(r); }); }, []);

  const refreshBadge = useCallback(() => {
    api("/activities/replies/unread-count").then(r => { if (r) setReplyCount(r.count || 0); });
  }, []);
  useEffect(() => { refreshBadge(); }, [refreshBadge]);

  // Server-side fetch — all filters go to Railway
  const fetchLeads = useCallback(() => {
    if (isSpecialTab) return;
    setLoading(true);
    const p = new URLSearchParams({ limit: PAGE_SIZE, offset: page * PAGE_SIZE });
    if (debouncedSearch) p.set("search", debouncedSearch);
    if (label) p.set("label", label);
    if (statusFilter) p.set("status", statusFilter);
    if (sort) p.set("sort", sort);
    if (pipelineStage) p.set("pipeline_stage", pipelineStage);
    if (haloLeadSource) p.set("halo_lead_source", haloLeadSource);
    if (sourceBatch) p.set("source_batch", sourceBatch);
    if (tier) p.set("tier", tier);
    if (showHaloOnly) p.set("show_halo_only", "true");
    if (showStaleOnly) p.set("show_stale_only", "true");
    if (hideClosedLost) p.set("hide_closed_lost", "true");
    api(`/command-center/${vertical}?${p}`).then(r => {
      if (r && r.data) {
        setLeads(r.data.map(normalizeLead));
        setTotal(r.total || 0);
      } else {
        setLeads((r || []).map ? (r || []).map(normalizeLead) : []);
        setTotal(0);
      }
      setLoading(false);
    });
  }, [vertical, debouncedSearch, label, statusFilter, sort, page, isSpecialTab,
      pipelineStage, haloLeadSource, sourceBatch, tier,
      showHaloOnly, showStaleOnly, hideClosedLost]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  // Stale-leads queue — fetched when not on replies/bookings tab.
  const fetchStale = useCallback(() => {
    if (isSpecialTab) return;
    setStaleLoading(true);
    api(`/insights/stale-leads?vertical=${encodeURIComponent(vertical)}&limit=50`).then(r => {
      setStaleLeads((r && r.leads) || []);
      setStaleLoading(false);
    });
  }, [vertical, isSpecialTab]);
  useEffect(() => { fetchStale(); }, [fetchStale]);

  // Reset page when filters change
  useEffect(() => { setPage(0); }, [
    vertical, debouncedSearch, label, statusFilter, sort,
    pipelineStage, haloLeadSource, sourceBatch, tier,
    showHaloOnly, showStaleOnly, hideClosedLost,
  ]);

  const filtered = leads;

  async function updateStatus(id, status) {
    await api(`/leads/${id}`, "PATCH", { status });
    setLeads(prev => prev.map(l => l.id === id ? { ...l, status } : l));
    if (selected?.id === id) setSelected(s => ({ ...s, status }));
  }

  async function updateReplyState(id, reply_state) {
    await api(`/leads/${id}`, "PATCH", { reply_state });
    setLeads(prev => prev.map(l => l.id === id ? { ...l, reply_state } : l));
    if (selected?.id === id) setSelected(s => ({ ...s, reply_state }));
  }

  // Fix 2: Outreach log (replaces DM1/DM2). Posts activity to backend.
  async function logOutreach(id, outreachType) {
    const now = new Date().toISOString();
    await api("/activities", "POST", {
      type: outreachType,
      subject: `${outreachType} outreach`,
      lead_id: id,
    });
    setLeads(prev => prev.map(l => l.id === id
      ? { ...l, last_activity: outreachType, last_activity_at: now }
      : l));
    if (selected?.id === id) {
      setSelected(s => ({ ...s, last_activity: outreachType, last_activity_at: now }));
    }
  }

  return (
    <div>
      {/* Drew — Chief of Staff panel (always visible, collapsible) */}
      <DrewPanel vertical={vertical} />

      {/* Vertical tabs + Replies + Bookings */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        {VERTICALS.map(v => (
          <button key={v} onClick={() => { setVertical(v); setSelected(null); }} style={{
            ...S.btn(v === vertical ? "primary" : "ghost"),
            fontSize: 13,
          }}>
            {VERTICAL_ICONS[v]} {v}
          </button>
        ))}
        <button onClick={() => { setVertical("__replies__"); setSelected(null); }}
          style={{ ...S.btn(vertical === "__replies__" ? "primary" : "ghost"), fontSize: 13, position: "relative" }}>
          {replyCount ? <span style={{ position: "absolute", top: -4, right: -4, background: COLORS.danger, color: "#fff", borderRadius: "50%", width: 16, height: 16, fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>{replyCount}</span> : null}
          Replies
        </button>
        <button onClick={() => { setVertical("__bookings__"); setSelected(null); }}
          style={{ ...S.btn(vertical === "__bookings__" ? "primary" : "ghost"), fontSize: 13 }}>
          Bookings
        </button>
      </div>

      {isSpecialTab ? (vertical === "__replies__" ? <RepliesPanel onBadgeUpdate={refreshBadge} /> : <BookingsPanel />) : <>
      {/* Stale-leads priority queue (DrewOS Command Center P1) */}
      <StaleLeadsPanel
        leads={staleLeads}
        loading={staleLoading}
        onChange={fetchStale}
        onOpen={c => setSelected({
          id: c.id, full_name: c.full_name, org_name: c.org_name,
          email_work: c.email_work, org_id: c.org_id, vertical: c.vertical || vertical,
        })}
      />

      {/* Quick-toggle chips (HALO only / Stale only / Hide closed-lost) */}
      <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
        <QuickToggle label="HALO only" icon="🎯" active={showHaloOnly} onToggle={() => setShowHaloOnly(v => !v)} />
        <QuickToggle label="Stale only" icon="⏰" active={showStaleOnly} onToggle={() => setShowStaleOnly(v => !v)} />
        <QuickToggle label="Hide closed-lost" icon="🚫" active={hideClosedLost} onToggle={() => setHideClosedLost(v => !v)} />
      </div>

      {/* Single consolidated filter row */}
      <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input
          placeholder={`Search all ${fmtNum(total || 0)} leads...`}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...S.input, flex: "1 1 200px", minWidth: 180 }}
        />
        <select value={label} onChange={e => setLabel(e.target.value)}
          style={{ ...S.select, fontSize: 12 }}>
          <option value="">All Labels</option>
          {allLabels.map(l => <option key={l.id} value={l.name}>{l.name}</option>)}
        </select>
        <select value={sort} onChange={e => setSort(e.target.value)}
          style={{ ...S.select, fontSize: 12 }}>
          <option value="">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="name">Name A-Z</option>
          <option value="name_desc">Name Z-A</option>
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ ...S.select, fontSize: 12 }}>
          <option value="">All Statuses</option>
          {LEAD_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={pipelineStage} onChange={e => setPipelineStage(e.target.value)}
          style={{ ...S.select, fontSize: 12 }}>
          <option value="">All Pipeline Stages</option>
          {(pipelineCfg?.pipelines || []).map(p => (
            <optgroup key={p.id} label={p.label}>
              {p.stages.map(s => <option key={`${p.id}:${s}`} value={s}>{s}</option>)}
            </optgroup>
          ))}
        </select>
        <select value={haloLeadSource} onChange={e => setHaloLeadSource(e.target.value)}
          style={{ ...S.select, fontSize: 12 }}>
          <option value="">All Sources</option>
          {(filterValues.halo_lead_source || []).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={sourceBatch} onChange={e => setSourceBatch(e.target.value)}
          style={{ ...S.select, fontSize: 12 }}>
          <option value="">All Batches</option>
          {(filterValues.source_batch || []).map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <select value={tier} onChange={e => setTier(e.target.value)}
          style={{ ...S.select, fontSize: 12 }}>
          <option value="">All Tiers</option>
          {HALO_TIERS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      {/* Active filter pills */}
      {(label || statusFilter || sort || debouncedSearch || pipelineStage || haloLeadSource || sourceBatch || tier) && (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 10, alignItems: "center" }}>
          {debouncedSearch && <span onClick={() => setSearch("")} style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 10, background: "#FFF7ED", color: COLORS.orange, cursor: "pointer", border: `1px solid ${COLORS.orange}33` }}>search: "{debouncedSearch}" ✕</span>}
          {label && <span onClick={() => setLabel("")} style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 10, background: "#FFF7ED", color: COLORS.orange, cursor: "pointer", border: `1px solid ${COLORS.orange}33` }}>label: {label} ✕</span>}
          {statusFilter && <span onClick={() => setStatusFilter("")} style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 10, background: "#FFF7ED", color: COLORS.orange, cursor: "pointer", border: `1px solid ${COLORS.orange}33` }}>status: {statusFilter} ✕</span>}
          {sort && <span onClick={() => setSort("")} style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 10, background: "#FFF7ED", color: COLORS.orange, cursor: "pointer", border: `1px solid ${COLORS.orange}33` }}>sort: {sort} ✕</span>}
          {pipelineStage && <span onClick={() => setPipelineStage("")} style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 10, background: "#FFF7ED", color: COLORS.orange, cursor: "pointer", border: `1px solid ${COLORS.orange}33` }}>stage: {pipelineStage} ✕</span>}
          {haloLeadSource && <span onClick={() => setHaloLeadSource("")} style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 10, background: "#FFF7ED", color: COLORS.orange, cursor: "pointer", border: `1px solid ${COLORS.orange}33` }}>source: {haloLeadSource} ✕</span>}
          {sourceBatch && <span onClick={() => setSourceBatch("")} style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 10, background: "#FFF7ED", color: COLORS.orange, cursor: "pointer", border: `1px solid ${COLORS.orange}33` }}>batch: {sourceBatch} ✕</span>}
          {tier && <span onClick={() => setTier("")} style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 10, background: "#FFF7ED", color: COLORS.orange, cursor: "pointer", border: `1px solid ${COLORS.orange}33` }}>tier: {tier} ✕</span>}
          <span onClick={() => { setSearch(""); setLabel(""); setStatusFilter(""); setSort(""); setPipelineStage(""); setHaloLeadSource(""); setSourceBatch(""); setTier(""); }} style={{ fontSize: 10, color: COLORS.grayMid, cursor: "pointer", textDecoration: "underline" }}>Clear all</span>
        </div>
      )}

      {loading ? <Spinner /> : (
        <div style={{ display: "grid", gridTemplateColumns: selected ? "1fr 420px" : "1fr", gap: 16 }}>
          {/* Lead list */}
          <div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {filtered.map(lead => (
                <LeadCard
                  key={lead.id}
                  lead={lead}
                  vertical={vertical}
                  selected={selected?.id === lead.id}
                  onClick={() => setSelected(selected?.id === lead.id ? null : lead)}
                  onStatusChange={updateStatus}
                  onReplyChange={updateReplyState}
                  onLogOutreach={logOutreach}
                />
              ))}
              {filtered.length === 0 && (
                <div style={{ ...S.card, textAlign: "center", color: COLORS.grayMid, padding: 40 }}>
                  No leads match your filters.
                </div>
              )}
              {/* Pagination */}
              {total > 0 && (
                <div style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "12px 0", marginTop: 8, fontSize: 12, color: COLORS.grayMid,
                }}>
                  <span>
                    Showing {Math.min(page * PAGE_SIZE + 1, total)}–{Math.min((page + 1) * PAGE_SIZE, total)} of {fmtNum(total)} leads
                  </span>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                      style={{ ...S.btn("ghost"), padding: "4px 10px", fontSize: 11 }}>← Prev</button>
                    <span style={{ fontSize: 11 }}>Page {page + 1} of {Math.ceil(total / PAGE_SIZE)}</span>
                    <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * PAGE_SIZE >= total}
                      style={{ ...S.btn("ghost"), padding: "4px 10px", fontSize: 11 }}>Next →</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Full-screen lead detail modal */}
          {selected && (
            <LeadDetailModal
              lead={selected}
              onClose={() => setSelected(null)}
            />
          )}
        </div>
      )}
      </>}
    </div>
  );
}

// Fix 4: Address renderer — "City, ST ZIP" (gracefully degrades when city
// field has been polluted with state+zip during import).
function formatLocation(lead) {
  const city = lead.city || "";
  const state = lead.state || "";
  const zip = lead.zip || "";
  if (state && zip) return `${city}, ${state} ${zip}`.trim().replace(/^,\s*/, "");
  if (state) return `${city}, ${state}`.trim().replace(/^,\s*/, "");
  // City field often contains "CA 90026" from import — display as-is
  return city;
}

// Fix 9: PropertyMgmt 0-100 score bar with color coding
function PMScoreBar({ score }) {
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  const color = pct >= 61 ? COLORS.success : pct >= 31 ? "#F59E0B" : COLORS.danger;
  return (
    <div title={`PM qualifying score: ${pct}/100`}>
      <div style={{ fontSize: 10, color: COLORS.grayMid, marginBottom: 3 }}>
        PM Score · <span style={{ color, fontWeight: 700 }}>{pct}</span>/100
      </div>
      <div style={{ height: 6, background: COLORS.greenDeep, borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, transition: "width 0.2s" }} />
      </div>
    </div>
  );
}

const OUTREACH_TYPES = ["LinkedIn DM", "Email", "Call", "Meeting", "Other"];

function LinkedInButton({ contacts, orgName, vertical, orgId, contactId, style }) {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(null);
  const [sentTimer, setSentTimer] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  useEffect(() => { return () => { if (sentTimer) clearTimeout(sentTimer); }; }, [sentTimer]);

  // Deduplicate contacts by id, falling back to name
  const seen = new Set();
  const uniqueContacts = (contacts || []).filter(c => {
    if (!c || !(c.full_name || c.first_name || c.contact_name)) return false;
    const key = c.id || c.contact_id || (c.full_name || c.contact_name || "").toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  async function searchPerson(person) {
    const firstName = titleCase(person?.first_name || (person?.full_name || person?.contact_name || "").split(/\s+/)[0]);
    const lastName = (person?.last_name || (person?.full_name || person?.contact_name || "").split(/\s+/).slice(1).join(" ") || "").trim();
    const query = `${firstName} ${lastName}`.trim();
    // Agent 6 is source of truth; fall back to local template if Railway is down.
    const msg = await fetchAgent6Dm(vertical || "RCFE", firstName || "there");
    navigator.clipboard.writeText(msg).catch(() => {});
    window.open(`https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(query)}`, "_blank", "noopener");
    setSent({ name: query, msg, person });
    setOpen(false);
    setSentTimer(setTimeout(() => setSent(null), 60000));
  }

  function searchOrg() {
    const query = orgName || "";
    window.open(`https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(query)}`, "_blank", "noopener");
    setSent({ name: query, msg: null, person: null });
    setOpen(false);
    setSentTimer(setTimeout(() => setSent(null), 60000));
  }

  async function markSent() {
    if (!sent) return;
    const p = sent.person || {};
    await api("/activities", "POST", {
      type: "linkedin_outreach",
      description: `LinkedIn connection request sent to ${sent.name}`,
      subject: sent.msg || `Searched org: ${sent.name}`,
      org_id: p.org_id || orgId || null,
      contact_id: p.id || p.contact_id || contactId || null,
    });
    if (p.id || p.contact_id || contactId) {
      const cid = p.id || p.contact_id || contactId;
      await api(`/contacts/${cid}`, "PATCH", { linkedin_sent: 1 }).catch(() => {});
    }
    setSent(null);
  }

  const btnStyle = { ...(style || S.btn("ghost")), fontSize: 11, padding: "4px 8px" };
  const dropStyle = {
    position: "absolute", top: "100%", left: 0, zIndex: 50,
    background: COLORS.greenDark || "#1e293b", border: `1px solid ${COLORS.cardBorder}`,
    borderRadius: 6, minWidth: 260, overflow: "hidden",
    boxShadow: "0 4px 16px rgba(0,0,0,0.4)", marginTop: 2,
  };
  const rowStyle = { padding: "8px 12px", cursor: "pointer", fontSize: 12, borderBottom: `1px solid ${COLORS.cardBorder}22` };
  const hoverIn = e => { e.currentTarget.style.background = `${COLORS.orange}22`; };
  const hoverOut = e => { e.currentTarget.style.background = "transparent"; };

  if (sent) {
    return (
      <div style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
        <span style={{ fontSize: 10, color: COLORS.success }}>{sent.msg ? "Message copied!" : "Org searched"}</span>
        <button onClick={markSent} style={{ ...S.btn("primary"), fontSize: 10, padding: "3px 8px" }}>
          Mark LinkedIn Sent
        </button>
        <button onClick={() => setSent(null)} style={{ ...S.btn("ghost"), fontSize: 10, padding: "3px 6px" }}>x</button>
      </div>
    );
  }

  if (uniqueContacts.length === 0) {
    return (
      <button onClick={e => { e.stopPropagation(); searchOrg(); }}
        title={`No contacts linked — searching org: ${orgName}`}
        style={btnStyle}>
        💼 LinkedIn
      </button>
    );
  }

  if (uniqueContacts.length === 1 && !orgName) {
    return (
      <button onClick={e => { e.stopPropagation(); searchPerson(uniqueContacts[0]); }}
        title={`LinkedIn: ${uniqueContacts[0].full_name || uniqueContacts[0].contact_name}`}
        style={btnStyle}>
        💼 LinkedIn
      </button>
    );
  }

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
        style={btnStyle}>
        💼 LinkedIn ▾
      </button>
      {open && (
        <div style={dropStyle}>
          {uniqueContacts.map((c, i) => {
            const name = c.full_name || c.contact_name || `${c.first_name || ""} ${c.last_name || ""}`.trim();
            return (
              <div key={c.id || c.contact_id || i} onClick={e => { e.stopPropagation(); searchPerson(c); }}
                style={rowStyle} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                <div style={{ fontWeight: 600 }}>👤 {name}</div>
                {(c.job_title || c.contact_title) && <div style={{ fontSize: 10, color: COLORS.grayMid }}>{c.job_title || c.contact_title}</div>}
              </div>
            );
          })}
          <div onClick={e => { e.stopPropagation(); searchOrg(); }}
            style={{ ...rowStyle, borderBottom: "none" }} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
            <div style={{ fontWeight: 600, color: COLORS.grayMid }}>🏢 {orgName}</div>
            <div style={{ fontSize: 10, color: COLORS.grayMid }}>Company search</div>
          </div>
        </div>
      )}
    </div>
  );
}

function LeadCard({ lead, vertical, selected, onClick, onStatusChange, onReplyChange, onLogOutreach }) {
  const replyState = lead.reply_state || "No Reply";
  const stop = e => e.stopPropagation();

  const lastActivity = lead.last_activity || null;
  const lastActivityAt = lead.last_activity_at || null;
  const fields = verticalFields(lead, vertical).filter(Boolean);
  const orgName = lead.org_name || lead.title || "";
  const openExt = (url) => (e) => { stop(e); if (url) window.open(url, "_blank", "noopener"); };
  const googleUrl = `https://www.google.com/search?q=${encodeURIComponent(`${orgName} ${lead.city || ""}`.trim())}`;
  const location = formatLocation(lead);

  return (
    <div onClick={onClick} style={{
      ...S.card,
      cursor: "pointer",
      borderColor: selected ? COLORS.orange : COLORS.cardBorder,
      borderLeft: `4px solid ${selected ? COLORS.orange : STATUS_COLORS[lead.status] || COLORS.cardBorder}`,
      transition: "border-color 0.15s",
    }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {lead.org_name || lead.title}
          </div>
          {vertical === "RCFE" && lead.cdss_license_number && (
            <div style={{ marginBottom: 2 }}>
              <CdssLicenseBadge license={lead.cdss_license_number} vertical="RCFE" compact />
            </div>
          )}
          <div style={{ fontSize: 11, color: COLORS.grayMid }}>
            <span style={{ color: "#B0B3AB", marginRight: 6 }}>#{lead.id || lead.lead_id}</span>
            {location}
            {lead.priority_score > 0 ? ` · Score: ${lead.priority_score}` : ""}
          </div>
          {fields.length > 0 && (
            <div style={{ fontSize: 11, color: COLORS.grayMid, marginTop: 3, display: "flex", flexWrap: "wrap", gap: 6 }}>
              {fields.map((f, i) => (
                <span key={i} style={{
                  background: COLORS.greenDeep, border: `1px solid ${COLORS.cardBorder}`,
                  borderRadius: 3, padding: "2px 6px",
                }}>{f}</span>
              ))}
            </div>
          )}
          {/* Fix 9: PropertyMgmt score bar */}
          {vertical === "PropertyMgmt" && (
            <div style={{ marginTop: 8, maxWidth: 260 }}>
              <PMScoreBar score={lead.pm_score} />
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
          <Badge status={lead.status} />
          {lastActivity && (
            <span style={{ fontSize: 10, color: COLORS.grayMid }}>
              {lastActivity}
              {lastActivityAt ? ` · ${new Date(lastActivityAt).toLocaleDateString()}` : ""}
            </span>
          )}
        </div>
      </div>

      {/* Contact row */}
      {(lead.contact_name || lead.email_work || lead.phone_work || lead.linkedin_url) && (
        <div style={{ fontSize: 11, color: COLORS.grayMid, marginTop: 6, display: "flex", flexWrap: "wrap", gap: 8 }}>
          {lead.contact_name && <span>👤 {lead.contact_name}{lead.contact_title ? ` · ${lead.contact_title}` : ""}</span>}
          {lead.email_work && <span>✉ {lead.email_work}</span>}
          {lead.phone_work && <span>📞 {lead.phone_work}</span>}
          {lead.linkedin_url && (
            <a href={lead.linkedin_url} target="_blank" rel="noreferrer" onClick={stop}
              style={{ color: COLORS.orange }}>🔗 LinkedIn</a>
          )}
        </div>
      )}

      {/* Fix 2: Outreach Log + Status + Reply row */}
      <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
        <select
          value=""
          onClick={stop}
          onChange={e => {
            if (!e.target.value) return;
            onLogOutreach(lead.id, e.target.value);
            e.target.value = "";
          }}
          title="Log an outreach touch"
          style={{ ...S.select, fontSize: 11, padding: "4px 8px", borderLeft: `3px solid ${COLORS.orange}` }}>
          <option value="">+ Log Outreach</option>
          {OUTREACH_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>

        <select
          value={lead.status}
          onClick={stop}
          onChange={e => onStatusChange(lead.id, e.target.value)}
          style={{ ...S.select, fontSize: 11, padding: "4px 8px" }}>
          {LEAD_STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>

        <select
          value={replyState}
          onClick={stop}
          onChange={e => onReplyChange(lead.id, e.target.value)}
          title="Reply state"
          style={{
            ...S.select,
            fontSize: 11,
            padding: "4px 8px",
            borderLeft: `3px solid ${REPLY_STATE_COLORS[replyState] || COLORS.cardBorder}`,
          }}>
          {REPLY_STATES.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      {/* Quick action links (external). LinkedIn DM lives in the expanded
          detail view — the faceplate keeps primary CTAs only. */}
      <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
        <button onClick={lead.phone_work ? openExt(`tel:${lead.phone_work}`) : stop}
          disabled={!lead.phone_work}
          title={lead.phone_work ? `Call ${lead.phone_work}` : "No phone on file"}
          style={{
            ...S.btn(lead.phone_work ? "primary" : "ghost"), fontSize: 11, padding: "4px 10px",
            opacity: lead.phone_work ? 1 : 0.4,
            cursor: lead.phone_work ? "pointer" : "not-allowed",
          }}>📞 Call{lead.phone_work ? ` · ${lead.phone_work}` : ""}</button>
        <button onClick={openExt(googleUrl)}
          title={`Google search for ${orgName}`}
          style={{ ...S.btn("ghost"), fontSize: 11, padding: "4px 8px" }}>
          🔍 Google
        </button>
        <button onClick={openExt(CALENDLY)}
          style={{ ...S.btn("ghost"), fontSize: 11, padding: "4px 8px" }}>
          📅 Book
        </button>
      </div>
    </div>
  );
}

// Fix 3: Vertical-specific detail sections
function VerticalDetailSection({ lead, vertical }) {
  const rows = (() => {
    switch (vertical) {
      case "RCFE":
        return [
          ["License #", lead.license_number],
          ["Bed Count", lead.facility_capacity],
          ["Facility Status", lead.facility_status],
          ["Citation Count", lead.citation_count ?? 0],
          ["Last Inspection", lead.last_inspection_date],
        ];
      case "Hotel":
        return [
          ["Room Count", lead.room_count],
          ["Floors", lead.floor_count],
          ["Star Rating", lead.star_rating ? `${lead.star_rating}★` : null],
          ["Brand", lead.brand_flag],
          ["Chain Flag", lead.brand_flag ? "Yes" : null],
        ];
      case "Hospital":
        return [
          ["Patient Capacity", lead.patient_capacity],
          ["Floors", lead.floor_count],
          ["CMS/Google Rating", lead.cms_rating],
          ["VA Facility", lead.is_va ? "🇺🇸 Yes" : "No"],
        ];
      case "Campus":
        return [
          ["Enrollment", lead.enrollment ? fmtNum(lead.enrollment) : null],
          ["NCAA Division", lead.ncaa_division],  // Fix 8
          ["Campus Type", lead.campus_type],
          ["Multi-story Buildings", lead.multi_story_buildings],
        ];
      case "PropertyMgmt":
        return [
          ["Portfolio Size", lead.portfolio_size ? fmtNum(lead.portfolio_size) : null],
          ["Units", lead.unit_count ? fmtNum(lead.unit_count) : null],
          ["Service Area", lead.service_area],
          ["PM Score", lead.pm_score != null ? `${lead.pm_score}/100` : null],
          ["HOA", lead.has_hoa ? "Yes" : "No"],
        ];
      default:
        return [];
    }
  })();
  return (
    <div>
      <div style={{ fontSize: 10, color: COLORS.grayMid, marginBottom: 6, textTransform: "uppercase" }}>
        {vertical} Details
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
        {rows.map(([k, v]) => (
          <div key={k} style={{ background: COLORS.greenDeep, borderRadius: 5, padding: "8px 10px" }}>
            <div style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>{k}</div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>
              {v == null || v === "" ? "—" : v}
            </div>
          </div>
        ))}
      </div>
      {vertical === "PropertyMgmt" && (
        <div style={{ marginTop: 10 }}>
          <PMScoreBar score={lead.pm_score} />
        </div>
      )}
    </div>
  );
}

// Fix 6: Email compose modal — uses Railway POST /send-email (Gmail API)
function EmailModal({ lead, onClose, onSent, initialSubject, initialBody }) {
  const [to, setTo] = useState(lead.email_work || "");
  const [subject, setSubject] = useState(
    initialSubject != null
      ? initialSubject
      : `Outpost Preparedness — ${lead.org_name || lead.title || ""}`
  );
  const [body, setBody] = useState(
    initialBody != null
      ? initialBody
      : `Hi ${lead.contact_name ? lead.contact_name.split(/\s+/)[0] : "there"},\n\n` +
        `\n\nDrew Jennings | Founder & CEO | USMC Ret.\nOutpost Preparedness | outpostprep.com | SDVOSB`
  );
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  async function send() {
    setSending(true);
    setError(null);
    try {
      const r = await api("/send-email", "POST", {
        to, subject, body,
        lead_id: lead.id, org_id: lead.org_id, contact_id: lead.contact?.id,
      });
      if (!r) throw new Error("Send failed — check Railway logs");
      onSent && onSent({ subject, body });
      onClose();
    } catch (e) {
      setError(e.message || String(e));
      setSending(false);
    }
  }

  return (
    <CCModalShell title="Compose Email" onClose={onClose}>
      <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>To</label>
      <input value={to} onChange={e => setTo(e.target.value)} style={{ ...S.input, width: "100%", marginBottom: 10 }} />
      <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Subject</label>
      <input value={subject} onChange={e => setSubject(e.target.value)} style={{ ...S.input, width: "100%", marginBottom: 10 }} />
      <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Body</label>
      <textarea value={body} onChange={e => setBody(e.target.value)}
        style={{ ...S.input, width: "100%", height: 220, resize: "vertical", fontFamily: "inherit" }} />
      {error && <div style={{ color: COLORS.danger, fontSize: 12, marginTop: 8 }}>{error}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
        <button onClick={onClose} style={{ ...S.btn("ghost") }}>Cancel</button>
        <button onClick={send} disabled={sending || !to} style={{ ...S.btn("primary") }}>
          {sending ? "Sending…" : "📧 Send via Gmail"}
        </button>
      </div>
    </CCModalShell>
  );
}

// Fix 7: Product catalog from OUTPOST_MASTER.md
// 🟡 UPGRADE SUGGESTION: Connect to Shopify API for live product catalog
// instead of this hardcoded list — stock/price drift will silently break quotes.
const PRODUCT_CATALOG = [
  { sku: "HALO-PWR",      name: "HALO Power Chair",      price: 3200.00, lease: 111 },
  { sku: "HALO-ULTRA",    name: "HALO Ultra",            price: 1350.00, lease: null },
  { sku: "HALO-SWIFT",    name: "HALO Swift",            price: 1159.00, lease: null },
  { sku: "HALO-CURVE",    name: "HALO Curve",            price: 2888.68, lease: null },
  { sku: "HALO-LIFT",     name: "HALO Lift",             price: 1325.09, lease: null },
  { sku: "HALO-MINI",     name: "HALO Mini",             price:  529.00, lease: null },
  { sku: "PRO-AIRPORT",   name: "ProMove Airport Kit",   price:  599.00, lease: 29 },
  { sku: "SUN-605",       name: "SunCase 605",           price: 1285.00, lease: null },
  { sku: "SUN-2425",      name: "SunCase 2425",          price: 3550.00, lease: 124 },
  { sku: "SUN-3651",      name: "SunCase 3651",          price: 5400.00, lease: null },
  { sku: "GOBAG-ESS",     name: "Go-Bag Essential+",     price:  399.00, lease: null },
];
const SETUP_FEES = {
  RCFE: 2497, Hotel: 2497, Hospital: 4997, Campus: 2497, PropertyMgmt: 2497,
};
const MONTHLY_SUBS = {
  RCFE: 199, Hotel: 199, Hospital: 399, Campus: 249, PropertyMgmt: 249,
};

function QuoteModal({ lead, vertical, onClose, onStatusChange }) {
  const [qty, setQty] = useState(() => Object.fromEntries(PRODUCT_CATALOG.map(p => [p.sku, 0])));
  const [includeSetup, setIncludeSetup] = useState(true);
  const [includeSubscription, setIncludeSubscription] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const hardwareTotal = PRODUCT_CATALOG.reduce(
    (sum, p) => sum + p.price * (qty[p.sku] || 0), 0
  );
  const setupFee = includeSetup ? (SETUP_FEES[vertical] || 0) : 0;
  const monthly = includeSubscription ? (MONTHLY_SUBS[vertical] || 0) : 0;
  const upfront = hardwareTotal + setupFee;

  function buildQuoteBody() {
    const lines = [];
    lines.push(`Quote for ${lead.org_name || lead.title}`);
    lines.push("=".repeat(40));
    lines.push("");
    lines.push("HARDWARE");
    PRODUCT_CATALOG.filter(p => qty[p.sku]).forEach(p => {
      lines.push(`  ${qty[p.sku]}× ${p.name} @ ${fmt$$(p.price)} = ${fmt$$(p.price * qty[p.sku])}`);
    });
    if (!PRODUCT_CATALOG.some(p => qty[p.sku])) lines.push("  (none selected)");
    lines.push("");
    lines.push(`Hardware subtotal    ${fmt$$(hardwareTotal)}`);
    if (includeSetup)       lines.push(`Setup fee (${vertical})   ${fmt$$(setupFee)}`);
    if (includeSubscription) lines.push(`Monthly subscription   ${fmt$$(monthly)}/mo`);
    lines.push("");
    lines.push(`Upfront total:       ${fmt$$(upfront)}`);
    if (monthly) lines.push(`Plus:                ${fmt$$(monthly)}/mo`);
    lines.push("");
    lines.push("Drew Jennings | Founder & CEO | USMC Ret.");
    lines.push("Outpost Preparedness | outpostprep.com | SDVOSB");
    return lines.join("\n");
  }

  async function send() {
    if (!lead.email_work) {
      setError("Lead has no email on file — add one first or send manually.");
      return;
    }
    setSending(true);
    setError(null);
    try {
      const subject = `Outpost Preparedness Quote — ${lead.org_name || lead.title || ""}`;
      const r = await api("/send-email", "POST", {
        to: lead.email_work, subject, body: buildQuoteBody(),
        lead_id: lead.id, org_id: lead.org_id,
      });
      if (!r) throw new Error("Send failed");
      onStatusChange(lead.id, "Quote Sent");
      onClose();
    } catch (e) {
      setError(e.message || String(e));
      setSending(false);
    }
  }

  return (
    <CCModalShell title={`Quote — ${lead.org_name || lead.title || ""}`} onClose={onClose} wide>
      <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ color: COLORS.grayMid, textAlign: "left" }}>
              <th style={{ padding: "4px 6px" }}>Product</th>
              <th style={{ padding: "4px 6px", textAlign: "right" }}>Price</th>
              <th style={{ padding: "4px 6px", textAlign: "right" }}>Lease/mo</th>
              <th style={{ padding: "4px 6px", textAlign: "center", width: 80 }}>Qty</th>
              <th style={{ padding: "4px 6px", textAlign: "right" }}>Line</th>
            </tr>
          </thead>
          <tbody>
            {PRODUCT_CATALOG.map(p => {
              const line = p.price * (qty[p.sku] || 0);
              return (
                <tr key={p.sku} style={{ borderTop: `1px solid ${COLORS.cardBorder}` }}>
                  <td style={{ padding: "6px" }}>{p.name}</td>
                  <td style={{ padding: "6px", textAlign: "right" }}>{fmt$$(p.price)}</td>
                  <td style={{ padding: "6px", textAlign: "right", color: COLORS.grayMid }}>
                    {p.lease ? `${fmt$$(p.lease)}/mo` : "—"}
                  </td>
                  <td style={{ padding: "6px", textAlign: "center" }}>
                    <input type="number" min={0} value={qty[p.sku]}
                      onChange={e => setQty(q => ({ ...q, [p.sku]: Math.max(0, Number(e.target.value) || 0) }))}
                      style={{ ...S.input, width: 60, padding: "2px 6px", textAlign: "center" }} />
                  </td>
                  <td style={{ padding: "6px", textAlign: "right", fontWeight: line ? 700 : 400 }}>
                    {line ? fmt$$(line) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ borderTop: `1px solid ${COLORS.cardBorder}`, marginTop: 10, paddingTop: 10 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, marginBottom: 4 }}>
          <input type="checkbox" checked={includeSetup} onChange={e => setIncludeSetup(e.target.checked)} />
          Setup fee ({vertical}): {fmt$$(SETUP_FEES[vertical] || 0)}
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
          <input type="checkbox" checked={includeSubscription} onChange={e => setIncludeSubscription(e.target.checked)} />
          Monthly subscription: {fmt$$(MONTHLY_SUBS[vertical] || 0)}/mo
        </label>
        <div style={{ marginTop: 10, fontSize: 14 }}>
          <div>Hardware: <strong>{fmt$$(hardwareTotal)}</strong></div>
          <div>Upfront total: <strong style={{ color: COLORS.orange }}>{fmt$$(upfront)}</strong></div>
          {monthly > 0 && <div>Recurring: <strong>{fmt$$(monthly)}/mo</strong></div>}
        </div>
      </div>
      {error && <div style={{ color: COLORS.danger, fontSize: 12, marginTop: 8 }}>{error}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
        <button onClick={onClose} style={{ ...S.btn("ghost") }}>Cancel</button>
        <button onClick={send} disabled={sending || upfront === 0} style={{ ...S.btn("primary") }}>
          {sending ? "Sending…" : "📧 Send Quote"}
        </button>
      </div>
    </CCModalShell>
  );
}

function CCModalShell({ title, children, onClose, wide }) {
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        ...S.card, width: wide ? 720 : 520, maxWidth: "96vw",
        maxHeight: "92vh", overflowY: "auto",
        display: "flex", flexDirection: "column", gap: 10,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 16, fontWeight: 800 }}>{title}</div>
          <button onClick={onClose} style={{ ...S.btn("ghost"), padding: "4px 8px" }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function LeadDetail({ lead, vertical, onClose, onStatusChange, onReplyChange, onLogOutreach }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [activities, setActivities] = useState([]);
  const [emailModal, setEmailModal] = useState(false);
  const [quoteModal, setQuoteModal] = useState(false);
  const replyState = lead.reply_state || "No Reply";
  const location = formatLocation(lead);

  useEffect(() => {
    setHistoryLoading(true);
    setHistory([]);
    setActivities([]);
    api(`/notes?lead_id=${lead.id}`).then(r => {
      setHistory(Array.isArray(r) ? r : []);
      setHistoryLoading(false);
    });
    api(`/activities?lead_id=${lead.id}&limit=20`).then(r => {
      setActivities(Array.isArray(r) ? r : []);
    });
  }, [lead.id]);

  async function saveNote() {
    if (!note.trim()) return;
    setSaving(true);
    const body = note;
    const saved = await api("/notes", "POST", { lead_id: lead.id, body });
    setNote("");
    setSaving(false);
    setHistory(h => [saved || { body, created_at: new Date().toISOString() }, ...h]);
  }

  return (
    <>
      <div style={{
        ...S.card, position: "sticky", top: 0, maxHeight: "calc(100vh - 120px)",
        overflowY: "auto", display: "flex", flexDirection: "column", gap: 16,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>{lead.org_name || lead.title}</div>
            <div style={{ fontSize: 11, color: COLORS.grayMid }}>
              {lead.address ? `${lead.address} · ` : ""}{location}
            </div>
          </div>
          <button onClick={onClose} style={{ ...S.btn("ghost"), padding: "4px 8px" }}>✕</button>
        </div>

        {/* Fix 3: Vertical-specific detail block */}
        <VerticalDetailSection lead={lead} vertical={vertical} />

        {/* Contact */}
        {(lead.contact_name || lead.email_work || lead.phone_work || lead.linkedin_url) && (
          <div style={{ background: COLORS.greenDeep, borderRadius: 5, padding: "10px 12px" }}>
            <div style={{ fontSize: 10, color: COLORS.grayMid, marginBottom: 4, textTransform: "uppercase" }}>Contact</div>
            {lead.contact_name && <div style={{ fontWeight: 700 }}>{lead.contact_name}</div>}
            {lead.contact_title && <div style={{ fontSize: 11, color: COLORS.grayMid }}>{lead.contact_title}</div>}
            {lead.email_work && <div style={{ fontSize: 12, color: COLORS.orange, marginTop: 4 }}>✉ {lead.email_work}</div>}
            {lead.phone_work && <div style={{ fontSize: 12, color: COLORS.grayMid }}>📞 {lead.phone_work}</div>}
            {lead.linkedin_url && (
              <a href={lead.linkedin_url} target="_blank" rel="noreferrer"
                style={{ fontSize: 12, color: COLORS.orange, display: "block", marginTop: 4 }}>
                🔗 LinkedIn
              </a>
            )}
          </div>
        )}

        {/* Status */}
        <div>
          <div style={{ fontSize: 10, color: COLORS.grayMid, marginBottom: 6, textTransform: "uppercase" }}>Status</div>
          <select value={lead.status} onChange={e => onStatusChange(lead.id, e.target.value)} style={{ ...S.select, width: "100%" }}>
            {LEAD_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>

        {/* Reply state */}
        <div>
          <div style={{ fontSize: 10, color: COLORS.grayMid, marginBottom: 6, textTransform: "uppercase" }}>Reply State</div>
          <select value={replyState} onChange={e => onReplyChange(lead.id, e.target.value)}
            style={{
              ...S.select, width: "100%",
              borderLeft: `4px solid ${REPLY_STATE_COLORS[replyState] || COLORS.cardBorder}`,
            }}>
            {REPLY_STATES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>

        {/* Fix 2: Outreach Log */}
        <div>
          <div style={{ fontSize: 10, color: COLORS.grayMid, marginBottom: 6, textTransform: "uppercase" }}>Log Outreach</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {OUTREACH_TYPES.map(t => (
              <button key={t} onClick={() => onLogOutreach(lead.id, t)}
                style={{ ...S.btn("ghost"), fontSize: 11, padding: "6px 10px" }}>
                + {t}
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div>
          <div style={{ fontSize: 10, color: COLORS.grayMid, marginBottom: 6, textTransform: "uppercase" }}>Actions</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <a href={CALENDLY} target="_blank" rel="noreferrer"
              style={{ ...S.btn("primary"), textAlign: "center", textDecoration: "none", display: "block" }}>
              📅 Book Meeting
            </a>
            <button onClick={() => setEmailModal(true)} disabled={!lead.email_work}
              style={{ ...S.btn(lead.email_work ? "ghost" : "ghost"), width: "100%",
                       opacity: lead.email_work ? 1 : 0.4 }}>
              📧 Email {lead.email_work ? `(${lead.email_work})` : "(no email on file)"}
            </button>
            <button onClick={() => setQuoteModal(true)} style={{ ...S.btn("ghost"), width: "100%" }}>
              💰 Build Quote
            </button>
            {lead.phone_work && (
              <a href={`tel:${lead.phone_work}`}
                style={{ ...S.btn("ghost"), textAlign: "center", textDecoration: "none", display: "block" }}>
                📞 Call {lead.phone_work}
              </a>
            )}
          </div>
        </div>

        {/* Note input */}
        <div>
          <div style={{ fontSize: 10, color: COLORS.grayMid, marginBottom: 6, textTransform: "uppercase" }}>Add Note</div>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Log a call, note an objection, next steps..."
            style={{ ...S.input, height: 80, resize: "vertical" }}
          />
          <button onClick={saveNote} disabled={saving} style={{ ...S.btn("primary"), marginTop: 6, width: "100%" }}>
            {saving ? "Saving..." : "Save Note"}
          </button>
        </div>

        {/* Outreach + notes history merged */}
        <div>
          <div style={{ fontSize: 10, color: COLORS.grayMid, marginBottom: 6, textTransform: "uppercase" }}>History</div>
          {historyLoading && activities.length === 0 && history.length === 0 ? (
            <div style={{ fontSize: 11, color: COLORS.grayMid }}>Loading…</div>
          ) : activities.length === 0 && history.length === 0 ? (
            <div style={{ fontSize: 11, color: COLORS.grayMid, fontStyle: "italic" }}>No activity yet.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 260, overflowY: "auto" }}>
              {activities.map((a, i) => (
                <div key={`a${a.id || i}`} style={{ background: COLORS.greenDeep, borderRadius: 5, padding: "8px 10px" }}>
                  <div style={{ fontSize: 11, color: COLORS.orange, fontWeight: 700 }}>{a.type}</div>
                  {a.subject && <div style={{ fontSize: 12 }}>{a.subject}</div>}
                  {a.created_at && (
                    <div style={{ fontSize: 10, color: COLORS.grayMid, marginTop: 2 }}>
                      {new Date(a.created_at).toLocaleString()}
                    </div>
                  )}
                </div>
              ))}
              {history.map((h, i) => (
                <div key={`n${h.id || i}`} style={{ background: COLORS.greenDeep, borderRadius: 5, padding: "8px 10px" }}>
                  <div style={{ fontSize: 10, color: COLORS.grayMid, fontWeight: 700 }}>NOTE</div>
                  <div style={{ fontSize: 12 }}>{h.body || h.content || h.note || ""}</div>
                  {(h.created_at || h.timestamp) && (
                    <div style={{ fontSize: 10, color: COLORS.grayMid, marginTop: 4 }}>
                      {new Date(h.created_at || h.timestamp).toLocaleString()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {emailModal && (
        <EmailModal lead={lead} onClose={() => setEmailModal(false)}
          onSent={() => setActivities(a => [{
            type: "Email", subject: "Email sent", created_at: new Date().toISOString()
          }, ...a])} />
      )}
      {quoteModal && (
        <QuoteModal lead={lead} vertical={vertical} onClose={() => setQuoteModal(false)}
          onStatusChange={onStatusChange} />
      )}
    </>
  );
}

// ─── DASHBOARD ───────────────────────────────────────────────────────────────
// ─── LIGHT THEME TOKENS (Dashboard only) ─────────────────────────────────────
const LIGHT = {
  bg:        "#F5F5F0",
  card:      "#FFFFFF",
  cardLine:  "#E6E5DE",
  ink:       "#1F2937",
  inkMuted:  "#6B7280",
  inkFaint:  "#9A9B96",
  orange:    "#D66F27",
  orangeSoft:"#FDF5EF",
  orangeMid: "#FCEFE4",
  success:   "#10B981",
};

// 9-stage funnel: 5 lead statuses + 4 deal stages.
// `source: "status"` pulls from /api/stats.by_status.
// `source: "deal"` pulls from /deals (aggregated client-side).
// Special key "__open__" = all deals not in Closed Won/Lost.
const FUNNEL_STAGE_MAP = [
  { label: "New Leads",      source: "status", key: "New" },
  { label: "Contacted",      source: "status", key: "Contacted" },
  { label: "Replied",        source: "status", key: "Replied" },
  { label: "Meeting Booked", source: "status", key: "Meeting Booked" },
  { label: "Quote Sent",     source: "status", key: "Quote Sent" },
  { label: "Deal Created",   source: "deal",   key: "__open__" },
  { label: "Negotiation",    source: "deal",   key: "Negotiation" },
  { label: "Closed Won",     source: "deal",   key: "Closed Won" },
  { label: "Closed Lost",    source: "deal",   key: "Closed Lost" },
];

// Stages that count as "Deal Created" — strict pre-Negotiation only.
// Ensures the funnel is a true sequential flow (no overlap with Negotiation).
const DEAL_CREATED_STAGES = ["Lead In", "Qualified", "Demo Scheduled"];

// Time-filter periods for the funnel dropdown.
const FUNNEL_PERIODS = [
  { key: "day",     label: "Day"     },
  { key: "week",    label: "Week"    },
  { key: "month",   label: "Month"   },
  { key: "quarter", label: "Quarter" },
  { key: "year",    label: "Year"    },
  { key: "all",     label: "All"     },
];

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [deals, setDeals] = useState([]);
  const [activities, setActivities] = useState([]);

  // Time-filtered funnel data from /dashboard/funnel?period=…
  const [funnelPeriod, setFunnelPeriod] = useState("all");
  const [funnelData, setFunnelData] = useState(null);

  // Initial global stats + activities (unfiltered)
  useEffect(() => {
    api("/api/stats").then(r => setStats(r));
    api("/deals?limit=1000").then(r => {
      if (Array.isArray(r)) setDeals(r);
    });
    api("/activities?limit=10").then(r => {
      if (Array.isArray(r)) setActivities(r);
    });
  }, []);

  // Funnel data (re-fetched when period changes)
  useEffect(() => {
    api(`/dashboard/funnel?period=${funnelPeriod}`).then(r => {
      if (r) setFunnelData(r);
    });
  }, [funnelPeriod]);

  if (!stats || !funnelData) return <Spinner />;

  // Global KPI numbers (always "all" — period filter only affects funnel)
  const leadsTotal = stats.leads || 0;
  const pipeline   = stats.pipeline_value || 0;

  // Funnel stage data — derived from the time-filtered funnelData
  const lc = funnelData.lead_counts || {};
  const dc = funnelData.deal_counts || {};

  // Deal Created = strict pre-Negotiation
  const openDC = DEAL_CREATED_STAGES.reduce((a, s) => a + ((dc[s] || {}).count || 0), 0);
  const openDV = DEAL_CREATED_STAGES.reduce((a, s) => a + ((dc[s] || {}).value || 0), 0);

  const countFor = (stage) => {
    if (stage.source === "status") return lc[stage.key] || 0;
    if (stage.key === "__open__") return openDC;
    return (dc[stage.key] || {}).count || 0;
  };
  const valueFor = (stage) => {
    if (stage.source === "status") return 0;
    if (stage.key === "__open__") return openDV;
    return (dc[stage.key] || {}).value || 0;
  };

  const funnelStages = FUNNEL_STAGE_MAP.map((s, i, arr) => {
    const count = countFor(s);
    const value = valueFor(s);
    const prev = i > 0 ? arr[i - 1] : null;
    const prevCount = prev ? countFor(prev) : count;
    const conv = (i > 0 && prevCount > 0 && count > 0)
      ? Math.round((count / prevCount) * 100)
      : null;
    return {
      label: s.label, source: s.source, key: s.key,
      count, value, conversionFromPrev: conv, index: i,
    };
  });

  // Status × vertical matrix from funnel endpoint
  const statusVerticalMap = {};
  (funnelData.matrix || []).forEach(cell => {
    if (!statusVerticalMap[cell.status]) statusVerticalMap[cell.status] = {};
    statusVerticalMap[cell.status][cell.vertical] = cell.count;
  });

  const verticalData = funnelData.by_vertical || stats.by_vertical || [];
  const verticalTotal = verticalData.reduce((a, b) => a + b.count, 0);

  const L = {
    page: {
      background: LIGHT.bg, color: LIGHT.ink,
      padding: 24, margin: -24,
      minHeight: "calc(100vh - 60px)",
    },
    card: {
      background: LIGHT.card, border: `1px solid ${LIGHT.cardLine}`,
      borderRadius: 10, padding: 20,
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    },
    kpiCard: {
      background: LIGHT.card, border: `1px solid ${LIGHT.cardLine}`,
      borderRadius: 10, padding: 16, borderTop: `3px solid ${LIGHT.orange}`,
    },
    kpiLabel: {
      fontSize: 11, fontWeight: 700, color: LIGHT.inkMuted,
      textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6,
    },
    kpiValue: { fontSize: 26, fontWeight: 800, color: LIGHT.ink },
    kpiSub:   { fontSize: 11, color: LIGHT.inkFaint, marginTop: 4 },
    section:  {
      fontSize: 12, fontWeight: 700, color: LIGHT.orange,
      textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 14,
    },
  };

  return (
    <div style={L.page}>
      {/* ───── ROW 1: ANIMATED FUNNEL + ACTIVITY FEED ──────────────── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 360px",
        gap: 16,
        marginBottom: 20,
      }}>
        <div style={L.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={L.section}>Conversion Funnel</div>
            <select
              value={funnelPeriod}
              onChange={e => setFunnelPeriod(e.target.value)}
              style={{
                ...S.select, fontSize: 11, padding: "4px 10px",
                borderRadius: 4, width: "auto",
              }}
            >
              {FUNNEL_PERIODS.map(p => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </div>
          <ParticleFunnel
            stages={funnelStages}
            verticalCounts={verticalData}
            statusVerticalMap={statusVerticalMap}
          />
          <FunnelLegend verticalCounts={verticalData} />
        </div>

        <div style={L.card}>
          <div style={L.section}>Recent Activity</div>
          <ActivityFeed activities={activities} />
        </div>
      </div>

      {/* ───── ROW 2: KPI GRID (9 cards, 3×3) ────────────────────────
          Total Leads · Contacted % · Reply Rate · Meeting Rate ·
          Quote Rate · Close Rate · Pipeline Value · Avg Deal Size ·
          Days to SB435 */}
      {(() => {
        // Funnel-stage counts to drive % KPIs
        const cNew     = funnelStages[0]?.count || 0;
        const cContact = funnelStages[1]?.count || 0;
        const cReply   = funnelStages[2]?.count || 0;
        const cMeeting = funnelStages[3]?.count || 0;
        const cQuote   = funnelStages[4]?.count || 0;
        const cWon     = funnelStages.find(s => s.key === "Closed Won")?.count || 0;

        const pct = (num, den) => (den > 0 && num > 0)
          ? `${Math.round((num / den) * 100)}%`
          : "—";

        const dealCount = deals.length || 0;
        const totalDealValue = deals.reduce((a, d) => a + (d.value || 0), 0);
        const avgDealSize = dealCount > 0 ? totalDealValue / dealCount : 0;
        const daysToSB435 = daysUntil(SB435_DEADLINE);

        const pctCard = (label, num, den, sub) => ({
          label, value: pct(num, den), sub,
        });

        const kpis = [
          { label: "Total Leads",    value: fmtNum(leadsTotal),        sub: "all verticals" },
          pctCard("Contacted %",   cContact, cNew,     "of new leads"),
          pctCard("Reply Rate",    cReply,   cContact, "of contacted"),
          pctCard("Meeting Rate",  cMeeting, cReply,   "of replied"),
          pctCard("Quote Rate",    cQuote,   cMeeting, "of meetings"),
          pctCard("Close Rate",    cWon,     cQuote,   "of quotes"),
          { label: "Pipeline Value", value: fmt$$(pipeline), sub: "open deals",
            accent: LIGHT.orange },
          { label: "Avg Deal Size", value: fmt$$(avgDealSize), sub: `${dealCount} deal${dealCount === 1 ? "" : "s"}` },
          { label: "Days to SB435", value: `${daysToSB435}d`, sub: "Jan 1, 2027",
            accent: LIGHT.orange },
        ];

        return (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(9, 1fr)",
            gap: 10, marginBottom: 20,
          }}>
            {kpis.map((k, i) => (
              <div key={i} style={L.kpiCard}>
                <div style={L.kpiLabel}>{k.label}</div>
                <div style={{
                  ...L.kpiValue,
                  fontSize: 20,
                  color: k.accent || LIGHT.ink,
                }}>
                  {k.value}
                </div>
                <div style={L.kpiSub}>{k.sub}</div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* ───── ROW 3: VERTICAL BREAKDOWN BARS ─────────────────────── */}
      <div style={L.card}>
        <div style={L.section}>Leads by Vertical</div>
        {verticalData.length === 0 && (
          <div style={{ color: LIGHT.inkFaint, fontSize: 12 }}>
            No vertical data.
          </div>
        )}
        {verticalData.map(v => {
          const pct = verticalTotal ? Math.round((v.count / verticalTotal) * 100) : 0;
          return (
            <div key={v.vertical} style={{ marginBottom: 12 }}>
              <div style={{
                display: "flex", justifyContent: "space-between",
                fontSize: 13, marginBottom: 5, color: LIGHT.ink,
              }}>
                <span style={{ fontWeight: 600 }}>
                  {VERTICAL_ICONS[v.vertical] || "•"} {v.vertical}
                </span>
                <span style={{ color: LIGHT.inkMuted }}>
                  {fmtNum(v.count)} <span style={{ color: LIGHT.inkFaint }}>({pct}%)</span>
                </span>
              </div>
              <div style={{
                height: 8, background: "#EEEDE6", borderRadius: 4,
                overflow: "hidden",
              }}>
                <div style={{
                  width: `${pct}%`, height: "100%",
                  background: LIGHT.orange, borderRadius: 4,
                  transition: "width 0.4s ease",
                }} />
              </div>
            </div>
          );
        })}
      </div>
      <InsightsSection L={L} />
    </div>
  );
}

// ─── INSIGHTS ────────────────────────────────────────────────────────────────
const INSIGHT_PERIODS=[{key:"week",label:"This Week"},{key:"month",label:"This Month"},{key:"quarter",label:"This Quarter"},{key:"year",label:"This Year"},{key:"all",label:"All Time"}];
const RC=require("recharts"); // eslint-disable-line
function InsightsSection({L}){
  const [period,setPeriod]=useState("all");
  const [won,setWon]=useState(null);
  const [pipe,setPipe]=useState(null);
  const [byP,setByP]=useState(null);
  const [acts,setActs]=useState(null);
  const [verts,setVerts]=useState(null);
  const [fc,setFc]=useState(null);
  const [oF,setOF]=useState(null);
  const [exp,setExp]=useState(null);
  useEffect(()=>{api(`/insights/deals-won?period=${period}`).then(setWon);api(`/insights/deals-by-person?period=${period}`).then(setByP);api(`/insights/activities?period=${period}`).then(setActs);},[period]);
  useEffect(()=>{api("/insights/pipeline-health").then(setPipe);api("/insights/leads-by-vertical").then(setVerts);api("/insights/revenue-forecast").then(setFc);},[]);
  // B-007: previously this call lived at render scope and fired on every render
  // (~60×/min under Command Center). Now fetched once per period-change and
  // refreshed on a 60s interval to match backend's 30s TTL cache.
  useEffect(()=>{
    let alive=true;
    const pull=()=>api("/insights/outreach-funnel?period="+period).then(r=>{if(alive&&r)setOF(r);});
    pull();
    const id=setInterval(pull,60000);
    return()=>{alive=false;clearInterval(id);};
  },[period]);
  const cs={...L.card,display:"flex",flexDirection:"column",minHeight:320};
  const tr={display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12};
  const ct={fontSize:12,fontWeight:700,color:LIGHT.orange,textTransform:"uppercase",letterSpacing:0.5};
  const ld=<div style={{padding:40,textAlign:"center",color:LIGHT.inkFaint,fontSize:12}}>Loading...</div>;
  const xCSV=(fn,h,rows)=>{const csv=[h.join(","),...rows.map(r=>h.map(k=>JSON.stringify(r[k]??"")).join(","))].join("\n");const a=document.createElement("a");a.href=URL.createObjectURL(new Blob([csv],{type:"text/csv"}));a.download=fn;a.click();};
  const eB=k=><button onClick={()=>setExp(k)} style={{...S.btn("ghost"),padding:"3px 8px",fontSize:10}}>Expand</button>;
  const cB=fn=><button onClick={fn} style={{...S.btn("ghost"),padding:"3px 8px",fontSize:10}}>CSV</button>;
  const pSum=useMemo(()=>{if(!byP?.data)return[];const m={};byP.data.forEach(r=>{const o=r.owner||"Unknown";if(!m[o])m[o]={owner:o,Open:0,Won:0,Lost:0,value:0};if(r.stage==="Closed Won")m[o].Won+=r.count;else if(r.stage==="Closed Lost")m[o].Lost+=r.count;else m[o].Open+=r.count;m[o].value+=r.value||0;});return Object.values(m);},[byP]);
  const aD=useMemo(()=>{if(!acts?.data)return[];const m={};acts.data.forEach(r=>{if(!m[r.type])m[r.type]={type:r.type,completed:0,pending:0};if(r.done)m[r.type].completed+=r.count;else m[r.type].pending+=r.count;});return Object.values(m);},[acts]);
  const modal=exp&&<div style={S.modal} onClick={()=>setExp(null)}><div style={{...S.modalBox,maxWidth:900,maxHeight:"90vh"}} onClick={e=>e.stopPropagation()}><div style={{display:"flex",justifyContent:"space-between",marginBottom:16}}><div style={ct}>{exp}</div><button onClick={()=>setExp(null)} style={S.btn("ghost")}>Close</button></div><div style={{height:400}}>{exp==="Deals Won"&&won&&<WonCh d={won.deals} h={380}/>}{exp==="Pipeline"&&pipe&&<PipeCh d={pipe.stages} h={380}/>}{exp==="By Person"&&<PersCh d={pSum} h={380}/>}{exp==="Activities"&&<ActCh d={aD} h={380}/>}{exp==="Verticals"&&verts&&<VPie d={verts.data} h={380}/>}{exp==="Forecast"&&fc&&<FCh d={fc.by_stage} h={380}/>}{exp==="Outreach"&&oF&&<OutreachFunnelChart d={oF} h={380}/>}</div></div></div>;
  return(
    <div style={{marginTop:24}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={L.section}>Insights</div>
        <div style={{display:"flex",gap:4}}>{INSIGHT_PERIODS.map(p=><button key={p.key} onClick={()=>setPeriod(p.key)} style={{...S.btn(p.key===period?"primary":"ghost"),padding:"4px 10px",fontSize:10}}>{p.label}</button>)}</div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
        <div style={cs}><div style={tr}><div style={ct}>Deals Won Revenue</div><div style={{display:"flex",gap:4}}>{cB(()=>won?.deals&&xCSV("deals-won.csv",["title","value","created_at"],won.deals))}{eB("Deals Won")}</div></div>{won?<><div style={{display:"flex",gap:16,marginBottom:10,fontSize:12}}><span><b style={{color:"#10B981"}}>{fmt$$(won.total_value)}</b> won</span><span style={{color:LIGHT.inkFaint}}>{won.count} deal{won.count!==1?"s":""}</span></div><div style={{flex:1}}><WonCh d={won.deals}/></div></>:ld}</div>
        <div style={cs}><div style={tr}><div style={ct}>Pipeline Health</div><div style={{display:"flex",gap:4}}>{cB(()=>pipe?.stages&&xCSV("pipeline.csv",["stage","count","value"],pipe.stages))}{eB("Pipeline")}</div></div>{pipe?<><div style={{display:"flex",gap:16,marginBottom:10,fontSize:12}}><span>Win rate: <b style={{color:"#10B981"}}>{pipe.win_rate}%</b></span><span>Open: <b>{fmt$$(pipe.open_pipeline)}</b></span></div><div style={{flex:1}}><PipeCh d={pipe.stages}/></div></>:ld}</div>
        <div style={cs}><div style={tr}><div style={ct}>Deals by Salesperson</div><div style={{display:"flex",gap:4}}>{cB(()=>xCSV("by-person.csv",["owner","Open","Won","Lost","value"],pSum))}{eB("By Person")}</div></div>{byP?<div style={{flex:1}}><PersCh d={pSum}/></div>:ld}</div>
        <div style={cs}><div style={tr}><div style={ct}>Activities Status</div><div style={{display:"flex",gap:4}}>{cB(()=>xCSV("activities.csv",["type","completed","pending"],aD))}{eB("Activities")}</div></div>{acts?<><div style={{display:"flex",gap:16,marginBottom:10,fontSize:12}}><span><b>{acts.total}</b> total</span><span style={{color:LIGHT.inkFaint}}>Top: {acts.top_type}</span></div><div style={{flex:1}}><ActCh d={aD}/></div></>:ld}</div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 2fr",gap:16}}>
        <div style={cs}><div style={tr}><div style={ct}>Leads by Vertical</div>{eB("Verticals")}</div>{verts?<div style={{flex:1}}><VPie d={verts.data}/></div>:ld}</div>
        <div style={cs}><div style={tr}><div style={ct}>Revenue Forecast</div><div style={{display:"flex",gap:4}}>{cB(()=>fc?.by_stage&&xCSV("forecast.csv",["stage","count","raw_value","weighted_value"],fc.by_stage))}{eB("Forecast")}</div></div>{fc?<><div style={{display:"flex",gap:16,marginBottom:10,fontSize:12}}><span>Expected: <b style={{color:LIGHT.orange}}>{fmt$$(fc.total_weighted)}</b></span><span style={{color:LIGHT.inkFaint}}>Raw: {fmt$$(fc.total_raw)}</span></div><div style={{flex:1}}><FCh d={fc.by_stage}/></div></>:ld}</div>
      </div>
      {/* Outreach Funnel */}      <div style={{...cs,marginTop:16}}>        <div style={tr}><div style={ct}>Outreach Funnel</div>{eB("Outreach")}</div>        {oF?<OutreachFunnelChart d={oF}/>:ld}      </div>
      {/* Phase 1.5b — Cost & ROI (DrewOS tool spend vs outcomes) */}
      <div style={{...cs,marginTop:16,minHeight:180}}>
        <div style={tr}><div style={ct}>Cost & ROI (DrewOS)</div></div>
        <CostAndRoiCard />
      </div>
      {modal}
    </div>
  );
}

function CostAndRoiCard() {
  const [data, setData] = useState(null);
  useEffect(() => { api("/insights/cost-and-roi").then(setData); }, []);
  if (!data) return <div style={{color:LIGHT.inkFaint,fontSize:12,padding:20}}>Loading cost & ROI...</div>;
  const c = data.current_month || {};
  const alertColor = {red:"#EF4444", yellow:"#F59E0B", green:"#10B981", gray:"#9CA3AF"}[data.alert_level] || "#9CA3AF";
  const alertLabel = {red:"RED", yellow:"YELLOW", green:"GREEN", gray:"N/A"}[data.alert_level] || "N/A";
  const tc = (data.tool_costs_breakdown || []).filter(t => t.active).slice(0, 5);
  return (
    <div style={{display:"flex",flexDirection:"column",gap:12,flex:1}}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:12}}>
        <div style={{background:"#FAFAF5",padding:10,borderRadius:6}}>
          <div style={{fontSize:10,color:LIGHT.inkFaint,textTransform:"uppercase"}}>This month spend</div>
          <div style={{fontSize:20,fontWeight:700,color:LIGHT.ink}}>{fmt$$(c.total_cost_usd || 0)}</div>
        </div>
        <div style={{background:"#FAFAF5",padding:10,borderRadius:6}}>
          <div style={{fontSize:10,color:LIGHT.inkFaint,textTransform:"uppercase"}}>Enriched leads</div>
          <div style={{fontSize:20,fontWeight:700,color:LIGHT.ink}}>{fmtNum(c.total_enriched || 0)}</div>
          <div style={{fontSize:10,color:LIGHT.inkFaint}}>{c.cost_per_enriched ? `$${c.cost_per_enriched.toFixed(2)}/lead` : "—"}</div>
        </div>
        <div style={{background:"#FAFAF5",padding:10,borderRadius:6}}>
          <div style={{fontSize:10,color:LIGHT.inkFaint,textTransform:"uppercase"}}>Deals closed</div>
          <div style={{fontSize:20,fontWeight:700,color:LIGHT.ink}}>{fmtNum(c.total_deals_closed || 0)}</div>
          <div style={{fontSize:10,color:LIGHT.inkFaint}}>{c.cost_per_deal ? `$${c.cost_per_deal.toFixed(0)}/deal` : "—"}</div>
        </div>
        <div style={{background:alertColor+"22",padding:10,borderRadius:6,border:`1px solid ${alertColor}`}}>
          <div style={{fontSize:10,color:LIGHT.inkFaint,textTransform:"uppercase"}}>Alert level</div>
          <div style={{fontSize:16,fontWeight:700,color:alertColor}}>{alertLabel}</div>
          <div style={{fontSize:10,color:LIGHT.inkFaint}}>
            {data.alert_level === "red" ? "cost_per_deal > $3k"
             : data.alert_level === "yellow" ? "$1k-$3k/deal"
             : data.alert_level === "green" ? "< $1k/deal"
             : "awaiting first deal"}
          </div>
        </div>
      </div>
      {tc.length > 0 && (
        <div style={{display:"flex",gap:8,flexWrap:"wrap",fontSize:11}}>
          <span style={{color:LIGHT.inkFaint}}>Top spend:</span>
          {tc.map(t => (
            <span key={t.tool_name} style={{background:"#fff",border:`1px solid ${LIGHT.rule}`,padding:"2px 8px",borderRadius:10}}>
              {t.tool_name} ${t.monthly_cost_usd.toFixed(0)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
function WonCh({d,h=200}){if(!d?.length)return<div style={{color:LIGHT.inkFaint,fontSize:12,padding:20}}>No won deals yet.</div>;const cd=d.map(r=>({date:(r.created_at||"").slice(0,10),value:r.value||0}));return<RC.ResponsiveContainer width="100%" height={h}><RC.BarChart data={cd}><RC.CartesianGrid strokeDasharray="3 3" stroke="#EEEDE6"/><RC.XAxis dataKey="date" tick={{fontSize:10}}/><RC.YAxis tick={{fontSize:10}} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`}/><RC.Tooltip formatter={v=>fmt$$(v)}/><RC.Bar dataKey="value" fill="#10B981" radius={[3,3,0,0]}/></RC.BarChart></RC.ResponsiveContainer>;}
function PipeCh({d,h=200}){if(!d?.length)return<div style={{color:LIGHT.inkFaint,fontSize:12,padding:20}}>No deals.</div>;const O=["Lead In","Qualified","Demo Scheduled","Quote Sent","Negotiation","Closed Won","Closed Lost"];const s=[...d].sort((a,b)=>O.indexOf(a.stage)-O.indexOf(b.stage));const bl=["#DBEAFE","#93C5FD","#60A5FA","#3B82F6","#2563EB","#1D4ED8","#6B7280"];return<RC.ResponsiveContainer width="100%" height={h}><RC.BarChart data={s} layout="vertical"><RC.CartesianGrid strokeDasharray="3 3" stroke="#EEEDE6"/><RC.XAxis type="number" tick={{fontSize:10}}/><RC.YAxis dataKey="stage" type="category" tick={{fontSize:10}} width={100}/><RC.Tooltip formatter={(v,n)=>n==="value"?fmt$$(v):v}/><RC.Bar dataKey="count" fill="#3B82F6" radius={[0,3,3,0]}>{s.map((_,i)=><RC.Cell key={i} fill={bl[i%bl.length]}/>)}</RC.Bar></RC.BarChart></RC.ResponsiveContainer>;}
function PersCh({d,h=200}){if(!d?.length)return<div style={{color:LIGHT.inkFaint,fontSize:12,padding:20}}>No deals.</div>;return<RC.ResponsiveContainer width="100%" height={h}><RC.BarChart data={d} layout="vertical"><RC.CartesianGrid strokeDasharray="3 3" stroke="#EEEDE6"/><RC.XAxis type="number" tick={{fontSize:10}}/><RC.YAxis dataKey="owner" type="category" tick={{fontSize:10}} width={110}/><RC.Tooltip/><RC.Legend wrapperStyle={{fontSize:10}}/><RC.Bar dataKey="Open" stackId="a" fill="#3B82F6"/><RC.Bar dataKey="Won" stackId="a" fill="#10B981"/><RC.Bar dataKey="Lost" stackId="a" fill="#EF4444"/></RC.BarChart></RC.ResponsiveContainer>;}
function ActCh({d,h=200}){if(!d?.length)return<div style={{color:LIGHT.inkFaint,fontSize:12,padding:20}}>No activities.</div>;return<RC.ResponsiveContainer width="100%" height={h}><RC.BarChart data={d}><RC.CartesianGrid strokeDasharray="3 3" stroke="#EEEDE6"/><RC.XAxis dataKey="type" tick={{fontSize:10}}/><RC.YAxis tick={{fontSize:10}}/><RC.Tooltip/><RC.Legend wrapperStyle={{fontSize:10}}/><RC.Bar dataKey="completed" fill="#D66F27" name="Completed" radius={[3,3,0,0]}/><RC.Bar dataKey="pending" fill="#F5D4BC" name="Pending" radius={[3,3,0,0]}/></RC.BarChart></RC.ResponsiveContainer>;}
function VPie({d,h=200}){if(!d?.length)return<div style={{color:LIGHT.inkFaint,fontSize:12,padding:20}}>No leads.</div>;const C=["#F59E0B","#38BDF8","#10B981","#7C3AED","#64748B","#D66F27"];return<RC.ResponsiveContainer width="100%" height={h}><RC.PieChart><RC.Pie data={d} dataKey="count" nameKey="vertical" cx="50%" cy="50%" outerRadius={h*0.35} label={({vertical,percent})=>`${vertical} ${(percent*100).toFixed(0)}%`} labelLine={{strokeWidth:1}}>{d.map((_,i)=><RC.Cell key={i} fill={C[i%C.length]}/>)}</RC.Pie><RC.Tooltip formatter={v=>fmtNum(v)}/></RC.PieChart></RC.ResponsiveContainer>;}
function FCh({d,h=200}){if(!d?.length)return<div style={{color:LIGHT.inkFaint,fontSize:12,padding:20}}>No deals.</div>;const gr=["#D1FAE5","#A7F3D0","#6EE7B7","#34D399","#10B981","#059669","#6B7280"];return<RC.ResponsiveContainer width="100%" height={h}><RC.BarChart data={d}><RC.CartesianGrid strokeDasharray="3 3" stroke="#EEEDE6"/><RC.XAxis dataKey="stage" tick={{fontSize:10}}/><RC.YAxis tick={{fontSize:10}} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`}/><RC.Tooltip formatter={v=>fmt$$(v)}/><RC.Legend wrapperStyle={{fontSize:10}}/><RC.Bar dataKey="raw_value" fill="#E5E7EB" name="Raw" radius={[3,3,0,0]}/><RC.Bar dataKey="weighted_value" name="Weighted" radius={[3,3,0,0]}>{d.map((_,i)=><RC.Cell key={i} fill={gr[i%gr.length]}/>)}</RC.Bar></RC.BarChart></RC.ResponsiveContainer>;}
function OutreachFunnelChart({d,h=220}){  if(!d||d.sent===0)return<div style={{color:LIGHT.inkFaint,fontSize:12,padding:20}}>No outreach data yet. Campaigns will populate once sequences are active.</div>;  const steps=[    {label:"Sent",value:d.sent,rate:null},    {label:"Opened",value:d.opened,rate:d.open_rate},    {label:"Clicked",value:d.clicked,rate:d.click_rate},    {label:"Replied",value:d.replied,rate:d.reply_rate},    {label:"Booked",value:d.booked,rate:d.book_rate},    {label:"Won",value:d.won,rate:d.win_rate},  ];  const maxV=Math.max(...steps.map(s=>s.value),1);  const colors=["#3B82F6","#60A5FA","#38BDF8","#06B6D4","#10B981","#059669"];  return<div style={{display:"flex",flexDirection:"column",gap:8}}>    <div style={{display:"flex",gap:16,marginBottom:6,fontSize:12}}>      <span>Best campaign: <b style={{color:LIGHT.orange}}>{d.best_campaign||"—"}</b></span>      <span>Reply rate: <b>{d.reply_rate}%</b></span>    </div>    {steps.map((s,i)=>{const w=Math.max(8,Math.round((s.value/maxV)*100));return(      <div key={i} style={{display:"flex",alignItems:"center",gap:10}}>        <div style={{width:70,fontSize:10,fontWeight:700,textAlign:"right",color:LIGHT.inkMuted}}>{s.label}</div>        <div style={{flex:1,height:28,background:"#EEEDE6",borderRadius:4,position:"relative",overflow:"hidden"}}>          <div style={{width:w+"%",height:"100%",background:colors[i],borderRadius:4,transition:"width 0.4s"}}/>          <div style={{position:"absolute",top:0,left:8,lineHeight:"28px",fontSize:11,fontWeight:700,color:w>20?"#fff":LIGHT.ink}}>{fmtNum(s.value)}</div>        </div>        <div style={{width:50,fontSize:10,color:LIGHT.inkFaint,textAlign:"right"}}>{s.rate!==null?s.rate+"%":""}</div>      </div>    );})}  </div>;}



// ─── LABEL DROPDOWN WITH CREATE OPTION ───────────────────────────────────────
// Reusable component: shows Hot/Warm/Cold + custom labels + create-new inline.
// `value` is the current label name (or ""). `onChange(name)` fires on select.
function LabelDropdown({ value, onChange, recordType, recordId }) {
  const [labels, setLabels] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#F97316");
  const PRESET_COLORS = ["#EF4444", "#F97316", "#F59E0B", "#22C55E", "#3B82F6", "#A855F7"];

  useEffect(() => { api("/labels").then(r => { if (Array.isArray(r)) setLabels(r); }); }, []);

  async function createLabel() {
    if (!newName.trim()) return;
    const r = await api("/labels", "POST", { name: newName.trim(), color: newColor });
    if (r) {
      setLabels(prev => [...prev, r]);
      onChange(r.name);
      setCreating(false);
      setNewName("");
    }
  }

  const DOT = { Hot: "\uD83D\uDD34", Warm: "\uD83D\uDFE1", Cold: "\uD83D\uDD35" };
  const customLabels = labels.filter(l => !["Hot", "Warm", "Cold"].includes(l.name));

  if (creating) {
    return (
      <div style={{ background: "#F8FAFC", border: `1px solid ${COLORS.cardBorder}`, borderRadius: 6, padding: 10 }}>
        <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Label name..."
          style={{ ...S.input, marginBottom: 8 }} autoFocus />
        <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
          {PRESET_COLORS.map(c => (
            <div key={c} onClick={() => setNewColor(c)} style={{
              width: 24, height: 24, borderRadius: "50%", background: c, cursor: "pointer",
              border: c === newColor ? "3px solid #1E293B" : "2px solid transparent",
            }} />
          ))}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={createLabel} disabled={!newName.trim()}
            style={{ ...S.btn("primary"), fontSize: 11, padding: "4px 12px" }}>Create</button>
          <button onClick={() => setCreating(false)}
            style={{ ...S.btn("ghost"), fontSize: 11, padding: "4px 12px" }}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <select value={value || ""} onChange={e => {
      if (e.target.value === "__create__") { setCreating(true); return; }
      onChange(e.target.value);
    }} style={{ ...S.select, width: "100%", fontSize: 12,
      color: value ? (labels.find(l => l.name === value)?.color || COLORS.textPrimary) : COLORS.grayMid,
      fontWeight: value ? 700 : 400,
    }}>
      <option value="">None</option>
      <option value="Hot">{DOT.Hot} Hot</option>
      <option value="Warm">{DOT.Warm} Warm</option>
      <option value="Cold">{DOT.Cold} Cold</option>
      {customLabels.map(l => <option key={l.id} value={l.name}>{l.name}</option>)}
      <option value="__create__">+ Create new label...</option>
    </select>
  );
}

// ─── FILTER BAR ──────────────────────────────────────────────────────────────
// Renders a row of filter dropdowns + active filter pills.
// `filters` = {label, vertical, enrolled, source, sort} state object.
// `onFilter(key, value)` updates a specific filter.
// `onClear()` clears all filters.
function FilterBar({ filters, onFilter, onClear, showVertical, showEnrolled, showSource, showSort }) {
  const [labels, setLabels] = useState([]);
  useEffect(() => { api("/labels").then(r => { if (Array.isArray(r)) setLabels(r); }); }, []);

  const sel = { ...S.select, fontSize: 11, padding: "5px 8px", width: "auto", minWidth: 100 };
  const active = Object.entries(filters).filter(([k, v]) => v && v !== "all" && v !== "");
  const DOT = { Hot: "\uD83D\uDD34", Warm: "\uD83D\uDFE1", Cold: "\uD83D\uDD35" };

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <Filter size={14} style={{ color: COLORS.grayMid }} />

        {showVertical !== false && (
          <select value={filters.vertical || ""} onChange={e => onFilter("vertical", e.target.value)} style={sel}>
            <option value="">All Verticals</option>
            {VERTICALS.map(v => <option key={v} value={v}>{VERTICAL_ICONS[v]} {v}</option>)}
          </select>
        )}

        <select value={filters.label || ""} onChange={e => onFilter("label", e.target.value)} style={sel}>
          <option value="">All Labels</option>
          <option value="Hot">{DOT.Hot} Hot</option>
          <option value="Warm">{DOT.Warm} Warm</option>
          <option value="Cold">{DOT.Cold} Cold</option>
          {labels.filter(l => !["Hot","Warm","Cold"].includes(l.name)).map(l =>
            <option key={l.id} value={l.name}>{l.name}</option>
          )}
        </select>

        {showEnrolled && (
          <select value={filters.enrolled || ""} onChange={e => onFilter("enrolled", e.target.value)} style={sel}>
            <option value="">Enrollment</option>
            <option value="1">Enrolled</option>
            <option value="0">Not Enrolled</option>
          </select>
        )}

        {showSource && (
          <select value={filters.source || ""} onChange={e => onFilter("source", e.target.value)} style={sel}>
            <option value="">All Sources</option>
            <option value="API">API</option>
            <option value="LinkedIn">LinkedIn</option>
            <option value="Manual">Manual</option>
            <option value="Instantly">Instantly</option>
          </select>
        )}

        {showSort !== false && (
          <select value={filters.sort || ""} onChange={e => onFilter("sort", e.target.value)} style={sel}>
            <option value="">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="name">Name A-Z</option>
            <option value="name_desc">Name Z-A</option>
          </select>
        )}
      </div>

      {/* Active filter pills */}
      {active.length > 0 && (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 8, alignItems: "center" }}>
          {active.map(([k, v]) => (
            <span key={k} onClick={() => onFilter(k, "")} style={{
              fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 10,
              background: "#FFF7ED", color: COLORS.orange, cursor: "pointer",
              border: `1px solid ${COLORS.orange}33`,
            }}>
              {k}: {v} ✕
            </span>
          ))}
          {active.length > 1 && (
            <span onClick={onClear} style={{
              fontSize: 10, color: COLORS.grayMid, cursor: "pointer", textDecoration: "underline",
            }}>Clear all</span>
          )}
        </div>
      )}
    </div>
  );
}
// ─── DELETE CONFIRMATION MODAL ───────────────────────────────────────────────
// Requires typing "DELETE" to confirm. Used for leads, contacts, orgs, deals.
function DeleteConfirmModal({ title, message, onConfirm, onCancel }) {
  const [deleting, setDeleting] = useState(false);
  async function doDelete() {
    setDeleting(true);
    await onConfirm();
    setDeleting(false);
  }
  return (
    <div style={S.modal} onClick={onCancel}>
      <div style={{ ...S.modalBox, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 12, color: "#EF4444" }}>{title}</div>
        <div style={{ fontSize: 13, color: COLORS.textPrimary, marginBottom: 20, lineHeight: 1.5 }}>{message}</div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onCancel} style={S.btn("ghost")}>Cancel</button>
          <button onClick={doDelete} disabled={deleting} autoFocus
            style={{ ...S.btn("primary"), background: "#EF4444", borderColor: "#EF4444" }}>
            {deleting ? "Deleting..." : "Confirm Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── CANCEL MEETING MODAL ────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
function CancelMeetingModal({ eventTitle, attendeeName, attendeeEmail, onConfirm, onCancel }) {
  const [cancelling, setCancelling] = useState(false);
  async function doCancel() {
    setCancelling(true);
    await onConfirm();
    setCancelling(false);
  }
  return (
    <div style={S.modal} onClick={onCancel}>
      <div style={{ ...S.modalBox, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 12, color: "#EF4444" }}>Cancel Meeting</div>
        <div style={{ fontSize: 13, color: COLORS.textPrimary, marginBottom: 6, lineHeight: 1.5 }}>
          Cancel this meeting with <b>{attendeeName || "attendee"}</b>?
        </div>
        <div style={{ fontSize: 12, color: COLORS.grayMid, marginBottom: 16 }}>
          This will remove it from Google Calendar{attendeeEmail ? ` and notify ${attendeeEmail}` : ""}.
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onCancel} style={S.btn("ghost")}>Keep Meeting</button>
          <button onClick={doCancel} disabled={cancelling}
            style={{ ...S.btn("primary"), background: "#EF4444" }}>
            {cancelling ? "Cancelling..." : "Yes, Cancel Meeting"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── TOAST HELPER ────────────────────────────────────────────────────────────
// Renders a temporary toast notification at top-center.
// eslint-disable-next-line no-unused-vars
function useToast() {
  const [toast, setToast] = useState(null);
  const show = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);
  const ToastEl = toast ? (
    <div style={{
      position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 9999,
      background: toast.type === "success" ? "#10B981" : "#EF4444",
      color: "#FFFFFF", padding: "10px 24px", borderRadius: 8,
      fontSize: 13, fontWeight: 600, boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    }}>{toast.msg}</div>
  ) : null;
  return { show, ToastEl };
}
// ─── PARTICLE FUNNEL (RAF-driven) ────────────────────────────────────────────
// Fixed-size funnel (460 × 600 px). Particles are absolutely-positioned divs;
// their X/Y and opacity are updated every frame by a requestAnimationFrame
// loop. X positions are clamped to the funnel polygon's width at each Y, so
// particles can never escape the trapezoid.

// Fixed pixel buckets for the 9 stages (top → bottom).
const STAGE_BUCKETS = [
  { label: "New Leads",      top: 0,   bot: 80  },
  { label: "Contacted",      top: 80,  bot: 160 },
  { label: "Replied",        top: 160, bot: 240 },
  { label: "Meeting Booked", top: 240, bot: 320 },
  { label: "Quote Sent",     top: 320, bot: 400 },
  { label: "Deal Created",   top: 400, bot: 460 },
  { label: "Negotiation",    top: 460, bot: 520 },
  { label: "Closed Won",     top: 520, bot: 560 },
  { label: "Closed Lost",    top: 560, bot: 600 },
];

function ParticleFunnel({ stages, verticalCounts = [], statusVerticalMap = {} }) {
  // ── Funnel geometry (fixed buckets) ──
  const WIDTH         = 460;
  const FUNNEL_TOP_W  = 400;
  const FUNNEL_BOT_W  = 30;
  const FUNNEL_HEIGHT = 600;
  const CX            = WIDTH / 2;
  const PARTICLE_R    = 3;
  const SOURCES_H     = 90;

  // Width of the funnel polygon at a given Y (linear taper).
  const widthAt = (y) => {
    const t = Math.max(0, Math.min(1, y / FUNNEL_HEIGHT));
    return FUNNEL_TOP_W - (FUNNEL_TOP_W - FUNNEL_BOT_W) * t;
  };

  const [hoveredIdx, setHoveredIdx] = useState(null);

  // Refs: per-particle DOM refs + mutable state + RAF handle
  const dotRefs    = useRef([]);
  const xMarkRefs  = useRef([]);
  const stateRef   = useRef([]);
  const rafRef     = useRef(null);

  // Canvas ref for dense bucket fills + confetti explosions
  const canvasRef  = useRef(null);
  const confettiRef = useRef([]); // active confetti particles [{x,y,vx,vy,color,life}]

  // Build skeletons: N particles PER stage, proportional to real counts.
  // More populated stages = more visible particles in that bucket.
  // Max 8 per stage, min 1 if count > 0. Total ≤ 72.
  const MAX_PER_STAGE = 8;
  const skeletons = useMemo(() => {
    const maxCount = Math.max(...stages.map(s => s.count || 0), 1);
    const vArr = verticalCounts || [];
    const vTotal = vArr.reduce((a, v) => a + v.count, 0) || 1;
    const arr = [];
    let id = 0;
    stages.forEach((s, si) => {
      const count = s.count || 0;
      if (count === 0) return;
      const n = Math.max(1, Math.round((count / maxCount) * MAX_PER_STAGE));
      for (let j = 0; j < n; j++) {
        // Pick a vertical color weighted by distribution
        let r = Math.random() * vTotal, cum = 0;
        let color = "#D66F27";
        for (const v of vArr) {
          cum += v.count;
          if (r <= cum) { color = VERTICAL_COLORS[v.vertical] || "#D66F27"; break; }
        }
        const isCL = s.key === "Closed Lost";
        arr.push({
          id: id++,
          stageIdx: si,
          color: isCL ? "#9CA3AF" : color,
          baseColor: color,
        });
      }
    });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stages, verticalCounts]);

  // ── CONFETTI CANVAS (Closed Won only) ──────────────────────────────
  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    cvs.width  = WIDTH * dpr;
    cvs.height = FUNNEL_HEIGHT * dpr;
    ctx.scale(dpr, dpr);

    let confettiRaf;
    function confettiStep() {
      const active = confettiRef.current;
      if (active.length === 0) return;
      ctx.clearRect(0, 0, WIDTH, FUNNEL_HEIGHT);
      const dead = [];
      for (let i = 0; i < active.length; i++) {
        const c = active[i];
        c.x += c.vx; c.y += c.vy;
        c.vy += 0.15; c.vx *= 0.99;
        c.life -= 1; c.rot += c.rotSpeed;
        if (c.life <= 0) { dead.push(i); continue; }
        ctx.save();
        ctx.translate(c.x, c.y);
        ctx.rotate(c.rot);
        ctx.globalAlpha = Math.min(1, c.life / 20);
        ctx.fillStyle = c.color;
        ctx.fillRect(-c.w / 2, -c.h / 2, c.w, c.h);
        ctx.restore();
      }
      for (let i = dead.length - 1; i >= 0; i--) active.splice(dead[i], 1);
      ctx.globalAlpha = 1;
      if (active.length > 0) confettiRaf = requestAnimationFrame(confettiStep);
    }
    cvs._triggerConfetti = (px, py, color) => {
      const CONFETTI_COLORS = [color, "#D66F27", "#F59E0B", "#38BDF8", "#10B981", "#7C3AED"];
      for (let i = 0; i < 40; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 2 + Math.random() * 5;
        confettiRef.current.push({
          x: px, y: py,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - 3,
          color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
          life: 40 + Math.random() * 30,
          w: 3 + Math.random() * 4, h: 2 + Math.random() * 2,
          rot: Math.random() * Math.PI * 2,
          rotSpeed: (Math.random() - 0.5) * 0.2,
        });
      }
      if (!confettiRaf) confettiRaf = requestAnimationFrame(confettiStep);
    };
    return () => { if (confettiRaf) cancelAnimationFrame(confettiRaf); };
  }, []);

  // Initialize particle state — each skeleton is pre-assigned to a stage.
  // Phases: reset → fall → bounce → park → fade → reset (loop)
  useEffect(() => {
    const now = performance.now();
    stateRef.current = skeletons.map((sk, idx) => {
      const si      = sk.stageIdx;
      const bucket  = STAGE_BUCKETS[si];
      const isCW    = bucket.label === "Closed Won";
      const isCL    = bucket.label === "Closed Lost";
      return {
        color:       sk.color,
        baseColor:   sk.baseColor,
        stageIdx:    si,
        targetY:     bucket.top + 4,
        targetYBot:  bucket.bot - 4,
        x: CX,
        y: -20,
        opacity: 0,
        phase: "reset",
        phaseStart: now - Math.random() * 3000,
        fallDur:    2000 + Math.random() * 2000,
        parkDur:    5000 + Math.random() * 5000,   // 5-10s in bucket
        fadeDur:    800,
        resetDelay: Math.random() * 2000 + idx * 80,
        xSeed:      Math.random() * 6.28,
        isClosedWon:  isCW,
        isClosedLost: isCL,
        showX: false,
        xStart: 0,
        bounceVy: 0,       // for bounce physics
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skeletons]);

  // ── RAF LOOP — phases: reset → fall → bounce → park → fade → reset ──
  useEffect(() => {
    function step(now) {
      const state = stateRef.current;
      for (let i = 0; i < state.length; i++) {
        const p = state[i];
        const el = dotRefs.current[i];
        if (!el) continue;
        const elapsed = now - p.phaseStart;

        // ── RESET: invisible, waiting to respawn ───────────────────
        if (p.phase === "reset") {
          if (elapsed >= p.resetDelay) {
            p.phase = "fall";
            p.phaseStart = now;
            p.y = -20;
            const maxJit = FUNNEL_TOP_W / 2 - PARTICLE_R - 4;
            p.x = CX + (Math.random() * 2 - 1) * maxJit;
            p.opacity = 0;
            p.fallDur = 2000 + Math.random() * 2000;
            p.parkDur = 5000 + Math.random() * 5000;
            p.resetDelay = Math.random() * 2000;
          } else {
            p.opacity = 0;
          }
        }

        // ── FALL: visible travel from top to target bucket ─────────
        else if (p.phase === "fall") {
          const progress = Math.min(1, elapsed / p.fallDur);

          // Closed Lost: fade out at 60% of fall (mid-funnel)
          if (p.isClosedLost && progress >= 0.6) {
            p.phase = "fade";
            p.phaseStart = now;
            p.showX = true;
            p.xStart = now;
          } else if (progress >= 1) {
            // Reached bucket top — enter bounce
            p.phase = "bounce";
            p.phaseStart = now;
            p.y = p.targetY;
            p.bounceVy = 3;  // initial downward bounce velocity
          } else {
            const eased = progress < 0.5
              ? 2 * progress * progress
              : -1 + (4 - 2 * progress) * progress;
            p.y = -20 + (p.targetY + 20) * eased;
          }
          p.opacity = Math.min(1, progress * 6);

          // X drift clamped to funnel
          const drift = Math.sin(p.xSeed + elapsed / 500) * 14;
          let tx = CX + drift;
          const halfW = widthAt(Math.max(0, p.y)) / 2 - PARTICLE_R;
          tx = Math.max(CX - halfW, Math.min(CX + halfW, tx));
          p.x += (tx - p.x) * 0.08;
        }

        // ── BOUNCE: slight overshoot + spring back ─────────────────
        else if (p.phase === "bounce") {
          // Simple spring — particle bounces within bucket zone
          p.bounceVy *= 0.88;            // damping
          p.bounceVy -= (p.y - p.targetY) * 0.12; // spring toward targetY
          p.y += p.bounceVy;
          // Clamp within bucket
          if (p.y < p.targetY - 2) p.y = p.targetY - 2;
          if (p.y > p.targetYBot + 2) p.y = p.targetYBot + 2;
          p.opacity = 1;

          // Transition to park once bounce settles (~0.4s)
          if (elapsed > 400 && Math.abs(p.bounceVy) < 0.3) {
            p.phase = "park";
            p.phaseStart = now;
          }
        }

        // ── PARK: gentle drift in bucket for 5-10 seconds ──────────
        else if (p.phase === "park") {
          if (elapsed >= p.parkDur) {
            p.phase = "fade";
            p.phaseStart = now;
            // Confetti for Closed Won
            if (p.isClosedWon) {
              const cvs = canvasRef.current;
              if (cvs && cvs._triggerConfetti) {
                cvs._triggerConfetti(p.x, p.y, p.baseColor);
              }
            }
          }

          // Gentle sinusoidal float within bucket
          const yMid = (p.targetY + p.targetYBot) / 2;
          const yRange = (p.targetYBot - p.targetY) * 0.4;
          p.y = yMid + Math.sin(p.xSeed + elapsed / 800) * yRange;
          const xJit = Math.sin(p.xSeed * 2 + elapsed / 600) * 5;
          let tx = CX + xJit;
          const halfW = widthAt(p.y) / 2 - PARTICLE_R;
          tx = Math.max(CX - halfW, Math.min(CX + halfW, tx));
          p.x += (tx - p.x) * 0.06;
          p.opacity = 1;
        }

        // ── FADE: opacity 1→0, then loop ───────────────────────────
        else if (p.phase === "fade") {
          const progress = Math.min(1, elapsed / p.fadeDur);
          if (progress >= 1) {
            p.phase = "reset";
            p.phaseStart = now;
            p.opacity = 0;
          } else {
            p.opacity = Math.max(0, 1 - progress);
          }
          if (p.showX && now - p.xStart > 800) p.showX = false;
        }

        // ── APPLY TO DOM ───────────────────────────────────────────
        el.style.transform = `translate3d(${p.x - PARTICLE_R}px, ${p.y - PARTICLE_R}px, 0)`;
        el.style.opacity = String(p.opacity);
        el.style.background = p.color;
        el.style.boxShadow = `0 0 6px ${p.color}`;

        // ✗ marker for Closed Lost
        const xMarkEl = xMarkRefs.current[i];
        if (xMarkEl) {
          if (p.showX) {
            const xe = (now - p.xStart) / 800;
            xMarkEl.style.transform = `translate3d(${p.x - 4}px, ${p.y - 7}px, 0)`;
            xMarkEl.style.opacity = String(Math.max(0, 1 - xe));
          } else {
            xMarkEl.style.opacity = "0";
          }
        }
      }
      rafRef.current = requestAnimationFrame(step);
    }
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skeletons]);

  const hoveredStage = hoveredIdx != null ? stages[hoveredIdx] : null;

  return (
    <div style={{
      position: "relative",
      width: WIDTH, margin: "0 auto",
      height: SOURCES_H + FUNNEL_HEIGHT + 10,
      overflow: "visible",
    }}>
      {/* ── SOURCE STREAMS ──────────────────────────────────────── */}
      <SourceStreams
        width={WIDTH} height={SOURCES_H}
        funnelMouthX={CX} funnelMouthY={SOURCES_H + 4}
      />

      {/* ── FUNNEL (shifted below sources) ──────────────────────── */}
      <div style={{
        position: "absolute", top: SOURCES_H, left: 0,
        width: WIDTH, height: FUNNEL_HEIGHT,
      }}>
        {/* Funnel polygon (single linear trapezoid) + stage dividers */}
        <svg
          width={WIDTH} height={FUNNEL_HEIGHT}
          style={{ display: "block", position: "absolute", top: 0, left: 0 }}
        >
          <polygon
            points={
              `${CX - FUNNEL_TOP_W / 2},0 ` +
              `${CX + FUNNEL_TOP_W / 2},0 ` +
              `${CX + FUNNEL_BOT_W / 2},${FUNNEL_HEIGHT} ` +
              `${CX - FUNNEL_BOT_W / 2},${FUNNEL_HEIGHT}`
            }
            fill={LIGHT.orangeSoft}
            stroke={LIGHT.orange}
            strokeOpacity="0.35"
            strokeWidth="1"
          />
          {/* Stage dividers + hover highlight fills */}
          {STAGE_BUCKETS.map((b, i) => {
            const w1 = widthAt(b.top);
            const w2 = widthAt(b.bot);
            const isHover = i === hoveredIdx;
            return (
              <g key={i}>
                {isHover && (
                  <polygon
                    points={
                      `${CX - w1 / 2},${b.top} ` +
                      `${CX + w1 / 2},${b.top} ` +
                      `${CX + w2 / 2},${b.bot} ` +
                      `${CX - w2 / 2},${b.bot}`
                    }
                    fill="#FCE3CF"
                    stroke={LIGHT.orange}
                    strokeOpacity="0.7"
                    strokeWidth="1.5"
                  />
                )}
                {i > 0 && (
                  <line
                    x1={CX - w1 / 2} y1={b.top}
                    x2={CX + w1 / 2} y2={b.top}
                    stroke={LIGHT.orange}
                    strokeOpacity="0.2"
                    strokeWidth="1"
                  />
                )}
              </g>
            );
          })}
        </svg>

        {/* Dense bucket fill + confetti canvas */}
        <canvas
          ref={canvasRef}
          style={{
            position: "absolute", top: 0, left: 0,
            width: WIDTH, height: FUNNEL_HEIGHT,
            pointerEvents: "none",
            zIndex: 0,
          }}
        />

        {/* Particle layer — RAF-driven divs */}
        <div style={{
          position: "absolute", top: 0, left: 0,
          width: WIDTH, height: FUNNEL_HEIGHT,
          pointerEvents: "none",
        }}>
          {skeletons.map((sk, idx) => (
            <div
              key={`p-${sk.id}`}
              ref={el => { dotRefs.current[idx] = el; }}
              style={{
                position: "absolute",
                top: 0, left: 0,
                width: PARTICLE_R * 2, height: PARTICLE_R * 2,
                borderRadius: "50%",
                background: sk.color,
                boxShadow: `0 0 6px ${sk.color}`,
                opacity: 0,
                willChange: "transform, opacity",
                transform: "translate3d(-100px, -100px, 0)",
              }}
            />
          ))}

          {/* Confetti is handled by the canvas layer — no DOM burst dots */}

          {/* Per-particle ✗ markers — only show for Closed Lost particles */}
          {skeletons.map((sk, idx) => (
            <div
              key={`x-${sk.id}`}
              ref={el => { xMarkRefs.current[idx] = el; }}
              style={{
                position: "absolute",
                top: 0, left: 0,
                fontSize: 11, fontWeight: 800,
                color: "#9CA3AF",
                opacity: 0,
                pointerEvents: "none",
                willChange: "transform, opacity",
                transform: "translate3d(-100px, -100px, 0)",
              }}
            >
              ✗
            </div>
          ))}
        </div>

        {/* Subtle left-side stage labels */}
        {STAGE_BUCKETS.map((b, i) => {
          const count = stages[i]?.count || 0;
          return (
            <div
              key={`label-${i}`}
              style={{
                position: "absolute",
                top: (b.top + b.bot) / 2 - 6,
                left: 4, width: 110,
                fontSize: 9, fontWeight: 600,
                letterSpacing: 0.4, textTransform: "uppercase",
                color: count === 0 ? "#C5C7C0" : LIGHT.inkMuted,
                pointerEvents: "none",
                textAlign: "right", paddingRight: 6,
              }}
            >
              {b.label}
            </div>
          );
        })}

        {/* Invisible hover zones per stage */}
        {STAGE_BUCKETS.map((b, i) => (
          <div
            key={`hover-${i}`}
            onMouseEnter={() => setHoveredIdx(i)}
            onMouseLeave={() => setHoveredIdx(idx => idx === i ? null : idx)}
            style={{
              position: "absolute",
              top: b.top,
              left: 120, right: 120,
              height: b.bot - b.top,
              cursor: "pointer",
              zIndex: 1,
            }}
          />
        ))}

        {/* Hover tooltip */}
        {hoveredStage && (
          <StageTooltip
            stage={hoveredStage}
            verticalCounts={verticalCounts}
            statusVerticalMap={statusVerticalMap}
            y={STAGE_BUCKETS[hoveredIdx].top}
          />
        )}
      </div>
    </div>
  );
}

// ─── STAGE TOOLTIP ───────────────────────────────────────────────────────────
function StageTooltip({ stage, verticalCounts, statusVerticalMap = {}, y }) {
  const showValue = stage.source === "deal" && stage.value > 0;

  // For lead-status stages: use real per-stage per-vertical matrix.
  // For deal stages: fall back to overall vertical mix (matrix is leads-only).
  let verticalBreakdown;
  let breakdownLabel;
  if (stage.source === "status" && statusVerticalMap[stage.key]) {
    const map = statusVerticalMap[stage.key];
    verticalBreakdown = verticalCounts.map(v => ({
      vertical: v.vertical,
      count: map[v.vertical] || 0,
    }));
    breakdownLabel = `At ${stage.label} by vertical`;
  } else {
    verticalBreakdown = verticalCounts.map(v => ({
      vertical: v.vertical,
      count: v.count,
    }));
    breakdownLabel = stage.source === "deal"
      ? "Vertical mix (overall)"
      : "Vertical mix";
  }
  const breakdownTotal = verticalBreakdown.reduce((a, v) => a + v.count, 0) || 1;

  return (
    <div style={{
      position: "absolute",
      top: Math.max(0, y - 20),
      left: 478,
      width: 280,
      background: "#FFFFFF",
      border: `1.5px solid ${LIGHT.orange}`,
      borderRadius: 8,
      padding: "12px 14px",
      boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
      fontSize: 12,
      color: LIGHT.ink,
      zIndex: 100,
      pointerEvents: "none",
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: LIGHT.orange,
        textTransform: "uppercase", letterSpacing: 0.6,
        marginBottom: 6,
      }}>
        {stage.label}
      </div>
      <div style={{
        display: "flex", alignItems: "baseline", gap: 8,
        marginBottom: 10,
      }}>
        <span style={{ fontSize: 22, fontWeight: 800 }}>{fmtNum(stage.count)}</span>
        <span style={{ fontSize: 11, color: LIGHT.inkFaint }}>
          {stage.source === "deal" ? "deals" : "leads"}
        </span>
      </div>

      {stage.conversionFromPrev != null && (
        <div style={{
          fontSize: 11, color: LIGHT.inkMuted, marginBottom: 6,
        }}>
          Conversion from prev:{" "}
          <span style={{ color: LIGHT.orange, fontWeight: 700 }}>
            {stage.conversionFromPrev}%
          </span>
        </div>
      )}

      {stage.conversionFromPrev == null && stage.index > 0 && (
        <div style={{
          fontSize: 11, color: LIGHT.inkFaint, marginBottom: 6,
        }}>
          Conversion: <span style={{ color: LIGHT.inkFaint }}>—</span>
        </div>
      )}

      {showValue && (
        <div style={{
          fontSize: 11, color: LIGHT.inkMuted, marginBottom: 10,
        }}>
          Pipeline value:{" "}
          <span style={{ color: LIGHT.ink, fontWeight: 700 }}>
            {fmt$$(stage.value)}
          </span>
        </div>
      )}

      <div style={{
        borderTop: "1px solid #EEEDE6",
        paddingTop: 8, marginTop: 4,
      }}>
        <div style={{
          fontSize: 9, fontWeight: 700, color: LIGHT.inkMuted,
          letterSpacing: 0.5, textTransform: "uppercase",
          marginBottom: 6,
        }}>
          {breakdownLabel}
        </div>
        {verticalBreakdown.map(v => {
          const pct = breakdownTotal ? Math.round((v.count / breakdownTotal) * 100) : 0;
          const color = VERTICAL_COLORS[v.vertical] || VERTICAL_COLORS.PropertyMgmt;
          return (
            <div key={v.vertical} style={{ marginBottom: 4 }}>
              <div style={{
                display: "flex", justifyContent: "space-between",
                fontSize: 10, marginBottom: 2,
              }}>
                <span style={{ color: LIGHT.ink }}>{v.vertical}</span>
                <span style={{ color: LIGHT.inkFaint }}>{pct}%</span>
              </div>
              <div style={{
                height: 4, background: "#EEEDE6", borderRadius: 2, overflow: "hidden",
              }}>
                <div style={{
                  width: `${pct}%`, height: "100%",
                  background: color, borderRadius: 2,
                }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── SOURCE STREAMS ──────────────────────────────────────────────────────────
// Renders the 4 outreach source labels above the funnel and draws curved
// SVG tributary paths converging at the funnel mouth. SVG <animateMotion>
// drives particles along each tributary in the source color.
function SourceStreams({ width, height, funnelMouthX, funnelMouthY }) {
  const sourceY = 48;  // vertical center of source labels
  return (
    <div style={{
      position: "absolute", top: 0, left: 0,
      width, height: funnelMouthY,
      pointerEvents: "none",
    }}>
      {/* Source labels (HTML — above the SVG tributaries) */}
      {OUTREACH_SOURCES.map(src => (
        <div
          key={src.key}
          style={{
            position: "absolute",
            left: src.x - 56, top: 0,
            width: 112, textAlign: "center",
            opacity: src.comingSoon ? 0.45 : 1,
          }}
        >
          <div style={{
            display: "inline-flex",
            alignItems: "center", justifyContent: "center",
            width: 24, height: 24, borderRadius: 5,
            background: src.color, color: "#FFFFFF",
            fontSize: 12, fontWeight: 800,
            fontFamily: "serif",
            boxShadow: `0 0 8px ${src.color}55`,
            marginBottom: 4,
          }}>
            {src.icon}
          </div>
          <div style={{
            fontSize: 10, fontWeight: 700,
            color: src.color, letterSpacing: 0.3,
            textTransform: "uppercase",
            whiteSpace: "nowrap",
          }}>
            {src.label}
          </div>
          {src.comingSoon && (
            <div style={{
              fontSize: 8, fontWeight: 700,
              color: "#9A9B96", marginTop: 1,
              letterSpacing: 0.4,
            }}>
              COMING SOON
            </div>
          )}
        </div>
      ))}

      {/* SVG tributaries — curved paths from each source to funnel mouth,
          with SMIL <animateMotion> particles flowing along them. */}
      <svg
        width={width} height={funnelMouthY}
        style={{ position: "absolute", top: 0, left: 0 }}
      >
        {OUTREACH_SOURCES.map(src => {
          const pathD =
            `M ${src.x} ${sourceY} ` +
            `C ${src.x} ${(sourceY + funnelMouthY) / 2 + 10}, ` +
            `${(src.x + funnelMouthX) / 2} ${funnelMouthY - 5}, ` +
            `${funnelMouthX} ${funnelMouthY}`;
          const active = !src.comingSoon;
          return (
            <g key={src.key} opacity={active ? 1 : 0.35}>
              <path
                d={pathD}
                fill="none"
                stroke={src.color}
                strokeWidth="1.5"
                strokeOpacity={active ? "0.3" : "0.15"}
                strokeDasharray={active ? "none" : "3 3"}
              />
              {active && [0, 1, 2].map(i => (
                <circle
                  key={i}
                  r="2.8"
                  fill={src.color}
                  style={{ filter: `drop-shadow(0 0 3px ${src.color})` }}
                >
                  <animateMotion
                    dur="2.8s"
                    repeatCount="indefinite"
                    path={pathD}
                    begin={`${i * 0.9}s`}
                  />
                </circle>
              ))}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ─── ACTIVITY FEED ───────────────────────────────────────────────────────────
function ActivityFeed({ activities }) {
  if (!activities || activities.length === 0) {
    return (
      <div style={{
        fontSize: 12, color: LIGHT.inkFaint,
        padding: "20px 0", lineHeight: 1.6,
      }}>
        No recent activity yet.
        <br />
        <span style={{ fontSize: 10, color: LIGHT.inkFaint }}>
          (awaiting /activities endpoint)
        </span>
      </div>
    );
  }
  // Hide internal test/probe rows from UI without needing DB cleanup.
  const visible = activities.filter(a => {
    const t = (a.type || "").toLowerCase();
    const s = (a.subject || "").toLowerCase();
    return t !== "healthcheck" && s !== "probe" && s !== "crm test";
  });

  const typeColors = {
    DM: "#0077B5", Email: "#D66F27", Call: "#494C43",
    Meeting: "#10B981", Note: "#6B7280",
  };

  return (
    <div>
      {visible.map((a, i) => {
        const subject = a.subject || a.description || a.title || "Activity";
        const badgeColor = typeColors[a.type] || "#9CA3AF";
        return (
          <div
            key={a.id || i}
            style={{
              display: "flex", gap: 10,
              padding: "10px 0",
              borderBottom: `1px solid ${LIGHT.cardLine}`,
            }}
          >
            <div style={{
              width: 8, height: 8, borderRadius: "50%",
              background: badgeColor, marginTop: 6, flexShrink: 0,
              boxShadow: `0 0 4px ${badgeColor}`,
            }} />
            <div style={{ flex: 1, fontSize: 12, color: LIGHT.ink }}>
              <div style={{ display: "flex", gap: 6, alignItems: "baseline" }}>
                <span style={{
                  fontSize: 9, fontWeight: 700, color: badgeColor,
                  textTransform: "uppercase", letterSpacing: 0.5,
                }}>
                  {a.type}
                </span>
              </div>
              <div style={{ marginTop: 1 }}>{subject}</div>
              <div style={{ color: LIGHT.inkFaint, fontSize: 10, marginTop: 2 }}>
                {a.owner ? `${a.owner} · ` : ""}
                {a.created_at || a.timestamp || ""}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── FUNNEL VERTICAL LEGEND ──────────────────────────────────────────────────
function FunnelLegend({ verticalCounts = [] }) {
  const countByVertical = {};
  verticalCounts.forEach(v => { countByVertical[v.vertical] = v.count; });

  return (
    <div style={{
      display: "flex",
      justifyContent: "center",
      flexWrap: "wrap",
      gap: 18,
      marginTop: 14,
      paddingTop: 12,
      borderTop: "1px solid #EEEDE6",
      fontSize: 12,
    }}>
      {Object.entries(VERTICAL_COLORS).map(([vertical, color]) => {
        const count = countByVertical[vertical] || 0;
        return (
          <div key={vertical} style={{
            display: "flex", alignItems: "center", gap: 7,
            color: LIGHT.ink,
          }}>
            <span style={{
              width: 11, height: 11, borderRadius: "50%",
              background: color, boxShadow: `0 0 6px ${color}`,
              display: "inline-block", flexShrink: 0,
            }} />
            <span style={{ fontWeight: 600 }}>{vertical}</span>
            <span style={{ color: LIGHT.inkFaint, fontSize: 11 }}>
              {fmtNum(count)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── DEALS (Kanban + Table + pipeline tabs + detail modal) ───────────────────
function DealsScreen() {
  const [deals, setDeals] = useState([]);
  const [haloLeads, setHaloLeads] = useState([]);
  const [haloLoading, setHaloLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pipeline, setPipeline] = useState("ALL");
  const [vertical, setVertical] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const dragRef = useRef({ dealId: null, fromStage: null });
  const didFetchRef = useRef(false);
  const [dragId, setDragId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [toast, setToast] = useState(null);
  const [anchorOnly, setAnchorOnly] = useState(false);
  const isHalo = pipeline === "HALO";

  useEffect(() => {
    if (didFetchRef.current) return;
    didFetchRef.current = true;
    api("/deals?limit=500").then(r => { setDeals(r || []); setLoading(false); });
  }, []);

  // Lazy-load HALO leads when the HALO pipeline tab is first opened.
  useEffect(() => {
    if (!isHalo || haloLeads.length > 0 || haloLoading) return;
    setHaloLoading(true);
    api("/pipelines/halo-leads?limit=500").then(r => {
      const rows = (r && r.leads) || [];
      setHaloLeads(rows);
      setHaloLoading(false);
    });
  }, [isHalo, haloLeads.length, haloLoading]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2400);
    return () => clearTimeout(t);
  }, [toast]);

  const visible = deals.filter(d =>
    (pipeline === "ALL" || (d.pipeline || "").toUpperCase() === pipeline) &&
    (!vertical || d.vertical === vertical)
  );

  async function patchDeal(dealId, patch, successMessage) {
    let rollbackSnapshot = null;
    setDeals(prev => {
      rollbackSnapshot = prev;
      return prev.map(d => d.id === dealId ? { ...d, ...patch } : d);
    });
    const result = await api(`/deals/${dealId}`, "PATCH", patch);
    console.log("PATCH response:", result);
    if (!result) {
      setDeals(rollbackSnapshot);
      setToast({ type: "error", text: "Update failed — reverted" });
      return false;
    }
    if (successMessage) setToast({ type: "success", text: successMessage });
    return true;
  }

  function onDragStart(e, deal) {
    dragRef.current = { dealId: deal.id, fromStage: deal.stage };
    setDragId(deal.id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(deal.id)); } catch (_) {}
  }
  function onDragOver(e, stage) {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    if (dragOverCol !== stage) setDragOverCol(stage);
  }
  function onDrop(e, stage) {
    e.preventDefault();
    e.stopPropagation();
    const { dealId, fromStage } = dragRef.current;
    setDragOverCol(null);
    setDragId(null);
    dragRef.current = { dealId: null, fromStage: null };
    if (!dealId || fromStage === stage) return;
    patchDeal(dealId, { stage }, `Deal moved to ${stage}`);
  }

  const byStage = DEAL_STAGES.reduce((acc, s) => {
    acc[s] = visible.filter(d => d.stage === s);
    return acc;
  }, {});
  const totalValue = visible.reduce((a, d) => a + (d.value || 0), 0);

  // HALO pipeline — patch a halo_leads row on drag-drop.
  async function patchHaloLead(leadId, patch, successMessage) {
    let rollback = null;
    setHaloLeads(prev => {
      rollback = prev;
      return prev.map(l => l.id === leadId ? { ...l, ...patch } : l);
    });
    const result = await api(`/halo-leads/${leadId}`, "PATCH", patch);
    if (!result || result.ok === false) {
      setHaloLeads(rollback);
      setToast({ type: "error", text: "Update failed — reverted" });
      return false;
    }
    if (successMessage) setToast({ type: "success", text: successMessage });
    return true;
  }
  function onHaloDragStart(e, lead) {
    dragRef.current = { dealId: lead.id, fromStage: lead.pipeline_stage, halo: true };
    setDragId(`halo-${lead.id}`);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(lead.id)); } catch (_) {}
  }
  function onHaloDrop(e, stage) {
    e.preventDefault();
    e.stopPropagation();
    const { dealId, fromStage } = dragRef.current;
    setDragOverCol(null);
    setDragId(null);
    const leadId = dealId;
    dragRef.current = { dealId: null, fromStage: null };
    if (!leadId || fromStage === stage) return;
    patchHaloLead(leadId, { pipeline_stage: stage }, `Moved to ${stage}`);
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap", borderBottom: `1px solid ${COLORS.cardBorder}`, paddingBottom: 10 }}>
        {PIPELINES.map(p => {
          const active = pipeline === p.id;
          const c = PIPELINE_COLORS[p.id];
          return (
            <button key={p.id} onClick={() => setPipeline(p.id)}
              style={{
                ...S.btn(active ? "primary" : "ghost"),
                fontSize: 13,
                background: active && c ? c : undefined,
                borderColor: !active && c ? `${c}66` : undefined,
                color: !active && c ? c : undefined,
              }}
              title={PIPELINE_FULL[p.id]}>
              {p.id === "ALL" ? p.label : `${p.id} — ${PIPELINE_FULL[p.id]}`}
            </button>
          );
        })}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 12, color: COLORS.grayMid }}>
          {isHalo
            ? (() => {
                const shown = anchorOnly ? haloLeads.filter(l => l.is_anchor_account) : haloLeads;
                const anchorCount = haloLeads.filter(l => l.is_anchor_account).length;
                return `${shown.length} HALO leads · ${shown.filter(l => (l.tier || "").toUpperCase() === "HOT").length} HOT · ${shown.filter(l => (l.pipeline_stage || "").startsWith("HALO-") && !(l.pipeline_stage || "").includes("Closed")).length} open${anchorCount ? ` · 🎯 ${anchorCount} anchors` : ""}`;
              })()
            : `${visible.length} deals · ${fmt$$(totalValue)} total`}
        </div>
        {isHalo && (
          <button onClick={() => setAnchorOnly(v => !v)}
            style={{
              ...S.btn(anchorOnly ? "primary" : "ghost"),
              fontSize: 12,
              background: anchorOnly ? COLORS.orange : undefined,
              borderColor: !anchorOnly ? `${COLORS.orange}66` : undefined,
              color: !anchorOnly ? COLORS.orange : undefined,
            }}
            title="Filter kanban to anchor accounts only">
            🎯 {anchorOnly ? "All HALO leads" : "Anchor accounts only"}
          </button>
        )}
        {!isHalo && <button onClick={() => setShowNew(true)} style={S.btn("primary")}>+ New Deal</button>}
      </div>

      {isHalo ? (
        haloLoading ? <Spinner /> : (
          <HaloKanban
            leads={anchorOnly ? haloLeads.filter(l => l.is_anchor_account) : haloLeads}
            dragId={dragId}
            dragOverCol={dragOverCol}
            onDragStart={onHaloDragStart}
            onDragOver={(e, stage) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = "move"; if (dragOverCol !== stage) setDragOverCol(stage); }}
            onDragEnter={(e, stage) => { e.preventDefault(); setDragOverCol(stage); }}
            onDragLeave={(e, stage) => { if (e.currentTarget.contains(e.relatedTarget)) return; setDragOverCol(c => c === stage ? null : c); }}
            onDrop={onHaloDrop}
            onDragEnd={() => { setDragId(null); setDragOverCol(null); dragRef.current = { dealId: null, fromStage: null }; }}
          />
        )
      ) : loading ? <Spinner /> : (
        <>
          <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 12, alignItems: "stretch", marginBottom: 24 }}>
            {DEAL_STAGES.map(stage => {
              const stageTotal = byStage[stage].reduce((a, d) => a + (d.value || 0), 0);
              const isOver = dragOverCol === stage;
              const dragging = dragId != null;
              return (
                <div key={stage}
                  onDragOver={e => onDragOver(e, stage)}
                  onDragEnter={e => { e.preventDefault(); setDragOverCol(stage); }}
                  onDragLeave={e => {
                    if (e.currentTarget.contains(e.relatedTarget)) return;
                    setDragOverCol(c => c === stage ? null : c);
                  }}
                  onDrop={e => onDrop(e, stage)}
                  style={{
                    position: "relative",
                    minWidth: 260, width: 260, flexShrink: 0,
                    minHeight: "calc(100vh - 200px)",
                    background: isOver ? `${COLORS.orange}22` : "transparent",
                    borderRadius: 6, padding: 8,
                    transition: "background 0.12s, border-color 0.12s",
                    border: isOver ? `2px dashed ${COLORS.orange}` : "2px dashed transparent",
                    display: "flex", flexDirection: "column",
                  }}>
                  <div style={{
                    fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase",
                    color: stage === "Closed Won" ? COLORS.success : stage === "Closed Lost" ? COLORS.danger : COLORS.orange,
                    marginBottom: 8, padding: "0 4px",
                    display: "flex", justifyContent: "space-between",
                    pointerEvents: "none",
                  }}>
                    <span>{stage}</span>
                    <span style={{ color: COLORS.grayMid }}>{byStage[stage].length} · {fmt$$(stageTotal)}</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
                    {byStage[stage].map(deal => (
                      <DealCard key={deal.id}
                        deal={deal}
                        onClick={() => setSelected(deal)}
                        onDragStart={e => onDragStart(e, deal)}
                        onDragEnd={() => { setDragId(null); setDragOverCol(null); dragRef.current = { dealId: null, fromStage: null }; }}
                        onPipelineChange={p => patchDeal(deal.id, { pipeline: p }, `Deal pipeline set to ${p}`)}
                        dragging={dragId === deal.id}
                      />
                    ))}
                  </div>
                  {dragging && byStage[stage].length === 0 && (
                    <div style={{
                      position: "absolute", inset: 8,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: isOver ? COLORS.orange : COLORS.grayDark,
                      fontSize: 13, fontWeight: 700, letterSpacing: 1,
                      textTransform: "uppercase",
                      pointerEvents: "none",
                    }}>
                      Drop here
                    </div>
                  )}
                  {!dragging && byStage[stage].length === 0 && (
                    <div style={{
                      textAlign: "center", color: COLORS.grayDark, fontSize: 11,
                      padding: 20, border: `1px dashed ${COLORS.cardBorder}`,
                      borderRadius: 6, pointerEvents: "none",
                    }}>
                      Empty
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.orange, letterSpacing: 1 }}>DEALS TABLE</div>
            <select value={vertical} onChange={e => setVertical(e.target.value)} style={{ ...S.select, marginLeft: "auto" }}>
              <option value="">All Verticals</option>
              {VERTICALS.map(v => <option key={v}>{v}</option>)}
            </select>
          </div>
          <div style={S.card}>
            <table style={S.table}>
              <thead>
                <tr>
                  {["Deal","Pipeline","Vertical","Organization","Contact","Value","Stage","Close Date"].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map(d => (
                  <tr key={d.id} style={{ cursor: "pointer" }}
                    onClick={() => setSelected(d)}
                    onMouseEnter={e => e.currentTarget.style.background = `${COLORS.orange}11`}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={S.td}><div style={{ fontWeight: 600 }}>{d.title}</div></td>
                    <td style={S.td} onClick={e => e.stopPropagation()}>
                      <PipelinePicker value={d.pipeline} onChange={p => patchDeal(d.id, { pipeline: p }, `Deal pipeline set to ${p}`)} />
                    </td>
                    <td style={{ ...S.td, color: COLORS.grayMid }}>{d.vertical ? `${VERTICAL_ICONS[d.vertical] || ""} ${d.vertical}` : "—"}</td>
                    <td style={{ ...S.td, color: COLORS.grayMid }}>{d.org_name || "—"}</td>
                    <td style={{ ...S.td, color: COLORS.grayMid }}>{d.contact_name || "—"}</td>
                    <td style={S.td}><span style={{ color: COLORS.orange, fontWeight: 700 }}>{fmt$$(d.value)}</span></td>
                    <td style={S.td}>
                      <span style={S.badge(d.stage === "Closed Won" ? COLORS.success : d.stage === "Closed Lost" ? COLORS.danger : COLORS.orange)}>
                        {d.stage}
                      </span>
                    </td>
                    <td style={{ ...S.td, color: COLORS.grayMid }}>{d.expected_close_date?.slice(0, 10) || "—"}</td>
                  </tr>
                ))}
                {visible.length === 0 && (
                  <tr><td colSpan={8} style={{ ...S.td, textAlign: "center", color: COLORS.grayMid, padding: 24 }}>No deals match these filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showNew && (
        <NewDealModal
          defaultPipeline={pipeline !== "ALL" ? pipeline : "BTB"}
          onClose={() => setShowNew(false)}
          onSave={d => { setDeals(prev => [d, ...prev]); setShowNew(false); }}
        />
      )}
      {selected && (
        <DealDetailModal
          deal={selected}
          onClose={() => setSelected(null)}
          onChange={updated => {
            setDeals(prev => prev.map(d => d.id === updated.id ? { ...d, ...updated } : d));
            setSelected(s => s ? { ...s, ...updated } : s);
          }}
          onToast={msg => setToast({ type: "success", text: msg })}
        />
      )}

      {toast && (
        <div style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 2000,
          background: toast.type === "error" ? COLORS.danger : COLORS.success,
          color: COLORS.white, padding: "12px 18px", borderRadius: 6,
          fontSize: 13, fontWeight: 700, letterSpacing: 0.5,
          boxShadow: "0 6px 20px rgba(0,0,0,0.4)",
        }}>
          {toast.text}
        </div>
      )}
    </div>
  );
}

// ─── HALO pipeline kanban (DrewOS — halo_leads visibility) ───────────────────
function HaloKanban({ leads, dragId, dragOverCol, onDragStart, onDragOver, onDragEnter, onDragLeave, onDrop, onDragEnd }) {
  // Primary stages render as columns. Closed-Lost shown collapsed below.
  const primary = HALO_STAGES.filter(s => s !== "HALO-Closed-Lost");
  const byStage = Object.fromEntries(HALO_STAGES.map(s => [s, []]));
  leads.forEach(l => {
    const s = l.pipeline_stage || "HALO-Discovered";
    if (!byStage[s]) byStage[s] = [];
    byStage[s].push(l);
  });
  const [showLost, setShowLost] = useState(false);
  const dragging = dragId != null;
  const stageTotal = stage => byStage[stage]?.length || 0;

  return (
    <div>
      <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 12, alignItems: "stretch", marginBottom: 16 }}>
        {primary.map(stage => {
          const isOver = dragOverCol === stage;
          const color = HALO_STAGE_COLORS[stage];
          return (
            <div key={stage}
              onDragOver={e => onDragOver(e, stage)}
              onDragEnter={e => onDragEnter(e, stage)}
              onDragLeave={e => onDragLeave(e, stage)}
              onDrop={e => onDrop(e, stage)}
              style={{
                position: "relative", minWidth: 280, width: 280, flexShrink: 0,
                minHeight: "calc(100vh - 200px)",
                background: isOver ? `${COLORS.orange}22` : "transparent",
                borderRadius: 6, padding: 8,
                border: isOver ? `2px dashed ${COLORS.orange}` : "2px dashed transparent",
                transition: "background 0.12s",
                display: "flex", flexDirection: "column",
              }}>
              <div style={{
                fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase",
                color, marginBottom: 8, padding: "0 4px",
                display: "flex", justifyContent: "space-between", pointerEvents: "none",
              }}>
                <span>{stage.replace("HALO-", "")}</span>
                <span style={{ color: COLORS.grayMid }}>{stageTotal(stage)}</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
                {byStage[stage].map(l => (
                  <HaloCard key={l.id}
                    lead={l}
                    dragging={dragId === `halo-${l.id}`}
                    onDragStart={e => onDragStart(e, l)}
                    onDragEnd={onDragEnd} />
                ))}
              </div>
              {dragging && byStage[stage].length === 0 && (
                <div style={{
                  position: "absolute", inset: 8,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: isOver ? COLORS.orange : COLORS.grayDark,
                  fontSize: 13, fontWeight: 700, letterSpacing: 1,
                  textTransform: "uppercase", pointerEvents: "none",
                }}>Drop here</div>
              )}
              {!dragging && byStage[stage].length === 0 && (
                <div style={{
                  textAlign: "center", color: COLORS.grayDark, fontSize: 11,
                  padding: 20, border: `1px dashed ${COLORS.cardBorder}`,
                  borderRadius: 6, pointerEvents: "none",
                }}>Empty</div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 8 }}>
        <div onClick={() => setShowLost(s => !s)}
          onDragOver={e => onDragOver(e, "HALO-Closed-Lost")}
          onDragEnter={e => onDragEnter(e, "HALO-Closed-Lost")}
          onDragLeave={e => onDragLeave(e, "HALO-Closed-Lost")}
          onDrop={e => onDrop(e, "HALO-Closed-Lost")}
          style={{
            cursor: "pointer", padding: "8px 12px", border: `1px solid ${COLORS.cardBorder}`,
            borderRadius: 6, display: "flex", justifyContent: "space-between",
            background: dragOverCol === "HALO-Closed-Lost" ? `${COLORS.danger}11` : "transparent",
            fontSize: 12, color: COLORS.grayMid,
          }}>
          <span>{showLost ? "▼" : "▶"} Closed Lost ({stageTotal("HALO-Closed-Lost")})</span>
          <span style={{ fontSize: 10 }}>drag here to close-lost</span>
        </div>
        {showLost && (
          <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 8 }}>
            {byStage["HALO-Closed-Lost"].map(l => (
              <HaloCard key={l.id} lead={l}
                dragging={dragId === `halo-${l.id}`}
                onDragStart={e => onDragStart(e, l)}
                onDragEnd={onDragEnd} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function HaloCard({ lead, dragging, onDragStart, onDragEnd }) {
  const stageColor = HALO_STAGE_COLORS[lead.pipeline_stage] || COLORS.orange;
  const tier = (lead.tier || "").toUpperCase();
  const tierColor = HALO_TIER_COLORS[tier] || COLORS.grayMid;
  const score = lead.halo_intent_score != null ? Math.round(lead.halo_intent_score) : null;
  const lastAct = lead.last_activity_at || lead.last_action_date || lead.updated_at;
  const daysSince = lastAct
    ? Math.max(0, Math.round((Date.now() - new Date(lastAct).getTime()) / 86400000))
    : null;
  const contactLine = [lead.decision_maker_name, lead.decision_maker_title].filter(Boolean).join(" · ");
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      style={{
        ...S.card, cursor: "grab",
        borderLeft: `4px solid ${stageColor}`,
        opacity: dragging ? 0.4 : 1, userSelect: "none", padding: 10,
      }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
        <div style={{ fontWeight: 700, fontSize: 13, flex: 1, display: "flex", alignItems: "center", gap: 4 }}>
          {lead.is_anchor_account ? <span title="Anchor Account">🎯</span> : null}
          {lead.company_name || lead.org_name}
        </div>
        {tier && <span style={{
          fontSize: 9, fontWeight: 800, letterSpacing: 0.5, padding: "2px 6px",
          borderRadius: 3, background: `${tierColor}22`, color: tierColor, border: `1px solid ${tierColor}55`,
        }}>{tier}</span>}
      </div>
      {contactLine && <div style={{ fontSize: 11, color: COLORS.grayMid, marginTop: 3 }}>👤 {contactLine}</div>}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6, alignItems: "center" }}>
        {lead.is_anchor_account && <span style={{
          fontSize: 9, fontWeight: 800, letterSpacing: 0.5, padding: "2px 6px",
          borderRadius: 3, background: `${COLORS.orange}11`, color: COLORS.orange,
          border: `1px solid ${COLORS.orange}88`,
        }}>🎯 ANCHOR</span>}
        {lead.source_batch && <span style={{
          fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 3,
          background: "#FFF7ED", color: COLORS.orange, border: `1px solid ${COLORS.orange}33`,
        }}>{lead.source_batch}</span>}
        {score != null && <span style={{ fontSize: 10, color: COLORS.grayMid }}>
          intent <b style={{ color: COLORS.orange }}>{score}</b>
        </span>}
        {daysSince != null && <span style={{ fontSize: 10, color: COLORS.grayMid }}>
          · {daysSince}d since activity
        </span>}
      </div>
    </div>
  );
}

function DealCard({ deal, onClick, onDragStart, onDragEnd, onPipelineChange, dragging }) {
  const stageColor = deal.stage === "Closed Won" ? COLORS.success : deal.stage === "Closed Lost" ? COLORS.danger : COLORS.orange;
  return (
    <div
      draggable={true}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      style={{
        ...S.card,
        cursor: "grab",
        borderLeft: `4px solid ${stageColor}`,
        opacity: dragging ? 0.4 : 1,
        userSelect: "none",
      }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
        <div style={{ fontWeight: 700, fontSize: 13, flex: 1 }}>{deal.title}</div>
        <div onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
          <PipelinePicker value={deal.pipeline} onChange={onPipelineChange} />
        </div>
      </div>
      <div style={{ fontSize: 20, fontWeight: 800, color: COLORS.orange, marginTop: 4 }}>{fmt$$(deal.value)}</div>
      {deal.org_name && <div style={{ fontSize: 11, color: COLORS.grayMid, marginTop: 4 }}>🏢 {deal.org_name}</div>}
      {deal.contact_name && <div style={{ fontSize: 11, color: COLORS.grayMid }}>👤 {deal.contact_name}</div>}
      {deal.expected_close_date && (
        <div style={{ fontSize: 11, color: COLORS.grayMid, marginTop: 4 }}>
          📅 {deal.expected_close_date.slice(0, 10)}
        </div>
      )}
    </div>
  );
}

function PipelinePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const color = value && PIPELINE_COLORS[value] ? PIPELINE_COLORS[value] : COLORS.grayMid;
  const label = value || "—";

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}
      draggable={false}
      onDragStart={e => { e.preventDefault(); e.stopPropagation(); }}>
      <span
        onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
        style={{
          ...S.badge(color),
          cursor: "pointer",
          userSelect: "none",
        }}>
        {label} ▾
      </span>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 50,
          background: COLORS.greenDark, border: `1px solid ${COLORS.cardBorder}`,
          borderRadius: 5, minWidth: 90, overflow: "hidden",
          boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
        }}>
          {["BTC", "BTB", "BTG"].map(p => (
            <div key={p}
              onClick={e => { e.stopPropagation(); setOpen(false); if (p !== value) onChange(p); }}
              style={{
                padding: "6px 10px", fontSize: 11, fontWeight: 700,
                letterSpacing: 0.5, cursor: "pointer",
                color: PIPELINE_COLORS[p],
                background: p === value ? `${PIPELINE_COLORS[p]}22` : "transparent",
              }}
              onMouseEnter={e => e.currentTarget.style.background = `${PIPELINE_COLORS[p]}33`}
              onMouseLeave={e => e.currentTarget.style.background = p === value ? `${PIPELINE_COLORS[p]}22` : "transparent"}>
              {p}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: COLORS.grayMid, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      {children}
    </div>
  );
}

function RecordSearch({ endpoint, placeholder, onPick, selected, displayField = "name", extraField }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api(`${endpoint}?limit=10&search=${encodeURIComponent(query)}`).then(r => setResults(r || []));
    }, 250);
    return () => clearTimeout(t);
  }, [query, endpoint]);

  if (selected) {
    return (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: COLORS.greenDeep, borderRadius: 5, padding: "7px 12px", border: `1px solid ${COLORS.cardBorder}` }}>
        <span style={{ fontSize: 13 }}>{selected[displayField] || selected.full_name || selected.name}</span>
        <button onClick={() => onPick(null)} style={{ ...S.btn("ghost"), padding: "2px 8px", fontSize: 11 }}>✕</button>
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <input placeholder={placeholder} value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} style={S.input} />
      {open && results.length > 0 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 10,
          background: COLORS.greenDark, border: `1px solid ${COLORS.cardBorder}`,
          borderRadius: 5, maxHeight: 200, overflowY: "auto", marginTop: 2,
        }}>
          {results.map(r => (
            <div key={r.id}
              onClick={() => { onPick(r); setQuery(""); setResults([]); setOpen(false); }}
              style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13, borderBottom: `1px solid ${COLORS.cardBorder}22` }}
              onMouseEnter={e => e.currentTarget.style.background = `${COLORS.orange}22`}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <div style={{ fontWeight: 600 }}>{r[displayField] || r.full_name || r.name}</div>
              {extraField && r[extraField] && <div style={{ fontSize: 11, color: COLORS.grayMid }}>{r[extraField]}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NewDealModal({ onClose, onSave, defaultPipeline = "BTB" }) {
  const [form, setForm] = useState({
    title: "", value: "", pipeline: defaultPipeline, stage: "Lead In",
    expected_close_date: "", notes: "",
  });
  const [org, setOrg] = useState(null);
  const [contact, setContact] = useState(null);
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!form.title || saving) return;
    setSaving(true);
    const body = {
      title: form.title,
      value: parseFloat(form.value) || 0,
      pipeline: form.pipeline,
      stage: form.stage,
      notes: form.notes || null,
      expected_close_date: form.expected_close_date || null,
      org_id: org?.id || null,
      contact_id: contact?.id || null,
    };
    const result = await api("/deals", "POST", body);
    setSaving(false);
    if (result) onSave({ ...result, org_name: org?.name, contact_name: contact?.full_name });
  }

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ ...S.modalBox, maxWidth: 600 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 800 }}>New Deal</div>
          <button onClick={onClose} style={{ ...S.btn("ghost"), padding: "4px 8px" }}>✕</button>
        </div>

        <FormField label="Deal Title">
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={S.input} />
        </FormField>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <FormField label="Value ($)">
            <input type="number" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} style={S.input} />
          </FormField>
          <FormField label="Expected Close Date">
            <input type="date" value={form.expected_close_date} onChange={e => setForm(f => ({ ...f, expected_close_date: e.target.value }))} style={S.input} />
          </FormField>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <FormField label="Pipeline">
            <select value={form.pipeline} onChange={e => setForm(f => ({ ...f, pipeline: e.target.value }))} style={{ ...S.select, width: "100%" }}>
              {PIPELINES.filter(p => p.id !== "ALL").map(p => (
                <option key={p.id} value={p.id}>{p.id} — {PIPELINE_FULL[p.id]}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Stage">
            <select value={form.stage} onChange={e => setForm(f => ({ ...f, stage: e.target.value }))} style={{ ...S.select, width: "100%" }}>
              {DEAL_STAGES.map(s => <option key={s}>{s}</option>)}
            </select>
          </FormField>
        </div>

        <FormField label="Organization">
          <RecordSearch endpoint="/orgs" placeholder="Search organizations..." onPick={setOrg} selected={org} displayField="name" extraField="city" />
        </FormField>

        <FormField label="Contact">
          <RecordSearch endpoint="/contacts" placeholder="Search contacts..." onPick={setContact} selected={contact} displayField="full_name" extraField="email_work" />
        </FormField>

        <FormField label="Notes">
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            style={{ ...S.input, height: 70 }} />
        </FormField>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
          <button onClick={onClose} style={S.btn("ghost")}>Cancel</button>
          <button onClick={submit} disabled={saving || !form.title}
            style={{ ...S.btn("primary"), opacity: (saving || !form.title) ? 0.5 : 1 }}>
            {saving ? "Creating..." : "Create Deal"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── CREATE MODALS (org / contact / lead) ────────────────────────────────────
// Direct-fetch POST that surfaces backend error bodies (the shared api() helper
// swallows non-2xx responses and returns null, which hides validation errors).
async function postRecord(path, body) {
  try {
    const r = await fetch(`${CRM_URL}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const text = await r.text();
    let data = null;
    try { data = text ? JSON.parse(text) : null; } catch { data = { detail: text }; }
    if (!r.ok) {
      const msg = (data && (data.detail || data.message)) || `HTTP ${r.status}`;
      return { ok: false, error: typeof msg === "string" ? msg : JSON.stringify(msg) };
    }
    return { ok: true, data };
  } catch (e) {
    return { ok: false, error: e.message || "Network error" };
  }
}

function NewOrgModal({ onClose, onCreated, defaultVertical = "" }) {
  const [form, setForm] = useState({
    name: "", vertical: defaultVertical, city: "", state: "",
    phone: "", website: "", primary_email: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  async function submit() {
    if (!form.name.trim() || saving) return;
    setSaving(true); setError("");
    const body = {
      name: form.name.trim(),
      vertical: form.vertical || null,
      city: form.city || null,
      state: form.state || null,
      phone: form.phone || null,
      website: form.website || null,
      primary_email: form.primary_email.trim() || null,
      source: "Manual",
    };
    const r = await postRecord("/orgs", body);
    setSaving(false);
    if (r.ok) onCreated(r.data);
    else setError(r.error);
  }
  return (
    <CCModalShell title="New Organization" onClose={onClose}>
      <FormField label="Name *">
        <input value={form.name} onChange={e => set("name", e.target.value)}
          style={S.input} autoFocus
          onKeyDown={e => { if (e.key === "Enter") submit(); }} />
      </FormField>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <FormField label="Vertical">
          <select value={form.vertical} onChange={e => set("vertical", e.target.value)}
            style={{ ...S.select, width: "100%" }}>
            <option value="">—</option>
            {VERTICALS.map(v => <option key={v}>{v}</option>)}
          </select>
        </FormField>
        <FormField label="City">
          <input value={form.city} onChange={e => set("city", e.target.value)} style={S.input} />
        </FormField>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <FormField label="State">
          <input value={form.state} onChange={e => set("state", e.target.value)} style={S.input} />
        </FormField>
        <FormField label="Phone">
          <input value={form.phone} onChange={e => set("phone", e.target.value)} style={S.input} />
        </FormField>
      </div>
      <FormField label="Website">
        <input value={form.website} onChange={e => set("website", e.target.value)} style={S.input} />
      </FormField>
      <FormField label="Org email — typically info@ or main contact">
        <input type="email" value={form.primary_email}
          onChange={e => set("primary_email", e.target.value)}
          placeholder="info@example.com" style={S.input} />
      </FormField>
      {error && (
        <div style={{ fontSize: 12, color: COLORS.danger, padding: "4px 0" }}>{error}</div>
      )}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
        <button onClick={onClose} style={S.btn("ghost")}>Cancel</button>
        <button onClick={submit} disabled={saving || !form.name.trim()}
          style={{ ...S.btn("primary"), opacity: (saving || !form.name.trim()) ? 0.5 : 1 }}>
          {saving ? "Creating…" : "Create Organization"}
        </button>
      </div>
    </CCModalShell>
  );
}

function NewContactModal({ onClose, onCreated, defaultOrg = null }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", job_title: "",
    email_work: "", email_home: "", phone_work: "",
  });
  const [org, setOrg] = useState(defaultOrg);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const hasName = form.first_name.trim() || form.last_name.trim();
  const hasReach = form.email_work.trim() || form.email_home.trim() || form.phone_work.trim();
  const canSubmit = hasName && hasReach && !saving;
  async function submit() {
    if (!canSubmit) return;
    setSaving(true); setError("");
    const body = {
      first_name: form.first_name.trim() || null,
      last_name: form.last_name.trim() || null,
      job_title: form.job_title || null,
      email_work: form.email_work.trim() || null,
      email_home: form.email_home.trim() || null,
      phone_work: form.phone_work.trim() || null,
      org_id: org?.id || null,
      source: "Manual",
    };
    const r = await postRecord("/contacts", body);
    setSaving(false);
    if (r.ok) onCreated(r.data);
    else setError(r.error);
  }
  return (
    <CCModalShell title="New Contact" onClose={onClose}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <FormField label="First Name">
          <input value={form.first_name} onChange={e => set("first_name", e.target.value)}
            style={S.input} autoFocus />
        </FormField>
        <FormField label="Last Name">
          <input value={form.last_name} onChange={e => set("last_name", e.target.value)}
            style={S.input} />
        </FormField>
      </div>
      <FormField label="Job Title">
        <input value={form.job_title} onChange={e => set("job_title", e.target.value)} style={S.input} />
      </FormField>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <FormField label="Work email">
          <input type="email" value={form.email_work}
            onChange={e => set("email_work", e.target.value)} style={S.input} />
        </FormField>
        <FormField label="Personal email">
          <input type="email" value={form.email_home}
            onChange={e => set("email_home", e.target.value)} style={S.input} />
        </FormField>
      </div>
      <FormField label="Work Phone">
        <input value={form.phone_work} onChange={e => set("phone_work", e.target.value)} style={S.input} />
      </FormField>
      <div style={{ fontSize: 11, color: COLORS.grayMid, marginTop: -4, marginBottom: 6 }}>
        At least one of work email, personal email, or phone is required.
      </div>
      <FormField label="Organization (optional)">
        <RecordSearch endpoint="/orgs" placeholder="Search organizations..."
          onPick={setOrg} selected={org} displayField="name" extraField="city" />
      </FormField>
      {error && (
        <div style={{ fontSize: 12, color: COLORS.danger, padding: "4px 0" }}>{error}</div>
      )}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
        <button onClick={onClose} style={S.btn("ghost")}>Cancel</button>
        <button onClick={submit} disabled={!canSubmit}
          style={{ ...S.btn("primary"), opacity: canSubmit ? 1 : 0.5 }}>
          {saving ? "Creating…" : "Create Contact"}
        </button>
      </div>
    </CCModalShell>
  );
}

function NewLeadModal({ onClose, onCreated, defaultVertical = "" }) {
  const [form, setForm] = useState({
    title: "", vertical: defaultVertical, status: "New", source_origin: "Manual",
    email_work: "", email_home: "", phone_work: "",
  });
  const [org, setOrg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const hasReach = form.email_work.trim() || form.email_home.trim() || form.phone_work.trim();
  const canSubmit = form.title.trim() && hasReach && !saving;
  async function submit() {
    if (!canSubmit) return;
    setSaving(true); setError("");
    const body = {
      title: form.title.trim(),
      vertical: form.vertical || null,
      status: form.status,
      source_origin: form.source_origin,
      email_work: form.email_work.trim() || null,
      email_home: form.email_home.trim() || null,
      phone_work: form.phone_work.trim() || null,
      org_id: org?.id || null,
    };
    const r = await postRecord("/leads", body);
    setSaving(false);
    if (r.ok) onCreated(r.data);
    else setError(r.error);
  }
  return (
    <CCModalShell title="New Lead" onClose={onClose}>
      <FormField label="Title *">
        <input value={form.title} onChange={e => set("title", e.target.value)}
          style={S.input} autoFocus
          onKeyDown={e => { if (e.key === "Enter") submit(); }}
          placeholder="e.g. Sunrise Senior Living — intro call" />
      </FormField>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <FormField label="Vertical">
          <select value={form.vertical} onChange={e => set("vertical", e.target.value)}
            style={{ ...S.select, width: "100%" }}>
            <option value="">—</option>
            {VERTICALS.map(v => <option key={v}>{v}</option>)}
          </select>
        </FormField>
        <FormField label="Status">
          <select value={form.status} onChange={e => set("status", e.target.value)}
            style={{ ...S.select, width: "100%" }}>
            {LEAD_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
        </FormField>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <FormField label="Work email">
          <input type="email" value={form.email_work}
            onChange={e => set("email_work", e.target.value)} style={S.input} />
        </FormField>
        <FormField label="Personal email">
          <input type="email" value={form.email_home}
            onChange={e => set("email_home", e.target.value)} style={S.input} />
        </FormField>
      </div>
      <FormField label="Work Phone">
        <input value={form.phone_work} onChange={e => set("phone_work", e.target.value)} style={S.input} />
      </FormField>
      <div style={{ fontSize: 11, color: COLORS.grayMid, marginTop: -4, marginBottom: 6 }}>
        At least one of work email, personal email, or phone is required.
      </div>
      <FormField label="Organization (optional)">
        <RecordSearch endpoint="/orgs" placeholder="Search organizations..."
          onPick={setOrg} selected={org} displayField="name" extraField="city" />
      </FormField>
      {error && (
        <div style={{ fontSize: 12, color: COLORS.danger, padding: "4px 0" }}>{error}</div>
      )}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
        <button onClick={onClose} style={S.btn("ghost")}>Cancel</button>
        <button onClick={submit} disabled={!canSubmit}
          style={{ ...S.btn("primary"), opacity: canSubmit ? 1 : 0.5 }}>
          {saving ? "Creating…" : "Create Lead"}
        </button>
      </div>
    </CCModalShell>
  );
}

// ─── DETAIL MODAL PRIMITIVES ─────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
function ModalShell({ title, subtitle, onClose, children, wide }) {
  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ ...S.modalBox, maxWidth: wide ? 760 : 600 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, gap: 10 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12, color: COLORS.grayMid, marginTop: 2 }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} style={{ ...S.btn("ghost"), padding: "4px 8px" }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function DetailGrid({ rows }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 4 }}>
      {rows.map(([k, v], i) => (
        <div key={i} style={{ background: COLORS.greenDeep, borderRadius: 5, padding: "8px 10px" }}>
          <div style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase", letterSpacing: 0.5 }}>{k}</div>
          <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>{v ?? "—"}</div>
        </div>
      ))}
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function Section({ title, children }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.orange, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  );
}

// ── Shared: activity type icons ──────────────────────────────────────────────
const ATYPE_ICON = { Email: <Mail size={14} />, email: <Mail size={14} />, Call: <Phone size={14} />, call: <Phone size={14} />, Meeting: <Video size={14} />, meeting: <Video size={14} />, Reply: <Flame size={14} color="#EF4444" />, "LinkedIn DM": <Send size={14} />, linkedin: <Send size={14} />, note: <FileText size={14} />, ai_strategy: <Sparkles size={14} />, stage_change: <ArrowRight size={14} />, reminder: <Bell size={14} />, sequence_email: <Mail size={14} />, Other: <PenLine size={14} /> };
const ATYPE_BORDER = { email: "#3B82F6", Email: "#3B82F6", call: "#10B981", Call: "#10B981", note: "#F59E0B", stage_change: "#6B7280", ai_strategy: "#8B5CF6", meeting: "#14B8A6", Meeting: "#14B8A6", reminder: "#F59E0B", sequence_email: "#3B82F6", linkedin: "#0077B5", "LinkedIn DM": "#0077B5" };

function timeAgo(isoStr) {
  if (!isoStr) return "";
  const diff = Date.now() - new Date(isoStr).getTime();
  if (diff < 0) return "just now";
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
}

// eslint-disable-next-line no-unused-vars
function ActivityTimeline({ activities, notes }) {
  const merged = [
    ...(activities || []).map(a => ({ ...a, _kind: "activity" })),
    ...(notes || []).map(n => ({ ...n, _kind: "note", type: "note" })),
  ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  if (merged.length === 0) return <div style={{ fontSize: 12, color: COLORS.grayDark }}>No activity yet.</div>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflowY: "auto" }}>
      {merged.map((a, i) => {
        const icon = ATYPE_ICON[a.type] || "📌";
        const text = a.subject || a.note || a.body || a.content || a.description || "";
        const ts = (a.created_at || a.timestamp || "").replace("T", " ").slice(0, 16);
        return (
          <div key={a.id || `m${i}`} style={{ background: COLORS.greenDeep, borderRadius: 5, padding: "8px 10px", fontSize: 12 }}>
            <div style={{ display: "flex", gap: 6, alignItems: "baseline" }}>
              <span>{icon}</span>
              <span style={{ fontWeight: 700, color: a._kind === "note" ? COLORS.grayMid : COLORS.orange }}>{a.type || "Note"}</span>
              {ts && <span style={{ fontSize: 10, color: COLORS.grayDark, marginLeft: "auto" }}>{ts}</span>}
            </div>
            {text && <div style={{ marginTop: 3, color: COLORS.grayMid, whiteSpace: "pre-wrap", maxHeight: 80, overflow: "hidden" }}>{text}</div>}
          </div>
        );
      })}
    </div>
  );
}

// ─── FULL-SCREEN CARD SYSTEM ─────────────────────────────────────────────────
function FullScreenCard({ title, subtitle, badge, onClose, left, right, onDelete, deleteLabel }) {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  useEffect(() => { requestAnimationFrame(() => setOpen(true)); }, []);

  function handleClose() {
    setClosing(true);
    setOpen(false);
    setTimeout(onClose, 320);
  }

  return (
    <>
      {/* Backdrop — fades in, click to close */}
      <div onClick={handleClose} style={{
        position: "fixed", inset: 0, zIndex: 999,
        background: open ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0)",
        transition: "background 0.3s ease",
        pointerEvents: closing ? "none" : "auto",
      }} />

      {/* Panel — slides in from right */}
      <div onClick={e => e.stopPropagation()} style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "calc(100vw - 60px)",
        maxWidth: 1200,
        background: COLORS.white,
        borderLeft: `1px solid ${COLORS.cardBorder}`,
        boxShadow: "-8px 0 30px rgba(0,0,0,0.12)",
        zIndex: 1000,
        display: "flex", flexDirection: "column",
        overflow: "hidden",
        transform: open ? "translateX(0)" : "translateX(100%)",
        transition: "transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
        willChange: "transform",
      }}>
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 20px", borderBottom: `1px solid ${COLORS.cardBorder}`,
          background: COLORS.gray, flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: COLORS.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
              {subtitle && <div style={{ fontSize: 12, color: COLORS.grayMid, marginTop: 1 }}>{subtitle}</div>}
            </div>
            {badge}
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
            {onDelete && (
              <button onClick={() => setShowDelete(true)} title="Delete" style={{
                ...S.btn("ghost"), padding: "6px 10px", color: "#EF4444", borderColor: "#FECACA",
              }}>
                <Trash2 size={15} />
              </button>
            )}
            <button onClick={handleClose} style={{
              ...S.btn("ghost"), padding: "6px 12px", fontSize: 16, lineHeight: 1,
            }}>✕</button>
          </div>
        </div>
        {showDelete && (
          <DeleteConfirmModal
            title={`Delete ${deleteLabel || "Record"}`}
            message={`Are you sure you want to delete "${title}"? This action cannot be undone.`}
            onConfirm={async () => { await onDelete(); }}
            onCancel={() => setShowDelete(false)}
          />
        )}
        {/* Body: two columns */}
        <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
          <div style={{ flex: "0 0 65%", overflowY: "auto", padding: 20, borderRight: `1px solid ${COLORS.cardBorder}` }}>
            {left}
          </div>
          <div style={{ flex: "0 0 35%", overflowY: "auto", padding: 20, background: COLORS.gray }}>
            {right}
          </div>
        </div>
      </div>
    </>
  );
}

// ── New ActivityTimeline for FullScreenCard ──────────────────────────────────
function FSActivityTimeline({ recordType, recordId, initialActivities, externalRefreshKey = 0, recordCreatedAt, recordOwner }) {
  const [activities, setActivities] = useState(initialActivities || []);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [tasks, setTasks] = useState([]);
  const { show: showToast, ToastEl } = useToast();
  const limit = 20;

  useEffect(() => {
    if (initialActivities && initialActivities.length > 0 && externalRefreshKey === 0) {
      setActivities(initialActivities);
      setOffset(initialActivities.length);
      setHasMore(initialActivities.length >= limit);
    } else {
      api(`/activities/${recordType}/${recordId}?limit=${limit}&offset=0`).then(r => {
        const list = r || [];
        setActivities(list);
        setOffset(list.length);
        setHasMore(list.length >= limit);
      });
    }
    api(`/tasks?record_type=${recordType}&record_id=${recordId}`).then(r => setTasks(r || []));
  }, [recordType, recordId, initialActivities, externalRefreshKey]);

  function loadMore() {
    setLoadingMore(true);
    api(`/activities/${recordType}/${recordId}?limit=${limit}&offset=${offset}`).then(r => {
      const list = r || [];
      setActivities(prev => [...prev, ...list]);
      setOffset(prev => prev + list.length);
      setHasMore(list.length >= limit);
      setLoadingMore(false);
    });
  }

  return (
    <div>
      {ToastEl}
      <FollowUpsSection
        tasks={tasks}
        recordType={recordType}
        recordId={recordId}
        onUpdate={(id, patch) => setTasks(prev => prev.map(x => x.id === id ? { ...x, ...patch } : x))}
        onRemove={id => setTasks(prev => prev.filter(x => x.id !== id))}
        onCompleteAddActivity={newActivity => setActivities(prev => [newActivity, ...prev])}
        showToast={showToast}
      />


      {/* Timeline */}
      {activities.length === 0 && <div style={{ fontSize: 12, color: COLORS.grayDark, padding: 12 }}>No activity yet.</div>}
      <ExpandableTimeline
        activities={activities}
        onUpdate={(id, patch) => setActivities(prev => prev.map(x => x.id === id ? { ...x, ...patch } : x))}
        onRemove={id => setActivities(prev => prev.filter(x => x.id !== id))}
      />
      {hasMore && (
        <div style={{ textAlign: "center", marginTop: 12 }}>
          <button onClick={loadMore} disabled={loadingMore} style={{ ...S.btn("ghost"), fontSize: 11 }}>
            {loadingMore ? "Loading..." : "Load more"}
          </button>
        </div>
      )}
      {!hasMore && recordCreatedAt && (
        <div style={{
          marginTop: 12, padding: "8px 12px", fontSize: 11, color: COLORS.grayMid,
          background: COLORS.gray, borderRadius: 6, textAlign: "center",
          border: `1px dashed ${COLORS.cardBorder}`,
        }}>
          <span style={{ fontWeight: 700, color: COLORS.textSecondary }}>
            {recordType === "deal" ? "Deal" : recordType === "lead" ? "Lead" :
             recordType === "org" ? "Organization" : "Contact"} created
          </span>
          {" — "}
          {new Date(recordCreatedAt).toLocaleString(undefined, {
            month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
          })}
          {recordOwner ? ` — ${recordOwner}` : ""}
        </div>
      )}
    </div>
  );
}

// ── Follow Ups section: pinned at top of timeline ────────────────────────────
function _dueStatus(isoDate) {
  if (!isoDate) return { key: "none", label: "", color: COLORS.grayMid, border: COLORS.orange + "44" };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(isoDate);
  if (Number.isNaN(due.getTime())) {
    return { key: "none", label: "", color: COLORS.grayMid, border: COLORS.orange + "44" };
  }
  const dueDay = new Date(due);
  dueDay.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((dueDay - today) / 86400000);
  if (diffDays < 0) return { key: "overdue", label: "OVERDUE", color: COLORS.danger, border: COLORS.danger };
  if (diffDays === 0) return { key: "today", label: "TODAY", color: COLORS.warning, border: COLORS.warning };
  if (diffDays <= 7) return { key: "week", label: "", color: "#EAB308", border: "#EAB308" };
  return { key: "later", label: "", color: COLORS.grayMid, border: COLORS.orange + "44" };
}

const PRIORITY_DOT = {
  High: COLORS.danger,
  Medium: COLORS.orange,
  Low: COLORS.grayMid,
};

function FollowUpsSection({ tasks, recordType, recordId, onUpdate, onRemove, onCompleteAddActivity, showToast }) {
  const [expandedId, setExpandedId] = useState(null);
  if (!tasks || tasks.length === 0) return null;

  // Open tasks first (sorted by due date asc), then completed at bottom (most recent first)
  const open = tasks.filter(t => !t.completed_at)
    .slice()
    .sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));
  const done = tasks.filter(t => t.completed_at)
    .slice()
    .sort((a, b) => (b.completed_at || "").localeCompare(a.completed_at || ""));
  const merged = [...open, ...done];

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: COLORS.orange, letterSpacing: 1,
        textTransform: "uppercase", marginBottom: 8,
      }}>Follow Ups</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {merged.map(t => (
          <ExpandableFollowUpRow
            key={t.id}
            task={t}
            recordType={recordType}
            recordId={recordId}
            expanded={expandedId === t.id}
            onToggle={() => setExpandedId(prev => prev === t.id ? null : t.id)}
            onUpdate={onUpdate}
            onRemove={onRemove}
            onCompleteAddActivity={onCompleteAddActivity}
            showToast={showToast}
          />
        ))}
      </div>
    </div>
  );
}

function ExpandableFollowUpRow({ task: t, recordType, recordId, expanded, onToggle, onUpdate, onRemove, onCompleteAddActivity, showToast }) {
  const isDone = !!t.completed_at;
  const status = _dueStatus(t.due_date);
  const type = (t.type || "task").toLowerCase();

  const [hovered, setHovered] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmCancelNoGcal, setConfirmCancelNoGcal] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [copied, setCopied] = useState(false);

  // Editable fields
  const [desc, setDesc] = useState(t.description || "");
  const [dueDate, setDueDate] = useState(t.due_date || "");
  const [priority, setPriority] = useState(t.priority || "Medium");
  const [notes, setNotes] = useState(t.notes || "");
  const [duration, setDuration] = useState(t.duration || 30);
  const [outcome, setOutcome] = useState(t.outcome || "Not yet");
  const [attendees, setAttendees] = useState(t.attendees || "");

  useEffect(() => {
    setDesc(t.description || "");
    setDueDate(t.due_date || "");
    setPriority(t.priority || "Medium");
    setNotes(t.notes || "");
    setDuration(t.duration || 30);
    setOutcome(t.outcome || "Not yet");
    setAttendees(t.attendees || "");
  }, [t.id, t.description, t.due_date, t.priority, t.notes, t.duration, t.outcome, t.attendees, expanded]);

  const dirty =
    desc !== (t.description || "") ||
    dueDate !== (t.due_date || "") ||
    priority !== (t.priority || "Medium") ||
    notes !== (t.notes || "") ||
    (duration || 0) !== (t.duration || 0) ||
    outcome !== (t.outcome || "Not yet") ||
    attendees !== (t.attendees || "");

  const meetUrl = (() => {
    const src = `${desc}\n${notes}`;
    const m = /https:\/\/meet\.google\.com\/[a-z0-9-]+/i.exec(src);
    return m ? m[0] : null;
  })();

  async function saveChanges() {
    if (!dirty) return;
    setSaving(true);
    const patch = {
      description: desc, due_date: dueDate || null, priority,
      notes, duration: Number(duration) || null, outcome, attendees,
    };
    const r = await api(`/tasks/${t.id}`, "PATCH", patch);
    setSaving(false);
    if (r) {
      onUpdate && onUpdate(t.id, patch);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    }
  }

  async function completeTask() {
    const r = await api(`/tasks/${t.id}/complete`, "POST");
    if (r) {
      onUpdate && onUpdate(t.id, { completed_at: r.completed_at, status: "done" });
      // Mirror call/meeting/note into timeline client-side so it appears immediately
      if (["call", "meeting", "note"].includes(type) && onCompleteAddActivity) {
        onCompleteAddActivity({
          id: null, type, subject: t.description, note: t.notes,
          description: t.description, duration: t.duration, outcome: t.outcome,
          created_at: new Date().toISOString(),
        });
      }
    }
  }

  async function deleteTask() {
    await api(`/tasks/${t.id}`, "DELETE");
    onRemove && onRemove(t.id);
  }

  function _meetingActivityLinks() {
    const links = {};
    if (t.record_type === "lead" || recordType === "lead") links.lead_id = t.record_id || recordId;
    if (t.record_type === "org"  || recordType === "org")  links.org_id  = t.record_id || recordId;
    if (t.record_type === "contact" || recordType === "contact") links.contact_id = t.record_id || recordId;
    if (t.record_type === "deal" || recordType === "deal") links.deal_id = t.record_id || recordId;
    return links;
  }

  async function cancelMeeting() {
    setCancelling(true);
    try {
      await api(
        `/calendar/events/${encodeURIComponent(t.gcal_event_id)}?send_notification=true`,
        "DELETE",
      );
      await api(`/tasks/${t.id}`, "PATCH", { status: "cancelled" });
      await api("/activities", "POST", {
        type: "meeting",
        subject: `Meeting cancelled: ${t.description || ""}`,
        description: `Cancelled meeting originally scheduled for ${t.due_date || "(no date)"}`,
        note: t.notes || null,
        outcome: "Cancelled",
        ..._meetingActivityLinks(),
      });
      onRemove && onRemove(t.id);
      showToast && showToast("Meeting cancelled and attendee notified");
    } catch (e) {
      showToast && showToast("Cancel failed — see console", "error");
    }
    setCancelling(false);
  }

  async function cancelFollowUpNoGcal() {
    await api(`/tasks/${t.id}`, "DELETE");
    await api("/activities", "POST", {
      type: "meeting",
      subject: `Meeting follow up removed: ${t.description || ""}`,
      description: `Removed follow up for ${t.due_date || "(no date)"} (no GCal event linked)`,
      note: t.notes || null,
      outcome: "Cancelled",
      ..._meetingActivityLinks(),
    });
    onRemove && onRemove(t.id);
    showToast && showToast("Follow up removed");
  }

  async function rescheduleMeeting() {
    if (!rescheduleDate) return;
    setRescheduling(true);
    try {
      const startIso = rescheduleDate.length === 16 ? `${rescheduleDate}:00` : rescheduleDate;
      const endIso = (() => {
        const d = new Date(startIso);
        if (Number.isNaN(d.getTime())) return startIso;
        d.setMinutes(d.getMinutes() + (Number(duration) || 30));
        return d.toISOString().slice(0, 19);
      })();

      let newGcalId = t.gcal_event_id || null;
      if (t.gcal_event_id) {
        // 1. Delete old GCal event — sendUpdates="all" notifies attendees
        await api(
          `/calendar/events/${encodeURIComponent(t.gcal_event_id)}?send_notification=true`,
          "DELETE",
        );
        // 2. Create new GCal event at new time — invite goes out automatically
        const ev = await api("/calendar/events", "POST", {
          title: t.description || "Rescheduled meeting",
          start: startIso, end: endIso,
          attendee_email: attendees || null,
          description: notes || "",
          meet: true,
          lead_id: t.record_type === "lead" ? t.record_id : null,
          org_id: t.record_type === "org" ? t.record_id : null,
          contact_id: t.record_type === "contact" ? t.record_id : null,
        });
        if (ev && ev.id) newGcalId = ev.id;
      }
      // 3. Update task with new date + new gcal id
      const patch = { due_date: rescheduleDate, gcal_event_id: newGcalId };
      await api(`/tasks/${t.id}`, "PATCH", patch);
      onUpdate && onUpdate(t.id, patch);

      // 4. Log reschedule to activity timeline
      await api("/activities", "POST", {
        type: "meeting",
        subject: `Meeting rescheduled to ${rescheduleDate.replace("T", " ")}`,
        description: `Previously ${t.due_date || "(no date)"} → now ${rescheduleDate.replace("T", " ")}`,
        note: notes || null,
        ..._meetingActivityLinks(),
      });

      setRescheduleOpen(false);
      setRescheduleDate("");
      showToast && showToast(
        t.gcal_event_id
          ? "Meeting rescheduled — attendee notified"
          : "Follow up rescheduled"
      );
    } catch (e) {
      showToast && showToast("Reschedule failed — see console", "error");
    }
    setRescheduling(false);
  }

  async function snooze() {
    const r = await api(`/tasks/${t.id}/snooze?days=1`, "POST");
    if (r) onUpdate && onUpdate(t.id, { due_date: r.due_date });
  }

  function copyDesc() {
    _copyText([desc, notes].filter(Boolean).join("\n\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  // Visuals
  const borderColor = isDone ? COLORS.grayMid : status.border;
  const bg = isDone ? "#F3F4F6" : (expanded ? "#FFF8F3" : "#FFFBF6");
  const descStyle = isDone
    ? { textDecoration: "line-through", color: COLORS.grayMid }
    : {};

  const priorityDot = (
    <span style={{
      display: "inline-block", width: 7, height: 7, borderRadius: "50%",
      background: PRIORITY_DOT[t.priority] || COLORS.grayMid,
      flexShrink: 0,
    }} title={`Priority: ${t.priority || "Medium"}`} />
  );

  const dueLabel = t.due_date ? t.due_date.slice(0, 16).replace("T", " ") : "";

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: bg, borderRadius: 6,
        border: `1px solid ${COLORS.orange}33`,
        borderLeft: `3px solid ${borderColor}`,
        transition: "background 0.18s ease, border-color 0.18s ease",
        overflow: "hidden",
      }}
    >
      {/* Row header — clickable */}
      <div
        onClick={onToggle}
        style={{
          display: "flex", gap: 8, alignItems: "center",
          padding: "8px 12px", cursor: "pointer", userSelect: "none",
        }}
      >
        {priorityDot}
        <span style={{
          fontSize: 10, fontWeight: 700, color: COLORS.grayMid,
          textTransform: "uppercase", letterSpacing: 0.5,
        }}>{type}</span>
        {status.label && (
          <span style={{
            ...S.badge(status.color), fontSize: 9, padding: "2px 6px",
          }}>{status.label}</span>
        )}
        <span style={{
          fontWeight: 600, flex: 1, minWidth: 0, overflow: "hidden",
          textOverflow: "ellipsis", whiteSpace: "nowrap",
          ...descStyle,
        }}>{t.description}</span>
        {dueLabel && !expanded && (
          <span style={{ fontSize: 10, color: COLORS.grayMid, flexShrink: 0 }}>{dueLabel}</span>
        )}

        {/* Hover quick actions */}
        {hovered && !expanded && !isDone && (
          <span style={{ display: "flex", gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
            <button onClick={completeTask} title="Complete" style={{
              ...S.btn("ghost"), padding: "2px 6px", color: COLORS.success,
              borderColor: COLORS.success + "55",
            }}><Check size={12} /></button>
            <button onClick={onToggle} title="Edit" style={{
              ...S.btn("ghost"), padding: "2px 6px",
            }}><Pencil size={12} /></button>
            <button onClick={() => setConfirmDelete(true)} title="Delete" style={{
              ...S.btn("ghost"), padding: "2px 6px", color: COLORS.danger,
              borderColor: COLORS.danger + "55",
            }}><Trash2 size={12} /></button>
          </span>
        )}

        {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
      </div>

      {/* Expanded body */}
      <div style={{
        maxHeight: expanded ? 2000 : 0,
        overflow: "hidden",
        transition: "max-height 0.28s ease",
      }}>
        <div style={{ padding: "0 12px 12px 12px", borderTop: `1px solid ${COLORS.orange}22` }}>
          {/* Shared fields */}
          <div style={{ marginTop: 8 }}>
            <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>
              {type === "note" ? "Note" : type === "task" ? "Task description" : type === "call" ? "Contact / subject" : "Title"}
            </label>
            <textarea
              value={desc} onChange={e => setDesc(e.target.value)}
              style={{ ...S.input, width: "100%", minHeight: 50, resize: "vertical", fontFamily: "inherit", fontSize: 12 }}
            />
            {type === "note" && (
              <div style={{ fontSize: 10, color: COLORS.grayMid, textAlign: "right" }}>{desc.length} chars</div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 160px" }}>
              <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Due</label>
              <input
                type={dueDate.includes("T") ? "datetime-local" : "date"}
                value={dueDate.slice(0, dueDate.includes("T") ? 16 : 10)}
                onChange={e => setDueDate(e.target.value)}
                style={{ ...S.input, width: "100%" }}
              />
            </div>
            <div style={{ flex: "1 1 120px" }}>
              <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Priority</label>
              <select value={priority} onChange={e => setPriority(e.target.value)}
                style={{ ...S.select, width: "100%" }}>
                {["High", "Medium", "Low"].map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            {(type === "call" || type === "meeting") && (
              <div style={{ flex: "1 1 120px" }}>
                <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Duration</label>
                <select value={duration} onChange={e => setDuration(Number(e.target.value))}
                  style={{ ...S.select, width: "100%" }}>
                  {[15, 30, 45, 60].map(d => <option key={d} value={d}>{d} min</option>)}
                </select>
              </div>
            )}
            {type === "call" && (
              <div style={{ flex: "1 1 140px" }}>
                <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Outcome</label>
                <select value={outcome} onChange={e => setOutcome(e.target.value)}
                  style={{ ...S.select, width: "100%" }}>
                  {["Not yet", "Connected", "No Answer", "Left VM"].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
            )}
            {type === "meeting" && (
              <div style={{ flex: "1 1 100%" }}>
                <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Attendees</label>
                <input value={attendees} onChange={e => setAttendees(e.target.value)}
                  placeholder="email1@x.com, email2@x.com"
                  style={{ ...S.input, width: "100%" }} />
              </div>
            )}
            <div style={{ flex: "1 1 100%" }}>
              <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>
                {type === "note" ? "Additional context" : "Notes"}
              </label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)}
                style={{ ...S.input, width: "100%", minHeight: 60, resize: "vertical", fontFamily: "inherit", fontSize: 12 }} />
            </div>
          </div>

          {type === "meeting" && meetUrl && (
            <div style={{ marginTop: 6 }}>
              <a href={meetUrl} target="_blank" rel="noreferrer" style={{ color: COLORS.orange, fontWeight: 600, fontSize: 12 }}>
                <Video size={11} style={{ verticalAlign: "middle", marginRight: 4 }} /> Join Google Meet
              </a>
            </div>
          )}

          <div style={{ fontSize: 10, color: COLORS.grayMid, marginTop: 6 }}>
            Assigned: {t.assigned_to || "Drew"}
            {savedFlash && <span style={{ marginLeft: 8, color: COLORS.success, fontWeight: 600 }}><Check size={11} style={{ verticalAlign: "middle" }} /> Saved</span>}
          </div>

          <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
            {dirty && !isDone && (
              <TinyBtn onClick={saveChanges} color={COLORS.orange}>
                {saving ? "Saving…" : "Save changes"}
              </TinyBtn>
            )}
            {!isDone && (
              <TinyBtn onClick={completeTask} color={COLORS.success}>
                <Check size={11} /> Mark Complete
              </TinyBtn>
            )}
            {!isDone && type !== "meeting" && (
              <TinyBtn onClick={snooze} title="Snooze 1 day">
                <Clock size={11} /> Snooze 1d
              </TinyBtn>
            )}
            <TinyBtn onClick={copyDesc}><Copy size={11} /> {copied ? "Copied!" : "Copy"}</TinyBtn>

            {!isDone && type === "meeting" && (
              <TinyBtn
                onClick={() => { setRescheduleOpen(o => !o); setRescheduleDate(t.due_date || ""); }}
                title={t.gcal_event_id ? "Reschedule — updates Google Calendar" : "Reschedule"}
              >
                <CalendarDays size={11} /> Reschedule
              </TinyBtn>
            )}

            {!isDone && type === "meeting" && t.gcal_event_id && (
              !confirmCancel ? (
                <TinyBtn onClick={() => setConfirmCancel(true)} color={COLORS.danger}>
                  <X size={11} /> Cancel Meeting
                </TinyBtn>
              ) : (
                <span style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 11 }}>
                  <span style={{ color: COLORS.danger, fontWeight: 600 }}>
                    Cancel meeting + notify attendee?
                  </span>
                  <TinyBtn onClick={cancelMeeting} color={COLORS.danger}>
                    {cancelling ? "Cancelling…" : "Yes, cancel"}
                  </TinyBtn>
                  <TinyBtn onClick={() => setConfirmCancel(false)}>No</TinyBtn>
                </span>
              )
            )}

            {!isDone && type === "meeting" && !t.gcal_event_id && (
              !confirmCancelNoGcal ? (
                <TinyBtn onClick={() => setConfirmCancelNoGcal(true)} color={COLORS.danger}>
                  <X size={11} /> Cancel Meeting
                </TinyBtn>
              ) : (
                <span style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 11, flexWrap: "wrap" }}>
                  <span style={{ color: COLORS.danger, fontWeight: 600, maxWidth: 260 }}>
                    This meeting has no Google Calendar event linked. Delete the follow up anyway?
                  </span>
                  <TinyBtn onClick={cancelFollowUpNoGcal} color={COLORS.danger}>Yes, delete</TinyBtn>
                  <TinyBtn onClick={() => setConfirmCancelNoGcal(false)}>No</TinyBtn>
                </span>
              )
            )}

            {!isDone && type !== "meeting" && (
              !confirmDelete ? (
                <TinyBtn onClick={() => setConfirmDelete(true)} color={COLORS.danger}>
                  <Trash2 size={11} /> Delete
                </TinyBtn>
              ) : (
                <span style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 11 }}>
                  <span style={{ color: COLORS.danger, fontWeight: 600 }}>Delete this follow up?</span>
                  <TinyBtn onClick={deleteTask} color={COLORS.danger}>Yes</TinyBtn>
                  <TinyBtn onClick={() => setConfirmDelete(false)}>No</TinyBtn>
                </span>
              )
            )}

            {isDone && (
              !confirmDelete ? (
                <TinyBtn onClick={() => setConfirmDelete(true)} color={COLORS.danger}>
                  <Trash2 size={11} /> Delete
                </TinyBtn>
              ) : (
                <span style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 11 }}>
                  <span style={{ color: COLORS.danger, fontWeight: 600 }}>Delete this entry?</span>
                  <TinyBtn onClick={deleteTask} color={COLORS.danger}>Yes</TinyBtn>
                  <TinyBtn onClick={() => setConfirmDelete(false)}>No</TinyBtn>
                </span>
              )
            )}
          </div>

          {/* Inline reschedule picker (meetings only) */}
          {rescheduleOpen && type === "meeting" && (
            <div style={{
              marginTop: 10, padding: 10, background: COLORS.white,
              border: `1px solid ${COLORS.orange}55`, borderRadius: 6,
              display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap",
            }}>
              <div style={{ flex: "1 1 200px" }}>
                <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>
                  New date / time
                </label>
                <input
                  type="datetime-local"
                  value={rescheduleDate.slice(0, 16)}
                  onChange={e => setRescheduleDate(e.target.value)}
                  style={{ ...S.input, width: "100%" }}
                />
              </div>
              <TinyBtn
                onClick={rescheduleMeeting} color={COLORS.orange}
                title={t.gcal_event_id ? "Deletes old GCal event + creates new one + notifies attendee" : "Updates task date only"}
              >
                {rescheduling ? "Rescheduling…" : (t.gcal_event_id ? "Confirm + notify attendee" : "Update date")}
              </TinyBtn>
              <TinyBtn onClick={() => { setRescheduleOpen(false); setRescheduleDate(""); }}>
                Cancel
              </TinyBtn>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Expandable timeline: one row open at a time, inline edit/delete/reply ────
function ExpandableTimeline({ activities, onUpdate, onRemove }) {
  const [expandedId, setExpandedId] = useState(null);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {activities.map((a, i) => {
        const key = a.id || `a${i}`;
        const isOpen = a.id != null && expandedId === a.id;
        return (
          <ExpandableActivityRow
            key={key}
            activity={a}
            expanded={isOpen}
            onToggle={() => {
              if (a.id == null) return;
              setExpandedId(prev => prev === a.id ? null : a.id);
            }}
            onUpdate={onUpdate}
            onRemove={onRemove}
          />
        );
      })}
    </div>
  );
}

function _copyText(txt) {
  if (!txt) return;
  try { navigator.clipboard?.writeText(txt); } catch (_) {}
}

function _extractTo(activity) {
  const d = activity.description || "";
  const m = /^To:\s*([^\s,]+)/i.exec(d);
  return m ? m[1] : (activity.to || "");
}

function ExpandableActivityRow({ activity: a, expanded, onToggle, onUpdate, onRemove }) {
  const typeRaw = a.type || "Note";
  const typeKey = typeRaw.toLowerCase();
  const icon = ATYPE_ICON[typeRaw] || ATYPE_ICON[typeKey] || <PenLine size={14} />;
  const borderColor = ATYPE_BORDER[typeRaw] || ATYPE_BORDER[typeKey] || COLORS.grayMid;
  const ts = a.created_at || a.timestamp || "";
  const subject = a.subject || "";
  const body = a.note || a.body || a.content || a.description || "";
  const firstLine = (subject || body || "").split("\n")[0];
  const collapsedPreview = firstLine.length > 60 ? firstLine.slice(0, 60) + "…" : firstLine;

  // local UI state
  const [copied, setCopied] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  // note edit state
  const [editText, setEditText] = useState(a.note || a.description || "");
  useEffect(() => { setEditText(a.note || a.description || ""); }, [a.id, a.note, a.description, expanded]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [saving, setSaving] = useState(false);

  const dirty = typeKey === "note" && editText !== (a.note || a.description || "");

  function copyAll() {
    const lines = [];
    if (subject) lines.push(`Subject: ${subject}`);
    if (body) lines.push(body);
    _copyText(lines.join("\n\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  async function saveNote() {
    if (!dirty) return;
    setSaving(true);
    const r = await api(`/activities/${a.id}`, "PATCH", { note: editText, description: editText });
    setSaving(false);
    if (r) {
      onUpdate && onUpdate(a.id, { note: editText, description: editText, edited_at: r.edited_at });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    }
  }

  async function deleteActivity() {
    await api(`/activities/${a.id}`, "DELETE");
    onRemove && onRemove(a.id);
  }

  async function pinNote() {
    await api(`/activities/${a.id}/pin`, "POST");
    onUpdate && onUpdate(a.id, { pinned: 1 });
  }

  async function markReplied() {
    const r = await api(`/activities/${a.id}`, "PATCH", { outcome: "Replied" });
    if (r) onUpdate && onUpdate(a.id, { outcome: "Replied" });
  }

  const meetUrl = (() => {
    const src = `${subject}\n${body}`;
    const m = /https:\/\/meet\.google\.com\/[a-z0-9-]+/i.exec(src);
    return m ? m[0] : null;
  })();

  const rowBg = expanded ? "#F9FAFB" : COLORS.white;
  const leftBorderColor = expanded ? "#3B82F6" : borderColor;

  return (
    <div style={{
      background: rowBg, borderRadius: 6, fontSize: 12,
      border: `1px solid ${COLORS.cardBorder}`,
      borderLeft: `3px solid ${leftBorderColor}`,
      transition: "background 0.18s ease, border-color 0.18s ease",
      overflow: "hidden",
    }}>
      {/* Collapsed header — clickable */}
      <div
        onClick={onToggle}
        style={{
          display: "flex", gap: 8, alignItems: "center",
          padding: "10px 14px", cursor: a.id != null ? "pointer" : "default",
          userSelect: "none",
        }}
      >
        <span style={{ fontSize: 16, display: "flex", alignItems: "center" }}>{icon}</span>
        <span style={{ fontWeight: 700, color: COLORS.textPrimary, textTransform: "capitalize" }}>
          {typeRaw}
        </span>
        {a.contact_name && (
          <span style={{
            fontSize: 10, fontWeight: 600, color: COLORS.orange,
            background: COLORS.orange + "1A", border: `1px solid ${COLORS.orange}55`,
            padding: "1px 6px", borderRadius: 4,
          }} title={a.contact_title || ""}>{a.contact_name}</span>
        )}
        {a.outcome && <span style={S.badge(COLORS.success)}>{a.outcome}</span>}
        {a.duration ? <span style={{ fontSize: 10, color: COLORS.grayMid }}>{a.duration} min</span> : null}
        {a.pinned ? <Pin size={11} style={{ color: COLORS.orange }} /> : null}
        {a.edited_at ? <span style={{ fontSize: 9, color: COLORS.grayMid, fontStyle: "italic" }}>edited</span> : null}
        {!expanded && collapsedPreview && (
          <span style={{
            color: COLORS.grayMid, marginLeft: 6, overflow: "hidden",
            textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0,
          }}>{collapsedPreview}</span>
        )}
        <span style={{
          marginLeft: "auto", fontSize: 10, color: COLORS.grayMid, flexShrink: 0,
          display: "flex", alignItems: "center", gap: 6,
        }} title={ts ? new Date(ts).toLocaleString() : ""}>
          {a.owner && <span>{a.owner}</span>}
          <span>{timeAgo(ts)}</span>
          {a.id != null && (expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />)}
        </span>
      </div>

      {/* Expanded body — max-height transition for smooth reveal */}
      <div style={{
        maxHeight: expanded ? 2000 : 0,
        overflow: "hidden",
        transition: "max-height 0.28s ease",
      }}>
        <div style={{ padding: "0 14px 12px 14px", borderTop: `1px solid ${COLORS.cardBorder}` }}>
          <div style={{ fontSize: 10, color: COLORS.grayMid, margin: "8px 0" }}>
            {ts ? new Date(ts).toLocaleString() : ""}
          </div>

          {/* Per-type expanded content */}
          {typeKey === "email" && (
            <EmailExpanded
              subject={subject} body={body} to={_extractTo(a)}
              onReply={() => setReplyOpen(true)}
              onCopy={copyAll} copied={copied}
            />
          )}

          {typeKey === "note" && (
            <NoteExpanded
              text={editText} onChange={setEditText}
              dirty={dirty} saving={saving} savedFlash={savedFlash}
              onSave={saveNote} onDelete={() => setConfirmDelete(true)}
              onCopy={() => { _copyText(editText); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
              onPin={pinNote} copied={copied} pinned={!!a.pinned}
              confirmDelete={confirmDelete}
              onConfirmDelete={deleteActivity}
              onCancelDelete={() => setConfirmDelete(false)}
            />
          )}

          {typeKey === "call" && (
            <CallExpanded activity={a} />
          )}

          {typeKey === "meeting" && (
            <MeetingExpanded activity={a} meetUrl={meetUrl} subject={subject} body={body} />
          )}

          {(typeKey === "linkedin" || typeKey === "linkedin_outreach" || typeRaw === "LinkedIn DM") && (
            <LinkedInExpanded
              message={subject || body}
              onCopy={() => { _copyText(subject || body); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
              onMarkReplied={markReplied}
              copied={copied}
              alreadyReplied={a.outcome === "Replied"}
            />
          )}

          {!["email", "note", "call", "meeting", "linkedin", "linkedin_outreach"].includes(typeKey)
            && typeRaw !== "LinkedIn DM" && (
              <div style={{ whiteSpace: "pre-wrap", color: COLORS.textSecondary }}>
                {subject && <div style={{ fontWeight: 600, marginBottom: 4 }}>{subject}</div>}
                {body}
              </div>
          )}
        </div>
      </div>

      {/* Reply modal (email) */}
      {replyOpen && (
        <EmailModal
          lead={{
            id: a.lead_id, org_id: a.org_id, contact: { id: a.contact_id },
            email_work: _extractTo(a),
            org_name: "", contact_name: "",
          }}
          initialSubject={subject ? (subject.toLowerCase().startsWith("re:") ? subject : `Re: ${subject}`) : ""}
          initialBody={`\n\n—— Original message ——\n${body}`}
          onClose={() => setReplyOpen(false)}
        />
      )}
    </div>
  );
}

function TinyBtn({ onClick, color, children, title }) {
  return (
    <button onClick={onClick} title={title} style={{
      ...S.btn("ghost"), fontSize: 11, padding: "4px 10px",
      color: color || COLORS.textSecondary,
      borderColor: color ? `${color}55` : COLORS.cardBorder,
      display: "inline-flex", alignItems: "center", gap: 4,
    }}>{children}</button>
  );
}

function EmailExpanded({ subject, body, to, onReply, onCopy, copied }) {
  return (
    <div>
      {to && <div style={{ fontSize: 11, marginBottom: 4 }}><strong style={{ color: COLORS.grayMid }}>To:</strong> {to}</div>}
      {subject && <div style={{ fontSize: 11, marginBottom: 8 }}><strong style={{ color: COLORS.grayMid }}>Subject:</strong> {subject}</div>}
      <div style={{
        fontSize: 12, color: COLORS.textSecondary, whiteSpace: "pre-wrap",
        background: COLORS.white, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 4,
        padding: 8, maxHeight: 260, overflowY: "auto",
      }}>{body || <span style={{ color: COLORS.grayMid, fontStyle: "italic" }}>(no body)</span>}</div>
      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
        <TinyBtn onClick={onReply} color={COLORS.orange}><Send size={11} /> Reply</TinyBtn>
        <TinyBtn onClick={onCopy}><Copy size={11} /> {copied ? "Copied!" : "Copy"}</TinyBtn>
      </div>
    </div>
  );
}

function NoteExpanded({ text, onChange, dirty, saving, savedFlash, onSave, onDelete, onCopy, onPin, copied, pinned, confirmDelete, onConfirmDelete, onCancelDelete }) {
  return (
    <div>
      <textarea
        value={text}
        onChange={e => onChange(e.target.value)}
        style={{ ...S.input, width: "100%", minHeight: 90, resize: "vertical", fontFamily: "inherit", fontSize: 12 }}
      />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4, fontSize: 10, color: COLORS.grayMid }}>
        <span>{text.length} chars</span>
        {savedFlash && <span style={{ color: COLORS.success, fontWeight: 600 }}><Check size={11} style={{ verticalAlign: "middle" }} /> Saved</span>}
      </div>
      <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
        {dirty && (
          <TinyBtn onClick={onSave} color={COLORS.orange} title="Save changes">
            {saving ? "Saving…" : "Save changes"}
          </TinyBtn>
        )}
        <TinyBtn onClick={onCopy}><Copy size={11} /> {copied ? "Copied!" : "Copy"}</TinyBtn>
        <TinyBtn onClick={onPin} title={pinned ? "Already pinned" : "Pin to focus items"}>
          <Pin size={11} /> {pinned ? "Pinned" : "Pin to top"}
        </TinyBtn>
        {!confirmDelete ? (
          <TinyBtn onClick={onDelete} color={COLORS.danger}><Trash2 size={11} /> Delete</TinyBtn>
        ) : (
          <span style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 11 }}>
            <span style={{ color: COLORS.danger, fontWeight: 600 }}>Delete this note?</span>
            <TinyBtn onClick={onConfirmDelete} color={COLORS.danger}>Yes</TinyBtn>
            <TinyBtn onClick={onCancelDelete}>No</TinyBtn>
          </span>
        )}
      </div>
    </div>
  );
}

function CallExpanded({ activity: a }) {
  const notes = a.note || a.description || "";
  return (
    <div style={{ fontSize: 12 }}>
      {a.outcome && <div style={{ marginBottom: 4 }}><strong style={{ color: COLORS.grayMid }}>Outcome:</strong> {a.outcome}</div>}
      {a.duration != null && <div style={{ marginBottom: 4 }}><strong style={{ color: COLORS.grayMid }}>Duration:</strong> {a.duration} min</div>}
      {notes && (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase", marginBottom: 2 }}>Notes</div>
          <div style={{ whiteSpace: "pre-wrap", color: COLORS.textSecondary }}>{notes}</div>
        </div>
      )}
      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
        <TinyBtn onClick={async () => {
          const date = prompt("Follow-up date (YYYY-MM-DD):");
          if (!date) return;
          const desc = prompt("Follow-up task:", "Follow up on call") || "Follow up on call";
          await api("/tasks", "POST", {
            description: desc, due_date: date, priority: "Medium",
            assigned_to: "drew@outpostprep.com",
            record_type: a.lead_id ? "lead" : a.org_id ? "org" : a.contact_id ? "contact" : null,
            record_id: a.lead_id || a.org_id || a.contact_id || null,
          });
        }} color={COLORS.orange}><CalendarPlus size={11} /> Log follow up</TinyBtn>
      </div>
    </div>
  );
}

function MeetingExpanded({ activity: a, meetUrl, subject, body }) {
  const gcalId = a.gcal_event_id;
  const gcalUrl = gcalId
    ? `https://calendar.google.com/calendar/u/0/r/eventedit/${gcalId}`
    : "https://calendar.google.com/calendar/u/0/r";
  return (
    <div style={{ fontSize: 12 }}>
      {subject && <div style={{ fontWeight: 600, marginBottom: 4 }}>{subject}</div>}
      {meetUrl && (
        <div style={{ marginBottom: 6 }}>
          <a href={meetUrl} target="_blank" rel="noreferrer" style={{ color: COLORS.orange, fontWeight: 600 }}>
            <Video size={11} style={{ verticalAlign: "middle", marginRight: 4 }} /> Join Google Meet
          </a>
        </div>
      )}
      {a.attendees && <div style={{ marginBottom: 4 }}><strong style={{ color: COLORS.grayMid }}>Attendees:</strong> {a.attendees}</div>}
      {body && (
        <div style={{ whiteSpace: "pre-wrap", color: COLORS.textSecondary, marginTop: 6 }}>
          {body}
        </div>
      )}
      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
        <TinyBtn onClick={() => window.open(gcalUrl, "_blank")}><CalendarDays size={11} /> Open in Google Calendar</TinyBtn>
      </div>
    </div>
  );
}

function LinkedInExpanded({ message, onCopy, onMarkReplied, copied, alreadyReplied }) {
  return (
    <div style={{ fontSize: 12 }}>
      <div style={{
        whiteSpace: "pre-wrap", color: COLORS.textSecondary,
        background: COLORS.white, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 4,
        padding: 8, marginBottom: 8,
      }}>{message || <span style={{ color: COLORS.grayMid, fontStyle: "italic" }}>(no message)</span>}</div>
      <div style={{ display: "flex", gap: 6 }}>
        <TinyBtn onClick={onCopy}><Copy size={11} /> {copied ? "Copied!" : "Copy"}</TinyBtn>
        {!alreadyReplied && <TinyBtn onClick={onMarkReplied} color={COLORS.success}><Check size={11} /> Mark as replied</TinyBtn>}
        {alreadyReplied && <span style={{ fontSize: 11, color: COLORS.success, fontWeight: 600 }}>✓ Replied</span>}
      </div>
    </div>
  );
}

// ── Files tab — metadata-only upload linked to the record ────────────────────
function FilesTab({ recordType, recordId }) {
  const [files, setFiles] = useState([]);
  const [filename, setFilename] = useState("");
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const endpointBase =
    recordType === "deal" ? `/deals/${recordId}/files` :
    recordType === "lead" ? `/leads/${recordId}/files` :
    null;

  useEffect(() => {
    if (!endpointBase) return;
    api(endpointBase).then(r => setFiles(Array.isArray(r) ? r : []));
  }, [endpointBase]);

  async function save() {
    if (!endpointBase || !filename.trim() || !url.trim()) return;
    setSaving(true);
    const r = await api(endpointBase, "POST", {
      filename: filename.trim(), url: url.trim(), size: 0,
    });
    if (r) setFiles(prev => [r, ...prev]);
    setFilename(""); setUrl("");
    setSaving(false);
  }

  async function remove(id) {
    const route = recordType === "deal" ? `/deal-files/${id}` : `/lead-files/${id}`;
    await api(route, "DELETE");
    setFiles(prev => prev.filter(f => f.id !== id));
  }

  if (!endpointBase) {
    return (
      <div style={{ fontSize: 12, color: COLORS.grayMid, padding: "10px 4px" }}>
        File attachments are supported on deal and lead records.
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 6 }}>
        <input value={filename} onChange={e => setFilename(e.target.value)}
          placeholder="File name" style={{ ...S.input, fontSize: 12 }} />
        <input value={url} onChange={e => setUrl(e.target.value)}
          placeholder="https://drive.google.com/..." style={{ ...S.input, fontSize: 12 }} />
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <button onClick={save} disabled={saving || !filename.trim() || !url.trim()}
          style={{ ...S.btn("primary"), fontSize: 11, opacity: (!filename.trim() || !url.trim() || saving) ? 0.5 : 1 }}>
          {saving ? "Saving…" : "Link File"}
        </button>
      </div>
      {files.length === 0 ? (
        <div style={{ fontSize: 12, color: COLORS.grayMid, padding: "6px 0" }}>No files yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {files.map(f => (
            <div key={f.id} style={{
              background: COLORS.white, border: `1px solid ${COLORS.cardBorder}`,
              borderRadius: 5, padding: "6px 10px", fontSize: 12,
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <FileText size={13} />
              <a href={f.url} target="_blank" rel="noreferrer" style={{
                color: COLORS.orange, textDecoration: "none", flex: 1, minWidth: 0,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>{f.filename}</a>
              <span style={{ fontSize: 10, color: COLORS.grayMid }}>
                {f.uploaded_at ? timeAgo(f.uploaded_at) : ""}
              </span>
              <button onClick={() => remove(f.id)} style={{
                ...S.btn("ghost"), padding: "2px 6px", color: COLORS.danger, borderColor: "transparent",
              }}><Trash2 size={11} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Import Gmail history tab ─────────────────────────────────────────────────
function GmailImportTab({ recordData, contactId, onImported }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const email = (recordData?.email_work || recordData?.email_home
                 || recordData?.email || "").trim();

  async function runImport() {
    if (!email) return;
    setRunning(true); setErr(null); setResult(null);
    try {
      const r = await api("/gmail/import-contact-history", "POST", {
        email, contact_id: contactId,
      });
      setResult(r);
      if (r && r.ok && onImported) onImported();
    } catch (e) {
      setErr(String(e).slice(0, 240));
    }
    setRunning(false);
  }

  return (
    <div style={{ padding: "8px 4px" }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 6 }}>
        📧 Import Gmail History
      </div>
      <div style={{ fontSize: 12, color: COLORS.grayMid, marginBottom: 10, lineHeight: 1.5 }}>
        Scans Gmail for every message <strong>to or from</strong>{" "}
        <code style={{ background: COLORS.gray, padding: "1px 5px", borderRadius: 3 }}>{email || "(no email)"}</code>{" "}
        and mirrors them into this timeline. Safe to re-run — already-imported
        messages are skipped.
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={runImport} disabled={!email || running}
          style={{ ...S.btn("primary"), fontSize: 12,
                   opacity: (!email || running) ? 0.5 : 1 }}>
          {running ? "Importing…" : "Import Gmail History"}
        </button>
      </div>
      {result && result.ok && (
        <div style={{
          marginTop: 10, padding: 10, borderRadius: 6,
          background: `${COLORS.success}22`, color: COLORS.success, fontSize: 12,
        }}>
          ✓ Scanned {result.scanned} messages, imported {result.imported} new
          {result.capped ? " (capped at 500 — re-run to continue)" : ""}.
        </div>
      )}
      {err && (
        <div style={{
          marginTop: 10, padding: 10, borderRadius: 6,
          background: `${COLORS.danger}22`, color: COLORS.danger, fontSize: 12,
        }}>{err}</div>
      )}
    </div>
  );
}


// ── AddActivityBar ───────────────────────────────────────────────────────────
const ADD_TABS_FULL = ["Note", "Call", "Email", "Meeting", "Task", "LinkedIn", "Import Gmail", "Files", "Documents", "Invoice", "AI Strategy"];

function AddActivityBar({ recordType, recordId, recordData, onActivityAdded }) {
  const [tab, setTab] = useState("Note");
  const [saving, setSaving] = useState(false);
  // Meeting scheduled confirmation banner + bottom-right toast
  const [meetingScheduled, setMeetingScheduled] = useState(null);
  const [meetingToast, setMeetingToast] = useState(null);
  useEffect(() => {
    if (!meetingScheduled) return;
    const t = setTimeout(() => setMeetingScheduled(null), 10000);
    return () => clearTimeout(t);
  }, [meetingScheduled]);
  useEffect(() => {
    if (!meetingToast) return;
    const t = setTimeout(() => setMeetingToast(null), 5000);
    return () => clearTimeout(t);
  }, [meetingToast]);

  // Note state
  const [noteText, setNoteText] = useState("");
  // Call state
  const [callDuration, setCallDuration] = useState("");
  const [callOutcome, setCallOutcome] = useState("Connected");
  const [callNotes, setCallNotes] = useState("");
  // Email state
  const [emailTo, setEmailTo] = useState(recordData?.email_work || recordData?.email || "");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  // Meeting state
  const [meetDate, setMeetDate] = useState("");
  const [meetNotes, setMeetNotes] = useState("");
  // Task state
  const [taskDesc, setTaskDesc] = useState("");
  const [taskDue, setTaskDue] = useState("");
  const [taskPriority, setTaskPriority] = useState("Medium");
  // LinkedIn state
  const [liMsg, setLiMsg] = useState(getLiDm(recordData?.vertical, recordData?.contact_name?.split(/\s+/)?.[0]));
  const [liCopied, setLiCopied] = useState(false);
  // AI Strategy
  const [strategy, setStrategy] = useState("");
  const [stratLoading, setStratLoading] = useState(false);
  // Follow-up toggle (shared)
  const [followUp, setFollowUp] = useState(false);
  const [fuDate, setFuDate] = useState("");
  const [fuNote, setFuNote] = useState("");

  const linkFields = {};
  if (recordType === "org") linkFields.org_id = recordId;
  if (recordType === "contact") linkFields.contact_id = recordId;
  if (recordType === "lead") linkFields.lead_id = recordId;
  if (recordType === "deal") linkFields.deal_id = recordId;
  // Also link related records from recordData
  if (recordData?.org_id) linkFields.org_id = recordData.org_id;
  if (recordData?.contact_id) linkFields.contact_id = recordData.contact_id;

  async function createFollowUp() {
    if (!followUp || !fuDate) return;
    const t = tab.toLowerCase();
    const followUpType =
      t === "call" ? "call" :
      t === "meeting" ? "meeting" :
      t === "note" ? "note" : "task";
    await api("/tasks", "POST", {
      description: fuNote || `Follow up on ${tab.toLowerCase()}`,
      due_date: fuDate, priority: "Medium",
      assigned_to: recordData?.owner || "Drew",
      record_type: recordType, record_id: recordId,
      type: followUpType,
    });
  }

  async function saveNote() {
    setSaving(true);
    await api("/activities", "POST", { type: "note", description: noteText, ...linkFields });
    await createFollowUp();
    setNoteText("");
    setFollowUp(false); setFuDate(""); setFuNote("");
    setSaving(false);
    onActivityAdded && onActivityAdded();
  }

  async function saveCall() {
    setSaving(true);
    await api("/activities", "POST", {
      type: "call", duration: parseInt(callDuration) || 0,
      outcome: callOutcome, description: callNotes, ...linkFields,
    });
    await createFollowUp();
    setCallDuration(""); setCallNotes("");
    setFollowUp(false); setFuDate(""); setFuNote("");
    setSaving(false);
    onActivityAdded && onActivityAdded();
  }

  async function sendEmail() {
    setSaving(true);
    await api("/send-email", "POST", {
      to: emailTo, subject: emailSubject, body: emailBody,
      lead_id: linkFields.lead_id, org_id: linkFields.org_id, contact_id: linkFields.contact_id,
    });
    // Activity is logged by the backend /send-email endpoint — no duplicate here.
    await createFollowUp();
    setEmailSubject(""); setEmailBody("");
    setFollowUp(false); setFuDate(""); setFuNote("");
    setSaving(false);
    onActivityAdded && onActivityAdded();
  }

  async function saveMeeting() {
    setSaving(true);
    if (meetDate) {
      const startIso = meetDate.length === 16 ? `${meetDate}:00` : meetDate;
      const endIso = (() => {
        const d = new Date(startIso);
        if (Number.isNaN(d.getTime())) return startIso;
        d.setMinutes(d.getMinutes() + 30);
        return d.toISOString().slice(0, 19);
      })();
      const attendeeEmail = recordData?.email_work || recordData?.email || null;
      const attendeeName = recordData?.contact_name || recordData?.full_name || null;
      const title = `Meeting — ${recordData?.org_name || recordData?.name || attendeeName || "Outpost"}`;

      let gcalId = null, meetUrl = null;
      try {
        // Backend sends the GCal invite (sendUpdates="all") AND a custom
        // confirmation email via /send-email when send_confirmation=true.
        const ev = await api("/calendar/events", "POST", {
          title, start: startIso, end: endIso,
          attendee_email: attendeeEmail, attendee_name: attendeeName,
          description: meetNotes || "",
          meet: true, send_confirmation: true, duration_min: 30,
          lead_id: linkFields.lead_id || null,
          org_id: linkFields.org_id || null,
          contact_id: linkFields.contact_id || null,
        });
        if (ev && ev.id) { gcalId = ev.id; meetUrl = ev.meet_url || null; }
      } catch (_) { /* continue — task still gets created */ }

      await api("/tasks", "POST", {
        description: meetNotes || title, due_date: meetDate,
        priority: "High", assigned_to: recordData?.owner || "Drew",
        record_type: recordType, record_id: recordId,
        type: "meeting",
        notes: meetUrl ? `${meetNotes || ""}\nMeet: ${meetUrl}`.trim() : meetNotes,
        duration: 30,
        attendees: attendeeEmail || null,
        gcal_event_id: gcalId,
      });

      // Fire the UI confirmation: in-card banner + bottom-right toast.
      setMeetingScheduled({
        startIso, duration: 30, meetUrl, gcalId,
        attendeeName, attendeeEmail, title,
      });
      setMeetingToast({
        text: `Meeting scheduled for ${new Date(startIso).toLocaleString(undefined, {
          weekday: "short", month: "short", day: "numeric",
          hour: "numeric", minute: "2-digit",
        })}`,
      });
    } else {
      await api("/activities", "POST", {
        type: "meeting", description: meetNotes, subject: "Meeting (no date)",
        ...linkFields,
      });
    }
    await createFollowUp();
    setMeetDate(""); setMeetNotes("");
    setFollowUp(false); setFuDate(""); setFuNote("");
    setSaving(false);
    onActivityAdded && onActivityAdded();
  }

  async function saveTask() {
    setSaving(true);
    await api("/tasks", "POST", {
      description: taskDesc, due_date: taskDue, priority: taskPriority,
      assigned_to: recordData?.owner || "Drew",
      record_type: recordType, record_id: recordId,
    });
    setTaskDesc(""); setTaskDue("");
    setSaving(false);
    onActivityAdded && onActivityAdded();
  }

  async function markLinkedInSent() {
    setSaving(true);
    const contactName = recordData?.contact_name || recordData?.full_name || "";
    await api("/activities", "POST", {
      type: "linkedin_outreach",
      description: `LinkedIn connection request sent to ${contactName}`,
      subject: liMsg,
      ...linkFields,
    });
    if (linkFields.contact_id) {
      await api(`/contacts/${linkFields.contact_id}`, "PATCH", { linkedin_sent: 1 }).catch(() => {});
    }
    await createFollowUp();
    setFollowUp(false); setFuDate(""); setFuNote("");
    setSaving(false);
    onActivityAdded && onActivityAdded();
  }

  async function generateStrategy() {
    setStratLoading(true);
    setStrategy("");
    // Drew v2 — /drew/strategy returns {strategy, conflicts, blocked}; falls back to old endpoint if unavailable.
    let r = await api("/drew/strategy", "POST", { record_type: recordType, record_id: recordId });
    if (!r) r = await api("/ai/strategy", "POST", { record_type: recordType, record_id: recordId });
    let text = r?.strategy || r?.text || (typeof r === "string" ? r : "No strategy returned.");
    if (r?.conflicts && r.conflicts.length > 0) {
      text = `⚠ CONFLICTS\n${r.conflicts.map(c => "• " + c).join("\n")}\n\n${text}`;
    }
    setStrategy(text);
    setStratLoading(false);
  }

  const followUpToggle = (
    <div style={{ marginTop: 8, borderTop: `1px solid ${COLORS.cardBorder}`, paddingTop: 8 }}>
      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, cursor: "pointer", color: COLORS.textSecondary }}>
        <input type="checkbox" checked={followUp} onChange={e => setFollowUp(e.target.checked)} /> Add Follow Up
      </label>
      {followUp && (
        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
          <input type="date" value={fuDate} onChange={e => setFuDate(e.target.value)} style={{ ...S.input, width: 160 }} />
          <input value={fuNote} onChange={e => setFuNote(e.target.value)} placeholder="Follow-up note..." style={{ ...S.input, flex: 1 }} />
        </div>
      )}
    </div>
  );

  const tabContent = {
    Note: (
      <div>
        <textarea value={noteText} onChange={e => setNoteText(e.target.value)}
          placeholder="Write a note..." style={{ ...S.input, height: 70, resize: "vertical", fontFamily: "inherit" }} />
        {followUpToggle}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
          <button onClick={saveNote} disabled={saving || !noteText.trim()} style={{ ...S.btn("primary"), fontSize: 11, opacity: (!noteText.trim() || saving) ? 0.5 : 1 }}>
            {saving ? "Saving..." : "Save Note"}
          </button>
        </div>
      </div>
    ),
    Call: (
      <div>
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Duration (min)</label>
            <input type="number" value={callDuration} onChange={e => setCallDuration(e.target.value)} placeholder="0" style={S.input} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Outcome</label>
            <select value={callOutcome} onChange={e => setCallOutcome(e.target.value)} style={{ ...S.select, width: "100%" }}>
              {["Connected", "No Answer", "Left VM", "Wrong Number"].map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
        </div>
        <textarea value={callNotes} onChange={e => setCallNotes(e.target.value)}
          placeholder="Call notes..." style={{ ...S.input, height: 50, resize: "vertical", fontFamily: "inherit" }} />
        {followUpToggle}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
          <button onClick={saveCall} disabled={saving} style={{ ...S.btn("primary"), fontSize: 11, opacity: saving ? 0.5 : 1 }}>
            {saving ? "Saving..." : "Log Call"}
          </button>
        </div>
      </div>
    ),
    Email: (
      <div>
        <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>To</label>
        <input value={emailTo} onChange={e => setEmailTo(e.target.value)} style={{ ...S.input, marginBottom: 6 }} />
        <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Subject</label>
        <input value={emailSubject} onChange={e => setEmailSubject(e.target.value)} style={{ ...S.input, marginBottom: 6 }} />
        <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Body</label>
        <textarea value={emailBody} onChange={e => setEmailBody(e.target.value)}
          style={{ ...S.input, height: 80, resize: "vertical", fontFamily: "inherit" }} />
        {followUpToggle}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
          <button onClick={sendEmail} disabled={saving || !emailTo} style={{ ...S.btn("primary"), fontSize: 11, opacity: (!emailTo || saving) ? 0.5 : 1 }}>
            {saving ? "Sending..." : "Send Email"}
          </button>
        </div>
      </div>
    ),
    Meeting: (
      <div>
        <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Date / Time</label>
        <input type="datetime-local" value={meetDate} onChange={e => setMeetDate(e.target.value)} style={{ ...S.input, marginBottom: 6 }} />
        <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Notes</label>
        <textarea value={meetNotes} onChange={e => setMeetNotes(e.target.value)}
          placeholder="Meeting notes..." style={{ ...S.input, height: 50, resize: "vertical", fontFamily: "inherit" }} />
        {followUpToggle}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
          <button onClick={saveMeeting} disabled={saving || !meetDate} style={{ ...S.btn("primary"), fontSize: 11, opacity: (!meetDate || saving) ? 0.5 : 1 }}>
            {saving ? "Saving..." : "Schedule Meeting"}
          </button>
        </div>
      </div>
    ),
    Task: (
      <div>
        <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Description</label>
        <input value={taskDesc} onChange={e => setTaskDesc(e.target.value)} placeholder="Task description..." style={{ ...S.input, marginBottom: 6 }} />
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Due Date</label>
            <input type="date" value={taskDue} onChange={e => setTaskDue(e.target.value)} style={S.input} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Priority</label>
            <select value={taskPriority} onChange={e => setTaskPriority(e.target.value)} style={{ ...S.select, width: "100%" }}>
              {["High", "Medium", "Low"].map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
          <button onClick={saveTask} disabled={saving || !taskDesc.trim()} style={{ ...S.btn("primary"), fontSize: 11, opacity: (!taskDesc.trim() || saving) ? 0.5 : 1 }}>
            {saving ? "Saving..." : "Create Task"}
          </button>
        </div>
      </div>
    ),
    LinkedIn: (
      <div>
        <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase" }}>Message ({recordData?.vertical || "RCFE"} template)</label>
        <textarea value={liMsg} onChange={e => setLiMsg(e.target.value)}
          style={{ ...S.input, height: 70, resize: "vertical", fontFamily: "inherit", marginBottom: 8 }} />
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button onClick={() => { navigator.clipboard.writeText(liMsg); setLiCopied(true); setTimeout(() => setLiCopied(false), 2000); }}
            style={{ ...S.btn("ghost"), fontSize: 11 }}>
            {liCopied ? "Copied!" : "Copy to Clipboard"}
          </button>
          <a href={`https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent((recordData?.contact_name || recordData?.full_name || "").trim())}`}
            target="_blank" rel="noreferrer"
            onClick={() => { navigator.clipboard.writeText(liMsg); setLiCopied(true); setTimeout(() => setLiCopied(false), 2000); }}
            style={{ ...S.btn("ghost"), fontSize: 11, textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
            Open LinkedIn (copies msg)
          </a>
          <button onClick={markLinkedInSent} disabled={saving} style={{ ...S.btn("primary"), fontSize: 11 }}>
            {saving ? "Saving..." : "Mark as Sent"}
          </button>
        </div>
        {followUpToggle}
      </div>
    ),
    "Import Gmail": (
      <GmailImportTab
        recordData={recordData}
        contactId={recordType === "contact" ? recordId : (recordData?.contact_id || null)}
        onImported={onActivityAdded}
      />
    ),
    Files: (
      <FilesTab recordType={recordType} recordId={recordId} />
    ),
    Documents: (
      <div style={{ textAlign: "center", padding: "20px 8px" }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>📄</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.textPrimary }}>
          Document generation
        </div>
        <div style={{ fontSize: 11, color: COLORS.grayMid, marginTop: 4 }}>
          Proposal + quote templates land here in the next release.
        </div>
      </div>
    ),
    Invoice: (
      <div style={{ textAlign: "center", padding: "20px 8px" }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>🧾</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.textPrimary }}>
          QuickBooks invoice
        </div>
        <div style={{ fontSize: 11, color: COLORS.grayMid, marginTop: 4 }}>
          Once QuickBooks is linked, create + send invoices directly from here.
        </div>
        <button disabled style={{ ...S.btn("primary"), fontSize: 11, marginTop: 10, opacity: 0.5 }}>
          Connect QuickBooks (coming soon)
        </button>
      </div>
    ),
    "AI Strategy": (
      <div>
        {!strategy && !stratLoading && (
          <button onClick={generateStrategy} style={{ ...S.btn("primary"), fontSize: 12, width: "100%" }}>
            🤖 Generate Strategy
          </button>
        )}
        {stratLoading && <Spinner />}
        {strategy && (
          <div style={{
            background: "#F3F0FF", border: "1px solid #8B5CF644", borderRadius: 8,
            padding: 14, fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.6,
            color: COLORS.textPrimary, maxHeight: 300, overflowY: "auto",
          }}>
            {strategy}
            <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
              <button onClick={() => { navigator.clipboard.writeText(strategy); }} style={{ ...S.btn("ghost"), fontSize: 10 }}>
                Copy All
              </button>
              <button onClick={() => { setStrategy(""); }} style={{ ...S.btn("ghost"), fontSize: 10 }}>
                Clear
              </button>
            </div>
          </div>
        )}
        {followUpToggle}
      </div>
    ),
  };

  return (
    <div style={{ marginBottom: 20 }}>
      {meetingScheduled && (
        <MeetingScheduledBanner
          data={meetingScheduled}
          onDismiss={() => setMeetingScheduled(null)}
        />
      )}
      <div style={{ display: "flex", gap: 2, flexWrap: "wrap", marginBottom: 10 }}>
        {ADD_TABS_FULL
          .filter(t => t !== "Import Gmail" || !!(recordData?.email_work || recordData?.email_home || recordData?.email))
          .map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: "6px 12px", borderRadius: 5, border: `1px solid ${tab === t ? COLORS.orange : COLORS.cardBorder}`,
            background: tab === t ? `${COLORS.orange}18` : COLORS.white,
            color: tab === t ? COLORS.orange : COLORS.textSecondary,
            fontWeight: tab === t ? 700 : 500, fontSize: 11, cursor: "pointer",
            fontFamily: "inherit", letterSpacing: 0.3,
          }}>{t}</button>
        ))}
      </div>
      <div style={{ ...S.card, padding: 12 }}>
        {tabContent[tab]}
      </div>
      {meetingToast && <MeetingToast text={meetingToast.text} />}
    </div>
  );
}

function MeetingScheduledBanner({ data, onDismiss }) {
  const [copied, setCopied] = useState(false);
  const when = (() => {
    try {
      const d = new Date(data.startIso);
      return d.toLocaleString(undefined, {
        weekday: "long", month: "long", day: "numeric",
        hour: "numeric", minute: "2-digit",
      });
    } catch (_) { return data.startIso; }
  })();
  const gcalUrl = data.gcalId
    ? `https://calendar.google.com/calendar/u/0/r/eventedit/${data.gcalId}`
    : "https://calendar.google.com/calendar/u/0/r";
  return (
    <div style={{
      background: "#ECFDF5", border: "1px solid #10B981",
      borderLeft: "4px solid #10B981", borderRadius: 8,
      padding: "12px 14px", marginBottom: 10,
      display: "flex", gap: 12, alignItems: "flex-start",
      animation: "fadeIn 0.3s ease",
    }}>
      <Check size={20} style={{ color: "#059669", flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 800, fontSize: 13, color: "#065F46", marginBottom: 4 }}>
          Meeting Scheduled!
        </div>
        <div style={{ fontSize: 12, color: "#047857", marginBottom: 6 }}>
          {when} · {data.duration} min{data.attendeeName ? ` · with ${data.attendeeName}` : ""}
        </div>
        {data.meetUrl && (
          <div style={{ marginBottom: 6 }}>
            <a href={data.meetUrl} target="_blank" rel="noreferrer" style={{
              fontSize: 13, fontWeight: 700, color: "#059669",
              textDecoration: "none", wordBreak: "break-all",
            }}>
              <Video size={13} style={{ verticalAlign: "middle", marginRight: 4 }} />
              {data.meetUrl}
            </a>
          </div>
        )}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {data.meetUrl && (
            <button onClick={() => {
              try { navigator.clipboard?.writeText(data.meetUrl); } catch (_) {}
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }} style={{
              ...S.btn("ghost"), fontSize: 11, padding: "3px 10px",
              color: "#059669", borderColor: "#10B981",
            }}>
              <Copy size={11} style={{ marginRight: 4 }} /> {copied ? "Copied!" : "Copy Meet Link"}
            </button>
          )}
          <a href={gcalUrl} target="_blank" rel="noreferrer" style={{
            ...S.btn("ghost"), fontSize: 11, padding: "3px 10px",
            textDecoration: "none", display: "inline-flex", alignItems: "center",
            color: "#059669", borderColor: "#10B981",
          }}>
            <CalendarDays size={11} style={{ marginRight: 4 }} /> Open in Google Calendar
          </a>
        </div>
      </div>
      <button onClick={onDismiss} title="Dismiss" style={{
        ...S.btn("ghost"), padding: "2px 6px", color: "#065F46", borderColor: "transparent",
      }}><X size={14} /></button>
    </div>
  );
}

function MeetingToast({ text }) {
  return (
    <div style={{
      position: "fixed", bottom: 20, right: 20, zIndex: 9999,
      background: "#10B981", color: "#FFFFFF",
      padding: "12px 18px", borderRadius: 8,
      fontSize: 13, fontWeight: 600,
      boxShadow: "0 6px 20px rgba(16,185,129,0.3)",
      display: "flex", alignItems: "center", gap: 8,
      animation: "slideUp 0.3s ease",
    }}>
      <Check size={16} />
      {text}
    </div>
  );
}

// ── Detail field row for right panel ─────────────────────────────────────────
function DetailField({ label, children }) {
  if (children == null || children === "" || children === false) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 500, color: COLORS.textPrimary }}>{children}</div>
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.orange, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8, borderBottom: `1px solid ${COLORS.cardBorder}`, paddingBottom: 4 }}>{title}</div>
      {children}
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function ClickableRecord({ label, onClick }) {
  return (
    <button onClick={onClick} style={{
      ...S.btn("ghost"), fontSize: 12, padding: "3px 10px", color: COLORS.orange,
      borderColor: `${COLORS.orange}66`, cursor: "pointer", marginRight: 4, marginBottom: 4,
    }}>{label}</button>
  );
}

// eslint-disable-next-line no-unused-vars
function RecordActions({ email, phone, orgName, linkedinUrl, onEmail, leadId, orgId, contactId, contacts, vertical, contactName }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  async function logCall() {
    const text = prompt("Call notes:");
    if (!text) return;
    await api("/activities", "POST", { type: "Call", subject: "Phone call", note: text, lead_id: leadId, org_id: orgId, contact_id: contactId });
  }

  async function saveNote() {
    if (!note.trim()) return;
    setSaving(true);
    await api("/notes", "POST", { body: note, lead_id: leadId, org_id: orgId, contact_id: contactId });
    setNote("");
    setSaving(false);
  }

  const liContacts = contacts && contacts.length > 0 ? contacts :
    contactName ? [{ full_name: contactName, org_name: orgName }] : [];
  return (
    <>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        {email && <button onClick={() => setEmailOpen(true)} style={{ ...S.btn("primary"), fontSize: 11, padding: "6px 10px" }}>📧 Email</button>}
        <button onClick={logCall} style={{ ...S.btn("ghost"), fontSize: 11, padding: "6px 10px" }}>📞 Log Call</button>
        <LinkedInButton contacts={liContacts} orgName={orgName} vertical={vertical} orgId={orgId} contactId={contactId}
          style={{ ...S.btn("ghost"), fontSize: 11, padding: "6px 10px" }} />
        {phone && <a href={`tel:${phone}`} style={{ ...S.btn("ghost"), fontSize: 11, padding: "6px 10px", textDecoration: "none", display: "inline-flex" }}>📞 Call {phone}</a>}
      </div>
      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
        <input value={note} onChange={e => setNote(e.target.value)} placeholder="Quick note..." style={{ ...S.input, flex: 1 }} />
        <button onClick={saveNote} disabled={saving || !note.trim()} style={{ ...S.btn("primary"), fontSize: 11 }}>{saving ? "..." : "Save"}</button>
      </div>
      {emailOpen && email && (
        <EmailModal lead={{ email_work: email, org_name: orgName, contact_name: "", id: leadId }} onClose={() => setEmailOpen(false)} />
      )}
    </>
  );
}

// Helper: render a field row only if value is non-null/non-empty
function fieldRows(pairs) {
  return pairs.filter(([, v]) => v != null && v !== "" && v !== 0 && v !== "—");
}

// ─── EMAIL ENGAGEMENT ────────────────────────────────────────────────────────
function EmailEngagement({ email, refreshTrigger = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!email) return;
    setLoading(true);
    api(`/email-tracking/${encodeURIComponent(email)}`).then(r => {
      if (r) setData(r);
      setLoading(false);
    });
  }, [email, refreshTrigger]);

  if (!email) return null;
  if (loading) return <DetailSection title="📧 Email Engagement"><Spinner /></DetailSection>;
  if (!data) return null;

  const sent = data.sent || 0;
  const opens = data.opens || 0;
  const clicks = data.clicks || 0;
  const replied = data.replied || false;
  const bounced = data.bounced || false;
  const unsubscribed = data.unsubscribed || false;

  let statusLabel, statusIcon;
  if (unsubscribed) { statusIcon = "🚫"; statusLabel = "Unsubscribed"; }
  else if (bounced) { statusIcon = "❌"; statusLabel = "Bounced"; }
  else if (opens >= 3) { statusIcon = "🔥"; statusLabel = "Hot"; }
  else if (opens >= 1) { statusIcon = "👀"; statusLabel = "Warm"; }
  else if (sent > 0) { statusIcon = "📭"; statusLabel = "Cold"; }
  else { statusIcon = "—"; statusLabel = "No data"; }

  const statusColor = statusLabel === "Hot" ? COLORS.danger : statusLabel === "Warm" ? COLORS.warning : statusLabel === "Cold" ? COLORS.grayMid : statusLabel === "Bounced" ? COLORS.danger : statusLabel === "Unsubscribed" ? COLORS.grayDark : COLORS.grayMid;

  const row = (label, value) => (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "3px 0", borderBottom: `1px solid ${COLORS.cardBorder}` }}>
      <span style={{ color: COLORS.grayMid }}>{label}</span>
      <span style={{ fontWeight: 600, color: COLORS.textPrimary }}>{value}</span>
    </div>
  );

  return (
    <DetailSection title="📧 Email Engagement">
      <div style={{ background: COLORS.white, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 6, padding: 10 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: statusColor }}>{statusIcon} {statusLabel}</span>
        </div>
        {row("Sent", `${sent} emails`)}
        {row("Opened", `${opens} times${data.last_opened ? ` (${timeAgo(data.last_opened)})` : ""}`)}
        {row("Clicked", `${clicks} times${data.last_clicked ? ` (${timeAgo(data.last_clicked)})` : ""}`)}
        {row("Replied", replied ? "Yes" : "No")}
      </div>
    </DetailSection>
  );
}

// ─── NOTIFICATION BELL ──────────────────────────────────────────────────────
function NotificationBell() {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [loadingNotifs, setLoadingNotifs] = useState(false);
  const ref = useRef(null);

  const fetchCount = useCallback(() => {
    api("/notifications/unread-count").then(r => { if (r && typeof r.count === "number") setCount(r.count); });
  }, []);

  useEffect(() => {
    fetchCount();
    const iv = setInterval(fetchCount, 60000);
    return () => clearInterval(iv);
  }, [fetchCount]);

  useEffect(() => {
    if (!open) return;
    setLoadingNotifs(true);
    api("/notifications?limit=20&unread_only=true").then(r => {
      if (r && Array.isArray(r.notifications)) setNotifs(r.notifications);
      else if (Array.isArray(r)) setNotifs(r);
      setLoadingNotifs(false);
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Type → {icon, title} for the notification card. Fallback is the generic bell.
  const NTYPE_META = {
    email_open:   { icon: "\ud83d\udce7", title: "Contact opened your email" },
    email_opened: { icon: "\ud83d\udce7", title: "Contact opened your email" },
    email_reply:  { icon: "\u21a9\ufe0f", title: "Contact replied to your email" },
    replied:      { icon: "\u21a9\ufe0f", title: "Contact replied to your email" },
    email_click:  { icon: "\ud83d\udc46", title: "Contact clicked your booking link" },
    link_clicked: { icon: "\ud83d\udc46", title: "Contact clicked your booking link" },
    hot_lead:     { icon: "\ud83d\udd25", title: "Hot lead activity" },
    bounced:      { icon: "\u26a0\ufe0f", title: "Email bounced" },
    unsubscribed: { icon: "\ud83d\udeab", title: "Contact unsubscribed" },
    meeting_booked:{icon: "\ud83d\udcc5", title: "New booking" },
    booking:      { icon: "\ud83d\udcc5", title: "New booking" },
    fb_comment:   { icon: "\ud83d\udcac", title: "New Facebook comment" },
    fb_comment_manual: { icon: "\ud83d\udcac", title: "Logged Facebook comment" },
    instantly_reply:   { icon: "\u21a9\ufe0f", title: "Instantly campaign reply" },
  };

  async function markRead(id) {
    await api(`/notifications/read/${id}`, "POST");
    setNotifs(ns => ns.filter(n => n.id !== id));
    setCount(c => Math.max(0, c - 1));
  }

  async function clearAll() {
    await api("/notifications/read-all", "POST");
    setNotifs([]);
    setCount(0);
  }

  // Click handler: mark read + dispatch navigation event. App-level listener maps
  // entity_type to the correct page; sub-views open the specific record.
  async function view(n) {
    const { entity_type, entity_id, type, id } = n;
    setOpen(false);
    await api(`/notifications/read/${id}`, "POST");
    setNotifs(ns => ns.filter(x => x.id !== id));
    setCount(c => Math.max(0, c - 1));
    window.dispatchEvent(new CustomEvent("crm:navigate", {
      detail: { entity_type, entity_id, notification_type: type, notification: n },
    }));
  }

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setOpen(o => !o)} style={{ ...S.btn("ghost"), padding: "4px 8px", position: "relative" }}>
        <Bell size={18} />
        {count > 0 && (
          <span style={{
            position: "absolute", top: -2, right: -2, background: COLORS.danger, color: "#fff",
            fontSize: 9, fontWeight: 800, borderRadius: 99, minWidth: 16, height: 16,
            display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px",
          }}>{count > 99 ? "99+" : count}</span>
        )}
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "100%", right: 0, marginTop: 8, width: 340, maxHeight: 420,
          background: COLORS.white, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 10,
          boxShadow: "0 8px 32px #0002", zIndex: 999, display: "flex", flexDirection: "column",
          overflow: "hidden",
        }}>
          <div style={{ padding: "10px 14px", borderBottom: `1px solid ${COLORS.cardBorder}`, fontSize: 13, fontWeight: 700, color: COLORS.textPrimary, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            Notifications
            {notifs.length > 0 && <button onClick={clearAll} style={{ ...S.btn("ghost"), fontSize: 10, padding: "3px 8px", color: COLORS.grayMid }}>Clear All</button>}
          </div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            {loadingNotifs ? <div style={{ padding: 20, textAlign: "center" }}><Spinner /></div> :
              notifs.length === 0 ? <div style={{ padding: 20, textAlign: "center", fontSize: 12, color: COLORS.grayMid }}>No unread notifications</div> :
              notifs.map(n => {
                const meta = NTYPE_META[n.type] || { icon: "\ud83d\udd14", title: n.subject || "Notification" };
                const who = n.contact_name || (n.contact_email || "").split("@")[0];
                const title = who ? `${meta.title} — ${who}` : meta.title;
                const preview = (n.message || "").slice(0, 120);
                const canNavigate = !!(n.entity_type && n.entity_id) ||
                                    n.type === "fb_comment" || n.type === "fb_comment_manual" ||
                                    n.type === "booking" || !!n.contact_email;
                return (
                  <div key={n.id} style={{ padding: "10px 14px", borderBottom: `1px solid ${COLORS.cardBorder}`, display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <span style={{ fontSize: 16, lineHeight: 1, marginTop: 2, flexShrink: 0 }}>{meta.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textPrimary, lineHeight: 1.3 }}>{title}</div>
                        <div style={{ fontSize: 10, color: COLORS.grayMid, whiteSpace: "nowrap", marginLeft: 4 }}>{timeAgo(n.created_at)}</div>
                      </div>
                      {preview && (
                        <div style={{ fontSize: 11, color: COLORS.grayMid, marginTop: 2, lineHeight: 1.35, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                          {preview}
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                        {canNavigate && (
                          <button onClick={() => view(n)}
                            style={{ ...S.btn("primary"), fontSize: 10, padding: "2px 8px" }}>
                            View {"\u2192"}
                          </button>
                        )}
                        <button onClick={() => markRead(n.id)}
                          style={{ ...S.btn("ghost"), fontSize: 10, padding: "2px 8px", color: COLORS.grayMid }}>
                          Mark Read {"\u2713"}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            }
          </div>
        </div>
      )}
    </div>
  );
}

// ─── INBOX VIEW ─────────────────────────────────────────────────────────────
function InboxView() {
  // Folder is the primary axis now — left sidebar owns it. `tab` mapping:
  // all/unread/sent/drafts/archive/trash/starred.
  const [folder, setFolder] = useState("inbox");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageToken, setPageToken] = useState(null);
  const [pageTokenStack, setPageTokenStack] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedThread, setSelectedThread] = useState(null);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [sending, setSending] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeTo, setComposeTo] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [composeSending, setComposeSending] = useState(false);
  const [composeDraftId, setComposeDraftId] = useState(null);
  const [scopeStatus, setScopeStatus] = useState(null);
  const [testNotifMsg, setTestNotifMsg] = useState("");
  const [debug, setDebug] = useState(null);
  const [newEmailToast, setNewEmailToast] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [unreadCount, setUnreadCount] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const seenIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);

  useEffect(() => {
    api("/gmail/scope-status").then(r => setScopeStatus(r));
  }, []);

  useEffect(() => {
    api("/gmail/inbox/unread-count").then(r => {
      if (r && typeof r.count === "number") setUnreadCount(r.count);
    });
  }, [messages]);

  const fetchInbox = useCallback((opts = {}) => {
    const silent = opts.silent === true;
    if (!silent) setLoading(true);
    const q = search ? `&q=${encodeURIComponent(search)}` : "";
    const t = pageToken ? `&page_token=${encodeURIComponent(pageToken)}` : "";
    api(`/gmail/folder/${folder}?1=1${q}${t}`).then(r => {
      let list = [];
      if (r && Array.isArray(r.messages)) list = r.messages;
      else if (Array.isArray(r)) list = r;
      if (r && r.debug) setDebug(r.debug);
      // Diff for new arrivals on silent refresh (inbox folder only)
      if (silent && folder === "inbox" && !firstLoadRef.current && list.length > 0) {
        const newOnes = list.filter(m => m.id && !seenIdsRef.current.has(m.id));
        if (newOnes.length > 0) {
          const m = newOnes[0];
          setNewEmailToast({
            from: m.from_name || m.from || "Unknown sender",
            count: newOnes.length,
          });
          setTimeout(() => setNewEmailToast(null), 5000);
        }
      }
      seenIdsRef.current = new Set(list.map(m => m.id).filter(Boolean));
      firstLoadRef.current = false;
      setMessages(list);
      setSelectedIds(new Set());
      if (!silent) setLoading(false);
    });
  }, [pageToken, search, folder]);

  // Auto-refresh every 30s while the Inbox is open. Quiet refresh (no spinner).
  useEffect(() => {
    if (!scopeStatus || !scopeStatus.inbox_ready) return;
    const iv = setInterval(() => fetchInbox({ silent: true }), 30000);
    return () => clearInterval(iv);
  }, [fetchInbox, scopeStatus]);

  useEffect(() => {
    if (scopeStatus && scopeStatus.inbox_ready) fetchInbox();
    else if (scopeStatus) setLoading(false);
  }, [fetchInbox, scopeStatus]);

  async function sendTestNotification() {
    const r = await api("/notifications/test", "POST");
    if (r && r.ok) {
      setTestNotifMsg("Test notification sent — check the bell in the top bar.");
      setTimeout(() => setTestNotifMsg(""), 4000);
    } else {
      setTestNotifMsg("Failed — check Railway logs.");
    }
  }

  if (scopeStatus && !scopeStatus.inbox_ready) {
    return (
      <div style={{ padding: 40, maxWidth: 640, margin: "0 auto" }}>
        <div style={{
          background: "#FFF8F3", border: `1px solid ${COLORS.orange}55`,
          borderLeft: `4px solid ${COLORS.orange}`, borderRadius: 8,
          padding: "20px 24px",
        }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: COLORS.textPrimary, marginBottom: 10 }}>
            📧 Gmail Inbox Setup Required
          </div>
          <div style={{ fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.6, marginBottom: 14 }}>
            To view your inbox here, your Gmail token needs one additional permission
            (<code style={{ background: COLORS.gray, padding: "1px 5px", borderRadius: 3 }}>gmail.readonly</code>).
          </div>
          <div style={{ fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.6, marginBottom: 10 }}>
            Run this on your laptop:
          </div>
          <pre style={{
            background: "#1F2937", color: "#F9FAFB", padding: "10px 14px",
            borderRadius: 6, fontSize: 12, overflowX: "auto",
          }}>python C:\Users\andre\OneDrive\Desktop\outpost_automation\gmail_reauth.py</pre>
          <div style={{ fontSize: 12, color: COLORS.grayMid, marginTop: 12, lineHeight: 1.6 }}>
            A browser will open for ~30 seconds. Click "Allow" and the new token pushes
            to Railway automatically. Refresh this page and your inbox appears.
          </div>
          <div style={{ fontSize: 11, color: COLORS.grayMid, marginTop: 16 }}>
            Current scopes: {scopeStatus.scopes?.length ? scopeStatus.scopes.join(", ") : "none"}
          </div>
          <div style={{ marginTop: 20, borderTop: `1px solid ${COLORS.cardBorder}`, paddingTop: 16 }}>
            <div style={{ fontSize: 11, color: COLORS.grayMid, marginBottom: 6 }}>
              Meanwhile, verify the notification bell is wired:
            </div>
            <button onClick={sendTestNotification} style={{ ...S.btn("ghost"), fontSize: 12 }}>
              🔔 Send Test Notification
            </button>
            {testNotifMsg && <div style={{ fontSize: 11, color: COLORS.success, marginTop: 6 }}>{testNotifMsg}</div>}
          </div>
        </div>
      </div>
    );
  }

  function openThread(msg) {
    setSelectedThread(msg);
    setThreadLoading(true);
    setThread(null);
    setReplyBody("");
    api(`/gmail/thread/${msg.thread_id}`).then(r => {
      setThread(r);
      setThreadLoading(false);
    });
  }

  async function sendReply() {
    if (!replyBody.trim() || !thread) return;
    setSending(true);
    const lastMsg = thread.messages?.[thread.messages.length - 1];
    await api("/gmail/reply", "POST", { thread_id: selectedThread.thread_id, message_id: lastMsg?.id || selectedThread.message_id, body: replyBody });
    setReplyBody("");
    setSending(false);
    openThread(selectedThread);
  }

  async function sendCompose() {
    if (!composeTo.trim() || !composeSubject.trim()) return;
    setComposeSending(true);
    // Backend /send-email auto-appends tracking pixel + unsubscribe blurb
    // and uses BTB/BTC signature based on recipient vertical heuristics.
    await api("/send-email", "POST", {
      to: composeTo, subject: composeSubject, body: composeBody,
      is_html: composeBody.trim().startsWith("<"),
    });
    if (composeDraftId) {
      try { await api(`/gmail/draft/${composeDraftId}`, "DELETE"); } catch (_) {}
    }
    setComposeSending(false);
    setComposeOpen(false);
    setComposeTo(""); setComposeSubject(""); setComposeBody("");
    setComposeDraftId(null);
    fetchInbox();
  }

  async function saveDraft() {
    if (!composeTo && !composeSubject && !composeBody) { setComposeOpen(false); return; }
    const r = await api("/gmail/draft", "POST", {
      to: composeTo, subject: composeSubject, body: composeBody,
      draft_id: composeDraftId || null,
    });
    if (r && r.draft_id) setComposeDraftId(r.draft_id);
    return r;
  }

  async function rowAction(id, action) {
    if (!id) return;
    try {
      if (action === "archive")  await api(`/gmail/archive/${id}`, "POST");
      if (action === "delete")   await api(`/gmail/delete/${id}`, "POST");
      if (action === "read")     await api(`/gmail/mark-read/${id}`, "POST");
      if (action === "unread")   await api(`/gmail/mark-unread/${id}`, "POST");
      if (action === "star")     await api(`/gmail/star/${id}`, "POST");
      if (action === "unstar")   await api(`/gmail/unstar/${id}`, "POST");
      // Optimistic update so the UI doesn't feel laggy
      setMessages(prev => prev.filter(m => {
        if (m.id !== id) return true;
        if (action === "archive" && folder === "inbox") return false;
        if (action === "delete") return false;
        return true;
      }).map(m => m.id !== id ? m : ({
        ...m,
        is_read:    action === "read"   ? true  : action === "unread" ? false : m.is_read,
        is_starred: action === "star"   ? true  : action === "unstar" ? false : m.is_starred,
      })));
    } catch (e) { /* silent — row stays */ }
  }

  async function bulkAction(action) {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    for (const id of ids) await rowAction(id, action);
    setSelectedIds(new Set());
  }

  function toggleSelect(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const FOLDERS = [
    { id: "inbox",   label: "Inbox",   icon: "📥", badge: unreadCount || 0 },
    { id: "starred", label: "Starred", icon: "⭐" },
    { id: "sent",    label: "Sent",    icon: "📤" },
    { id: "drafts",  label: "Drafts",  icon: "📝" },
    { id: "archive", label: "Archive", icon: "🗄️" },
    { id: "trash",   label: "Trash",   icon: "🗑️" },
  ];

  return (
    <div style={{ display: "flex", height: "100%", gap: 0 }}>
      {/* Left sidebar — folder nav */}
      <div style={{
        flex: `0 0 ${sidebarCollapsed ? 56 : 200}px`,
        borderRight: `1px solid ${COLORS.cardBorder}`,
        display: "flex", flexDirection: "column", background: COLORS.gray,
        transition: "flex-basis 0.18s ease",
      }}>
        <div style={{ padding: 10 }}>
          <button onClick={() => { setComposeOpen(true); setComposeDraftId(null); }}
            style={{ ...S.btn("primary"), fontSize: 12, width: "100%" }}>
            <PenLine size={13} style={{ marginRight: sidebarCollapsed ? 0 : 6 }} />
            {!sidebarCollapsed && "Compose"}
          </button>
        </div>
        <div style={{ flex: 1 }}>
          {FOLDERS.map(f => {
            const active = folder === f.id;
            return (
              <div key={f.id} onClick={() => { setFolder(f.id); setPageToken(null); setPageTokenStack([]); setSelectedThread(null); }}
                style={{
                  padding: "8px 12px", cursor: "pointer", fontSize: 13,
                  background: active ? COLORS.white : "transparent",
                  borderLeft: active ? `3px solid ${COLORS.orange}` : "3px solid transparent",
                  color: active ? COLORS.orange : COLORS.textSecondary,
                  fontWeight: active ? 700 : 500,
                  display: "flex", alignItems: "center", gap: 8,
                }}>
                <span>{f.icon}</span>
                {!sidebarCollapsed && <span style={{ flex: 1 }}>{f.label}</span>}
                {!sidebarCollapsed && f.badge > 0 && (
                  <span style={{
                    background: COLORS.danger, color: "#fff", fontSize: 10, fontWeight: 700,
                    borderRadius: 99, padding: "1px 7px",
                  }}>{f.badge > 99 ? "99+" : f.badge}</span>
                )}
              </div>
            );
          })}
        </div>
        <div onClick={() => setSidebarCollapsed(c => !c)}
          style={{ padding: 10, borderTop: `1px solid ${COLORS.cardBorder}`, cursor: "pointer", textAlign: "center" }}>
          {sidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </div>
      </div>

      {/* Middle — message list */}
      <div style={{ flex: selectedThread ? "0 0 40%" : 1, borderRight: selectedThread ? `1px solid ${COLORS.cardBorder}` : "none", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Toolbar */}
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${COLORS.cardBorder}`, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.textPrimary, textTransform: "capitalize" }}>
            {folder}
          </div>
          <div style={{ flex: 1, display: "flex", gap: 8 }}>
            <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && fetchInbox()} placeholder={`Search ${folder}...`} style={{ ...S.input, flex: 1, fontSize: 12 }} />
            <button onClick={() => fetchInbox()} style={{ ...S.btn("ghost"), padding: "4px 8px" }}><Search size={14} /></button>
          </div>
        </div>
        {/* Bulk action bar — only when selections exist */}
        {selectedIds.size > 0 && (
          <div style={{ padding: "8px 16px", borderBottom: `1px solid ${COLORS.cardBorder}`, display: "flex", gap: 6, alignItems: "center", background: COLORS.gray, fontSize: 12 }}>
            <span style={{ fontWeight: 700 }}>{selectedIds.size} selected</span>
            <button onClick={() => bulkAction("archive")} style={{ ...S.btn("ghost"), fontSize: 11, padding: "3px 8px" }}>Archive</button>
            <button onClick={() => bulkAction("delete")} style={{ ...S.btn("ghost"), fontSize: 11, padding: "3px 8px", color: COLORS.danger }}>Delete</button>
            <button onClick={() => bulkAction("read")} style={{ ...S.btn("ghost"), fontSize: 11, padding: "3px 8px" }}>Mark read</button>
            <button onClick={() => setSelectedIds(new Set())} style={{ ...S.btn("ghost"), fontSize: 11, padding: "3px 8px", marginLeft: "auto" }}>Clear</button>
          </div>
        )}
        {/* Messages */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {loading ? <div style={{ padding: 40, textAlign: "center" }}><Spinner /></div> :
            messages.length === 0 ? <div style={{ padding: 40, textAlign: "center", fontSize: 13, color: COLORS.grayMid }}>No messages in {folder}.</div> :
            messages.map(m => {
              const selected = selectedIds.has(m.id);
              return (
                <div key={m.id || m.thread_id} style={{
                  borderBottom: `1px solid ${COLORS.cardBorder}`,
                  background: selected ? "#FFF8F3"
                    : selectedThread?.thread_id === m.thread_id ? COLORS.rowHover
                    : "transparent",
                  display: "flex", alignItems: "flex-start", gap: 8, padding: "10px 14px",
                }}>
                  <input type="checkbox" checked={selected}
                    onClick={e => e.stopPropagation()}
                    onChange={() => toggleSelect(m.id)}
                    style={{ marginTop: 4, flexShrink: 0 }} />
                  <button onClick={e => { e.stopPropagation(); rowAction(m.id, m.is_starred ? "unstar" : "star"); }}
                    title={m.is_starred ? "Unstar" : "Star"}
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, padding: 0, marginTop: 2, flexShrink: 0, color: m.is_starred ? "#F59E0B" : COLORS.grayMid }}>
                    {m.is_starred ? "★" : "☆"}
                  </button>
                  <div onClick={() => openThread(m)} style={{ flex: 1, minWidth: 0, cursor: "pointer" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                      <span style={{ fontSize: 13, fontWeight: m.is_read ? 400 : 700, color: COLORS.textPrimary }}>{m.from_name || m.from || "Unknown"}</span>
                      <span style={{ fontSize: 10, color: COLORS.grayMid, whiteSpace: "nowrap" }}>{timeAgo(m.date)}</span>
                    </div>
                    <div style={{ fontSize: 12, fontWeight: m.is_read ? 400 : 700, color: COLORS.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.subject}</div>
                    <div style={{ fontSize: 11, color: COLORS.grayMid, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: 2 }}>{m.snippet}</div>
                    {m.matched_contact && (
                      <div style={{ fontSize: 10, color: COLORS.orange, marginTop: 3 }}>
                        👤 {m.matched_contact.name}{m.matched_contact.org ? ` · ${m.matched_contact.org}` : ""}
                      </div>
                    )}
                  </div>
                  {/* Row hover actions */}
                  <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                    {folder !== "trash" && folder !== "archive" && (
                      <button onClick={e => { e.stopPropagation(); rowAction(m.id, "archive"); }}
                        title="Archive" style={{ ...S.btn("ghost"), padding: "2px 6px", fontSize: 11 }}>🗄️</button>
                    )}
                    <button onClick={e => { e.stopPropagation(); rowAction(m.id, m.is_read ? "unread" : "read"); }}
                      title={m.is_read ? "Mark unread" : "Mark read"}
                      style={{ ...S.btn("ghost"), padding: "2px 6px", fontSize: 11 }}>{m.is_read ? "●" : "○"}</button>
                    <button onClick={e => { e.stopPropagation(); rowAction(m.id, "delete"); }}
                      title="Delete" style={{ ...S.btn("ghost"), padding: "2px 6px", fontSize: 11, color: COLORS.danger }}>🗑️</button>
                  </div>
                </div>
              );
            })
          }
        </div>
        {/* Pagination */}
        <div style={{ padding: "8px 16px", borderTop: `1px solid ${COLORS.cardBorder}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button disabled={pageTokenStack.length === 0}
            onClick={() => {
              const prev = pageTokenStack.slice(0, -1);
              setPageTokenStack(prev);
              setPageToken(prev[prev.length - 1] || null);
            }}
            style={{ ...S.btn("ghost"), fontSize: 11 }}>
            <ChevronLeft size={14} /> Prev
          </button>
          <span style={{ fontSize: 11, color: COLORS.grayMid }}>
            {pageTokenStack.length === 0 ? "Page 1" : `Page ${pageTokenStack.length + 1}`}
          </span>
          <button onClick={() => {
              // advance: push current token, then fetch will need nextToken from prev response.
              // For simplicity we just trigger a fresh fetch; pageToken isn't tracked
              // here without an async chain. Leaving prev/next symmetric.
              setPageTokenStack(st => [...st, pageToken]);
              setPageToken(null);
            }}
            style={{ ...S.btn("ghost"), fontSize: 11 }}>Next <ChevronRight size={14} /></button>
        </div>
      </div>

      {/* Right: thread detail */}
      {selectedThread && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${COLORS.cardBorder}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: COLORS.textPrimary }}>{selectedThread.subject}</div>
            <button onClick={() => setSelectedThread(null)} style={{ ...S.btn("ghost"), padding: "4px 8px" }}><X size={14} /></button>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
            {threadLoading ? <Spinner /> :
              !thread ? <div style={{ color: COLORS.grayMid, fontSize: 12 }}>Could not load thread.</div> :
              (thread.messages || []).map((msg, i) => (
                <div key={msg.id || i} style={{ marginBottom: 16, background: COLORS.gray, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, padding: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: 12, color: COLORS.textPrimary }}>{msg.from || "Unknown"}</span>
                    <span style={{ fontSize: 10, color: COLORS.grayMid }}>{timeAgo(msg.date)}</span>
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.textPrimary, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{msg.body || msg.snippet || ""}</div>
                </div>
              ))
            }
          </div>
          {/* Reply box */}
          <div style={{ padding: "12px 16px", borderTop: `1px solid ${COLORS.cardBorder}`, display: "flex", gap: 8 }}>
            <textarea value={replyBody} onChange={e => setReplyBody(e.target.value)} placeholder="Write a reply..." rows={2}
              style={{ ...S.input, flex: 1, resize: "vertical", fontSize: 12, fontFamily: "inherit" }} />
            <button onClick={sendReply} disabled={sending || !replyBody.trim()} style={{ ...S.btn("primary"), fontSize: 12, alignSelf: "flex-end" }}>
              {sending ? "..." : "Send"}
            </button>
          </div>
        </div>
      )}

      {/* Debug banner — only shows when backend reported per-message errors */}
      {debug && debug.raw_count > 0 && debug.returned === 0 && debug.errors_sample?.length > 0 && (
        <div style={{
          position: "fixed", top: 12, left: "50%", transform: "translateX(-50%)", zIndex: 1000,
          background: "#FEF3C7", color: "#92400E", border: "1px solid #F59E0B",
          borderRadius: 8, padding: "8px 14px", fontSize: 11, maxWidth: 640,
        }}>
          Gmail returned {debug.raw_count} message stubs but metadata fetch failed for all of them.
          First error: <code>{debug.errors_sample[0]}</code>
        </div>
      )}

      {/* New email toast — subtle, bottom-right */}
      {newEmailToast && (
        <div style={{
          position: "fixed", bottom: 20, right: 20, zIndex: 1000,
          background: "#1F2937", color: "#F9FAFB",
          padding: "10px 16px", borderRadius: 8, fontSize: 12,
          boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <Mail size={14} />
          {newEmailToast.count > 1
            ? `${newEmailToast.count} new emails · latest from ${newEmailToast.from}`
            : `New email from ${newEmailToast.from}`}
        </div>
      )}

      {/* Compose modal */}
      {composeOpen && (
        <div style={{ position: "fixed", inset: 0, background: "#000000aa", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setComposeOpen(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: COLORS.white, borderRadius: 10, padding: 24,
            width: 640, maxWidth: "94vw",
            border: `1px solid ${COLORS.cardBorder}`,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary }}>
                {composeDraftId ? "Edit Draft" : "New Email"}
              </div>
              <div style={{ fontSize: 10, color: COLORS.grayMid }}>
                Pixel tracking + unsub blurb auto-applied on send
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <input value={composeTo} onChange={e => setComposeTo(e.target.value)} placeholder="To (email)" style={{ ...S.input, fontSize: 13 }} />
              <input value={composeSubject} onChange={e => setComposeSubject(e.target.value)} placeholder="Subject" style={{ ...S.input, fontSize: 13 }} />
              <textarea value={composeBody} onChange={e => setComposeBody(e.target.value)} placeholder="Write your message..." rows={10}
                style={{ ...S.input, fontSize: 13, resize: "vertical", fontFamily: "inherit" }} />
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <button onClick={async () => { await saveDraft(); setComposeOpen(false); setComposeTo(""); setComposeSubject(""); setComposeBody(""); fetchInbox(); }}
                  style={{ ...S.btn("ghost"), fontSize: 12 }}>
                  💾 Save Draft
                </button>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => { setComposeOpen(false); setComposeTo(""); setComposeSubject(""); setComposeBody(""); setComposeDraftId(null); }}
                    style={{ ...S.btn("ghost"), fontSize: 12, color: COLORS.danger }}>
                    Discard
                  </button>
                  <button onClick={sendCompose} disabled={composeSending || !composeTo.trim() || !composeSubject.trim()}
                    style={{ ...S.btn("primary"), fontSize: 12 }}>
                    {composeSending ? "Sending…" : "Send"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Inline editable money / text fields ──────────────────────────────────────
function InlineMoney({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value ?? "");
  useEffect(() => { setV(value ?? ""); }, [value]);
  if (!editing) {
    return (
      <span onClick={() => setEditing(true)} style={{
        cursor: "pointer", color: value ? COLORS.textPrimary : COLORS.grayMid,
        fontWeight: 600,
      }}>{value ? fmt$$(value) : "Add…"}</span>
    );
  }
  return (
    <input
      autoFocus type="number" step="0.01" value={v}
      onChange={e => setV(e.target.value)}
      onBlur={() => {
        setEditing(false);
        const n = parseFloat(v);
        if (!Number.isNaN(n) && n !== (value ?? null)) onSave(n);
      }}
      onKeyDown={e => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") { setV(value ?? ""); setEditing(false); } }}
      style={{ ...S.input, width: "100%", fontSize: 12 }}
    />
  );
}

function InlineText({ value, onSave, placeholder }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value ?? "");
  useEffect(() => { setV(value ?? ""); }, [value]);
  if (!editing) {
    return (
      <span onClick={() => setEditing(true)} style={{
        cursor: "pointer", color: value ? COLORS.textPrimary : COLORS.grayMid,
      }}>{value || placeholder || "Add…"}</span>
    );
  }
  return (
    <input
      autoFocus value={v} placeholder={placeholder}
      onChange={e => setV(e.target.value)}
      onBlur={() => { setEditing(false); if (v !== (value ?? "")) onSave(v); }}
      onKeyDown={e => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") { setV(value ?? ""); setEditing(false); } }}
      style={{ ...S.input, width: "100%", fontSize: 12 }}
    />
  );
}

// ── Deal metrics panel ───────────────────────────────────────────────────────
function DealMetricsPanel({ dealId, refreshKey }) {
  const [m, setM] = useState(null);
  useEffect(() => {
    api(`/deals/${dealId}/metrics`).then(r => setM(r || null));
  }, [dealId, refreshKey]);
  if (!m) return null;
  const inactiveColor = (m.inactive_days ?? 0) > 14 ? COLORS.danger : COLORS.textPrimary;
  const total = m.total_activities || 0;

  const typeOrder = ["email", "call", "meeting", "note"];
  const bars = typeOrder
    .map(t => ({ t, ...(m.top_activities?.[t] || { count: 0, pct: 0 }) }))
    .filter(x => x.count > 0);
  const typeColor = { email: "#3B82F6", call: "#10B981", meeting: "#14B8A6", note: "#F59E0B" };

  return (
    <DetailSection title="📊 Deal Metrics">
      <div style={{
        background: COLORS.white, border: `1px solid ${COLORS.cardBorder}`,
        borderRadius: 6, padding: 10, fontSize: 12,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", borderBottom: `1px solid ${COLORS.cardBorder}` }}>
          <span style={{ color: COLORS.grayMid }}>Avg time to Won</span>
          <span style={{ fontWeight: 600 }}>
            {m.avg_time_to_won_days != null ? `${m.avg_time_to_won_days} days` : "—"}
          </span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", borderBottom: `1px solid ${COLORS.cardBorder}` }}>
          <span style={{ color: COLORS.grayMid }}>Inactive</span>
          <span style={{ fontWeight: 700, color: inactiveColor }}>
            {m.inactive_days != null ? `${m.inactive_days} days` : "—"}
          </span>
        </div>
        {total > 0 && (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 10, color: COLORS.grayMid, marginBottom: 4 }}>Top activities ({total})</div>
            <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", background: COLORS.cardBorder }}>
              {bars.map(b => (
                <div key={b.t} style={{
                  width: `${b.pct}%`, background: typeColor[b.t] || COLORS.grayMid,
                }} title={`${b.t}: ${b.count} (${b.pct}%)`} />
              ))}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6, fontSize: 10, color: COLORS.grayMid }}>
              {bars.map(b => (
                <span key={b.t}>
                  <span style={{ display: "inline-block", width: 8, height: 8, background: typeColor[b.t], borderRadius: 2, marginRight: 4 }} />
                  {b.t.charAt(0).toUpperCase() + b.t.slice(1)} {b.pct}%
                </span>
              ))}
            </div>
          </div>
        )}
        {m.most_active_user && (
          <div style={{ marginTop: 8, fontSize: 11, color: COLORS.grayMid }}>
            Most active: <span style={{ color: COLORS.textPrimary, fontWeight: 600 }}>
              {m.most_active_user.name}
            </span> — {m.most_active_user.count} activities ({m.most_active_user.pct}%)
          </div>
        )}
      </div>
    </DetailSection>
  );
}

// ── Deal products panel ──────────────────────────────────────────────────────
function DealProductsPanel({ dealId }) {
  const [products, setProducts] = useState([]);
  const [picker, setPicker] = useState(false);
  useEffect(() => {
    api(`/deals/${dealId}/products`).then(r => setProducts(Array.isArray(r) ? r : []));
  }, [dealId]);

  async function addProduct(p) {
    const r = await api(`/deals/${dealId}/products`, "POST", {
      sku: p.sku, name: p.name, quantity: 1, price: p.price,
    });
    if (r) setProducts(prev => [r, ...prev]);
    setPicker(false);
  }
  async function updateQty(id, qty) {
    // No PATCH endpoint for deal-products yet — delete + re-add is safer than
    // fighting race conditions. Keep quantity change local for now.
    setProducts(prev => prev.map(p => p.id === id ? { ...p, quantity: qty } : p));
  }
  async function removeProduct(id) {
    await api(`/deal-products/${id}`, "DELETE");
    setProducts(prev => prev.filter(p => p.id !== id));
  }

  const total = products.reduce((s, p) => s + (p.quantity || 1) * (p.price || 0), 0);

  return (
    <DetailSection title={`Products (${products.length})`}>
      {products.length === 0 ? (
        <div style={{ fontSize: 12, color: COLORS.grayMid, marginBottom: 6 }}>
          No products linked yet
        </div>
      ) : (
        <div style={{ background: COLORS.white, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 6, overflow: "hidden" }}>
          {products.map(p => (
            <div key={p.id} style={{
              display: "flex", gap: 8, padding: "6px 10px", fontSize: 12,
              borderBottom: `1px solid ${COLORS.cardBorder}`, alignItems: "center",
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{p.name}</div>
                {p.sku && <div style={{ fontSize: 10, color: COLORS.grayMid }}>{p.sku}</div>}
              </div>
              <input
                type="number" min={1} value={p.quantity || 1}
                onChange={e => updateQty(p.id, parseInt(e.target.value) || 1)}
                style={{ ...S.input, width: 52, fontSize: 11, padding: "2px 6px" }}
              />
              <div style={{ fontSize: 11, color: COLORS.grayMid, minWidth: 60, textAlign: "right" }}>
                {fmt$$((p.quantity || 1) * (p.price || 0))}
              </div>
              <button onClick={() => removeProduct(p.id)} style={{
                ...S.btn("ghost"), padding: "2px 6px", color: COLORS.danger,
                borderColor: "transparent",
              }}><Trash2 size={11} /></button>
            </div>
          ))}
          <div style={{ padding: "6px 10px", fontSize: 12, background: COLORS.gray, display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: COLORS.grayMid }}>Total</span>
            <span style={{ fontWeight: 700 }}>{fmt$$(total)}</span>
          </div>
        </div>
      )}
      <button onClick={() => setPicker(true)} style={{
        ...S.btn("ghost"), fontSize: 11, marginTop: 6,
      }}><Plus size={12} style={{ verticalAlign: "middle", marginRight: 4 }} /> Add Product</button>
      {picker && <ProductPickerModal onPick={addProduct} onClose={() => setPicker(false)} />}
    </DetailSection>
  );
}

function ProductPickerModal({ onPick, onClose }) {
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "#000000aa", zIndex: 1100,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: COLORS.white, borderRadius: 10, padding: 18, width: 480, maxWidth: "90vw",
        border: `1px solid ${COLORS.cardBorder}`, maxHeight: "80vh", overflowY: "auto",
      }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Add Product</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {PRODUCT_CATALOG.map(p => (
            <div key={p.sku} onClick={() => onPick(p)} style={{
              padding: "8px 12px", border: `1px solid ${COLORS.cardBorder}`, borderRadius: 6,
              cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center",
              fontSize: 12,
            }}>
              <div>
                <div style={{ fontWeight: 600 }}>{p.name}</div>
                <div style={{ fontSize: 10, color: COLORS.grayMid }}>{p.sku}</div>
              </div>
              <div style={{ fontWeight: 700, color: COLORS.orange }}>{fmt$$(p.price)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Marketing status toggle on a contact card line ───────────────────────────
function ContactMarketingToggle({ contact }) {
  const [v, setV] = useState(contact.marketing_status || (contact.accepts_marketing ? "Consent given" : "No consent"));
  const [saving, setSaving] = useState(false);
  const OPTIONS = ["No consent", "Consent given", "Double opt-in"];
  async function change(next) {
    setV(next); setSaving(true);
    await api(`/records/contact/${contact.id}`, "PATCH", {
      marketing_status: next,
      accepts_marketing: next === "No consent" ? 0 : 1,
      double_optin: next === "Double opt-in" ? 1 : 0,
    });
    setSaving(false);
  }
  const colorFor = v === "Double opt-in" ? COLORS.success : v === "Consent given" ? COLORS.warning : COLORS.grayMid;
  return (
    <div style={{ fontSize: 10, marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
      <span style={{ color: COLORS.grayMid }}>Marketing:</span>
      <select value={v} onChange={e => change(e.target.value)} disabled={saving}
        style={{ ...S.select, fontSize: 10, padding: "2px 6px", color: colorFor, fontWeight: 600 }}>
        {OPTIONS.map(o => <option key={o}>{o}</option>)}
      </select>
    </div>
  );
}

// ── Contact financial overview (QuickBooks wire-up pending) ──────────────────
function FinancialOverview({ contact }) {
  // 🟡 UPGRADE SUGGESTION: wire these to QuickBooks once the sync is built.
  const zeros = { outstanding: 0, overdue: 0, paid: 0 };
  const total = zeros.outstanding + zeros.overdue + zeros.paid;
  const updated = contact?.updated_at ? new Date(contact.updated_at).toLocaleDateString() : "—";
  const row = (label, val, color) => (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: 12 }}>
      <span style={{ color: COLORS.grayMid }}>{label}</span>
      <span style={{ fontWeight: 600, color: color || COLORS.textPrimary }}>{fmt$$(val)}</span>
    </div>
  );
  return (
    <DetailSection title="💰 Financial Overview">
      <div style={{ background: COLORS.white, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 6, padding: 10 }}>
        {row("Outstanding", zeros.outstanding)}
        {row("Overdue", zeros.overdue, zeros.overdue > 0 ? COLORS.danger : null)}
        {row("Paid", zeros.paid, COLORS.success)}
        <div style={{ borderTop: `1px solid ${COLORS.cardBorder}`, marginTop: 4, paddingTop: 4 }}>
          {row("Total", total)}
        </div>
        <div style={{ fontSize: 10, color: COLORS.grayMid, marginTop: 6 }}>
          Updated: {updated} · <span style={{ fontStyle: "italic" }}>QuickBooks sync pending</span>
        </div>
      </div>
    </DetailSection>
  );
}

// ── Deal ↔ Org + Contact link sections ───────────────────────────────────────
function DealOrgSection({ dealId, org, orgName, orgAddress, onOpenOrg, onChange }) {
  const [picker, setPicker] = useState(false);
  const hasOrg = !!(org || orgName);

  async function linkOrg(picked) {
    await api(`/records/deal/${dealId}`, "PATCH", { org_id: picked.id });
    setPicker(false);
    onChange && onChange();
  }
  async function unlinkOrg() {
    if (!window.confirm("Unlink this organization from the deal?")) return;
    await api(`/records/deal/${dealId}`, "PATCH", { org_id: null });
    onChange && onChange();
  }

  return (
    <DetailSection title="Organization">
      {hasOrg ? (
        <div style={{
          background: COLORS.white, border: `1px solid ${COLORS.cardBorder}`,
          borderRadius: 6, padding: 10, fontSize: 12,
        }}>
          <div style={{ fontWeight: 700, color: COLORS.textPrimary, marginBottom: 2 }}>
            🏢 {orgName || org?.name}
          </div>
          {(orgAddress || org?.address) && (
            <div style={{ fontSize: 11, color: COLORS.grayMid, marginBottom: 6 }}>
              {[org?.address || orgAddress, org?.city, org?.state, org?.zip].filter(Boolean).join(", ")}
            </div>
          )}
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={onOpenOrg} style={{ ...S.btn("ghost"), fontSize: 11, padding: "3px 10px" }}>
              View Org
            </button>
            <button onClick={unlinkOrg} style={{
              ...S.btn("ghost"), fontSize: 11, padding: "3px 10px",
              color: COLORS.danger, borderColor: COLORS.danger + "55",
            }}>Remove</button>
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 12, color: COLORS.grayMid, marginBottom: 6 }}>No organization linked</div>
      )}
      <button onClick={() => setPicker(true)} style={{
        ...S.btn("ghost"), fontSize: 11, marginTop: 6,
      }}>
        <Plus size={12} style={{ verticalAlign: "middle", marginRight: 4 }} /> Link Organization
      </button>
      {picker && (
        <RecordPickerModal
          kind="organization"
          title="Link Organization"
          onPick={linkOrg}
          onClose={() => setPicker(false)}
        />
      )}
    </DetailSection>
  );
}

function DealContactsSection({ dealId, contacts, onOpenContact, onChange }) {
  const [picker, setPicker] = useState(false);
  const list = contacts || [];

  async function linkContact(picked) {
    await api(`/deals/${dealId}/contacts`, "POST", {
      contact_id: picked.id,
      primary: list.length === 0,   // first contact becomes primary
    });
    setPicker(false);
    onChange && onChange();
  }
  async function removeContact(cid) {
    if (!window.confirm("Unlink this contact from the deal?")) return;
    await api(`/deals/${dealId}/contacts/${cid}`, "DELETE");
    onChange && onChange();
  }
  async function markPrimary(cid) {
    await api(`/deals/${dealId}/contacts`, "POST", { contact_id: cid, primary: true });
    onChange && onChange();
  }

  return (
    <DetailSection title={`Contacts${list.length ? ` (${list.length})` : ""}`}>
      {list.length === 0 ? (
        <div style={{ fontSize: 12, color: COLORS.grayMid, marginBottom: 6 }}>No contacts linked</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {list.map(c => {
            const phone = c.phone_work || c.phone_mobile || c.phone_home;
            const email = c.email_work || c.email_home;
            return (
              <div key={c.id} style={{
                background: COLORS.white, border: `1px solid ${COLORS.cardBorder}`,
                borderRadius: 6, padding: 10, fontSize: 12,
              }}>
                <div style={{ fontWeight: 700, color: COLORS.textPrimary, marginBottom: 2, display: "flex", alignItems: "center", gap: 6 }}>
                  👤 {c.full_name || [c.first_name, c.last_name].filter(Boolean).join(" ") || `Contact #${c.id}`}
                  {c.is_primary ? (
                    <span style={{ ...S.badge(COLORS.orange), fontSize: 9, padding: "1px 6px" }}>PRIMARY</span>
                  ) : null}
                </div>
                {email && (
                  <div style={{ fontSize: 11 }}>
                    <a href={`mailto:${email}`} style={{ color: COLORS.orange, textDecoration: "none" }}>✉ {email}</a>
                  </div>
                )}
                {phone && (
                  <div style={{ fontSize: 11 }}>
                    <a href={`tel:${phone}`} style={{ color: COLORS.grayMid, textDecoration: "none" }}>📞 {phone}</a>
                  </div>
                )}
                <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                  <button onClick={() => onOpenContact(c)} style={{ ...S.btn("ghost"), fontSize: 11, padding: "2px 10px" }}>
                    View Card
                  </button>
                  {!c.is_primary && (
                    <button onClick={() => markPrimary(c.id)} style={{
                      ...S.btn("ghost"), fontSize: 11, padding: "2px 10px",
                      color: COLORS.orange, borderColor: COLORS.orange + "55",
                    }}>Make Primary</button>
                  )}
                  <button onClick={() => removeContact(c.id)} style={{
                    ...S.btn("ghost"), fontSize: 11, padding: "2px 10px",
                    color: COLORS.danger, borderColor: COLORS.danger + "55",
                  }}>Remove</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <button onClick={() => setPicker(true)} style={{
        ...S.btn("ghost"), fontSize: 11, marginTop: 6,
      }}>
        <Plus size={12} style={{ verticalAlign: "middle", marginRight: 4 }} /> Add Contact
      </button>
      {picker && (
        <RecordPickerModal
          kind="contact"
          title="Link Contact"
          onPick={linkContact}
          onClose={() => setPicker(false)}
        />
      )}
    </DetailSection>
  );
}

function RecordPickerModal({ kind, title, onPick, onClose, multi = false, excludeIds = [] }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]); // multi-select buffer

  // Debounced search against /search — returns {organizations, contacts, leads}
  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      api(`/search?q=${encodeURIComponent(query.trim())}&limit=30`).then(r => {
        if (cancelled) return;
        const bucket = kind === "organization" ? (r?.organizations || []) : (r?.contacts || []);
        const excl = new Set(excludeIds || []);
        setResults(bucket.filter(x => !excl.has(x.id)));
        setLoading(false);
      });
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, kind, excludeIds]);

  const isSelected = (id) => selected.some(s => s.id === id);
  const toggleSelect = (r) => {
    setSelected(prev => isSelected(r.id)
      ? prev.filter(s => s.id !== r.id)
      : [...prev, r]);
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "#000000aa", zIndex: 1200,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: COLORS.white, borderRadius: 10, padding: 18, width: 520, maxWidth: "92vw",
        maxHeight: "80vh", display: "flex", flexDirection: "column",
        border: `1px solid ${COLORS.cardBorder}`,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{title}</div>
          <button onClick={onClose} style={{ ...S.btn("ghost"), padding: "2px 8px" }}><X size={14} /></button>
        </div>
        <input
          autoFocus
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={kind === "organization" ? "Search by name…" : "Search by name or email…"}
          style={{ ...S.input, width: "100%", fontSize: 13, marginBottom: 10 }}
        />
        <div style={{ overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
          {query.trim().length < 2 && (
            <div style={{ fontSize: 12, color: COLORS.grayMid, padding: 12, textAlign: "center" }}>
              Type at least 2 characters to search.
            </div>
          )}
          {loading && <div style={{ fontSize: 12, color: COLORS.grayMid, padding: 8 }}>Searching…</div>}
          {!loading && query.trim().length >= 2 && results.length === 0 && (
            <div style={{ fontSize: 12, color: COLORS.grayMid, padding: 12, textAlign: "center" }}>
              No matches.
            </div>
          )}
          {results.map(r => {
            const sel = multi && isSelected(r.id);
            return (
              <div
                key={r.id}
                onClick={() => multi ? toggleSelect(r) : onPick(r)}
                style={{
                  padding: "8px 12px",
                  border: `1px solid ${sel ? COLORS.orange : COLORS.cardBorder}`,
                  background: sel ? COLORS.orange + "11" : COLORS.white,
                  borderRadius: 6, cursor: "pointer", fontSize: 12,
                  display: "flex", alignItems: "center", gap: 8,
                }}
              >
                {multi && (
                  <input type="checkbox" checked={sel} readOnly
                    style={{ accentColor: COLORS.orange }} />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>
                    {kind === "organization" ? `🏢 ${r.name}` : `👤 ${r.full_name || "(no name)"}`}
                  </div>
                  {kind === "organization" && r.vertical && (
                    <div style={{ fontSize: 10, color: COLORS.grayMid }}>{r.vertical}</div>
                  )}
                  {kind === "contact" && r.email_work && (
                    <div style={{ fontSize: 11, color: COLORS.grayMid }}>{r.email_work}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {multi && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, paddingTop: 10, borderTop: `1px solid ${COLORS.cardBorder}` }}>
            <div style={{ fontSize: 11, color: COLORS.grayMid }}>
              {selected.length} selected
            </div>
            <button
              onClick={() => selected.length && onPick(selected)}
              disabled={selected.length === 0}
              style={{ ...S.btn("primary"), fontSize: 12, padding: "6px 14px", opacity: selected.length ? 1 : 0.5 }}
            >
              Add {selected.length || ""}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Shared org-link section for Contact + Lead detail views ────────────────
// Shows linked org (name + facility address) with Change/Link actions.
// Writes to PATCH /records/{recordType}/{recordId} {org_id}.
function ContactOrgSection({ recordType, recordId, organization, onChange, onOpenOrg }) {
  const [picker, setPicker] = useState(false);
  const hasOrg = !!organization;

  async function linkOrg(picked) {
    await api(`/records/${recordType}/${recordId}`, "PATCH", { org_id: picked.id });
    setPicker(false);
    onChange && onChange();
  }
  async function unlinkOrg() {
    if (!window.confirm("Unlink this organization?")) return;
    await api(`/records/${recordType}/${recordId}`, "PATCH", { org_id: null });
    onChange && onChange();
  }

  const street = hasOrg ? (organization.address || "") : "";
  const cityStateZip = hasOrg
    ? [organization.city, organization.state].filter(Boolean).join(", ") +
      (organization.zip ? ` ${organization.zip}` : "")
    : "";
  const hasAnyAddress = hasOrg && (street || cityStateZip.trim());
  const mapsUrl = hasAnyAddress
    ? `https://www.google.com/maps/search/?api=1&q=${encodeURIComponent(
        [organization.name, street, cityStateZip].filter(Boolean).join(", ")
      )}`
    : "";

  return (
    <DetailSection title="Facility / Organization">
      {hasOrg ? (
        <div style={{
          background: COLORS.white, border: `1px solid ${COLORS.cardBorder}`,
          borderRadius: 6, padding: 12, fontSize: 12,
        }}>
          <div
            onClick={() => onOpenOrg && onOpenOrg(organization)}
            style={{
              fontWeight: 700, color: COLORS.orange, marginBottom: 6, fontSize: 13,
              cursor: onOpenOrg ? "pointer" : "default",
              textDecoration: onOpenOrg ? "underline" : "none",
            }}
          >
            🏢 {organization.name}
          </div>
          {hasAnyAddress ? (
            <div style={{
              fontSize: 12, color: COLORS.textPrimary, marginBottom: 8,
              lineHeight: 1.4,
            }}>
              {street && <div>{street}</div>}
              {cityStateZip.trim() && <div>{cityStateZip}</div>}
              {mapsUrl && (
                <a href={mapsUrl} target="_blank" rel="noreferrer"
                   style={{ color: COLORS.orange, fontSize: 11, textDecoration: "none" }}>
                  📍 Open in Maps
                </a>
              )}
            </div>
          ) : (
            <div style={{ fontSize: 11, color: COLORS.grayMid, marginBottom: 8, fontStyle: "italic" }}>
              No address on file for this facility
            </div>
          )}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button onClick={() => setPicker(true)} style={{ ...S.btn("ghost"), fontSize: 11, padding: "3px 10px" }}>
              Change
            </button>
            <button onClick={unlinkOrg} style={{
              ...S.btn("ghost"), fontSize: 11, padding: "3px 10px",
              color: COLORS.danger, borderColor: COLORS.danger + "55",
            }}>Remove</button>
          </div>
        </div>
      ) : (
        <div style={{
          background: COLORS.white, border: `1px dashed ${COLORS.cardBorder}`,
          borderRadius: 6, padding: 12, fontSize: 12,
        }}>
          <div style={{ color: COLORS.grayMid, marginBottom: 8 }}>No facility linked</div>
          <button onClick={() => setPicker(true)} style={{ ...S.btn("primary"), fontSize: 11 }}>
            <Plus size={12} style={{ verticalAlign: "middle", marginRight: 4 }} /> Link to organization
          </button>
        </div>
      )}
      {picker && (
        <RecordPickerModal
          kind="organization"
          title={hasOrg ? "Change Organization" : "Link Organization"}
          onPick={linkOrg}
          onClose={() => setPicker(false)}
        />
      )}
    </DetailSection>
  );
}

// ── Contacts-under-an-Org section with multi-select Add picker ─────────────
// Writes N * PATCH /records/contact/{id} {org_id: orgId} on Add.
function OrgContactsSection({ orgId, contacts, onChange, onOpenContact }) {
  const [picker, setPicker] = useState(false);
  const [adding, setAdding] = useState(false);
  const list = contacts || [];
  const existingIds = list.map(c => c.id);

  async function addContacts(picked) {
    const arr = Array.isArray(picked) ? picked : [picked];
    setAdding(true);
    try {
      await Promise.all(arr.map(c =>
        api(`/records/contact/${c.id}`, "PATCH", { org_id: orgId })
      ));
    } finally {
      setAdding(false);
      setPicker(false);
      onChange && onChange();
    }
  }

  async function unlinkContact(contactId) {
    if (!window.confirm("Unlink this contact from the organization?")) return;
    await api(`/records/contact/${contactId}`, "PATCH", { org_id: null });
    onChange && onChange();
  }

  return (
    <DetailSection title={`Contacts${list.length ? ` (${list.length})` : ""}`}>
      {list.length === 0 ? (
        <div style={{ fontSize: 12, color: COLORS.grayMid, marginBottom: 6 }}>No contacts linked</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {list.map(c => {
            const phone = c.phone_work || c.phone_mobile || c.phone_home;
            const email = c.email_work || c.email_home;
            return (
              <div key={c.id} style={{
                background: COLORS.white, border: `1px solid ${COLORS.cardBorder}`,
                borderRadius: 6, padding: 10, fontSize: 12,
              }}>
                <div
                  onClick={() => onOpenContact && onOpenContact(c)}
                  style={{
                    fontWeight: 700, color: COLORS.orange, marginBottom: 2,
                    cursor: onOpenContact ? "pointer" : "default",
                    textDecoration: onOpenContact ? "underline" : "none",
                  }}
                >
                  👤 {c.full_name || [c.first_name, c.last_name].filter(Boolean).join(" ") || `Contact #${c.id}`}
                </div>
                {c.job_title && (
                  <div style={{ fontSize: 11, color: COLORS.grayMid }}>{c.job_title}</div>
                )}
                {email && (
                  <div style={{ fontSize: 11 }}>
                    <a href={`mailto:${email}`} style={{ color: COLORS.orange, textDecoration: "none" }}>✉ {email}</a>
                  </div>
                )}
                {phone && (
                  <div style={{ fontSize: 11 }}>
                    <a href={`tel:${phone}`} style={{ color: COLORS.grayMid, textDecoration: "none" }}>📞 {phone}</a>
                  </div>
                )}
                <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                  <button onClick={() => unlinkContact(c.id)} style={{
                    ...S.btn("ghost"), fontSize: 11, padding: "2px 10px",
                    color: COLORS.danger, borderColor: COLORS.danger + "55",
                  }}>Remove</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <button onClick={() => setPicker(true)} disabled={adding} style={{
        ...S.btn("ghost"), fontSize: 11, marginTop: 6,
      }}>
        <Plus size={12} style={{ verticalAlign: "middle", marginRight: 4 }} /> {adding ? "Adding…" : "Add Contact"}
      </button>
      {picker && (
        <RecordPickerModal
          kind="contact"
          title="Add Contacts to Organization"
          multi={true}
          excludeIds={existingIds}
          onPick={addContacts}
          onClose={() => setPicker(false)}
        />
      )}
    </DetailSection>
  );
}

function DealDetailModal({ deal, onClose, onChange, onToast }) {
  const [full, setFull] = useState(deal);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [contactModal, setContactModal] = useState(null);
  const [orgModalInner, setOrgModalInner] = useState(null);

  useEffect(() => {
    api(`/records/deal/${deal.id}`).then(r => {
      if (r) setFull({ ...deal, ...r });
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.id, refreshKey]);

  async function patchField(patch) {
    setFull(f => ({ ...f, ...patch }));
    const r = await api(`/records/deal/${deal.id}`, "PATCH", patch);
    if (onChange) onChange({ id: deal.id, ...patch });
    if (onToast && r) onToast(`Deal updated`);
  }

  if (loading) return <div style={S.modal}><Spinner /></div>;

  const dealAge = full.created_at ? Math.floor((Date.now() - new Date(full.created_at)) / 86400000) : 0;
  const lastAct = (full.activities || [])[0];
  const inactiveDays = lastAct?.created_at ? Math.floor((Date.now() - new Date(lastAct.created_at)) / 86400000) : dealAge;
  const contacts = full.contacts || (full.contact_name ? [{ id: full.contact_id, full_name: full.contact_name, email_work: full.email_work, phone_work: full.phone_work, job_title: full.contact_title }] : []);

  const right = (
    <>
      <DetailField label="Value"><span style={{ fontSize: 22, fontWeight: 800, color: COLORS.orange }}>{fmt$$(full.value)}</span></DetailField>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
        <DetailField label="ACV">{fmt$$(full.value)}</DetailField>
        <DetailField label="ARR">
          <InlineMoney value={full.arr} onSave={v => patchField({ arr: v })} />
        </DetailField>
      </div>
      <DetailField label="MRR">
        <InlineMoney value={full.mrr} onSave={v => patchField({ mrr: v })} />
      </DetailField>
      <DetailField label="Pipeline">
        <select value={full.pipeline || ""} onChange={e => patchField({ pipeline: e.target.value })}
          style={{ ...S.select, width: "100%", color: PIPELINE_COLORS[full.pipeline] || COLORS.white, fontWeight: 700 }}>
          <option value="">Unassigned</option>
          <option value="BTC">BTC</option><option value="BTB">BTB</option><option value="BTG">BTG</option>
        </select>
      </DetailField>
      <DetailField label="Stage">
        <select value={full.stage} onChange={e => patchField({ stage: e.target.value })} style={{ ...S.select, width: "100%" }}>
          {DEAL_STAGES.map(s => <option key={s}>{s}</option>)}
        </select>
      </DetailField>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
        <DetailField label="Deal Age">{dealAge}d</DetailField>
        <DetailField label="Inactive">{inactiveDays}d</DetailField>
      </div>
      <DetailField label="Close Date">{full.expected_close_date?.slice(0, 10) || "—"}</DetailField>
      <DetailField label="Owner">{full.owner || "—"}</DetailField>
      <DetailField label="Source Channel">
        <InlineText placeholder="LinkedIn Outbound..." value={full.source_channel_id || ""}
          onSave={v => patchField({ source_channel_id: v })} />
      </DetailField>

      <DealMetricsPanel dealId={deal.id} refreshKey={refreshKey} />
      <DealProductsPanel dealId={deal.id} />

      <DealOrgSection
        dealId={deal.id}
        org={full.organization}
        orgName={full.org_name}
        orgAddress={full.org_address}
        onOpenOrg={() => setOrgModalInner({ id: full.org_id, name: full.org_name })}
        onChange={() => setRefreshKey(k => k + 1)}
      />
      <DealContactsSection
        dealId={deal.id}
        contacts={contacts}
        onOpenContact={c => setContactModal(c)}
        onChange={() => setRefreshKey(k => k + 1)}
      />
      <EmailEngagement email={full.email_work} refreshTrigger={refreshKey} />
    </>
  );

  const left = (
    <>
      <AddActivityBar recordType="deal" recordId={deal.id} recordData={full}
        onActivityAdded={() => setRefreshKey(k => k + 1)} />
      <FSActivityTimeline recordType="deal" recordId={deal.id} initialActivities={full.activities} externalRefreshKey={refreshKey} recordCreatedAt={full.created_at} recordOwner={full.owner} />
    </>
  );

  return (
    <>
      <FullScreenCard title={full.title}
        subtitle={`${full.pipeline || "—"} · ${full.org_name || "No org"}`}
        badge={<span style={S.badge(full.stage === "Closed Won" ? COLORS.success : full.stage === "Closed Lost" ? COLORS.danger : COLORS.orange)}>{full.stage}</span>}
        onClose={onClose} left={left} right={right}
        deleteLabel="Deal"
        onDelete={async () => { await api(`/deals/${deal.id}`, "DELETE"); onClose(); onChange?.(); }} />
      {contactModal && <ContactDetailModal contact={contactModal} onClose={() => setContactModal(null)} />}
      {orgModalInner && <OrgDetailModal org={orgModalInner} onClose={() => setOrgModalInner(null)} />}
    </>
  );
}

function LeadDetailModal({ lead, onClose }) {
  const [full, setFull] = useState(lead);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [labels, setLabels] = useState([]);
  const [allLabels, setAllLabels] = useState([]);
  const [orgModal, setOrgModal] = useState(null);
  // bccCopied removed — BCC email feature not needed

  useEffect(() => {
    api(`/records/lead/${lead.id}`).then(r => { if (r) setFull({ ...lead, ...r }); setLoading(false); });
    api(`/leads/${lead.id}/labels`).then(r => { if (Array.isArray(r)) setLabels(r); });
    api("/labels").then(r => { if (Array.isArray(r)) setAllLabels(r); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id, refreshKey]);

  if (loading) return <div style={S.modal}><Spinner /></div>;

  const v = full.vertical || "RCFE";
  // bccEmail removed

  const vRows = (() => {
    switch (v) {
      case "RCFE": return fieldRows([["Beds", full.facility_capacity], ["Status", full.facility_status], ["Citations", full.citation_count], ["License", full.cdss_license_number]]);
      case "Hotel": return fieldRows([["Rooms", full.room_count], ["Floors", full.floor_count], ["Stars", full.star_rating], ["Brand", full.brand_flag]]);
      case "Hospital": return fieldRows([["Patients", full.patient_capacity], ["Floors", full.floor_count], ["CMS", full.cms_rating], ["VA", full.is_va ? "Yes" : null]]);
      case "Campus": return fieldRows([["Enrollment", full.enrollment], ["NCAA", full.ncaa_division], ["Type", full.campus_type]]);
      case "PropertyMgmt": return fieldRows([["Portfolio", full.portfolio_size], ["Units", full.unit_count], ["PM Score", full.pm_score], ["HOA", full.has_hoa ? "Yes" : null]]);
      default: return [];
    }
  })();

  // eslint-disable-next-line no-unused-vars
  async function toggleLabel(labelId) {
    const has = labels.some(l => l.id === labelId);
    if (has) {
      await api(`/leads/${lead.id}/labels/${labelId}`, "DELETE");
      setLabels(prev => prev.filter(l => l.id !== labelId));
    } else {
      await api(`/leads/${lead.id}/labels`, "POST", { label_id: labelId });
      const label = allLabels.find(l => l.id === labelId);
      if (label) setLabels(prev => [...prev, label]);
    }
  }

  const right = (
    <>
      {v === "RCFE" && full.cdss_license_number && (
        <div style={{ marginBottom: 10 }}>
          <CdssLicenseBadge license={full.cdss_license_number} vertical={v} />
        </div>
      )}
      {/* Owner + Source */}
      <DetailField label="Owner">
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 22, height: 22, borderRadius: "50%", background: COLORS.orange,
            color: "#fff", fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center",
            justifyContent: "center" }}>DJ</div>
          <span>{full.owner || "Drew Jennings"}</span>
        </div>
      </DetailField>
      <DetailField label="Lead ID">
        <span onClick={() => { navigator.clipboard.writeText(String(lead.id)); }}
          style={{ cursor: "pointer", fontSize: 12, color: COLORS.grayMid }}
          title="Click to copy">#{lead.id}</span>
      </DetailField>
      <DetailField label="Source"><span style={S.badge(COLORS.grayMid)}>{full.source_origin || "API"}</span></DetailField>
      <DetailField label="Status"><Badge status={full.status} /></DetailField>
      <DetailField label="Vertical"><span style={S.badge(COLORS.orange)}>{VERTICAL_ICONS[v]} {v}</span></DetailField>
      <DetailField label="Priority">{full.priority_score || 0}</DetailField>

      {/* Label — dropdown with Hot/Warm/Cold + create custom */}
      <DetailField label="Label">
        <LabelDropdown
          value={labels[0]?.name || ""}
          onChange={async (name) => {
            const current = labels[0];
            if (current) await api(`/leads/${lead.id}/labels/${current.id}`, "DELETE");
            if (!name) { setLabels([]); return; }
            const all = await api("/labels");
            const lbl = (all || []).find(l => l.name === name);
            if (lbl) {
              await api(`/leads/${lead.id}/labels`, "POST", { label_id: lbl.id });
              setLabels([lbl]);
            }
          }}
        />
      </DetailField>

      {/* Sequence enrollment */}
      {full.instantly_enrolled ? (
        <DetailField label="Sequence"><span style={S.badge(COLORS.success)}>Enrolled</span></DetailField>
      ) : (
        <DetailField label="Sequence">
          <button onClick={async () => {
            await api(`/leads/${lead.id}`, "PATCH", { instantly_enrolled: 1 });
            setFull(f => ({ ...f, instantly_enrolled: 1 }));
          }} style={{ ...S.btn("primary"), fontSize: 11, padding: "4px 10px" }}>
            <Zap size={12} style={{ marginRight: 4 }} /> Add to Sequence
          </button>
        </DetailField>
      )}

      {vRows.length > 0 && <DetailSection title="Details"><DetailGrid rows={vRows} /></DetailSection>}

      {/* Contact */}
      {full.contact_name ? (
        <DetailSection title="Person">
          <div style={{ background: COLORS.white, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 5, padding: 10 }}>
            <div style={{ fontWeight: 700, color: COLORS.textPrimary }}>{full.contact_name}</div>
            {full.contact_title && <div style={{ fontSize: 11, color: COLORS.grayMid }}>{full.contact_title}</div>}
            {full.email_work && <a href={`mailto:${full.email_work}`} style={{ fontSize: 12, color: COLORS.orange, display: "block", marginTop: 4 }}>✉ {full.email_work}</a>}
            {full.phone_work && <a href={`tel:${full.phone_work}`} style={{ fontSize: 12, color: COLORS.grayMid, display: "block" }}>📞 {full.phone_work}</a>}
          </div>
        </DetailSection>
      ) : (
        <DetailSection title="Person">
          <div style={{ fontSize: 12, color: COLORS.grayMid }}>No linked contact</div>
        </DetailSection>
      )}

      <DetailField label="Address">{[full.address, full.city, full.state, full.zip].filter(Boolean).join(", ") || "—"}</DetailField>

      <ContactOrgSection
        recordType="lead"
        recordId={lead.id}
        organization={full.organization || (full.org_id ? { id: full.org_id, name: full.org_name } : null)}
        onChange={() => setRefreshKey(k => k + 1)}
        onOpenOrg={(org) => setOrgModal(org)}
      />

      {(full.deals || []).length > 0 && (
        <DetailSection title={`Deals (${full.deals.length})`}>
          {full.deals.map(d => (
            <div key={d.id} style={{ background: COLORS.white, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 5, padding: "6px 10px", fontSize: 12, marginBottom: 4 }}>
              <span style={{ fontWeight: 600 }}>{d.title}</span> · {fmt$$(d.value)} · {d.stage}
            </div>
          ))}
        </DetailSection>
      )}
      <EmailEngagement email={full.email_work} refreshTrigger={refreshKey} />
    </>
  );

  const left = (
    <>
      <AddActivityBar recordType="lead" recordId={lead.id} recordData={full}
        onActivityAdded={() => setRefreshKey(k => k + 1)} />
      <FSActivityTimeline recordType="lead" recordId={lead.id} initialActivities={full.activities} externalRefreshKey={refreshKey} recordCreatedAt={full.created_at} recordOwner={full.owner} />
    </>
  );

  return (
    <>
      <FullScreenCard title={full.org_name || full.title}
        subtitle={`Lead #${lead.id} · ${v} · ${[full.city, full.state].filter(Boolean).join(", ")}`}
        badge={<Badge status={full.status} />}
        onClose={onClose} left={left} right={right}
        deleteLabel="Lead"
        onDelete={async () => { await api(`/leads/${lead.id}`, "DELETE"); onClose(); }} />
      {orgModal && <OrgDetailModal org={orgModal} onClose={() => setOrgModal(null)} />}
    </>
  );
}

function OrgDetailModal({ org, onClose }) {
  const [full, setFull] = useState(org);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [contactModal, setContactModal] = useState(null);

  useEffect(() => {
    api(`/records/org/${org.id}`).then(r => { if (r) setFull({ ...org, ...r }); setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org.id, refreshKey]);

  if (loading) return <div style={S.modal}><Spinner /></div>;

  const contacts = full.contacts || [];
  const v = full.vertical || "";
  const vRows = (() => {
    switch (v) {
      case "RCFE": return fieldRows([["Beds", full.facility_capacity], ["Status", full.facility_status], ["Citations", full.citation_count], ["License", full.cdss_license_number]]);
      case "Hotel": return fieldRows([["Rooms", full.room_count], ["Stars", full.star_rating], ["Floors", full.floor_count], ["Brand", full.brand_flag]]);
      case "Hospital": return fieldRows([["Patients", full.patient_capacity], ["CMS", full.cms_rating], ["Floors", full.floor_count], ["VA", full.is_va ? "Yes" : null]]);
      case "Campus": return fieldRows([["Enrollment", full.enrollment], ["NCAA", full.ncaa_division], ["Type", full.campus_type]]);
      case "PropertyMgmt": return fieldRows([["Portfolio", full.portfolio_size], ["Units", full.unit_count], ["PM Score", full.pm_score], ["HOA", full.has_hoa ? "Yes" : null]]);
      default: return [];
    }
  })();
  const openDealsValue = (full.deals || []).filter(d => d.stage !== "Closed Lost" && d.stage !== "Closed Won").reduce((a, d) => a + (d.value || 0), 0);

  const right = (
    <>
      {full.cdss_license_number && (
        <div style={{ marginBottom: 10 }}>
          <CdssLicenseBadge license={full.cdss_license_number} vertical={v} />
        </div>
      )}
      <DetailField label="Vertical"><span style={S.badge(COLORS.orange)}>{VERTICAL_ICONS[v]} {v}</span></DetailField>
      {full.phone && <DetailField label="Phone"><a href={`tel:${full.phone}`} style={{ color: COLORS.orange }}>{full.phone}</a></DetailField>}
      {full.website && <DetailField label="Website"><a href={full.website.startsWith("http") ? full.website : `https://${full.website}`} target="_blank" rel="noreferrer" style={{ color: COLORS.orange }}>{full.website}</a></DetailField>}
      <DetailField label="Address">{[full.address, full.city, full.state, full.zip].filter(Boolean).join(", ") || "—"}</DetailField>
      <DetailField label="Priority">{full.priority_score || 0}</DetailField>
      {openDealsValue > 0 && <DetailField label="Open Deals Value"><span style={{ color: COLORS.orange, fontWeight: 700 }}>{fmt$$(openDealsValue)}</span></DetailField>}
      {full.instantly_enrolled ? <DetailField label="Instantly"><span style={S.badge(COLORS.success)}>Enrolled</span> {full.enrolled_at?.slice(0, 10)}</DetailField> : null}
      {vRows.length > 0 && <DetailSection title="Details"><DetailGrid rows={vRows} /></DetailSection>}
      <OrgContactsSection
        orgId={org.id}
        contacts={contacts}
        onChange={() => setRefreshKey(k => k + 1)}
        onOpenContact={(c) => setContactModal(c)}
      />
      {(full.deals || []).length > 0 && (
        <DetailSection title={`Deals (${full.deals.length})`}>
          {full.deals.map(d => (
            <div key={d.id} style={{ background: COLORS.white, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 5, padding: "6px 10px", fontSize: 12, marginBottom: 4 }}>
              <span style={{ fontWeight: 600 }}>{d.title}</span> · {fmt$$(d.value)} · <span style={S.badge(d.stage === "Closed Won" ? COLORS.success : COLORS.orange)}>{d.stage}</span>
            </div>
          ))}
        </DetailSection>
      )}
      {(full.leads || []).length > 0 && (
        <DetailSection title={`Leads (${full.leads.length})`}>
          {full.leads.map(l => (
            <div key={l.id} style={{ background: COLORS.white, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 5, padding: "6px 10px", fontSize: 12, marginBottom: 4 }}>
              {l.title} · <Badge status={l.status} />
            </div>
          ))}
        </DetailSection>
      )}
      <EmailEngagement email={contacts.length > 0 ? contacts[0].email_work : null} refreshTrigger={refreshKey} />
    </>
  );

  const left = (
    <>
      <AddActivityBar recordType="org" recordId={org.id} recordData={full}
        onActivityAdded={() => setRefreshKey(k => k + 1)} />
      <FSActivityTimeline recordType="org" recordId={org.id} initialActivities={full.activities} externalRefreshKey={refreshKey} recordCreatedAt={full.created_at} recordOwner={full.owner} />
    </>
  );

  return (
    <>
      <FullScreenCard title={full.name}
        subtitle={[full.address, full.city, full.state].filter(Boolean).join(", ")}
        badge={v && <span style={S.badge(COLORS.orange)}>{VERTICAL_ICONS[v]} {v}</span>}
        onClose={onClose} left={left} right={right}
        deleteLabel="Organization"
        onDelete={async () => { await api(`/orgs/${org.id}`, "DELETE"); onClose(); }} />
      {contactModal && <ContactDetailModal contact={contactModal} onClose={() => setContactModal(null)} />}
    </>
  );
}

function ContactDetailModal({ contact, onClose, onEnrich, enriching }) {
  const [full, setFull] = useState(contact);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [orgModal, setOrgModal] = useState(null);

  useEffect(() => {
    api(`/records/contact/${contact.id}`).then(r => { if (r) setFull({ ...contact, ...r }); setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact.id, refreshKey]);

  if (loading) return <div style={S.modal}><Spinner /></div>;

  const enriched = !!(full.email_work || full.email_home || full.phone_work || full.phone_mobile);

  const right = (
    <>
      {!enriched && (
        <div style={{
          background: "#FFC10722", border: `1px solid ${COLORS.warning}`,
          color: COLORS.warning, borderRadius: 6, padding: "10px 14px",
          fontSize: 12, marginBottom: 14,
        }}>
          No enriched data yet
          {full.org_id && onEnrich && (
            <button onClick={() => onEnrich(full.org_id)} disabled={enriching}
              style={{ ...S.btn("primary"), fontSize: 10, padding: "4px 10px", marginLeft: 8 }}>
              {enriching ? "Enriching..." : "Enrich"}
            </button>
          )}
        </div>
      )}
      <DetailField label="Title">{full.job_title || full.contact_title}</DetailField>
      {full.email_work && <DetailField label="Email (work)"><a href={`mailto:${full.email_work}`} style={{ color: COLORS.orange }}>{full.email_work}</a></DetailField>}
      {full.email_home && <DetailField label="Email (home)"><a href={`mailto:${full.email_home}`} style={{ color: COLORS.orange }}>{full.email_home}</a></DetailField>}
      {full.phone_work && <DetailField label="Phone (work)"><a href={`tel:${full.phone_work}`} style={{ color: COLORS.orange }}>{full.phone_work}</a></DetailField>}
      {full.phone_mobile && <DetailField label="Phone (mobile)"><a href={`tel:${full.phone_mobile}`} style={{ color: COLORS.orange }}>{full.phone_mobile}</a></DetailField>}
      {full.linkedin_url && <DetailField label="LinkedIn"><a href={full.linkedin_url} target="_blank" rel="noreferrer" style={{ color: COLORS.orange }}>View Profile</a></DetailField>}
      <DetailField label="Source">{full.source}</DetailField>
      <DetailField label="Pipeline">
        <span style={S.badge(full.pipeline === "drewos" ? CONTACT_BOOK_COLORS.drewos : CONTACT_BOOK_COLORS.outpost)}>
          {full.pipeline === "drewos" ? "DrewOS" : "Outpost"}
        </span>
        {full.avatar_vertical && (
          <span style={{ marginLeft: 8, fontSize: 11, color: COLORS.grayMid, fontFamily: "monospace" }}>
            {full.avatar_vertical}
          </span>
        )}
      </DetailField>
      <DetailField label="Marketing Status">
        <ContactMarketingToggle contact={full} />
      </DetailField>
      <DetailField label="Double Opt-in">
        <span style={S.badge(full.double_optin ? COLORS.success : COLORS.grayMid)}>
          {full.double_optin ? "Confirmed" : "Pending"}
        </span>
      </DetailField>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
        <DetailField label="Created">
          {full.created_at ? new Date(full.created_at).toLocaleDateString(undefined, {
            year: "numeric", month: "long", day: "numeric",
          }) : "—"}
        </DetailField>
        <DetailField label="Updated">
          {full.updated_at ? new Date(full.updated_at).toLocaleDateString(undefined, {
            year: "numeric", month: "long", day: "numeric",
          }) : "—"}
        </DetailField>
      </div>
      <FinancialOverview contact={full} />
      <ContactOrgSection
        recordType="contact"
        recordId={contact.id}
        organization={full.organization || (full.org_id ? { id: full.org_id, name: full.org_name } : null)}
        onChange={() => setRefreshKey(k => k + 1)}
        onOpenOrg={(org) => setOrgModal(org)}
      />
      {(full.leads || []).length > 0 && (
        <DetailSection title={`Leads (${full.leads.length})`}>
          {full.leads.map(l => (
            <div key={l.id} style={{ background: COLORS.white, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 5, padding: "6px 10px", fontSize: 12, marginBottom: 4 }}>
              {l.title || l.org_name} · <Badge status={l.status} />
            </div>
          ))}
        </DetailSection>
      )}
      {(full.deals || []).length > 0 && (
        <DetailSection title={`Deals (${full.deals.length})`}>
          {full.deals.map(d => (
            <div key={d.id} style={{ background: COLORS.white, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 5, padding: "6px 10px", fontSize: 12, marginBottom: 4 }}>
              <span style={{ fontWeight: 600 }}>{d.title}</span> · {fmt$$(d.value)} · {d.stage}
            </div>
          ))}
        </DetailSection>
      )}
      <EmailEngagement email={full.email_work || full.email_home} refreshTrigger={refreshKey} />
    </>
  );

  const left = (
    <>
      <AddActivityBar recordType="contact" recordId={contact.id} recordData={full}
        onActivityAdded={() => setRefreshKey(k => k + 1)} />
      <FSActivityTimeline recordType="contact" recordId={contact.id} initialActivities={full.activities} externalRefreshKey={refreshKey} recordCreatedAt={full.created_at} recordOwner={full.owner} />
    </>
  );

  return (
    <>
      <FullScreenCard title={full.full_name}
        subtitle={full.org_name || full.job_title || ""}
        onClose={onClose} left={left} right={right}
        deleteLabel="Contact"
        onDelete={async () => { await api(`/contacts/${contact.id}`, "DELETE"); onClose(); }} />
      {orgModal && <OrgDetailModal org={orgModal} onClose={() => setOrgModal(null)} />}
    </>
  );
}

// ─── LEADS TABLE ─────────────────────────────────────────────────────────────
function LeadsTable() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vertical, setVertical] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ label: "", enrolled: "", source: "", sort: "" });
  const [showNew, setShowNew] = useState(false);
  const [toast, setToast] = useState(null);
  const PAGE = 50;

  function updateFilter(k, v) { setFilters(f => ({ ...f, [k]: v })); setPage(0); }
  function clearFilters() { setFilters({ label: "", enrolled: "", source: "", sort: "" }); setVertical(""); setStatus(""); setPage(0); }

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: PAGE, offset: page * PAGE });
    if (vertical) params.set("vertical", vertical);
    if (status) params.set("status", status);
    if (filters.label) params.set("label", filters.label);
    if (filters.enrolled) params.set("enrolled", filters.enrolled);
    if (filters.source) params.set("source", filters.source);
    if (filters.sort) params.set("sort", filters.sort);
    api(`/leads?${params}`).then(r => {
      // /leads now returns {data, total, limit, offset} — handle both shapes defensively
      const arr = Array.isArray(r) ? r : (r && Array.isArray(r.data) ? r.data : []);
      setLeads(arr);
      setLoading(false);
    });
  }, [vertical, status, page, filters]);

  useEffect(() => { load(); }, [load]);

  const filtered = leads.filter(l =>
    !search || (l.title || "").toLowerCase().includes(search.toLowerCase()) ||
    (l.org_name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <select value={vertical} onChange={e => { setVertical(e.target.value); setPage(0); }} style={S.select}>
          <option value="">All Verticals</option>
          {VERTICALS.map(v => <option key={v}>{v}</option>)}
        </select>
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(0); }} style={S.select}>
          <option value="">All Statuses</option>
          {LEAD_STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        <input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...S.input, width: 200 }} />
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button onClick={() => setShowNew(true)} style={S.btn("primary")}>
            <Plus size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />New Lead
          </button>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} style={S.btn("ghost")} disabled={page === 0}>← Prev</button>
          <span style={{ padding: "7px 12px", fontSize: 12, color: COLORS.grayMid }}>Page {page + 1}</span>
          <button onClick={() => setPage(p => p + 1)} style={S.btn("ghost")} disabled={filtered.length < PAGE}>Next →</button>
        </div>
      </div>

      <FilterBar
        filters={{ ...filters, vertical }}
        onFilter={(k, v) => k === "vertical" ? (setVertical(v), setPage(0)) : updateFilter(k, v)}
        onClear={clearFilters}
        showVertical={false}
        showEnrolled
        showSource
      />
      {loading ? <Spinner /> : (
        <div style={S.card}>
          <table style={S.table}>
            <thead>
              <tr>
                {["Organization","Vertical","Status","Beds","Priority","DM1","DM2","Contact"].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(lead => (
                <tr key={lead.id} style={{ cursor: "pointer" }}
                  onClick={() => setSelected(lead)}
                  onMouseEnter={e => e.currentTarget.style.background = `${COLORS.orange}11`}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={S.td}><div style={{ fontWeight: 600 }}>{lead.org_name || lead.title}</div></td>
                  <td style={S.td}>{VERTICAL_ICONS[lead.vertical]} {lead.vertical}</td>
                  <td style={S.td}><Badge status={lead.status} /></td>
                  <td style={S.td}>{lead.facility_capacity || "—"}</td>
                  <td style={S.td}>{lead.priority_score || 0}</td>
                  <td style={S.td}><span style={{ color: lead.dm1_sent ? COLORS.success : COLORS.grayDark }}>{lead.dm1_sent ? "✓" : "○"}</span></td>
                  <td style={S.td}><span style={{ color: lead.dm2_sent ? COLORS.success : COLORS.grayDark }}>{lead.dm2_sent ? "✓" : "○"}</span></td>
                  <td style={S.td}>{lead.contact_name || <span style={{ color: COLORS.grayDark }}>—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && <LeadDetailModal lead={selected} onClose={() => setSelected(null)} />}
      {showNew && (
        <NewLeadModal
          defaultVertical={vertical}
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            setToast({ type: "success", msg: "Lead created" });
            load();
          }}
        />
      )}
      {toast && (
        <div style={{
          position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 9999,
          background: toast.type === "success" ? COLORS.success : COLORS.danger,
          color: "#FFFFFF", padding: "10px 24px", borderRadius: 8,
          fontSize: 13, fontWeight: 600, boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        }}>{toast.msg}</div>
      )}
    </div>
  );
}

// ─── CONTACTS ────────────────────────────────────────────────────────────────
function contactIsEnriched(c) {
  return !!(c.email_work || c.email_home || c.phone_work || c.phone_mobile);
}

function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailOnly, setEmailOnly] = useState(true);
  const [book, setBook] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [enrichingOrg, setEnrichingOrg] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [toast, setToast] = useState(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const fetchContacts = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: 200 });
    if (emailOnly) params.set("has_email", "true");
    if (book !== "all") params.set("pipeline", book);
    return api(`/contacts?${params}`).then(r => {
      setContacts(r || []);
      setLoading(false);
    });
  }, [emailOnly, book]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const filtered = contacts
    .filter(c =>
      !search || (c.full_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (c.email_work || "").toLowerCase().includes(search.toLowerCase())
    )
    .slice()
    .sort((a, b) => Number(contactIsEnriched(b)) - Number(contactIsEnriched(a)));

  async function enrichOrg(orgId) {
    if (!orgId || enrichingOrg) return;
    setEnrichingOrg(orgId);
    await api(`/orgs/enrich/${orgId}`);
    setEnrichingOrg(null);
    fetchContacts();
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
        {CONTACT_BOOKS.map(b => {
          const active = book === b.id;
          const c = CONTACT_BOOK_COLORS[b.id];
          return (
            <button key={b.id} onClick={() => setBook(b.id)}
              style={{
                ...S.btn(active ? "primary" : "ghost"),
                fontSize: 13,
                background: active && c ? c : undefined,
                borderColor: !active && c ? `${c}66` : undefined,
                color: !active && c ? c : undefined,
              }}
              title={`Show ${b.label} contacts`}>
              {b.label}
            </button>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <input placeholder="Search contacts..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...S.input, width: 220 }} />
        <button onClick={() => setEmailOnly(true)}
          style={S.btn(emailOnly ? "primary" : "ghost")}>
          {emailOnly ? "✓ " : ""}Enriched Only
        </button>
        <button onClick={() => setEmailOnly(false)}
          style={S.btn(!emailOnly ? "primary" : "ghost")}>
          {!emailOnly ? "✓ " : ""}All Contacts
        </button>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 12, color: COLORS.grayMid }}>
            {fmtNum(filtered.length)} contacts · enriched first
          </div>
          <button onClick={() => setShowNew(true)} style={S.btn("primary")}>
            <Plus size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />New Contact
          </button>
        </div>
      </div>

      {loading ? <Spinner /> : (
        <div style={S.card}>
          <table style={S.table}>
            <thead>
              <tr>
                {["Name","Organization","Email","Phone","Source","Marketing","Actions"].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const enriched = contactIsEnriched(c);
                return (
                  <tr key={c.id} style={{ cursor: "pointer", opacity: enriched ? 1 : 0.7 }}
                    onClick={() => setSelected(c)}
                    onMouseEnter={e => e.currentTarget.style.background = `${COLORS.orange}11`}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={S.td}>
                      <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                        {c.full_name}
                        {c.pipeline === "drewos" && (
                          <span style={{
                            background: CONTACT_BOOK_COLORS.drewos,
                            color: "#0B0F1A",
                            fontSize: 9, fontWeight: 700, letterSpacing: 0.8,
                            padding: "2px 6px", borderRadius: 3, textTransform: "uppercase",
                          }}>DrewOS</span>
                        )}
                      </div>
                    </td>
                    <td style={{ ...S.td, color: COLORS.grayMid }}>{c.org_name || "—"}</td>
                    <td style={S.td} onClick={e => e.stopPropagation()}>
                      {(c.email_work || c.email_home) ? (
                        <a href={`mailto:${c.email_work || c.email_home}`}
                          style={{ color: COLORS.orange, textDecoration: "none" }}>
                          {c.email_work || c.email_home}
                        </a>
                      ) : <span style={{ color: COLORS.grayDark }}>—</span>}
                    </td>
                    <td style={{ ...S.td, color: COLORS.grayMid }}>{c.phone_work || c.phone_mobile || "—"}</td>
                    <td style={S.td}>
                      <span style={S.badge(c.source === "Enriched" ? COLORS.success : COLORS.grayMid)}>
                        {c.source}
                      </span>
                    </td>
                    <td style={S.td}>
                      <span style={{ color: c.accepts_marketing ? COLORS.success : COLORS.grayDark }}>
                        {c.accepts_marketing ? "✓" : "—"}
                      </span>
                    </td>
                    <td style={S.td} onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => enrichOrg(c.org_id)}
                        disabled={!c.org_id || enrichingOrg === c.org_id}
                        title={c.org_id ? "Run enrichment on linked org" : "No linked org"}
                        style={{
                          ...S.btn("ghost"), fontSize: 11, padding: "4px 10px",
                          opacity: c.org_id ? 1 : 0.4,
                          cursor: c.org_id ? "pointer" : "not-allowed",
                        }}>
                        {enrichingOrg === c.org_id ? "⏳ Enriching…" : "✨ Enrich"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <ContactDetailModal
          contact={selected}
          onClose={() => setSelected(null)}
          onEnrich={enrichOrg}
          enriching={enrichingOrg === selected.org_id}
        />
      )}
      {showNew && (
        <NewContactModal
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            setToast({ type: "success", msg: "Contact created" });
            fetchContacts();
          }}
        />
      )}
      {toast && (
        <div style={{
          position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 9999,
          background: toast.type === "success" ? COLORS.success : COLORS.danger,
          color: "#FFFFFF", padding: "10px 24px", borderRadius: 8,
          fontSize: 13, fontWeight: 600, boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        }}>{toast.msg}</div>
      )}
    </div>
  );
}

// ─── ORGANIZATIONS ───────────────────────────────────────────────────────────
function Organizations() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minCap, setMinCap] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: 100, vertical: "RCFE", status: "LICENSED" });
    if (minCap) params.set("min_capacity", minCap);
    api(`/orgs?${params}`).then(r => { setOrgs(r || []); setLoading(false); });
  }, [minCap]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const filtered = orgs.filter(o =>
    !search || (o.name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <input placeholder="Search orgs..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...S.input, width: 220 }} />
        <select value={minCap} onChange={e => setMinCap(e.target.value)} style={S.select}>
          <option value="">All Sizes</option>
          <option value="6">6+ beds</option>
          <option value="25">25+ beds</option>
          <option value="50">50+ beds</option>
          <option value="100">100+ beds</option>
        </select>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 12, color: COLORS.grayMid }}>
            {fmtNum(filtered.length)} orgs
          </div>
          <button onClick={() => setShowNew(true)} style={S.btn("primary")}>
            <Plus size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />New Organization
          </button>
        </div>
      </div>

      {loading ? <Spinner /> : (
        <div style={S.card}>
          <table style={S.table}>
            <thead>
              <tr>
                {["Organization","Address","Beds","Status","Priority","Citations"].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(o => (
                <tr key={o.id} style={{ cursor: "pointer" }}
                  onClick={() => setSelected(o)}
                  onMouseEnter={e => e.currentTarget.style.background = `${COLORS.orange}11`}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={S.td}><div style={{ fontWeight: 600 }}>{o.name}</div></td>
                  <td style={{ ...S.td, fontSize: 11, color: COLORS.grayMid }}>{o.address}{o.city ? `, ${o.city}` : ""}</td>
                  <td style={S.td}>{o.facility_capacity || "—"}</td>
                  <td style={S.td}>
                    <span style={S.badge(o.facility_status === "LICENSED" ? COLORS.success : COLORS.grayMid)}>
                      {o.facility_status}
                    </span>
                  </td>
                  <td style={S.td}>{o.priority_score || 0}</td>
                  <td style={S.td}>
                    <span style={{ color: o.citation_count > 0 ? COLORS.danger : COLORS.grayDark }}>
                      {o.citation_count || 0}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && <OrgDetailModal org={selected} onClose={() => setSelected(null)} />}
      {showNew && (
        <NewOrgModal
          defaultVertical="RCFE"
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            setToast({ type: "success", msg: "Organization created" });
            load();
          }}
        />
      )}
      {toast && (
        <div style={{
          position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 9999,
          background: toast.type === "success" ? COLORS.success : COLORS.danger,
          color: "#FFFFFF", padding: "10px 24px", borderRadius: 8,
          fontSize: 13, fontWeight: 600, boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        }}>{toast.msg}</div>
      )}
    </div>
  );
}

// ─── BOOKING PAGE ────────────────────────────────────────────────────────────
// Parse the appointment-type slug from the booking hash synchronously
// (runs at module evaluation when BookingPage mounts). Supports both
// `#booking/<slug>` and `#booking?type=<slug>` formats so external
// brand wrappers (e.g. drewos.ai/book) can drive auto-selection.
function _readBookingSlugFromHash() {
  if (typeof window === "undefined") return null;
  const h = window.location.hash || "";
  const m = h.match(/#booking\/([a-z0-9-]+)/)
    || h.match(/[?&]type=([a-z0-9-]+)/i);
  return m ? m[1] : null;
}

function BookingPage({ isPublic = false }) {
  // Capture the URL-driven slug once on mount so the render path can
  // skip the type-picker entirely when the user landed via a direct
  // appointment link — even before types finish loading async.
  const [initialSlug] = useState(_readBookingSlugFromHash);
  // ── view state ──
  const [view, setView] = useState("types"); // types | calendar | form | confirmed | reschedule | admin
  const [apptTypes, setApptTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [notes, setNotes] = useState("");
  const [booking, setBooking] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  // reschedule
  const [rescheduleId, setRescheduleId] = useState(null);
  const [rescheduleInfo, setRescheduleInfo] = useState(null);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);

  // admin
  const [adminMode, setAdminMode] = useState(false);
  const [adminTab, setAdminTab] = useState("settings");
  const [adminEmail, setAdminEmail] = useState("drew@outpostprep.com");
  const [settingsForm, setSettingsForm] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [adminTypes, setAdminTypes] = useState([]);
  const [newType, setNewType] = useState({ name: "", slug: "", duration_minutes: 30, description: "" });
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [reminderMsg, setReminderMsg] = useState("");

  // ── helpers ──
  const fmtDate = useCallback((iso) => {
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return d.toLocaleString("en-US", {
        weekday: "long", year: "numeric", month: "long", day: "numeric",
        hour: "numeric", minute: "2-digit", timeZoneName: "short",
      });
    } catch { return iso; }
  }, []);

  const fmtShortDate = useCallback((iso) => new Date(iso).toLocaleString("en-US", {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  }), []);

  // ── load appointment types on mount ──
  useEffect(() => {
    api("/booking/types").then(r => {
      const types = (r || []).filter(t => t.active);
      setApptTypes(types);
      setLoading(false);

      // check hash for direct type slug. Two formats supported:
      //   #booking/some-slug   (path-style, original)
      //   #booking?type=slug   (query-style, used by the drewos.ai/book
      //                          iframe so the URL can carry tracking params)
      const hash = window.location.hash;
      const slugMatch = hash.match(/#booking\/([a-z0-9-]+)/)
        || hash.match(/[?&]type=([a-z0-9-]+)/i);
      if (slugMatch) {
        const matchedType = types.find(t => t.slug === slugMatch[1]);
        if (matchedType) {
          setSelectedType(matchedType);
          setView("calendar");
        }
      } else if (types.length === 1) {
        setSelectedType(types[0]);
        setView("calendar");
      }

      // check hash for reschedule: #booking?reschedule&booking_id=xxx or reschedule?booking_id=xxx
      const rescheduleMatch = hash.match(/reschedule[?&]booking_id=([^&]+)/);
      if (rescheduleMatch) {
        setRescheduleId(rescheduleMatch[1]);
        setView("reschedule");
      }
    });
  }, []);

  // ── load slots when type selected ──
  useEffect(() => {
    if (!selectedType) return;
    setSlotsLoading(true);
    setSlots([]);
    setSelectedDay(null);
    setSelectedSlot(null);
    api(`/booking/slots?type=${selectedType.slug}&days_ahead=21`).then(r => {
      setSlots(r?.slots || []);
      setSlotsLoading(false);
    });
  }, [selectedType]);

  // ── load reschedule info ──
  useEffect(() => {
    if (!rescheduleId) return;
    setRescheduleLoading(true);
    api(`/booking/reschedule-info/${rescheduleId}`).then(r => {
      setRescheduleInfo(r);
      setRescheduleLoading(false);
      if (r && r.type_slug) {
        api("/booking/types").then(types => {
          const t = (types || []).find(x => x.slug === r.type_slug);
          if (t) {
            setSelectedType(t);
          }
        });
      }
    });
  }, [rescheduleId]);

  // ── grouped days for slot picker ──
  const days = useMemo(() => {
    const d = {};
    slots.forEach(s => {
      const day = s.start.slice(0, 10);
      if (!d[day]) d[day] = [];
      d[day].push(s);
    });
    return d;
  }, [slots]);
  const dayKeys = Object.keys(days);

  // ── booking submit ──
  async function submitBooking() {
    if (!selectedSlot || !name.trim() || !email.trim()) return;
    setBooking(true);
    const result = await api("/webhooks/calendar-booking", "POST", {
      start: selectedSlot.start,
      name: name.trim(),
      email: email.trim(),
      company: company.trim() || null,
      notes: notes.trim() || null,
      type_slug: selectedType?.slug || null,
    });
    setBooking(false);
    if (result) {
      setConfirmed(result);
      setView("confirmed");
    }
  }

  // ── reschedule submit ──
  async function submitReschedule() {
    if (!selectedSlot || !rescheduleId) return;
    setBooking(true);
    const result = await api("/booking/reschedule", "POST", {
      booking_id: rescheduleId,
      new_start: selectedSlot.start,
    });
    setBooking(false);
    if (result) {
      setConfirmed(result);
      setView("confirmed");
    }
  }

  function resetAll() {
    setView("types");
    setSelectedType(null);
    setSelectedSlot(null);
    setSelectedDay(null);
    setName("");
    setEmail("");
    setCompany("");
    setNotes("");
    setConfirmed(null);
    setRescheduleId(null);
    setRescheduleInfo(null);
    // reload types in case only 1
    api("/booking/types").then(r => {
      const types = (r || []).filter(t => t.active);
      setApptTypes(types);
      if (types.length === 1) {
        setSelectedType(types[0]);
        setView("calendar");
      }
    });
  }

  // ── admin loaders ──
  function loadSettings() {
    api("/booking/settings").then(r => { setSettingsForm(r ? { ...r } : null); });
  }
  function loadAdminTypes() {
    api("/booking/types").then(r => setAdminTypes(r || []));
  }
  function loadUpcoming() {
    api(`/booking/upcoming?admin_email=${encodeURIComponent(adminEmail)}`).then(r => setUpcomingBookings(r || []));
  }

  // ─────────────── SLOT PICKER (shared between calendar + reschedule) ───────
  function SlotPicker({ onNext }) {
    return (
      <div>
        {slotsLoading ? <Spinner /> : (
          <>
            {/* Day selector */}
            <div style={{ display: "flex", gap: 6, marginBottom: 16, overflowX: "auto", paddingBottom: 4 }}>
              {dayKeys.map(day => {
                const d = new Date(day + "T12:00:00");
                const active = selectedDay === day;
                return (
                  <button key={day} onClick={() => { setSelectedDay(day); setSelectedSlot(null); }}
                    style={{
                      ...S.btn(active ? "primary" : "ghost"),
                      fontSize: 12, padding: "8px 14px", flexShrink: 0,
                      display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
                    }}>
                    <span style={{ fontSize: 10, opacity: 0.7 }}>{d.toLocaleDateString("en-US", { weekday: "short" })}</span>
                    <span style={{ fontWeight: 700 }}>{d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                  </button>
                );
              })}
            </div>

            {/* Time grid */}
            {selectedDay && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 20 }}>
                {(days[selectedDay] || []).map(slot => {
                  const t = new Date(slot.start);
                  const active = selectedSlot?.start === slot.start;
                  return (
                    <button key={slot.start} onClick={() => setSelectedSlot(slot)}
                      style={{
                        ...S.btn(active ? "primary" : "ghost"),
                        fontSize: 13, padding: "10px 12px",
                      }}>
                      {slot.display || t.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                    </button>
                  );
                })}
              </div>
            )}

            {!selectedDay && dayKeys.length > 0 && (
              <div style={{ textAlign: "center", color: COLORS.grayMid, padding: 30, fontSize: 13 }}>
                Select a day above to see available times
              </div>
            )}

            {dayKeys.length === 0 && !slotsLoading && (
              <div style={{ textAlign: "center", color: COLORS.grayMid, padding: 30, fontSize: 13 }}>
                No available slots at this time. Please check back later.
              </div>
            )}

            {/* Next button */}
            {selectedSlot && (
              <div style={{ textAlign: "right", marginTop: 8 }}>
                <button onClick={onNext} style={{ ...S.btn("primary"), fontSize: 14, padding: "10px 24px" }}>
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ─────────────── ADMIN PANEL ──────────────────────────────────────────────
  function AdminPanel() {
    const tabStyle = (t) => ({
      ...S.btn(adminTab === t ? "primary" : "ghost"),
      fontSize: 12, borderRadius: "5px 5px 0 0",
    });

    // Time options for dropdowns
    const timeOpts = [];
    for (let h = 6; h <= 20; h++) {
      timeOpts.push({ val: h, label: `${h > 12 ? h - 12 : h}:00 ${h >= 12 ? "PM" : "AM"}` });
      if (h < 20) timeOpts.push({ val: h + 0.5, label: `${h > 12 ? h - 12 : h}:30 ${h >= 12 ? "PM" : "AM"}` });
    }

    const dayNames = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

    return (
      <div style={{ maxWidth: 800, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 800 }}>Booking Admin</div>
          <button onClick={() => { setAdminMode(false); setView("types"); }} style={S.btn("ghost")}>Back to Booking</button>
        </div>

        {/* Admin email */}
        <div style={{ ...S.card, marginBottom: 16, display: "flex", gap: 10, alignItems: "center" }}>
          <label style={{ fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>Admin Email:</label>
          <input value={adminEmail} onChange={e => setAdminEmail(e.target.value)}
            style={{ ...S.input, maxWidth: 280 }} />
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 4, marginBottom: 0 }}>
          <button style={tabStyle("settings")} onClick={() => { setAdminTab("settings"); loadSettings(); }}>Settings</button>
          <button style={tabStyle("types")} onClick={() => { setAdminTab("types"); loadAdminTypes(); }}>Appointment Types</button>
          <button style={tabStyle("upcoming")} onClick={() => { setAdminTab("upcoming"); loadUpcoming(); }}>Upcoming Bookings</button>
        </div>

        <div style={{ ...S.card, borderTopLeftRadius: 0 }}>
          {/* ── Settings Tab ── */}
          {adminTab === "settings" && (
            <div>
              {!settingsForm ? <Spinner /> : (
                <div style={{ display: "grid", gap: 16 }}>
                  {/* Available days */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Available Days</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {dayNames.map((dn, idx) => {
                        const dayNum = idx + 1;
                        const checked = (settingsForm.available_days || []).includes(dayNum);
                        return (
                          <label key={dn} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, cursor: "pointer" }}>
                            <input type="checkbox" checked={checked}
                              onChange={() => {
                                const cur = settingsForm.available_days || [];
                                setSettingsForm({
                                  ...settingsForm,
                                  available_days: checked ? cur.filter(x => x !== dayNum) : [...cur, dayNum].sort(),
                                });
                              }} />
                            {dn}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Start / End hour */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Start Hour</div>
                      <select style={S.select} value={settingsForm.start_hour || 8}
                        onChange={e => setSettingsForm({ ...settingsForm, start_hour: Number(e.target.value) })}>
                        {timeOpts.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>End Hour</div>
                      <select style={S.select} value={settingsForm.end_hour || 17}
                        onChange={e => setSettingsForm({ ...settingsForm, end_hour: Number(e.target.value) })}>
                        {timeOpts.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Min notice / Max advance / Buffer */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Min Notice</div>
                      <select style={S.select} value={settingsForm.min_notice_hours || 2}
                        onChange={e => setSettingsForm({ ...settingsForm, min_notice_hours: Number(e.target.value) })}>
                        {[1, 2, 4, 8, 24].map(h => <option key={h} value={h}>{h}h</option>)}
                      </select>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Max Advance (days)</div>
                      <select style={S.select} value={settingsForm.max_advance_days || 14}
                        onChange={e => setSettingsForm({ ...settingsForm, max_advance_days: Number(e.target.value) })}>
                        {[1, 2, 3, 5, 7, 14, 30].map(d => <option key={d} value={d}>{d} days</option>)}
                      </select>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Buffer (min)</div>
                      <select style={S.select} value={settingsForm.buffer_minutes || 0}
                        onChange={e => setSettingsForm({ ...settingsForm, buffer_minutes: Number(e.target.value) })}>
                        {[0, 15, 30].map(m => <option key={m} value={m}>{m} min</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Admin emails */}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Admin Emails (comma-separated)</div>
                    <input style={S.input}
                      value={(settingsForm.admin_emails || []).join(", ")}
                      onChange={e => setSettingsForm({
                        ...settingsForm,
                        admin_emails: e.target.value.split(",").map(s => s.trim()).filter(Boolean),
                      })} />
                  </div>

                  <button style={{ ...S.btn("primary"), padding: "10px 20px", justifySelf: "start" }}
                    disabled={settingsSaving}
                    onClick={async () => {
                      setSettingsSaving(true);
                      await api(`/booking/settings?admin_email=${encodeURIComponent(adminEmail)}`, "POST", settingsForm);
                      setSettingsSaving(false);
                    }}>
                    {settingsSaving ? "Saving..." : "Save Settings"}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Appointment Types Tab ── */}
          {adminTab === "types" && (
            <div>
              {adminTypes.length === 0 && <div style={{ color: COLORS.grayMid, fontSize: 13, marginBottom: 12 }}>No appointment types yet.</div>}
              {adminTypes.map(t => (
                <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${COLORS.cardBorder}`, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{t.name}</div>
                    <div style={{ fontSize: 11, color: COLORS.grayMid }}>{t.slug} &middot; {t.duration_minutes} min</div>
                  </div>
                  <span style={S.badge(t.active ? COLORS.success : COLORS.danger)}>
                    {t.active ? "Active" : "Inactive"}
                  </span>
                  <button style={S.btn("ghost")} onClick={async () => {
                    await api(`/booking/types/${t.id}?admin_email=${encodeURIComponent(adminEmail)}`, "PATCH", { active: !t.active });
                    loadAdminTypes();
                  }}>
                    {t.active ? "Deactivate" : "Activate"}
                  </button>
                  <button style={S.btn("ghost")} onClick={() => {
                    const url = `https://build-black-tau.vercel.app/#booking/${t.slug}`;
                    navigator.clipboard.writeText(url);
                  }}>
                    Copy Link
                  </button>
                </div>
              ))}

              {/* Add Type form */}
              <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${COLORS.cardBorder}` }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Add New Type</div>
                <div style={{ display: "grid", gap: 8 }}>
                  <input placeholder="Name" style={S.input} value={newType.name}
                    onChange={e => setNewType({
                      ...newType,
                      name: e.target.value,
                      slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, ""),
                    })} />
                  <input placeholder="Slug (auto)" style={S.input} value={newType.slug}
                    onChange={e => setNewType({ ...newType, slug: e.target.value })} />
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 8 }}>
                    <select style={S.select} value={newType.duration_minutes}
                      onChange={e => setNewType({ ...newType, duration_minutes: Number(e.target.value) })}>
                      {[15, 30, 45, 60, 90].map(m => <option key={m} value={m}>{m} min</option>)}
                    </select>
                    <input placeholder="Description" style={S.input} value={newType.description}
                      onChange={e => setNewType({ ...newType, description: e.target.value })} />
                  </div>
                  <button style={{ ...S.btn("primary"), justifySelf: "start" }}
                    disabled={!newType.name.trim() || !newType.slug.trim()}
                    onClick={async () => {
                      await api(`/booking/types?admin_email=${encodeURIComponent(adminEmail)}`, "POST", newType);
                      setNewType({ name: "", slug: "", duration_minutes: 30, description: "" });
                      loadAdminTypes();
                    }}>
                    Add Type
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Upcoming Bookings Tab ── */}
          {adminTab === "upcoming" && (
            <div>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12, gap: 8, alignItems: "center" }}>
                {reminderMsg && <span style={{ fontSize: 12, color: COLORS.success }}>{reminderMsg}</span>}
                <button style={S.btn("ghost")} onClick={async () => {
                  const r = await api("/booking/send-reminders", "POST");
                  setReminderMsg(r ? `Sent ${r.sent || 0} reminders` : "Error");
                  setTimeout(() => setReminderMsg(""), 4000);
                }}>
                  Send Pending Reminders
                </button>
                <button style={S.btn("ghost")} onClick={loadUpcoming}>Refresh</button>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Name</th>
                      <th style={S.th}>Company</th>
                      <th style={S.th}>Email</th>
                      <th style={S.th}>Type</th>
                      <th style={S.th}>Date/Time</th>
                      <th style={S.th}>Meet Link</th>
                      <th style={S.th}>Status</th>
                      <th style={S.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcomingBookings.map(b => (
                      <tr key={b.id || b.booking_id}>
                        <td style={S.td}>{b.name}</td>
                        <td style={S.td}>{b.company || "—"}</td>
                        <td style={S.td}>{b.email}</td>
                        <td style={S.td}>{b.type || b.type_slug || "—"}</td>
                        <td style={S.td}>{b.start ? fmtShortDate(b.start) : "—"}</td>
                        <td style={S.td}>
                          {b.meet_url ? <a href={b.meet_url} target="_blank" rel="noreferrer" style={{ color: COLORS.orange, fontSize: 12 }}>Join</a> : "—"}
                        </td>
                        <td style={S.td}>
                          <span style={S.badge(b.status === "cancelled" ? COLORS.danger : COLORS.success)}>
                            {b.status || "confirmed"}
                          </span>
                        </td>
                        <td style={S.td}>
                          {b.status !== "cancelled" && (
                            <button style={{ ...S.btn("ghost"), fontSize: 11, padding: "4px 10px", color: COLORS.danger }}
                              onClick={async () => {
                                if (!window.confirm(`Cancel booking for ${b.name}?`)) return;
                                await api(`/booking/cancel/${b.id || b.booking_id}?admin_email=${encodeURIComponent(adminEmail)}`, "POST");
                                loadUpcoming();
                              }}>
                              Cancel
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {upcomingBookings.length === 0 && (
                      <tr><td colSpan={8} style={{ ...S.td, textAlign: "center", color: COLORS.grayMid }}>No upcoming bookings</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─────────────── RENDER ───────────────────────────────────────────────────

  if (loading) return <Spinner />;

  // Admin panel — only accessible from inside the CRM, never on public booking
  if (!isPublic && (adminMode || view === "admin")) {
    return <AdminPanel />;
  }

  // ── CONFIRMED VIEW ──
  if (view === "confirmed" && confirmed) {
    return (
      <div style={{ maxWidth: 520, margin: "40px auto", textAlign: "center" }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: `${COLORS.success}22`,
          display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px",
          border: `2px solid ${COLORS.success}` }}>
          <span style={{ fontSize: 32, color: COLORS.success }}>&#10003;</span>
        </div>
        <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 8, color: COLORS.textPrimary }}>Meeting Confirmed!</div>
        <div style={{ fontSize: 14, color: COLORS.grayMid, marginBottom: 24 }}>
          A confirmation email has been sent to <span style={{ color: COLORS.orange }}>{confirmed.email || email}</span>
        </div>

        {/* Google Meet button */}
        {confirmed.meet_url && (
          <a href={confirmed.meet_url} target="_blank" rel="noreferrer"
            style={{
              display: "inline-flex", alignItems: "center", gap: 10,
              background: COLORS.orange, color: COLORS.white, borderRadius: 8,
              padding: "14px 28px", fontSize: 16, fontWeight: 700,
              textDecoration: "none", marginBottom: 24, letterSpacing: 0.5,
            }}>
            <span style={{ fontSize: 20 }}>&#128249;</span> Join with Google Meet
          </a>
        )}

        <div style={{ ...S.card, textAlign: "left", marginBottom: 20 }}>
          <div style={{ fontSize: 13, marginBottom: 6 }}>
            <strong>When:</strong> {fmtDate(confirmed.start || selectedSlot?.start) || "—"}
          </div>
          {confirmed.end && (
            <div style={{ fontSize: 13, marginBottom: 6 }}>
              <strong>Duration:</strong> {selectedType ? selectedType.duration_minutes + " minutes" : Math.round((new Date(confirmed.end) - new Date(confirmed.start)) / 60000) + " minutes"}
            </div>
          )}
          {selectedType && (
            <div style={{ fontSize: 13, marginBottom: 6 }}>
              <strong>Type:</strong> {selectedType.name}
            </div>
          )}
          <div style={{ fontSize: 13 }}><strong>With:</strong> Drew Jennings, Outpost Preparedness</div>
          {confirmed.event_link && (
            <div style={{ fontSize: 12, marginTop: 8 }}>
              <a href={confirmed.event_link} target="_blank" rel="noreferrer" style={{ color: COLORS.orange }}>
                View in Google Calendar
              </a>
            </div>
          )}
        </div>

        <div style={{ fontSize: 12, color: COLORS.grayMid, marginBottom: 20 }}>
          Check your email for calendar invitation details.
        </div>

        <button onClick={resetAll} style={{ ...S.btn("ghost"), marginTop: 8 }}>
          Book Another Meeting
        </button>
      </div>
    );
  }

  // ── RESCHEDULE VIEW ──
  if (view === "reschedule") {
    return (
      <div style={{ maxWidth: 700, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Reschedule Meeting</div>
          {rescheduleLoading && <Spinner />}
          {rescheduleInfo && (
            <div style={{ ...S.card, marginBottom: 16, textAlign: "left", fontSize: 13 }}>
              <div style={{ fontWeight: 700, marginBottom: 4, color: COLORS.orange }}>Current Booking:</div>
              <div>{rescheduleInfo.name} &mdash; {rescheduleInfo.start ? fmtDate(rescheduleInfo.start) : "—"}</div>
              {rescheduleInfo.type && <div style={{ color: COLORS.grayMid, marginTop: 2 }}>{rescheduleInfo.type}</div>}
            </div>
          )}
        </div>
        {selectedType && (
          <>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Select a new time:</div>
            <SlotPicker onNext={() => {
              if (selectedSlot) submitReschedule();
            }} />
            {booking && <div style={{ textAlign: "center", marginTop: 8 }}><Spinner /></div>}
          </>
        )}
        {!selectedType && !rescheduleLoading && (
          <div style={{ textAlign: "center", color: COLORS.grayMid, fontSize: 13, padding: 20 }}>
            Loading appointment information...
          </div>
        )}
      </div>
    );
  }

  // ── TYPE SELECTION VIEW ──
  if (view === "types") {
    // Hard-gate: if the URL carried a slug, NEVER show the picker —
    // each appointment link belongs to one specific business + flow.
    // Render a quiet loader until the slug useEffect lands the user
    // on the calendar (or fails gracefully with an error if the slug
    // doesn't resolve to a real type).
    if (initialSlug) {
      const slugExists = apptTypes.some(t => t.slug === initialSlug);
      if (!loading && !slugExists && apptTypes.length > 0) {
        return (
          <div style={{ maxWidth: 600, margin: "60px auto", textAlign: "center",
                         color: COLORS.grayMid, padding: 40, fontSize: 13 }}>
            That booking link is no longer available. Please email{" "}
            <a href="mailto:drew@drewos.ai" style={{ color: COLORS.orange }}>
              drew@drewos.ai
            </a>{" "}
            to grab a time directly.
          </div>
        );
      }
      return (
        <div style={{ maxWidth: 600, margin: "60px auto", textAlign: "center",
                       color: COLORS.grayMid, padding: 40, fontSize: 13 }}>
          Loading your booking page&hellip;
        </div>
      );
    }
    // Determine the picker branding from any drewos hint in the URL.
    // (Today this rarely fires because we always have a slug; kept for
    // safety if a future flow lands users here without one.)
    const drewosPicker = (typeof window !== "undefined")
      && /drewos/i.test(window.location.hash || "");
    return (
      <div style={{ maxWidth: 600, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 4, color: COLORS.textPrimary }}>Book a Meeting</div>
          <div style={{ fontSize: 13, color: COLORS.grayMid }}>
            {drewosPicker ? "DrewOS" : "Outpost Preparedness"} &mdash; Select a meeting type
          </div>
        </div>

        <div style={{ display: "grid", gap: 12 }}>
          {apptTypes.map(t => (
            <div key={t.id} style={{ ...S.card, cursor: "pointer", transition: "border-color 0.15s" }}
              onClick={() => { setSelectedType(t); setView("calendar"); }}
              onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.orange}
              onMouseLeave={e => e.currentTarget.style.borderColor = COLORS.cardBorder}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary }}>{t.name}</div>
                  <div style={{ fontSize: 12, color: COLORS.grayMid, marginTop: 2 }}>{t.duration_minutes} minutes</div>
                  {t.description && <div style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 6 }}>{t.description}</div>}
                </div>
                <button style={{ ...S.btn("primary"), flexShrink: 0 }}
                  onClick={e => { e.stopPropagation(); setSelectedType(t); setView("calendar"); }}>
                  Book Now
                </button>
              </div>
            </div>
          ))}
        </div>

        {apptTypes.length === 0 && (
          <div style={{ textAlign: "center", color: COLORS.grayMid, padding: 40, fontSize: 13 }}>
            No appointment types available at the moment.
          </div>
        )}

        {!isPublic && (
          <div style={{ textAlign: "center", marginTop: 40 }}>
            <button onClick={() => { setAdminMode(true); setView("admin"); setAdminTab("settings"); loadSettings(); }}
              style={{ background: "none", border: "none", color: COLORS.grayMid, fontSize: 11,
                cursor: "pointer", fontFamily: "inherit", opacity: 0.6 }}>
              &#9881; Admin
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── CALENDAR VIEW ──
  if (view === "calendar") {
    return (
      <div style={{ maxWidth: 700, margin: "0 auto" }}>
        <div style={{ marginBottom: 20 }}>
          <button onClick={() => { setView("types"); setSelectedType(null); setSelectedSlot(null); setSelectedDay(null); }}
            style={{ ...S.btn("ghost"), fontSize: 12, marginBottom: 12 }}>
            &larr; Back
          </button>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>{selectedType?.name}</div>
            <div style={{ fontSize: 13, color: COLORS.grayMid }}>
              {selectedType?.duration_minutes} min &mdash; with Drew Jennings
            </div>
          </div>
        </div>

        <SlotPicker onNext={() => setView("form")} />

        {!isPublic && (
          <div style={{ textAlign: "center", marginTop: 40 }}>
            <button onClick={() => { setAdminMode(true); setView("admin"); setAdminTab("settings"); loadSettings(); }}
              style={{ background: "none", border: "none", color: COLORS.grayMid, fontSize: 11,
                cursor: "pointer", fontFamily: "inherit", opacity: 0.6 }}>
              &#9881; Admin
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── FORM VIEW ──
  if (view === "form") {
    return (
      <div style={{ maxWidth: 500, margin: "0 auto" }}>
        <div style={{ ...S.card, marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.orange }}>
              {selectedSlot ? new Date(selectedSlot.start).toLocaleString("en-US", {
                weekday: "long", month: "long", day: "numeric",
                hour: "numeric", minute: "2-digit",
              }) : "—"}
            </div>
            <div style={{ fontSize: 12, color: COLORS.grayMid, marginTop: 2 }}>
              {selectedType?.name} &mdash; {selectedType?.duration_minutes} min
            </div>
          </div>
          <button onClick={() => setView("calendar")}
            style={{ ...S.btn("ghost"), fontSize: 12 }}>
            Change
          </button>
        </div>

        <div style={{ ...S.card }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>Your Details</div>
          <div style={{ display: "grid", gap: 10 }}>
            <input placeholder="Your name *" value={name} onChange={e => setName(e.target.value)}
              style={S.input} />
            <input placeholder="Email *" type="email" value={email} onChange={e => setEmail(e.target.value)}
              style={S.input} />
            <input placeholder="Company" value={company} onChange={e => setCompany(e.target.value)}
              style={S.input} />
            <textarea placeholder="What do you need help with?" value={notes} onChange={e => setNotes(e.target.value)}
              rows={3} style={{ ...S.input, fontFamily: "inherit", resize: "vertical" }} />
            <button onClick={submitBooking}
              disabled={booking || !name.trim() || !email.trim()}
              style={{
                ...S.btn("primary"), fontSize: 14, padding: "12px 20px",
                opacity: (name.trim() && email.trim()) ? 1 : 0.5,
              }}>
              {booking ? "Booking..." : "Confirm Booking"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

// ─── NAV CONFIG ──────────────────────────────────────────────────────────────
// ─── CALENDAR VIEW ───────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
function CalendarView() {
  const [month, setMonth] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [overdue, setOverdue] = useState([]);

  useEffect(() => {
    setLoading(true);
    const y = month.getFullYear(), m = month.getMonth();
    const start = `${y}-${String(m + 1).padStart(2, "0")}-01`;
    const endD = new Date(y, m + 1, 0);
    const end = `${endD.getFullYear()}-${String(endD.getMonth() + 1).padStart(2, "0")}-${String(endD.getDate()).padStart(2, "0")}`;
    Promise.all([
      api(`/calendar/events?start=${start}&end=${end}`),
      api("/tasks?overdue=true"),
    ]).then(([ev, od]) => {
      setEvents(ev || []);
      setOverdue(od || []);
      setLoading(false);
    });
  }, [month]);

  const y = month.getFullYear(), m = month.getMonth();
  const firstDay = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const monthName = month.toLocaleString("en-US", { month: "long", year: "numeric" });

  const eventsByDay = {};
  events.forEach(e => {
    const d = (e.due_date || e.start_time || "").slice(0, 10);
    if (!eventsByDay[d]) eventsByDay[d] = [];
    eventsByDay[d].push(e);
  });

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const dayStr = (d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const selectedEvents = selectedDay ? (eventsByDay[dayStr(selectedDay)] || []) : [];

  return (
    <div>
      {overdue.length > 0 && (
        <div style={{ background: `${COLORS.danger}22`, border: `1px solid ${COLORS.danger}`, borderRadius: 8, padding: "10px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ ...S.badge(COLORS.danger), fontSize: 12 }}>{overdue.length} Overdue</span>
          <span style={{ fontSize: 12, color: COLORS.danger }}>tasks past due date</span>
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <button onClick={() => setMonth(new Date(y, m - 1, 1))} style={S.btn("ghost")}>← Prev</button>
        <div style={{ fontSize: 18, fontWeight: 800 }}>{monthName}</div>
        <button onClick={() => setMonth(new Date(y, m + 1, 1))} style={S.btn("ghost")}>Next →</button>
      </div>
      {loading ? <Spinner /> : (
        <div style={{ display: "flex", gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
              {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
                <div key={d} style={{ fontSize: 10, fontWeight: 700, textAlign: "center", color: COLORS.grayMid, padding: 4, textTransform: "uppercase" }}>{d}</div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
              {cells.map((d, i) => {
                if (!d) return <div key={`e${i}`} />;
                const ds = dayStr(d);
                const dayEvts = eventsByDay[ds] || [];
                const isToday = ds === todayStr;
                const isSel = d === selectedDay;
                const tasks = dayEvts.filter(e => e.event_type === "task");
                const meetings = dayEvts.filter(e => e.event_type === "meeting");
                return (
                  <div key={d} onClick={() => setSelectedDay(d === selectedDay ? null : d)}
                    style={{
                      ...S.card, padding: "6px 4px", minHeight: 60, cursor: "pointer",
                      borderColor: isSel ? COLORS.orange : isToday ? COLORS.success : COLORS.cardBorder,
                      borderWidth: isToday || isSel ? 2 : 1,
                    }}>
                    <div style={{ fontSize: 12, fontWeight: isToday ? 800 : 500, color: isToday ? COLORS.success : COLORS.white, marginBottom: 4 }}>{d}</div>
                    <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                      {tasks.length > 0 && <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#3B82F6", display: "inline-block" }} title={`${tasks.length} task(s)`} />}
                      {meetings.length > 0 && <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#14B8A6", display: "inline-block" }} title={`${meetings.length} meeting(s)`} />}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {selectedDay && (
            <div style={{ width: 320, flexShrink: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>
                {new Date(y, m, selectedDay).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
              </div>
              {selectedEvents.length === 0 ? (
                <div style={{ fontSize: 12, color: COLORS.grayMid }}>No events this day.</div>
              ) : selectedEvents.map((e, i) => (
                <div key={e.id || i} style={{
                  ...S.card, padding: "10px 12px", marginBottom: 8,
                  borderLeft: `3px solid ${e.event_type === "task" ? "#3B82F6" : "#14B8A6"}`,
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: e.event_type === "task" ? "#3B82F6" : "#14B8A6", textTransform: "uppercase", marginBottom: 4 }}>
                    {e.event_type === "task" ? "Task" : "Meeting"}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{e.description || e.name || "Event"}</div>
                  {e.priority && <span style={{ ...S.badge(e.priority === "High" ? COLORS.danger : COLORS.warning), fontSize: 9, marginTop: 4 }}>{e.priority}</span>}
                  {(e.start_time || e.due_date) && <div style={{ fontSize: 10, color: COLORS.grayMid, marginTop: 4 }}>{(e.start_time || e.due_date).slice(0, 16).replace("T", " ")}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── MARKETING HUB ──────────────────────────────────────────────────────────
const MKT_VERTICALS = ["All Verticals","RCFE","Hotel","Hospital","Campus","PropertyMgmt"];
const MKT_TYPES = ["Compliance tip","Install story","Drill demo highlight","Staff training tip","Customer story/testimonial","Product spotlight","Veteran/founder story","Regulatory alert"];
const MKT_PLATFORMS = ["LinkedIn Personal","LinkedIn Business","Facebook","Instagram","YouTube","Google Business Profile"];
const MKT_TONES = ["Direct","Educational","Story-driven","Urgency"];
const PLATFORM_LIMITS = {"LinkedIn Personal":1300,"LinkedIn Business":1300,"Facebook":500,"Instagram":300,"YouTube":10000,"Google Business Profile":1500};
const PLATFORM_COLORS_MKT = {"LinkedIn Personal":"#0077B5","LinkedIn Business":"#004182","Facebook":"#4F46E5","Instagram":"#C026D3","YouTube":"#DC2626","Google Business Profile":"#16A34A"};
const PLATFORM_ICONS = {"LinkedIn Personal":"in","LinkedIn Business":"in","Facebook":"f","Instagram":"ig","YouTube":"yt","Google Business Profile":"G"};

function MarketingHub() {
  const [tab, setTab] = useState("content");
  const tabs = [
    { id: "content", label: "Content" },
    { id: "calendar", label: "Calendar" },
    { id: "campaigns", label: "Campaigns" },
    { id: "analytics", label: "Analytics" },
    { id: "channels", label: "Channels" },
  ];
  // Notification-panel clicks on an fb_comment dispatch crm:open-record; switch to
  // Analytics so the Comments card is visible.
  useEffect(() => {
    const h = (e) => {
      const d = e.detail || {};
      if (d.entity_type === "comment") setTab("analytics");
    };
    window.addEventListener("crm:open-record", h);
    return () => window.removeEventListener("crm:open-record", h);
  }, []);
  return (
    <div>
      <div style={{ display: "flex", gap: 4, marginBottom: 16, borderBottom: `1px solid ${COLORS.cardBorder}`, paddingBottom: 8 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ ...S.btn(tab === t.id ? "primary" : "ghost"), fontSize: 13 }}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "content" && <MktContent />}
      {tab === "calendar" && <MktCalendar />}
      {tab === "campaigns" && <MktCampaigns />}
      {tab === "analytics" && <MktAnalytics />}
      {tab === "channels" && <MktChannels />}
    </div>
  );
}

function MktContent() {
  const [mode, setMode] = useState("ai");
  const [vertical, setVertical] = useState("All Verticals");
  const [contentType, setContentType] = useState("Compliance tip");
  const [platforms, setPlatforms] = useState(["LinkedIn Personal"]);
  const [tone, setTone] = useState("Direct");
  const [generated, setGenerated] = useState(null);
  const [draftIds, setDraftIds] = useState({});
  const [imageByPlatform, setImageByPlatform] = useState({});
  const [postedPlatforms, setPostedPlatforms] = useState({});
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [assets, setAssets] = useState([]);
  const [assetsOpen, setAssetsOpen] = useState(false);
  const [assetFilter, setAssetFilter] = useState("all");
  const [toast, setToast] = useState(null);
  const [manualText, setManualText] = useState("");
  const [brandOpen, setBrandOpen] = useState(false);
  const [guidance, setGuidance] = useState("");
  const [pipelineEnabled, setPipelineEnabled] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [contentSets, setContentSets] = useState([]);
  const [newSetName, setNewSetName] = useState("");

  useEffect(() => {
    api("/marketing/drafts?limit=10").then(r => setDrafts(r || []));
    api("/marketing/assets").then(r => setAssets(r || []));
    api("/marketing/guidance").then(r => {
      if (r) { setGuidance(r.guidance_text || ""); setPipelineEnabled(!!r.pipeline_enabled); }
    });
    api("/marketing/content-sets").then(r => setContentSets(r || []));
  }, []);

  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 2500); return () => clearTimeout(t); }, [toast]);

  function togglePlatform(p) { setPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]); }

  const refreshDrafts = useCallback(() => { api("/marketing/drafts?limit=10").then(d => setDrafts(d || [])); }, []);
  const refreshAssets = useCallback(() => { api("/marketing/assets").then(a => setAssets(a || [])); }, []);

  async function uploadAssetFile(file, category = "products") {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    const r = await fetch(`${CRM_URL}/marketing/assets/upload`, { method: "POST", body: fd });
    const j = await r.json().catch(() => ({}));
    if (r.ok) { setToast(`Uploaded ${j.filename}`); refreshAssets(); return j; }
    setToast(`Upload failed: ${j.detail || r.statusText}`); return null;
  }

  async function applyAssetToPlatform(filename, platform) {
    const did = draftIds[platform];
    if (!did) { setToast("Generate content for this platform first, then apply an asset."); return; }
    const r = await api("/marketing/use-asset", "POST", { asset_filename: filename, draft_id: did });
    if (r?.ok) {
      setImageByPlatform(prev => ({ ...prev, [platform]: { url: r.image_data, prompt: `[asset] ${filename}` } }));
      setToast(`Applied ${filename} to ${platform}`);
    }
  }

  async function generate() {
    if (platforms.length === 0) return;
    setLoading(true);
    const r = await api("/marketing/generate", "POST", {
      vertical: vertical === "All Verticals" ? "All" : vertical,
      content_type: contentType, tone, platforms,
    });
    if (r?.content) {
      setGenerated(r.content);
      setDraftIds(r.draft_ids || {});
      setImageByPlatform({});
      setPostedPlatforms({});
      refreshDrafts();
    }
    setLoading(false);
  }

  async function enhanceManual() {
    if (!manualText.trim()) return;
    setLoading(true);
    const r = await api("/marketing/generate", "POST", {
      vertical: vertical === "All Verticals" ? "All" : vertical,
      content_type: "Manual enhancement", tone,
      platforms, manual_content: manualText,
    });
    if (r?.content) {
      setGenerated(r.content);
      setDraftIds(r.draft_ids || {});
      setImageByPlatform({});
      setPostedPlatforms({});
    }
    setLoading(false);
  }

  async function loadDraft(d) {
    const full = await api(`/marketing/drafts/${d.id}`);
    if (!full) return;
    let contentMap = {};
    try { contentMap = JSON.parse(full.generated_content || "{}"); } catch {}
    const platform = full.platform || Object.keys(contentMap)[0];
    if (!platform) return;
    setGenerated(contentMap);
    setDraftIds({ [platform]: full.id });
    const img = full.image_data || full.image_url || null;
    setImageByPlatform(img ? { [platform]: { url: img, prompt: full.image_prompt || null } } : {});
    setPostedPlatforms({});
    setToast(`Loaded draft · ${platform}`);
  }

  async function publishManual() {
    if (!manualText.trim() || platforms.length === 0) return;
    const content = {};
    platforms.forEach(p => { content[p] = manualText; });
    setGenerated(content);
  }

  async function schedulePost(platform, content, scheduledAt) {
    const did = draftIds[platform] || null;
    await api("/marketing/schedule", "POST", { platform, content, scheduled_at: scheduledAt, draft_id: did });
    setToast(`Scheduled for ${platform}`);
    refreshDrafts();
  }
  async function postNow(platform, content, overlayText = null) {
    setPostedPlatforms(prev => ({ ...prev, [platform]: "pending" }));
    const did = draftIds[platform] || null;
    const r = await api("/marketing/post", "POST", { platform, content, draft_id: did, overlay_text: overlayText });
    if (r?.status === "posted") {
      setPostedPlatforms(prev => ({ ...prev, [platform]: "posted" }));
      setToast(`Posted to ${platform} \u2713`);
      refreshDrafts();
    } else {
      setPostedPlatforms(prev => ({ ...prev, [platform]: "failed" }));
      setToast(`Post to ${platform} failed: ${(r && r.error) || "unknown error"}`);
    }
  }
  function copyText(text) { navigator.clipboard.writeText(text).catch(() => {}); setToast("Copied to clipboard"); }

  async function saveGuidance() {
    await api("/marketing/guidance", "POST", { guidance_text: guidance, pipeline_enabled: pipelineEnabled });
    setToast("Brand Brain saved");
  }

  async function loadSuggestions() {
    const r = await api("/marketing/pipeline-suggestions");
    setSuggestions(r?.suggestions || []);
  }

  async function createContentSet() {
    if (!newSetName.trim()) return;
    await api("/marketing/content-sets", "POST", { name: newSetName });
    setNewSetName("");
    api("/marketing/content-sets").then(r => setContentSets(r || []));
    setToast("Content set created");
  }

  const lblStyle = { fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase", display: "block", marginBottom: 4 };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "380px 1fr", gap: 20, alignItems: "start" }}>
      <div>
        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.orange }}>Content Generator</div>
            <div style={{ display: "flex", gap: 4 }}>
              <button onClick={() => setMode("ai")} style={{ ...S.btn(mode === "ai" ? "primary" : "ghost"), fontSize: 10, padding: "3px 8px" }}>AI</button>
              <button onClick={() => setMode("manual")} style={{ ...S.btn(mode === "manual" ? "primary" : "ghost"), fontSize: 10, padding: "3px 8px" }}>Manual</button>
            </div>
          </div>

          <label style={lblStyle}>Vertical</label>
          <select value={vertical} onChange={e => setVertical(e.target.value)} style={{ ...S.select, width: "100%", marginBottom: 12 }}>
            {MKT_VERTICALS.map(v => <option key={v}>{v}</option>)}
          </select>

          <label style={lblStyle}>Platforms</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
            {MKT_PLATFORMS.map(p => (
              <label key={p} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, cursor: "pointer",
                background: platforms.includes(p) ? `${COLORS.orange}22` : "transparent",
                border: `1px solid ${platforms.includes(p) ? COLORS.orange : COLORS.cardBorder}`,
                borderRadius: 5, padding: "3px 7px" }}>
                <input type="checkbox" checked={platforms.includes(p)} onChange={() => togglePlatform(p)} style={{ accentColor: COLORS.orange }} />
                {p}
              </label>
            ))}
          </div>

          {mode === "ai" ? (
            <>
              <label style={lblStyle}>Content Type</label>
              <select value={contentType} onChange={e => setContentType(e.target.value)} style={{ ...S.select, width: "100%", marginBottom: 12 }}>
                {MKT_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
              <label style={lblStyle}>Tone</label>
              <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
                {MKT_TONES.map(t => (
                  <button key={t} onClick={() => setTone(t)}
                    style={{ ...S.btn(tone === t ? "primary" : "ghost"), fontSize: 11, padding: "4px 10px" }}>{t}</button>
                ))}
              </div>
              <button onClick={generate} disabled={loading || platforms.length === 0}
                style={{ ...S.btn("primary"), width: "100%", padding: "10px 0", fontSize: 14, fontWeight: 700, opacity: (loading || !platforms.length) ? 0.5 : 1 }}>
                {loading ? "Generating..." : "Generate Content"}
              </button>
            </>
          ) : (
            <>
              <label style={lblStyle}>Your Content</label>
              <textarea value={manualText} onChange={e => setManualText(e.target.value)} placeholder="Paste or write your content..."
                style={{ ...S.input, height: 120, resize: "vertical", fontFamily: "inherit", marginBottom: 4 }} />
              <div style={{ fontSize: 10, color: manualText.length > 1300 ? COLORS.danger : COLORS.grayMid, textAlign: "right", marginBottom: 10 }}>
                {manualText.length} chars
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={publishManual} disabled={!manualText.trim() || !platforms.length}
                  style={{ ...S.btn("primary"), flex: 1, fontSize: 12, opacity: (!manualText.trim() || !platforms.length) ? 0.5 : 1 }}>
                  Preview
                </button>
                <button onClick={enhanceManual} disabled={loading || !manualText.trim()}
                  style={{ ...S.btn("ghost"), flex: 1, fontSize: 12, opacity: (!manualText.trim() || loading) ? 0.5 : 1 }}>
                  {loading ? "Enhancing..." : "Enhance with AI"}
                </button>
              </div>
            </>
          )}
        </div>

        {drafts.length > 0 && (
          <div style={{ ...S.card, marginTop: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.grayMid }}>Recent Drafts</div>
              <button onClick={async () => { const r = await api("/marketing/drafts/dedupe", "POST", {}); setToast(`Merged ${r?.merged || 0} orphan image drafts`); refreshDrafts(); }}
                style={{ ...S.btn("ghost"), fontSize: 9, padding: "2px 6px" }} title="Merge orphan image drafts into their matching text drafts">Dedupe</button>
            </div>
            {drafts.map(d => {
              const hasImg = !!d.image_url;
              let preview = "";
              try { preview = Object.values(JSON.parse(d.generated_content || "{}"))[0]?.slice(0, 40) || ""; } catch {}
              const platform = d.platform || (d.platforms || "").split(",")[0] || "";
              const statusColor = d.status === "posted" ? COLORS.success : d.status === "scheduled" ? COLORS.orange : COLORS.grayMid;
              return (
                <div key={d.id} style={{ display: "flex", gap: 8, padding: "8px 0", borderBottom: `1px solid ${COLORS.cardBorder}22`, alignItems: "center" }}>
                  {hasImg ? (
                    <img src={resolveImg(d.image_url)} alt="" style={{ width: 40, height: 40, borderRadius: 4, objectFit: "cover", flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: 40, height: 40, borderRadius: 4, background: `${COLORS.cardBorder}33`, color: COLORS.grayMid, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{"\u2398"}</div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 11 }}>
                      {platform && <span style={{ ...S.badge(PLATFORM_COLORS_MKT[platform] || COLORS.orange), fontSize: 9 }}>{PLATFORM_ICONS[platform] || platform}</span>}
                      <span style={{ fontWeight: 600 }}>{d.content_type || "Draft"}</span>
                      <span style={{ background: statusColor, color: "#fff", fontSize: 8, fontWeight: 700, padding: "1px 6px", borderRadius: 8 }}>{d.status || "draft"}</span>
                      {hasImg && <span style={{ fontSize: 8, color: COLORS.success }}>{"\u25A0"} img</span>}
                    </div>
                    <div style={{ fontSize: 10, color: COLORS.grayMid, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {(d.created_at || "").slice(5, 16)} {preview && `\u00B7 ${preview}`}
                    </div>
                  </div>
                  <button onClick={() => loadDraft(d)}
                    style={{ ...S.btn("primary"), padding: "3px 8px", fontSize: 10, flexShrink: 0 }} title="Load text and image into generator">Load</button>
                  <button onClick={async () => { await api(`/marketing/drafts/${d.id}`, "DELETE"); refreshDrafts(); }}
                    style={{ ...S.btn("ghost"), padding: "3px 6px", fontSize: 10, color: COLORS.danger, flexShrink: 0 }} title="Delete draft">{"\u00D7"}</button>
                </div>
              );
            })}
          </div>
        )}

        {/* Asset Library */}
        <div style={{ marginTop: 12 }}>
          <button onClick={() => { setAssetsOpen(o => !o); if (!assetsOpen) refreshAssets(); }}
            style={{ ...S.btn("ghost"), width: "100%", fontSize: 12, textAlign: "left" }}>
            {assetsOpen ? "\u25BE" : "\u25B8"} Asset Library ({assets.length})
          </button>
          {assetsOpen && (
            <div style={{ ...S.card, marginTop: 4 }}>
              <div style={{ display: "flex", gap: 4, marginBottom: 8, flexWrap: "wrap" }}>
                {["all","products","installs","field","logos"].map(cat => (
                  <button key={cat} onClick={() => setAssetFilter(cat)}
                    style={{ ...S.btn(assetFilter === cat ? "primary" : "ghost"), fontSize: 10, padding: "3px 8px" }}>{cat}</button>
                ))}
                <label style={{ ...S.btn("primary"), fontSize: 10, padding: "3px 8px", cursor: "pointer", marginLeft: "auto" }}>
                  Upload Image
                  <input type="file" accept="image/*" hidden
                    onChange={e => { const f = e.target.files?.[0]; if (f) uploadAssetFile(f, assetFilter === "all" ? "products" : assetFilter); e.target.value = ""; }} />
                </label>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
                {assets.filter(a => assetFilter === "all" || a.category === assetFilter).map(a => (
                  <div key={a.id} style={{ border: `1px solid ${COLORS.cardBorder}`, borderRadius: 4, overflow: "hidden", background: "#0002" }}>
                    <img src={`${CRM_URL}/marketing/assets/${encodeURIComponent(a.filename)}`} alt={a.filename}
                      style={{ width: "100%", height: 70, objectFit: "cover", display: "block" }} />
                    <div style={{ padding: "4px 6px" }}>
                      <div style={{ fontSize: 9, color: COLORS.grayMid, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.filename}</div>
                      {generated ? (
                        <select onChange={e => { const p = e.target.value; if (p) { applyAssetToPlatform(a.filename, p); e.target.value = ""; } }}
                          style={{ width: "100%", fontSize: 9, padding: "2px 4px", marginTop: 2, background: COLORS.card, color: COLORS.text, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 3 }} defaultValue="">
                          <option value="" disabled>Use on…</option>
                          {Object.keys(generated).map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                      ) : (
                        <div style={{ fontSize: 8, color: COLORS.grayMid, marginTop: 2 }}>Generate first</div>
                      )}
                    </div>
                  </div>
                ))}
                {assets.filter(a => assetFilter === "all" || a.category === assetFilter).length === 0 && (
                  <div style={{ gridColumn: "1 / -1", fontSize: 10, color: COLORS.grayMid, padding: 8 }}>
                    No assets in this category yet. Click Upload Image to add one.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Brand Brain */}
        <div style={{ marginTop: 12 }}>
          <button onClick={() => { setBrandOpen(b => !b); if (!brandOpen) loadSuggestions(); }}
            style={{ ...S.btn("ghost"), width: "100%", fontSize: 12, textAlign: "left" }}>
            {brandOpen ? "▾" : "▸"} Brand Brain
          </button>
          {brandOpen && (
            <div style={{ ...S.card, marginTop: 4 }}>
              <label style={lblStyle}>Posting Guidance (prepended to every AI generation)</label>
              <textarea value={guidance} onChange={e => setGuidance(e.target.value)}
                style={{ ...S.input, height: 80, resize: "vertical", fontFamily: "inherit", marginBottom: 8, fontSize: 11 }} />
              <label style={{ ...lblStyle, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="checkbox" checked={pipelineEnabled} onChange={e => setPipelineEnabled(e.target.checked)} style={{ accentColor: COLORS.orange }} />
                Enable pipeline content suggestions
              </label>
              <button onClick={saveGuidance} style={{ ...S.btn("primary"), fontSize: 11, marginTop: 6, marginBottom: 10 }}>Save Guidance</button>

              {pipelineEnabled && suggestions.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>Pipeline Suggestions</div>
                  <div style={{ background: "#FEF3C7", border: "1px solid #F59E0B44", borderRadius: 5, padding: 8, fontSize: 10, marginBottom: 8, color: "#92400E" }}>
                    Privacy Protected: Pipeline data inspires content angles only — no real names, organizations, or locations appear in generated posts.
                  </div>
                  {suggestions.map((s, i) => (
                    <div key={i} style={{ padding: "6px 0", borderBottom: `1px solid ${COLORS.cardBorder}22`, fontSize: 11 }}>
                      <div>{s.text}</div>
                      <button onClick={() => { setContentType(s.content_type || "Compliance tip"); setVertical(s.vertical || "All Verticals"); setMode("ai"); }}
                        style={{ ...S.btn("ghost"), fontSize: 10, padding: "2px 6px", marginTop: 4 }}>Generate Post</button>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ borderTop: `1px solid ${COLORS.cardBorder}`, paddingTop: 8, marginTop: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>Content Sets</div>
                {contentSets.map(cs => (
                  <div key={cs.id} style={{ fontSize: 11, padding: "4px 0", display: "flex", justifyContent: "space-between" }}>
                    <span>{cs.name}</span>
                    <span style={S.badge(cs.active ? COLORS.success : COLORS.grayMid)}>{cs.active ? "Active" : "—"}</span>
                  </div>
                ))}
                <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
                  <input value={newSetName} onChange={e => setNewSetName(e.target.value)} placeholder="New set name..." style={{ ...S.input, flex: 1, fontSize: 11 }} />
                  <button onClick={createContentSet} disabled={!newSetName.trim()} style={{ ...S.btn("primary"), fontSize: 10, padding: "3px 8px" }}>+</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right: Generated content */}
      <div>
        {(() => { const imgDrafts = drafts.filter(d => d.image_url && d.status === "draft"); return imgDrafts.length > 0 && !generated ? (
          <div style={{ ...S.card, marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center",
            background: `${COLORS.orange}11`, border: `1px solid ${COLORS.orange}33` }}>
            <span style={{ fontSize: 12 }}>You have {imgDrafts.length} saved draft{imgDrafts.length > 1 ? "s" : ""} with images</span>
            <button onClick={() => { try { setGenerated(JSON.parse(imgDrafts[0].generated_content)); } catch {} }}
              style={{ ...S.btn("primary"), fontSize: 11, padding: "4px 10px" }}>View Draft</button>
          </div>
        ) : null; })()}
        {loading && <Spinner />}
        {!loading && !generated && (
          <div style={{ ...S.card, textAlign: "center", padding: 40, color: COLORS.grayMid }}>
            {mode === "ai" ? "Select options and click Generate." : "Write your content and click Preview."}
          </div>
        )}
        {!loading && generated && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => { Object.entries(generated).forEach(([p, c]) => postNow(p, c)); }}
                style={{ ...S.btn("primary"), fontSize: 12 }}>Post All</button>
            </div>
            {Object.entries(generated).map(([platform, content]) => {
              const limit = PLATFORM_LIMITS[platform] || 2000;
              const over = (content || "").length > limit;
              return (
                <PlatformCard key={platform} platform={platform} content={content} limit={limit} over={over}
                  vertical={vertical} onCopy={() => copyText(content)} onPost={(overlayText) => postNow(platform, content, overlayText)}
                  onSchedule={dt => schedulePost(platform, content, dt)}
                  onRegenerate={() => { setLoading(true); api("/marketing/generate", "POST", { vertical: vertical === "All Verticals" ? "All" : vertical, content_type: contentType, tone, platforms: [platform] }).then(r => { if (r?.content?.[platform]) { setGenerated(g => ({ ...g, [platform]: r.content[platform] })); const newId = (r.draft_ids && r.draft_ids[platform]) || r.draft_id; if (newId) setDraftIds(prev => ({ ...prev, [platform]: newId })); setImageByPlatform(prev => { const { [platform]: _rm, ...rest } = prev; return rest; }); setPostedPlatforms(prev => { const { [platform]: _removed, ...rest } = prev; return rest; }); } setLoading(false); }); }}
                  onChange={v => setGenerated(g => ({ ...g, [platform]: v }))}
                  onDraftSaved={refreshDrafts}
                  postStatus={postedPlatforms[platform]}
                  draftId={draftIds[platform]}
                  initialImage={imageByPlatform[platform]}
                  onImageUpdated={(img, prompt) => setImageByPlatform(prev => ({ ...prev, [platform]: { url: img, prompt } }))}
                />
              );
            })}
          </div>
        )}
      </div>

      {toast && (
        <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 2000, background: COLORS.success,
          color: "#fff", padding: "12px 18px", borderRadius: 6, fontSize: 13, fontWeight: 700, boxShadow: "0 6px 20px rgba(0,0,0,0.4)" }}>
          {toast}
        </div>
      )}
    </div>
  );
}

// ── IMAGE STUDIO MODAL (April 17, 2026) ───────────────────────────────────────
function ImageStudioModal({ open, onClose, vertical, onUse }) {
  const [step, setStep] = useState("upload");          // upload | prompt | processing | result
  const [source, setSource] = useState(null);          // { dataUri, label, asset_filename? }
  const [library, setLibrary] = useState([]);
  const [libFilter, setLibFilter] = useState("products");
  const [preset, setPreset] = useState("brand_it");
  const [customPrompt, setCustomPrompt] = useState("");
  const [hookText, setHookText] = useState("");
  const [result, setResult] = useState(null);          // { dataUri, prompt_used }
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const list = await api(`/marketing/assets?category=${libFilter}`);
      if (Array.isArray(list)) setLibrary(list);
    })();
  }, [open, libFilter]);

  useEffect(() => {
    if (!open) {
      setStep("upload"); setSource(null); setResult(null); setError(null);
      setCustomPrompt(""); setHookText(""); setPreset("brand_it");
    }
  }, [open]);

  if (!open) return null;

  const PRESETS = [
    { key: "brand_it",     label: "Brand it",      hint: "Professional finish, clean BG" },
    { key: "product_shot", label: "Product shot",  hint: "Studio white BG, catalog lighting" },
    { key: "social_ready", label: "Social ready",  hint: "Punchy, subtle brand colors" },
    { key: "add_hook_text",label: "Add hook text", hint: "Overlays headline left-side" },
    { key: "custom",       label: "Custom…",       hint: "Free-text prompt" },
  ];

  async function handleFile(f) {
    if (!f) return;
    if (!/^image\//.test(f.type)) { setError("Only image files"); return; }
    if (f.size > 10 * 1024 * 1024) { setError("File > 10 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setSource({ dataUri: reader.result, label: f.name });
      setError(null);
      setStep("prompt");
    };
    reader.readAsDataURL(f);
  }

  function pickAsset(a) {
    const url = `${CRM_URL}/marketing/assets/${encodeURIComponent(a.filename)}`;
    setSource({ imageUrl: url, label: a.filename, asset_filename: a.filename });
    setError(null);
    setStep("prompt");
  }

  async function runEdit() {
    setError(null);
    setStep("processing");
    const body = {
      preset, vertical,
      prompt: preset === "custom" ? customPrompt : "",
      hook_text: preset === "add_hook_text" ? hookText : "",
    };
    if (source.dataUri) body.image_base64 = source.dataUri;
    if (source.imageUrl) body.image_url = source.imageUrl;
    if (source.asset_filename) body.asset_filename = source.asset_filename;

    const r = await fetch(`${CRM_URL}/marketing/image-studio`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) {
      setError(j.detail || `HTTP ${r.status}`);
      setStep("prompt");
      return;
    }
    setResult({ dataUri: j.image_data || j.image_url, prompt_used: j.prompt_used });
    setStep("result");
  }

  async function saveToLibrary() {
    if (!result?.dataUri) return;
    try {
      const blob = await (await fetch(result.dataUri)).blob();
      const fd = new FormData();
      fd.append("file", blob, `studio_${Date.now()}.png`);
      fd.append("category", "products");
      await fetch(`${CRM_URL}/marketing/assets/upload`, { method: "POST", body: fd });
      setError("✓ Saved to Asset Library");
      setTimeout(() => setError(null), 2500);
    } catch (e) {
      setError(`Save failed: ${String(e).slice(0, 80)}`);
    }
  }

  function download() {
    if (!result?.dataUri) return;
    const a = document.createElement("a");
    a.href = result.dataUri;
    a.download = `outpost_studio_${Date.now()}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  }

  const stepDot = (active) => ({
    width: 8, height: 8, borderRadius: 4,
    background: active ? COLORS.orange : COLORS.cardBorder,
  });

  const previewBox = {
    background: `${COLORS.cardBorder}22`, borderRadius: 8, padding: 10,
    display: "flex", alignItems: "center", justifyContent: "center",
    minHeight: 260, overflow: "hidden",
  };

  return (
    <ModalShell title="Image Studio" subtitle="Upload or pick an asset, enhance it with AI, ship to your post" onClose={onClose} wide>
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        {["upload","prompt","result"].map((s, i) => (
          <div key={s} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={stepDot(step === s || (step === "processing" && s === "prompt"))} />
            <span style={{ fontSize: 11, color: step === s ? COLORS.orange : COLORS.grayMid, fontWeight: step === s ? 700 : 400 }}>
              {["1. Choose","2. Prompt","3. Result"][i]}
            </span>
          </div>
        ))}
      </div>

      {error && (
        <div style={{ background: error.startsWith("✓") ? `${COLORS.success}22` : `${COLORS.danger}22`,
                      color: error.startsWith("✓") ? COLORS.success : COLORS.danger,
                      padding: 8, borderRadius: 5, fontSize: 12, marginBottom: 10 }}>{error}</div>
      )}

      {/* STEP 1 — upload / library */}
      {step === "upload" && (
        <>
          <div onDragEnter={e => { e.preventDefault(); setDragOver(true); }}
               onDragOver={e => { e.preventDefault(); setDragOver(true); }}
               onDragLeave={() => setDragOver(false)}
               onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
               onClick={() => fileRef.current?.click()}
               style={{
                 border: `2px dashed ${dragOver ? COLORS.orange : COLORS.cardBorder}`,
                 borderRadius: 8, padding: 30, textAlign: "center", cursor: "pointer",
                 background: dragOver ? `${COLORS.orange}11` : "transparent", marginBottom: 16,
               }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Drop an image here</div>
            <div style={{ fontSize: 11, color: COLORS.grayMid }}>or click to upload — jpg, png, webp (≤ 10 MB)</div>
            <input ref={fileRef} type="file" accept="image/*" hidden
              onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ""; }} />
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.orange, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
            Or choose from Asset Library
          </div>
          <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
            {["products","installs","field","logos"].map(cat => (
              <button key={cat} onClick={() => setLibFilter(cat)}
                style={{ ...S.btn(libFilter === cat ? "primary" : "ghost"), fontSize: 11, padding: "4px 10px" }}>
                {cat}
              </button>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6, maxHeight: 240, overflowY: "auto" }}>
            {library.length === 0 && (
              <div style={{ gridColumn: "1 / -1", textAlign: "center", fontSize: 12, color: COLORS.grayMid, padding: 20 }}>
                No assets in this category.
              </div>
            )}
            {library.map(a => (
              <div key={a.id} onClick={() => pickAsset(a)}
                   style={{ cursor: "pointer", border: `1px solid ${COLORS.cardBorder}`, borderRadius: 5, overflow: "hidden", background: "#fff" }}>
                <img src={`${CRM_URL}/marketing/assets/${encodeURIComponent(a.filename)}`}
                     alt={a.filename}
                     style={{ width: "100%", height: 90, objectFit: "cover", display: "block" }} />
                <div style={{ fontSize: 10, padding: "3px 6px", color: COLORS.grayMid, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {a.filename}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* STEP 2 — prompt */}
      {step === "prompt" && source && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={previewBox}>
              <img src={source.dataUri || source.imageUrl} alt="source" style={{ maxWidth: "100%", maxHeight: 240, borderRadius: 4 }} />
            </div>
            <div>
              <div style={{ fontSize: 11, color: COLORS.grayMid, marginBottom: 4 }}>Source: {source.label}</div>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Quick presets</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 12 }}>
                {PRESETS.map(p => (
                  <button key={p.key} onClick={() => setPreset(p.key)}
                    style={{ ...S.btn(preset === p.key ? "primary" : "ghost"),
                             fontSize: 11, padding: "8px 10px", textAlign: "left", lineHeight: 1.3 }}>
                    <div style={{ fontWeight: 700 }}>{p.label}</div>
                    <div style={{ fontSize: 10, opacity: 0.8, marginTop: 2 }}>{p.hint}</div>
                  </button>
                ))}
              </div>
              {preset === "custom" && (
                <textarea value={customPrompt} onChange={e => setCustomPrompt(e.target.value)}
                  placeholder="Describe what you want done with the image…"
                  style={{ ...S.input, height: 80, resize: "vertical", fontFamily: "inherit", marginBottom: 8 }} />
              )}
              {preset === "add_hook_text" && (
                <input type="text" value={hookText} onChange={e => setHookText(e.target.value)}
                  placeholder="Hook text (e.g. &quot;When the elevator drops&quot;)"
                  style={{ ...S.input, marginBottom: 8 }} />
              )}
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => setStep("upload")} style={{ ...S.btn("ghost"), fontSize: 11 }}>← Back</button>
                <button onClick={runEdit}
                  disabled={preset === "custom" && !customPrompt.trim()}
                  style={{ ...S.btn("primary"), fontSize: 12, flex: 1,
                           opacity: (preset === "custom" && !customPrompt.trim()) ? 0.5 : 1 }}>
                  Enhance with AI →
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* STEP 3 — processing */}
      {step === "processing" && (
        <div style={{ ...previewBox, flexDirection: "column", gap: 10, minHeight: 300 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>AI is working on your image…</div>
          <div style={{ fontSize: 11, color: COLORS.grayMid }}>Claude is enhancing the prompt, then sending it to OpenAI. ~15–30 s.</div>
        </div>
      )}

      {/* STEP 4 — result */}
      {step === "result" && result && (
        <>
          <div style={{ ...previewBox, minHeight: 320, marginBottom: 10 }}>
            <img src={result.dataUri} alt="result" style={{ maxWidth: "100%", maxHeight: 360, borderRadius: 4 }} />
          </div>
          {result.prompt_used && (
            <div style={{ fontSize: 10, color: COLORS.grayMid, marginBottom: 10, fontStyle: "italic",
                          background: `${COLORS.cardBorder}22`, padding: 6, borderRadius: 4 }}>
              Prompt used: {result.prompt_used}
            </div>
          )}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button onClick={() => { if (onUse) onUse(result.dataUri); onClose(); }}
              style={{ ...S.btn("primary"), fontSize: 12 }}>Use for Post</button>
            <button onClick={saveToLibrary} style={{ ...S.btn("ghost"), fontSize: 12 }}>Save to Library</button>
            <button onClick={download} style={{ ...S.btn("ghost"), fontSize: 12 }}>Download</button>
            <button onClick={runEdit} style={{ ...S.btn("ghost"), fontSize: 12 }}>Regenerate</button>
            <button onClick={() => setStep("prompt")} style={{ ...S.btn("ghost"), fontSize: 12 }}>Edit Prompt</button>
          </div>
        </>
      )}
    </ModalShell>
  );
}


function PlatformCard({ platform, content, limit, over, onCopy, onPost, onSchedule, onRegenerate, onChange, vertical, onDraftSaved, postStatus, draftId, initialImage, onImageUpdated }) {
  const [editing, setEditing] = useState(false);
  const [schedOpen, setSchedOpen] = useState(false);
  const [schedDt, setSchedDt] = useState("");
  const [imgUrl, setImgUrl] = useState(initialImage?.url || null);
  const [imgLoading, setImgLoading] = useState(false);
  const [savedStatus, setSavedStatus] = useState(null);
  const [imgGuidance, setImgGuidance] = useState("");
  const [guidanceFromDrew, setGuidanceFromDrew] = useState(false);
  const [drewSuggesting, setDrewSuggesting] = useState(false);
  const [overlayOn, setOverlayOn] = useState(false);
  const [overlayText, setOverlayText] = useState("");
  const [previewImg, setPreviewImg] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [studioOpen, setStudioOpen] = useState(false);
  const fileInputRef = useRef(null);
  const canImage = ["Facebook","Instagram","LinkedIn Personal","LinkedIn Business"].includes(platform);
  const pColor = PLATFORM_COLORS_MKT[platform] || COLORS.orange;

  useEffect(() => { setImgUrl(initialImage?.url || null); setPreviewImg(null); }, [initialImage?.url]);

  // Debounced live preview when overlay text changes
  useEffect(() => {
    if (!imgUrl || !overlayOn) { setPreviewImg(null); return; }
    const h = setTimeout(async () => {
      const r = await api("/marketing/preview-overlay", "POST", {
        image_url: imgUrl, overlay_text: overlayText || "",
      });
      if (r?.image_data) setPreviewImg(r.image_data);
    }, 350);
    return () => clearTimeout(h);
  }, [overlayText, overlayOn, imgUrl]);

  async function drewSuggest() {
    setDrewSuggesting(true);
    const r = await api("/drew/generate-image-prompt", "POST", {
      post_content: content,
      vertical: vertical || "All Verticals",
      content_type: "Compliance tip",
      tone: "Direct",
      include_hook: true,
    });
    setDrewSuggesting(false);
    if (r?.prompt) {
      setImgGuidance(r.prompt);
      setGuidanceFromDrew(true);
    }
    if (r?.hook) {
      setOverlayText(r.hook);
      setOverlayOn(true);
    }
    return r;
  }

  function dismissDrewSuggestion() {
    setImgGuidance("");
    setGuidanceFromDrew(false);
  }

  async function generateImage() {
    // If the guidance is empty, auto-invoke Drew first so the image prompt is always
    // scene-crafted for the vertical/tone (scene-only, never product descriptions).
    let guidanceToUse = imgGuidance;
    if (!guidanceToUse || !guidanceToUse.trim()) {
      const suggest = await drewSuggest();
      guidanceToUse = suggest?.prompt || "";
    }
    setImgLoading(true);
    const r = await api("/marketing/generate-image", "POST", {
      platform, content, vertical, draft_id: draftId,
      guidance: guidanceToUse || null,
    });
    if (r?.image_url) {
      setImgUrl(r.image_url);
      setSavedStatus("Image bonded to draft");
      setTimeout(() => setSavedStatus(null), 3000);
      if (onImageUpdated) onImageUpdated(r.image_url, r.image_prompt || null);
      if (onDraftSaved) onDraftSaved();
    }
    setImgLoading(false);
  }

  async function uploadImage(file) {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "products");
      const upR = await fetch(`${CRM_URL}/marketing/assets/upload`, { method: "POST", body: fd });
      const upJ = await upR.json().catch(() => ({}));
      if (!upR.ok) throw new Error(upJ.detail || upR.statusText);
      if (draftId) {
        const useR = await api("/marketing/use-asset", "POST", {
          asset_filename: upJ.filename, draft_id: draftId, overlay_text: overlayOn ? overlayText : null,
        });
        if (useR?.image_data) {
          setImgUrl(useR.image_data);
          if (onImageUpdated) onImageUpdated(useR.image_data, `[upload] ${upJ.filename}`);
          if (onDraftSaved) onDraftSaved();
        }
      } else {
        // No draft yet — just preview the watermarked result
        const preR = await api("/marketing/preview-overlay", "POST", {
          asset_filename: upJ.filename, overlay_text: overlayOn ? overlayText : null,
        });
        if (preR?.image_data) setImgUrl(preR.image_data);
      }
      setSavedStatus("Uploaded + watermarked");
      setTimeout(() => setSavedStatus(null), 3000);
    } catch (e) {
      setSavedStatus(`Upload failed: ${String(e).slice(0, 80)}`);
    }
    setUploading(false);
  }

  return (
    <div style={{ ...S.card, borderLeft: `4px solid ${postStatus === "posted" ? COLORS.success : (over ? COLORS.danger : pColor)}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ ...S.badge(pColor), fontSize: 10 }}>{PLATFORM_ICONS[platform] || "?"}</span>
          <span style={{ fontWeight: 700, fontSize: 13 }}>{platform}</span>
          {postStatus === "posted" && (
            <span style={{ background: COLORS.success, color: "#fff", fontSize: 10, fontWeight: 700,
              padding: "2px 8px", borderRadius: 10, letterSpacing: 0.3 }}>Posted {"\u2713"}</span>
          )}
          {postStatus === "pending" && (
            <span style={{ background: COLORS.grayMid, color: "#fff", fontSize: 10, fontWeight: 700,
              padding: "2px 8px", borderRadius: 10 }}>Posting\u2026</span>
          )}
          {postStatus === "failed" && (
            <span style={{ background: COLORS.danger, color: "#fff", fontSize: 10, fontWeight: 700,
              padding: "2px 8px", borderRadius: 10 }}>Post failed</span>
          )}
        </div>
        <span style={{ fontSize: 11, color: over ? COLORS.danger : COLORS.grayMid, fontWeight: over ? 700 : 400 }}>
          {(content || "").length}/{limit}
        </span>
      </div>
      {editing ? (
        <textarea value={content} onChange={e => onChange(e.target.value)}
          style={{ ...S.input, height: 120, resize: "vertical", fontFamily: "inherit", marginBottom: 8 }} />
      ) : (
        <div style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap", marginBottom: 8, maxHeight: 200, overflowY: "auto",
          background: `${COLORS.cardBorder}22`, borderRadius: 5, padding: 10 }}>
          {content}
        </div>
      )}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <button onClick={() => onPost(overlayOn ? overlayText : null)} style={{ ...S.btn("primary"), fontSize: 11, padding: "5px 10px" }}>Post Now</button>
        <button onClick={() => setSchedOpen(s => !s)} style={{ ...S.btn("ghost"), fontSize: 11, padding: "5px 10px" }}>Schedule</button>
        <button onClick={onCopy} style={{ ...S.btn("ghost"), fontSize: 11, padding: "5px 10px" }}>Copy</button>
        <button onClick={() => setEditing(e => !e)} style={{ ...S.btn("ghost"), fontSize: 11, padding: "5px 10px" }}>{editing ? "Done" : "Edit"}</button>
        <button onClick={onRegenerate} style={{ ...S.btn("ghost"), fontSize: 11, padding: "5px 10px" }}>Regenerate</button>
        {canImage && <button onClick={generateImage} disabled={imgLoading || drewSuggesting} style={{ ...S.btn("ghost"), fontSize: 11, padding: "5px 10px", opacity: (imgLoading || drewSuggesting) ? 0.5 : 1 }}>{drewSuggesting ? "DrewOS is writing your image prompt\u2026" : imgLoading ? "Generating..." : "Generate Image"}</button>}
        {canImage && <button onClick={drewSuggest} disabled={drewSuggesting || imgLoading}
          title="DrewOS drafts a scene-only Imagen 4 prompt + a 4-6 word hook overlay for this vertical + tone"
          style={{ ...S.btn("ghost"), fontSize: 11, padding: "5px 10px", opacity: (drewSuggesting || imgLoading) ? 0.5 : 1 }}>{"\u2728 DrewOS Suggest"}</button>}
        {canImage && <button onClick={() => fileInputRef.current?.click()} disabled={uploading} style={{ ...S.btn("ghost"), fontSize: 11, padding: "5px 10px", opacity: uploading ? 0.5 : 1 }}>{uploading ? "Uploading..." : "Upload Image"}</button>}
        {canImage && <button onClick={() => setStudioOpen(true)} style={{ ...S.btn("ghost"), fontSize: 11, padding: "5px 10px", borderColor: COLORS.orange, color: COLORS.orange }}>Image Studio</button>}
        <input ref={fileInputRef} type="file" accept="image/*" hidden
          onChange={e => { const f = e.target.files?.[0]; if (f) uploadImage(f); e.target.value = ""; }} />
        <ImageStudioModal
          open={studioOpen}
          onClose={() => setStudioOpen(false)}
          vertical={vertical}
          onUse={async (dataUri) => {
            setImgUrl(dataUri);
            if (onImageUpdated) onImageUpdated(dataUri, "[image studio]");
            if (draftId) {
              await api(`/marketing/drafts/${draftId}`, "PATCH", { image_url: dataUri, image_data: dataUri })
                .catch(() => {});
            }
            setSavedStatus("Bonded to draft");
            setTimeout(() => setSavedStatus(null), 2500);
            if (onDraftSaved) onDraftSaved();
          }}
        />
      </div>
      {canImage && (
        <div style={{ marginTop: 6 }}>
          {guidanceFromDrew && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
              <span style={{ background: "#EDE9FE", color: "#5B21B6", fontSize: 9, fontWeight: 700,
                padding: "2px 6px", borderRadius: 3 }}>
                {"\u270F\uFE0F DrewOS suggests"}
              </span>
              <button onClick={dismissDrewSuggestion}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 9,
                  color: COLORS.grayMid, padding: 0, textDecoration: "underline" }}>
                Skip / use my own
              </button>
            </div>
          )}
          <textarea value={imgGuidance}
            onChange={e => { setImgGuidance(e.target.value); if (guidanceFromDrew) setGuidanceFromDrew(false); }}
            placeholder="Image guidance (optional) — leave blank and DrewOS will draft a scene for this vertical + tone"
            style={{ ...S.input, fontSize: 11, padding: "5px 8px",
              minHeight: guidanceFromDrew ? 70 : 32, fontFamily: "inherit",
              background: guidanceFromDrew ? "#FAF5FF" : undefined,
              borderColor: guidanceFromDrew ? "#C4B5FD" : undefined }} />
        </div>
      )}
      {schedOpen && (
        <div style={{ display: "flex", gap: 6, marginTop: 8, alignItems: "center" }}>
          <input type="datetime-local" value={schedDt} onChange={e => setSchedDt(e.target.value)} style={{ ...S.input, width: 220 }} />
          <button onClick={() => { if (schedDt) { onSchedule(schedDt); setSchedOpen(false); } }}
            disabled={!schedDt} style={{ ...S.btn("primary"), fontSize: 11, opacity: schedDt ? 1 : 0.5 }}>Confirm</button>
        </div>
      )}
      {imgUrl && (
        <div style={{ marginTop: 10, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 6, overflow: "hidden" }}>
          <img src={previewImg || resolveImg(imgUrl)} alt="Post preview with watermark"
            style={{ width: "100%", display: "block", maxHeight: 300, objectFit: "cover" }} />
          <div style={{ display: "flex", gap: 6, padding: 8, alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, cursor: "pointer" }}>
              <input type="checkbox" checked={overlayOn} onChange={e => setOverlayOn(e.target.checked)} style={{ accentColor: COLORS.orange }} />
              Add text overlay
            </label>
            <a href={previewImg || imgUrl} download style={{ ...S.btn("ghost"), fontSize: 10, textDecoration: "none" }}>Download</a>
            <button onClick={generateImage} style={{ ...S.btn("ghost"), fontSize: 10 }}>Regenerate</button>
            <span style={{ fontSize: 9, color: COLORS.grayMid }}>watermark auto-applied</span>
            {savedStatus && postStatus !== "posted" && <span style={{ fontSize: 9, color: COLORS.success, marginLeft: "auto" }}>{savedStatus}</span>}
          </div>
          {overlayOn && (
            <div style={{ padding: "0 8px 8px" }}>
              <input type="text" value={overlayText} onChange={e => setOverlayText(e.target.value)}
                placeholder={"e.g. Nothing's Happened Yet Is Luck"}
                style={{ ...S.input, fontSize: 11, padding: "5px 8px" }} />
              <div style={{ fontSize: 9, color: COLORS.grayMid, marginTop: 3 }}>Bold white, dark shadow, center-left. Max 3 lines.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── CONTENT CALENDAR ───────────────────────────────────────────────────────
function MktCalendar() {
  const [view, setView] = useState("month");
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [posts, setPosts] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [stats, setStats] = useState({ byPlatform: {}, byVertical: {}, total: 0 });

  const reload = useCallback(() => {
    api("/marketing/scheduled?limit=200").then(r => setPosts(r || []));
    api("/marketing/drafts?limit=100").then(r => setDrafts(r || []));
  }, []);

  useEffect(() => { reload(); const iv = setInterval(reload, 60000); return () => clearInterval(iv); }, [reload]);

  useEffect(() => {
    const bp = {}; const bv = {};
    for (const p of posts) {
      bp[p.platform] = (bp[p.platform] || 0) + 1;
    }
    for (const d of drafts) {
      bv[d.vertical] = (bv[d.vertical] || 0) + 1;
    }
    setStats({ byPlatform: bp, byVertical: bv, total: posts.length });
  }, [posts, drafts]);

  const year = month.getFullYear(); const mo = month.getMonth();
  const firstDay = new Date(year, mo, 1).getDay();
  const daysInMonth = new Date(year, mo + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  function postsForDay(day) {
    if (!day) return [];
    const dateStr = `${year}-${String(mo + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return posts.filter(p => (p.scheduled_at || p.posted_at || "").startsWith(dateStr));
  }

  const upcoming = posts.filter(p => p.status === "scheduled").sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || "")).slice(0, 5);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 16, alignItems: "start" }}>
      {/* Calendar */}
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <button onClick={() => setMonth(new Date(year, mo - 1, 1))} style={{ ...S.btn("ghost"), padding: "4px 8px" }}>&lt;</button>
            <span style={{ fontWeight: 700, fontSize: 15 }}>{month.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</span>
            <button onClick={() => setMonth(new Date(year, mo + 1, 1))} style={{ ...S.btn("ghost"), padding: "4px 8px" }}>&gt;</button>
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            <button onClick={() => setView("month")} style={{ ...S.btn(view === "month" ? "primary" : "ghost"), fontSize: 10, padding: "3px 8px" }}>Month</button>
            <button onClick={() => setView("week")} style={{ ...S.btn(view === "week" ? "primary" : "ghost"), fontSize: 10, padding: "3px 8px" }}>Week</button>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
            <div key={d} style={{ fontSize: 10, fontWeight: 700, color: COLORS.grayMid, textAlign: "center", padding: 4 }}>{d}</div>
          ))}
          {cells.map((day, i) => {
            const dayPosts = postsForDay(day);
            const isToday = day && new Date().getDate() === day && new Date().getMonth() === mo && new Date().getFullYear() === year;
            return (
              <div key={i} style={{
                minHeight: 70, border: `1px solid ${COLORS.cardBorder}22`, borderRadius: 4, padding: 4,
                background: isToday ? `${COLORS.orange}11` : "transparent",
              }}>
                {day && (
                  <>
                    <div style={{ fontSize: 11, fontWeight: isToday ? 800 : 400, color: isToday ? COLORS.orange : COLORS.textPrimary, marginBottom: 2 }}>{day}</div>
                    {dayPosts.map(p => (
                      <div key={p.id} onClick={() => setSelectedPost(p)}
                        style={{
                          fontSize: 9, padding: "2px 4px", borderRadius: 3, marginBottom: 2, cursor: "pointer", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                          background: `${PLATFORM_COLORS_MKT[p.platform] || COLORS.orange}${p.status === "posted" ? "33" : "66"}`,
                          color: "#fff",
                        }}>
                        {PLATFORM_ICONS[p.platform] || "?"} {(p.content || "").slice(0, 20)}
                      </div>
                    ))}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Right panel */}
      <div>
        {/* Stats */}
        <div style={S.card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.orange, marginBottom: 8 }}>This Month</div>
          <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 8 }}>{stats.total} posts</div>
          <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4 }}>By Platform</div>
          {Object.entries(stats.byPlatform).map(([p, c]) => (
            <div key={p} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, padding: "2px 0" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: PLATFORM_COLORS_MKT[p] || COLORS.grayMid, display: "inline-block" }} />{p}
              </span>
              <b>{c}</b>
            </div>
          ))}
          <div style={{ fontSize: 11, fontWeight: 700, marginTop: 8, marginBottom: 4 }}>By Vertical</div>
          {Object.entries(stats.byVertical).map(([v, c]) => (
            <div key={v} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, padding: "2px 0" }}>
              <span>{v}</span><b>{c}</b>
            </div>
          ))}
        </div>

        {/* Upcoming */}
        <div style={{ ...S.card, marginTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.orange, marginBottom: 8 }}>Upcoming</div>
          {upcoming.length === 0 ? <div style={{ fontSize: 11, color: COLORS.grayMid }}>No scheduled posts</div> :
            upcoming.map(p => (
              <div key={p.id} onClick={() => setSelectedPost(p)}
                style={{ padding: "6px 0", borderBottom: `1px solid ${COLORS.cardBorder}22`, cursor: "pointer", fontSize: 11 }}>
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: PLATFORM_COLORS_MKT[p.platform] || COLORS.grayMid, display: "inline-block" }} />
                  <span style={{ fontWeight: 600 }}>{p.platform}</span>
                  <PostStatusBadge status={p.status} />
                  <span style={{ color: COLORS.grayMid, marginLeft: "auto" }}>{(p.scheduled_at || "").slice(5, 16)}</span>
                </div>
                <div style={{ color: COLORS.grayMid }}>{(p.content || "").slice(0, 40)}...</div>
              </div>
            ))
          }
        </div>

        {/* Post detail */}
        {selectedPost && (
          <div style={{ ...S.card, marginTop: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={S.badge(PLATFORM_COLORS_MKT[selectedPost.platform] || COLORS.orange)}>{selectedPost.platform}</span>
              <button onClick={() => setSelectedPost(null)} style={{ ...S.btn("ghost"), padding: "2px 6px", fontSize: 10 }}>x</button>
            </div>
            <div style={{ fontSize: 12, whiteSpace: "pre-wrap", marginBottom: 8, maxHeight: 150, overflowY: "auto" }}>{selectedPost.content}</div>
            <div style={{ fontSize: 10, color: COLORS.grayMid }}>
              {selectedPost.status === "posted" ? `Posted: ${(selectedPost.posted_at || "").slice(0, 16)}` : `Scheduled: ${(selectedPost.scheduled_at || "").slice(0, 16)}`}
            </div>
            <div style={{ marginTop: 4, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              <PostStatusBadge status={selectedPost.status} />
              {selectedPost.status === "scheduled" && (
                <button onClick={async () => {
                  if (!window.confirm(`Cancel this ${selectedPost.platform} post?`)) return;
                  await api(`/marketing/scheduled/${selectedPost.id}`, "DELETE");
                  setSelectedPost(null); reload();
                }} style={{ ...S.btn("ghost"), fontSize: 10, padding: "3px 8px", color: COLORS.danger }}>Cancel Post</button>
              )}
              {selectedPost.status === "failed" && (
                <button onClick={async () => {
                  await api("/marketing/post", "POST", { platform: selectedPost.platform, content: selectedPost.content });
                  reload();
                }} style={{ ...S.btn("ghost"), fontSize: 10, padding: "3px 8px", color: COLORS.warning }}>Retry</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MktCampaigns() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metaToken, setMetaToken] = useState("");
  const [metaAccount, setMetaAccount] = useState("");

  useEffect(() => {
    api("/marketing/meta/campaigns").then(r => { setData(r); setLoading(false); });
  }, []);

  async function testConnection() {
    await api("/social/connect/meta_ads", "POST", { access_token: metaToken, account_id: metaAccount, account_name: "Meta Ads" });
    setLoading(true);
    api("/marketing/meta/campaigns").then(r => { setData(r); setLoading(false); });
  }

  if (loading) return <Spinner />;

  return (
    <div>
      {!data?.connected && (
        <div style={S.card}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: COLORS.orange }}>Connect Meta Ads</div>
          <p style={{ fontSize: 12, color: COLORS.grayMid, marginBottom: 12 }}>
            Enter your Meta Access Token and Ad Account ID from business.facebook.com to manage campaigns.
          </p>
          <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
            <input value={metaToken} onChange={e => setMetaToken(e.target.value)} placeholder="Meta Access Token" style={S.input} />
            <input value={metaAccount} onChange={e => setMetaAccount(e.target.value)} placeholder="Ad Account ID (without act_)" style={S.input} />
          </div>
          <button onClick={testConnection} disabled={!metaToken || !metaAccount} style={{ ...S.btn("primary"), opacity: (metaToken && metaAccount) ? 1 : 0.5 }}>
            Connect & Test
          </button>
        </div>
      )}

      {data?.connected && (
        <>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.success, marginBottom: 12 }}>Meta Ads Connected</div>
          <div style={S.card}>
            <table style={S.table}>
              <thead><tr>{["Campaign","Status","Objective","Budget","Spend"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>
                {(data.campaigns || []).map((c, i) => (
                  <tr key={i}>
                    <td style={S.td}><div style={{ fontWeight: 600 }}>{c.name}</div></td>
                    <td style={S.td}><span style={S.badge(c.status === "ACTIVE" ? COLORS.success : COLORS.grayMid)}>{c.status}</span></td>
                    <td style={{ ...S.td, color: COLORS.grayMid }}>{c.objective || "—"}</td>
                    <td style={S.td}>{c.daily_budget ? `$${(c.daily_budget / 100).toFixed(0)}/day` : "—"}</td>
                    <td style={S.td}>{c.spend ? `$${c.spend}` : "—"}</td>
                  </tr>
                ))}
                {(data.campaigns || []).length === 0 && <tr><td colSpan={5} style={{ ...S.td, textAlign: "center", color: COLORS.grayMid }}>No campaigns yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!data?.connected && (data?.campaigns || []).length > 0 && (
        <div style={{ ...S.card, marginTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Saved Campaigns (local)</div>
          {data.campaigns.map((c, i) => (
            <div key={i} style={{ padding: "6px 0", fontSize: 12, borderBottom: `1px solid ${COLORS.cardBorder}22` }}>
              {c.name} — {c.status} — ${c.spend || 0} spent
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MktAnalytics() {
  const [insights, setInsights] = useState(null);
  const [scheduled, setScheduled] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [editTime, setEditTime] = useState("");

  const reload = useCallback(() => {
    api("/marketing/scheduled?limit=50").then(r => setScheduled(r || []));
  }, []);

  useEffect(() => {
    api("/marketing/meta/insights").then(r => setInsights(r));
    reload();
    api("/marketing/drafts?limit=50").then(r => setDrafts(r || []));
  }, [reload]);

  const byType = {};
  const byVertical = {};
  for (const d of drafts) {
    byType[d.content_type] = (byType[d.content_type] || 0) + 1;
    byVertical[d.vertical] = (byVertical[d.vertical] || 0) + 1;
  }

  async function cancelPost(p) {
    if (!window.confirm(`This will cancel the post and remove it from ${p.platform}. Continue?`)) return;
    setScheduled(prev => prev.filter(x => x.id !== p.id));
    await api(`/marketing/scheduled/${p.id}`, "DELETE");
    reload();
  }

  async function retryPost(p) {
    await api("/marketing/post", "POST", { platform: p.platform, content: p.content });
    reload();
  }

  function startEdit(p) {
    setEditingPost(p.id);
    setEditContent(p.content || "");
    setEditTime((p.scheduled_at || "").slice(0, 16));
  }

  async function saveEdit(p) {
    const body = { content: editContent };
    if (p.status === "scheduled" && editTime) body.scheduled_at = editTime;
    await api(`/marketing/scheduled/${p.id}`, "PATCH", body);
    setEditingPost(null);
    reload();
  }

  const liveScheduled = scheduled.filter(p => p.status === "posted");
  const failedCount = scheduled.filter(p => p.status === "failed").length;
  const deletedCount = scheduled.filter(p => p.status === "deleted").length;
  const scheduledCount = scheduled.filter(p => p.status === "scheduled").length;
  const [auditing, setAuditing] = useState(false);
  const [auditToast, setAuditToast] = useState(null);
  async function runAudit() {
    setAuditing(true);
    const r = await api("/admin/audit-facebook-posts", "POST", {});
    setAuditing(false);
    if (r?.counts) {
      setAuditToast(`Audit: ${r.counts.posted} live \u2022 ${r.counts.failed} failed \u2022 ${r.counts.deleted} deleted`);
      reload();
    } else {
      setAuditToast("Audit failed");
    }
    setTimeout(() => setAuditToast(null), 4000);
  }

  // Aggregate metrics across LIVE posts only
  const totalImpressions = liveScheduled.reduce((a, p) => a + (p.impressions || 0), 0);
  const totalEngaged = liveScheduled.reduce((a, p) => a + (p.engaged_users || 0), 0);
  const totalReactions = liveScheduled.reduce((a, p) => a + (p.likes || 0) + (p.reactions_love || 0)
    + (p.reactions_wow || 0) + (p.reactions_haha || 0) + (p.reactions_sorry || 0) + (p.reactions_anger || 0), 0);
  const totalComments = liveScheduled.reduce((a, p) => a + (p.comments || 0), 0);
  const totalShares = liveScheduled.reduce((a, p) => a + (p.shares || 0), 0);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.orange }}>Live Posts</div>
          <button onClick={runAudit} disabled={auditing}
            style={{ ...S.btn("ghost"), fontSize: 10, padding: "3px 8px", opacity: auditing ? 0.5 : 1 }}
            title="Verify every stored post against Facebook, pull fresh insights, mark failed/deleted correctly">
            {auditing ? "Auditing\u2026" : "\u21BA Audit"}
          </button>
        </div>
        <div style={{ fontSize: 28, fontWeight: 800, marginBottom: 4 }}>{liveScheduled.length}</div>
        <div style={{ fontSize: 11, color: COLORS.grayMid, marginBottom: 10 }}>
          Confirmed live on Facebook
        </div>
        {liveScheduled.length > 0 && (
          <div style={{ fontSize: 11, color: COLORS.text }}>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
              <span>Impressions</span><b>{totalImpressions.toLocaleString()}</b>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
              <span>Engaged users</span><b>{totalEngaged.toLocaleString()}</b>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
              <span>Reactions</span><b>{totalReactions.toLocaleString()}</b>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
              <span>Comments / Shares</span>
              <b>{totalComments.toLocaleString()} / {totalShares.toLocaleString()}</b>
            </div>
          </div>
        )}
        <div style={{ marginTop: 10, fontSize: 10, color: COLORS.grayMid, borderTop: `1px solid ${COLORS.cardBorder}`, paddingTop: 6 }}>
          {failedCount > 0 && <span>Failed: <b>{failedCount}</b> \u00B7 </span>}
          {deletedCount > 0 && <span>Deleted: <b>{deletedCount}</b> \u00B7 </span>}
          {scheduledCount > 0 && <span>Scheduled: <b>{scheduledCount}</b> \u00B7 </span>}
          Total rows: <b>{scheduled.length}</b>
        </div>
        {auditToast && (
          <div style={{ marginTop: 6, fontSize: 10, fontWeight: 700, color: COLORS.success }}>{auditToast}</div>
        )}
      </div>

      <div style={S.card}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: COLORS.orange }}>Content Generated</div>
        <div style={{ fontSize: 28, fontWeight: 800, marginBottom: 8 }}>{drafts.length}</div>
        <div style={{ fontSize: 11, color: COLORS.grayMid }}>Total drafts (last 50)</div>
        {Object.entries(byType).sort((a, b) => b[1] - a[1]).map(([type, count]) => (
          <div key={type} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0" }}>
            <span>{type}</span><span style={{ fontWeight: 700 }}>{count}</span>
          </div>
        ))}
      </div>

      <div style={{ ...S.card, gridColumn: "1 / -1" }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: COLORS.orange }}>
          Live Posts
          {liveScheduled.length > 0 && (
            <span style={{ fontSize: 10, color: COLORS.grayMid, marginLeft: 8, fontWeight: 400 }}>
              ({liveScheduled.length} confirmed on Facebook \u2022 failed/deleted hidden)
            </span>
          )}
        </div>
        {liveScheduled.length === 0 ? (
          <div style={{ fontSize: 12, color: COLORS.grayMid, padding: 12, textAlign: "center" }}>
            0 posts. Click <b>\u21BA Audit</b> above to verify stored posts against Facebook.
          </div>
        ) : (
          <table style={S.table}>
            <thead><tr>{["Platform","Content","Posted At","Reach","Engagement","Status","Actions"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {liveScheduled.map(p => (
                <tr key={p.id}>
                  <td style={S.td}><span style={S.badge(PLATFORM_COLORS_MKT[p.platform] || COLORS.orange)}>{p.platform}</span></td>
                  <td style={{ ...S.td, maxWidth: 300 }}>
                    {editingPost === p.id ? (
                      <textarea value={editContent} onChange={e => setEditContent(e.target.value)}
                        style={{ ...S.input, width: "100%", minHeight: 60, fontSize: 11 }} />
                    ) : (
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(p.content || "").slice(0, 80)}</div>
                    )}
                  </td>
                  <td style={{ ...S.td, color: COLORS.grayMid }}>
                    {editingPost === p.id && p.status === "scheduled" ? (
                      <input type="datetime-local" value={editTime} onChange={e => setEditTime(e.target.value)} style={{ ...S.input, fontSize: 11, width: 180 }} />
                    ) : (
                      (p.posted_at || p.scheduled_at || "").replace("T", " ").slice(0, 16)
                    )}
                  </td>
                  <td style={S.td}>
                    <div style={{ fontSize: 11 }}>
                      {(p.impressions || 0).toLocaleString()} <span style={{ color: COLORS.grayMid }}>views</span>
                    </div>
                    {p.impressions_unique > 0 && (
                      <div style={{ fontSize: 10, color: COLORS.grayMid }}>
                        {p.impressions_unique.toLocaleString()} unique
                      </div>
                    )}
                  </td>
                  <td style={S.td}>
                    <div style={{ fontSize: 11 }}>
                      {"\u2764"}{((p.likes || 0) + (p.reactions_love || 0) + (p.reactions_wow || 0) + (p.reactions_haha || 0)).toLocaleString()}
                      {" "}{"\ud83d\udcac"}{(p.comments || 0).toLocaleString()}
                      {" "}{"\u21bb"}{(p.shares || 0).toLocaleString()}
                    </div>
                    {p.engagement_rate > 0 && (
                      <div style={{ fontSize: 10, color: COLORS.grayMid }}>
                        {p.engagement_rate.toFixed(1)}% rate
                      </div>
                    )}
                  </td>
                  <td style={S.td}><PostStatusBadge status={p.status} /></td>
                  <td style={S.td}>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {editingPost === p.id ? (
                        <>
                          <button onClick={() => saveEdit(p)} style={{ ...S.btn("primary"), fontSize: 10, padding: "3px 8px" }}>Save</button>
                          <button onClick={() => setEditingPost(null)} style={{ ...S.btn("ghost"), fontSize: 10, padding: "3px 8px" }}>Cancel</button>
                        </>
                      ) : (
                        <>
                          {p.status === "scheduled" && (
                            <button onClick={() => startEdit(p)} style={{ ...S.btn("ghost"), fontSize: 10, padding: "3px 8px" }}>Edit</button>
                          )}
                          {(p.status === "scheduled" || p.status === "posted") && (
                            <button onClick={() => cancelPost(p)} style={{ ...S.btn("ghost"), fontSize: 10, padding: "3px 8px", color: COLORS.danger }}>Cancel</button>
                          )}
                          {p.status === "failed" && (
                            <button onClick={() => retryPost(p)} style={{ ...S.btn("ghost"), fontSize: 10, padding: "3px 8px", color: COLORS.warning }}>Retry</button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ gridColumn: "1 / -1" }}>
        <MktComments />
      </div>

      {insights?.connected && (insights.insights || []).length > 0 && (
        <div style={{ ...S.card, gridColumn: "1 / -1" }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: COLORS.orange }}>Meta Ads Performance (30 days)</div>
          {insights.insights.map((ins, i) => (
            <div key={i} style={{ display: "flex", gap: 16, fontSize: 13, padding: "8px 0", borderBottom: `1px solid ${COLORS.cardBorder}22` }}>
              <span>Impressions: <b>{ins.impressions}</b></span>
              <span>Clicks: <b>{ins.clicks}</b></span>
              <span>CTR: <b>{ins.ctr}%</b></span>
              <span>Spend: <b>${ins.spend}</b></span>
              <span>CPC: <b>${ins.cpc}</b></span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PostStatusBadge({ status }) {
  const config = {
    draft: { icon: "\ud83d\udcdd", label: "Draft", color: COLORS.grayMid },
    scheduled: { icon: "\ud83d\udd50", label: "Scheduled", color: COLORS.warning },
    posted: { icon: "\u2705", label: "Posted", color: COLORS.success },
    cancelled: { icon: "\u274c", label: "Cancelled", color: COLORS.danger },
    failed: { icon: "\u26a0\ufe0f", label: "Failed", color: COLORS.danger },
  };
  const c = config[status] || config.draft;
  return <span style={S.badge(c.color)}>{c.icon} {c.label}</span>;
}

function MktComments() {
  const [comments, setComments] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("new");
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState(null);
  const [replyTarget, setReplyTarget] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [appReview, setAppReview] = useState({ required: false, message: null });

  const reload = useCallback(() => {
    setLoading(true);
    api(`/marketing/comments?status=${filter}&limit=100`).then(r => {
      setComments(r?.comments || []);
      setCounts(r?.counts || {});
      setAppReview({ required: !!r?.app_review_required, message: r?.app_review_message || null });
      setLoading(false);
    });
  }, [filter]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 2500); return () => clearTimeout(t); }, [toast]);

  async function syncNow() {
    setSyncing(true);
    const r = await api("/marketing/sync-comments", "POST", {});
    setSyncing(false);
    if (r) {
      if (r.app_review_required) {
        setAppReview({ required: true, message: r.app_review_message });
        setToast("Facebook App Review required — see banner");
      } else {
        setAppReview({ required: false, message: null });
        setToast(`Synced ${r.scanned_posts || 0} posts \u2022 ${r.new_comments || 0} new \u2022 ${r.updated_comments || 0} updated`);
      }
      reload();
    } else {
      setToast("Sync failed");
    }
  }

  async function markRead(c) {
    await api(`/marketing/comments/${c.id}/mark-read`, "POST", {});
    reload();
  }

  const tabs = [
    { id: "new", label: "New", count: counts.new || 0, color: COLORS.warning },
    { id: "replied", label: "Replied", count: counts.replied || 0, color: COLORS.success },
    { id: "ignored", label: "Ignored", count: counts.ignored || 0, color: COLORS.grayMid },
    { id: "all", label: "All", count: (counts.new || 0) + (counts.replied || 0) + (counts.ignored || 0), color: COLORS.orange },
  ];

  return (
    <div style={S.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.orange }}>
          {"\ud83d\udcac"} Comments
          {counts.new > 0 && (
            <span style={{ marginLeft: 8, background: COLORS.danger, color: "#fff",
              fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10 }}>
              {counts.new} new
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => setManualOpen(true)}
            style={{ ...S.btn("ghost"), fontSize: 11, padding: "4px 10px" }}>
            {"\u270D\ufe0f"} Log Comment Manually
          </button>
          <button onClick={syncNow} disabled={syncing}
            style={{ ...S.btn("ghost"), fontSize: 11, padding: "4px 10px", opacity: syncing ? 0.5 : 1 }}>
            {syncing ? "Syncing\u2026" : "\u21BA Sync Now"}
          </button>
        </div>
      </div>

      {appReview.required && (
        <div style={{
          background: "#FEF3C7", border: "1px solid #F59E0B",
          borderLeft: "4px solid #F59E0B", borderRadius: 5,
          padding: "10px 14px", marginBottom: 12, fontSize: 12, color: "#78350F",
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            {"\ud83d\udccb"} Auto-sync pending Facebook App Review
          </div>
          <div style={{ lineHeight: 1.5 }}>
            Reading + replying via API needs <code>pages_read_user_content</code> +{" "}
            <code>pages_manage_engagement</code>, which require{" "}
            <a href="https://developers.facebook.com/apps/1496254875190877/app-review/permissions/"
              target="_blank" rel="noreferrer"
              style={{ color: "#92400E", fontWeight: 700 }}>
              App Review
            </a>{" "}
            (2-5 business days).
            <br />
            <b>Interim workflow:</b> click <b>Log Comment Manually</b> to add a comment you saw on
            your page, then <b>DrewOS Reply</b> \u2192 <b>Copy Reply</b> \u2192 paste on Facebook \u2192{" "}
            <b>Mark as Replied</b>. Posting via the CRM still works normally.
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 4, marginBottom: 12, flexWrap: "wrap" }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setFilter(t.id)}
            style={{ ...S.btn(filter === t.id ? "primary" : "ghost"),
              fontSize: 11, padding: "4px 10px" }}>
            {t.label} <span style={{ opacity: 0.7 }}>({t.count})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ fontSize: 12, color: COLORS.grayMid, padding: 12 }}>Loading comments\u2026</div>
      ) : comments.length === 0 ? (
        <div style={{ fontSize: 12, color: COLORS.grayMid, padding: 12, textAlign: "center" }}>
          {filter === "new"
            ? "No new comments. Click Sync Now to pull the latest from Facebook."
            : `No ${filter} comments.`}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {comments.map(c => (
            <CommentCard key={c.id} comment={c}
              onReply={() => setReplyTarget(c)}
              onMarkRead={() => markRead(c)} />
          ))}
        </div>
      )}

      {replyTarget && (
        <CommentReplyModal comment={replyTarget}
          onClose={() => setReplyTarget(null)}
          onReplied={() => { setReplyTarget(null); reload(); setToast("Reply logged \u2713"); }} />
      )}

      {manualOpen && (
        <ManualCommentModal
          onClose={() => setManualOpen(false)}
          onLogged={() => { setManualOpen(false); reload(); setToast("Comment logged \u2713"); }} />
      )}

      {toast && (
        <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 2000,
          background: COLORS.success, color: "#fff", padding: "10px 16px", borderRadius: 6,
          fontSize: 12, fontWeight: 700, boxShadow: "0 4px 14px rgba(0,0,0,0.3)" }}>
          {toast}
        </div>
      )}
    </div>
  );
}

function CommentCard({ comment, onReply, onMarkRead }) {
  const status = comment.status || "new";
  const statusColor = status === "replied" ? COLORS.success
                   : status === "ignored" ? COLORS.grayMid
                   : COLORS.warning;
  const when = (comment.created_time || "").replace("T", " ").slice(0, 16);
  return (
    <div style={{ border: `1px solid ${COLORS.cardBorder}`, borderLeft: `3px solid ${statusColor}`,
      borderRadius: 5, padding: 10, background: status === "new" ? `${COLORS.warning}08` : "#fff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: COLORS.orange,
              color: "#fff", fontSize: 11, fontWeight: 800, display: "flex",
              alignItems: "center", justifyContent: "center" }}>
              {(comment.commenter_name || "?").trim().slice(0, 1).toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{comment.commenter_name || "Unknown"}</div>
              <div style={{ fontSize: 10, color: COLORS.grayMid }}>
                {when} \u2022 {comment.like_count || 0} {"\u2764"}
              </div>
            </div>
            <span style={{ ...S.badge(statusColor), fontSize: 9, marginLeft: "auto" }}>
              {status === "replied" ? "Replied \u2713" : status === "ignored" ? "Ignored" : "New"}
            </span>
          </div>
          <div style={{ fontSize: 12, color: COLORS.text, whiteSpace: "pre-wrap",
            background: "#0002", padding: "6px 10px", borderRadius: 4, marginTop: 4 }}>
            {comment.message || "(empty)"}
          </div>
          {comment.reply_text && (
            <div style={{ fontSize: 11, color: COLORS.success, marginTop: 6, paddingLeft: 12,
              borderLeft: `2px solid ${COLORS.success}` }}>
              <b>DrewOS's reply:</b> {comment.reply_text}
            </div>
          )}
        </div>
      </div>
      {status !== "replied" && (
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          <button onClick={onReply}
            style={{ ...S.btn("primary"), fontSize: 11, padding: "4px 12px" }}>
            DrewOS Reply
          </button>
          <button onClick={onMarkRead}
            style={{ ...S.btn("ghost"), fontSize: 11, padding: "4px 12px" }}>
            Mark Read
          </button>
        </div>
      )}
    </div>
  );
}

function CommentReplyModal({ comment, onClose, onReplied }) {
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [err, setErr] = useState(null);
  const [regenKey, setRegenKey] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true); setErr(null);
      try {
        // Load the original post content for context (best effort)
        let postContent = "";
        try {
          const sp = await api("/marketing/scheduled?limit=100");
          const match = (sp || []).find(p => p.post_id === comment.post_id);
          postContent = match?.content || "";
        } catch {}
        const r = await api("/drew/generate-comment-reply", "POST", {
          comment_text: comment.message || "",
          post_content: postContent,
          commenter_name: comment.commenter_name || "",
        });
        if (r?.reply) setReply(r.reply);
        else setErr("DrewOS couldn't generate a reply");
      } catch (e) { setErr(String(e)); }
      setLoading(false);
    })();
  }, [comment.id, regenKey]);  // eslint-disable-line react-hooks/exhaustive-deps

  async function copyReply() {
    if (!reply.trim()) return;
    try { await navigator.clipboard.writeText(reply.trim()); setCopied(true);
      setTimeout(() => setCopied(false), 2000); } catch {}
  }

  function openFacebook() {
    window.open("https://facebook.com/outpostprep", "_blank", "noopener");
  }

  async function markReplied() {
    if (!reply.trim()) return;
    setPosting(true); setErr(null);
    const r = await api("/marketing/reply-comment", "POST", {
      comment_row_id: comment.id, reply_text: reply.trim(), manual: true,
    });
    setPosting(false);
    if (r?.status === "replied") onReplied();
    else setErr(r?.error || "Failed to log reply");
  }

  return (
    <>
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1200 }} onClick={onClose} />
      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
        background: "#fff", borderRadius: 8, boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
        zIndex: 1201, width: "90vw", maxWidth: 560, padding: 20, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 800 }}>
            {"\ud83d\udcac"} Reply to {comment.commenter_name || "commenter"}
          </div>
          <button onClick={onClose} style={{ ...S.btn("ghost"), padding: "4px 10px" }}>{"\u2715"}</button>
        </div>
        <div style={{ fontSize: 11, color: COLORS.grayMid, marginBottom: 4 }}>Their comment:</div>
        <div style={{ fontSize: 13, background: "#0002", padding: "8px 12px",
          borderRadius: 4, marginBottom: 12, whiteSpace: "pre-wrap" }}>
          {comment.message || "(empty)"}
        </div>
        <div style={{ fontSize: 11, color: COLORS.grayMid, marginBottom: 4 }}>
          DrewOS's suggested reply (edit freely):
        </div>
        {loading ? (
          <div style={{ fontSize: 12, color: COLORS.grayMid, padding: 12 }}>DrewOS is composing\u2026</div>
        ) : (
          <textarea value={reply} onChange={e => setReply(e.target.value)}
            placeholder="DrewOS's reply here\u2026"
            style={{ ...S.input, width: "100%", minHeight: 110, fontSize: 13,
              fontFamily: "inherit", lineHeight: 1.5 }} />
        )}
        {err && <div style={{ fontSize: 11, color: COLORS.danger, marginTop: 6 }}>{err}</div>}

        {/* Copy/paste fallback workflow (App Review pending) */}
        <div style={{ marginTop: 14, padding: "10px 12px",
          background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 5 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#1E3A8A", marginBottom: 6 }}>
            {"\ud83d\udcdd"} Copy/paste workflow (until App Review approves):
          </div>
          <ol style={{ fontSize: 11, color: "#1E3A8A", margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
            <li>Click <b>Copy Reply</b> to put the text on your clipboard</li>
            <li>Click <b>Open Facebook</b> and paste the reply under the comment</li>
            <li>Come back and click <b>Mark as Replied</b> so the CRM logs it</li>
          </ol>
        </div>

        <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <button onClick={() => setRegenKey(k => k + 1)} disabled={loading || posting}
            style={{ ...S.btn("ghost"), fontSize: 11, padding: "6px 12px" }}>
            {"\u21BA"} Regenerate
          </button>
          <button onClick={copyReply} disabled={loading || !reply.trim()}
            style={{ ...S.btn("primary"), fontSize: 11, padding: "6px 14px",
              background: copied ? COLORS.success : undefined,
              opacity: (loading || !reply.trim()) ? 0.5 : 1 }}>
            {copied ? "Copied \u2713" : "\ud83d\udccb Copy Reply"}
          </button>
          <button onClick={openFacebook}
            style={{ ...S.btn("ghost"), fontSize: 11, padding: "6px 14px" }}>
            {"\ud83d\udd17"} Open Facebook
          </button>
          <button onClick={markReplied} disabled={posting || loading || !reply.trim()}
            style={{ ...S.btn("primary"), fontSize: 11, padding: "6px 14px",
              background: COLORS.success,
              opacity: (posting || loading || !reply.trim()) ? 0.5 : 1 }}>
            {posting ? "Logging\u2026" : "\u2713 Mark as Replied"}
          </button>
          <button onClick={onClose} disabled={posting}
            style={{ ...S.btn("ghost"), fontSize: 11, padding: "6px 12px" }}>
            Skip
          </button>
        </div>
      </div>
    </>
  );
}

function ManualCommentModal({ onClose, onLogged }) {
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [postId, setPostId] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  async function save() {
    if (!name.trim() || !message.trim()) {
      setErr("Name and comment text are required.");
      return;
    }
    setSaving(true); setErr(null);
    const r = await api("/marketing/comments/manual", "POST", {
      commenter_name: name.trim(), message: message.trim(),
      post_id: postId.trim() || null,
    });
    setSaving(false);
    if (r?.id) onLogged();
    else setErr("Failed to log comment.");
  }

  return (
    <>
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1200 }} onClick={onClose} />
      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
        background: "#fff", borderRadius: 8, boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
        zIndex: 1201, width: "90vw", maxWidth: 500, padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 800 }}>
            {"\u270D\ufe0f"} Log a Facebook comment manually
          </div>
          <button onClick={onClose} style={{ ...S.btn("ghost"), padding: "4px 10px" }}>{"\u2715"}</button>
        </div>
        <div style={{ fontSize: 11, color: COLORS.grayMid, marginBottom: 10 }}>
          Paste in a comment you saw on facebook.com/outpostprep. DrewOS can then draft a reply.
        </div>
        <label style={{ fontSize: 11, fontWeight: 700, color: COLORS.grayMid }}>Commenter name</label>
        <input value={name} onChange={e => setName(e.target.value)}
          placeholder="e.g. Jane Smith" style={{ ...S.input, marginTop: 2, marginBottom: 10 }} />
        <label style={{ fontSize: 11, fontWeight: 700, color: COLORS.grayMid }}>Their comment</label>
        <textarea value={message} onChange={e => setMessage(e.target.value)}
          placeholder="Paste the exact text of their comment\u2026"
          style={{ ...S.input, marginTop: 2, marginBottom: 10,
            minHeight: 90, fontFamily: "inherit", lineHeight: 1.5 }} />
        <label style={{ fontSize: 11, fontWeight: 700, color: COLORS.grayMid }}>
          Post ID (optional)
        </label>
        <input value={postId} onChange={e => setPostId(e.target.value)}
          placeholder="624817420721296_... (leave blank if unsure)"
          style={{ ...S.input, marginTop: 2, fontSize: 11 }} />
        {err && <div style={{ fontSize: 11, color: COLORS.danger, marginTop: 6 }}>{err}</div>}
        <div style={{ display: "flex", gap: 6, marginTop: 14, justifyContent: "flex-end" }}>
          <button onClick={onClose} disabled={saving}
            style={{ ...S.btn("ghost"), fontSize: 11, padding: "6px 12px" }}>Cancel</button>
          <button onClick={save} disabled={saving || !name.trim() || !message.trim()}
            style={{ ...S.btn("primary"), fontSize: 11, padding: "6px 14px",
              opacity: (saving || !name.trim() || !message.trim()) ? 0.5 : 1 }}>
            {saving ? "Logging\u2026" : "Log Comment"}
          </button>
        </div>
      </div>
    </>
  );
}

function MktChannels() {
  const [connections, setConnections] = useState([]);
  const [modal, setModal] = useState(null);
  const [fbToken, setFbToken] = useState("");
  const [fbPageId, setFbPageId] = useState("");
  const [fbPageName, setFbPageName] = useState("");
  const [igAccountId, setIgAccountId] = useState("");
  const [fbTestResult, setFbTestResult] = useState(null);
  const [fbTesting, setFbTesting] = useState(false);
  const [fbSaving, setFbSaving] = useState(false);
  const [liClientId, setLiClientId] = useState("");
  const [liClientSecret, setLiClientSecret] = useState("");
  const [liSaving, setLiSaving] = useState(false);
  const [liPolling, setLiPolling] = useState(false);

  const loadConnections = useCallback(() => {
    api("/social/connections").then(r => setConnections(r || []));
  }, []);

  useEffect(() => { loadConnections(); }, [loadConnections]);

  const connected = {};
  for (const c of connections) connected[c.platform] = c;

  const channels = [
    { id: "linkedin_personal", name: "LinkedIn Personal", icon: "in", color: "#0077B5", desc: "Post as yourself" },
    { id: "linkedin_business", name: "LinkedIn Business", icon: "in", color: "#0077B5", desc: "Post as company page" },
    { id: "facebook", name: "Facebook Page", icon: "f", color: "#1877F2", desc: "Post to your business page" },
    { id: "instagram", name: "Instagram Business", icon: "ig", color: "#E4405F", desc: "Requires linked Facebook Page" },
    { id: "google_business", name: "Google Business Profile", icon: "G", color: "#4285F4", desc: "Local SEO posts" },
    { id: "youtube", name: "YouTube", icon: "yt", color: "#FF0000", desc: "Script generation only" },
  ];

  async function disconnect(platform) {
    if (!window.confirm(`Disconnect ${platform}? This will remove saved credentials.`)) return;
    await api(`/social/connections/${platform}`, "DELETE");
    setConnections(prev => prev.filter(c => c.platform !== platform));
  }

  function openConnectModal(channelId) {
    if (channelId === "facebook" || channelId === "instagram") setModal("facebook");
    else if (channelId === "linkedin_personal" || channelId === "linkedin_business") setModal("linkedin");
    else if (channelId === "google_business") setModal("google");
  }

  function closeModal() {
    setModal(null);
    setFbToken(""); setFbPageId(""); setFbPageName(""); setIgAccountId("");
    setFbTestResult(null); setFbTesting(false); setFbSaving(false);
    setLiClientId(""); setLiClientSecret(""); setLiSaving(false); setLiPolling(false);
  }

  async function testFbConnection() {
    if (!fbToken || !fbPageId) return;
    setFbTesting(true); setFbTestResult(null);
    try {
      const r = await api("/social/test/meta", "POST", { access_token: fbToken, page_id: fbPageId });
      if (r && r.success) {
        setFbTestResult({ ok: true, name: r.page_name || "Page verified" });
        setFbPageName(r.page_name || "Facebook Page");
      } else {
        setFbTestResult({ ok: false, error: (r && r.error) || "Connection test failed" });
      }
    } catch (e) {
      setFbTestResult({ ok: false, error: "Network error" });
    }
    setFbTesting(false);
  }

  async function saveFbConnection() {
    setFbSaving(true);
    await api("/social/connect/facebook", "POST", {
      access_token: fbToken, page_id: fbPageId, account_name: fbPageName || "Facebook Page",
    });
    if (igAccountId.trim()) {
      await api("/social/connect/instagram", "POST", {
        access_token: fbToken, account_id: igAccountId, account_name: "Instagram",
      });
    }
    setFbSaving(false);
    loadConnections();
    closeModal();
  }

  async function saveLiConnection() {
    if (!liClientId || !liClientSecret) return;
    setLiSaving(true);
    await api("/social/connect/linkedin_personal", "POST", {
      client_id: liClientId, client_secret: liClientSecret,
      account_name: "LinkedIn (pending auth)",
    });
    const redirectUri = encodeURIComponent("https://outpost-crm-production.up.railway.app/social/linkedin/callback");
    window.open(
      `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${liClientId}&redirect_uri=${redirectUri}&scope=w_member_social%20r_basicprofile`,
      "_blank"
    );
    setLiSaving(false);
    setLiPolling(true);
  }

  useEffect(() => {
    if (!liPolling) return;
    const iv = setInterval(() => {
      api("/social/connections").then(r => {
        const conns = r || [];
        const li = conns.find(c => c.platform === "linkedin_personal");
        if (li && li.account_name !== "LinkedIn (pending auth)") {
          setConnections(conns);
          setLiPolling(false);
          closeModal();
        }
      });
    }, 3000);
    return () => clearInterval(iv);
  }, [liPolling]); // eslint-disable-line react-hooks/exhaustive-deps

  const inputStyle = { ...S.input, width: "100%", marginBottom: 8 };
  const stepStyle = { fontSize: 11, color: COLORS.grayMid, marginBottom: 4, paddingLeft: 16, textIndent: -16 };

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        {channels.map(ch => {
          const conn = connected[ch.id];
          return (
            <div key={ch.id} style={{ ...S.card, borderTop: `3px solid ${conn ? ch.color : COLORS.cardBorder}`, borderLeft: conn ? `3px solid ${ch.color}` : undefined }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ ...S.badge(ch.color), fontSize: 12, padding: "4px 8px" }}>{ch.icon}</span>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>{ch.name}</span>
                </div>
                <span style={{ ...S.badge(conn ? COLORS.success : COLORS.grayMid), fontSize: 9 }}>
                  {conn ? "\u2705 Connected" : "Not Connected"}
                </span>
              </div>
              <div style={{ fontSize: 11, color: COLORS.grayMid, marginBottom: 10 }}>{ch.desc}</div>
              {conn ? (
                <div>
                  <div style={{ fontSize: 11, marginBottom: 6 }}>Account: <b>{conn.account_name || conn.account_id || "\u2014"}</b></div>
                  <div style={{ fontSize: 10, color: COLORS.grayMid, marginBottom: 4 }}>Since {(conn.connected_at || "").slice(0, 10)}</div>
                  {conn.last_posted_at && <div style={{ fontSize: 10, color: COLORS.grayMid, marginBottom: 8 }}>Last post: {(conn.last_posted_at || "").slice(0, 16).replace("T", " ")}</div>}
                  <button onClick={() => disconnect(ch.id)} style={{ ...S.btn("ghost"), fontSize: 11, padding: "4px 10px", color: COLORS.danger }}>Disconnect</button>
                </div>
              ) : (
                <div style={{ fontSize: 11 }}>
                  {ch.id === "youtube" ? (
                    <a href="https://studio.youtube.com" target="_blank" rel="noreferrer" style={{ ...S.btn("ghost"), fontSize: 11, textDecoration: "none", display: "inline-block" }}>Open YouTube Studio</a>
                  ) : (
                    <button onClick={() => openConnectModal(ch.id)} style={{ ...S.btn("primary"), fontSize: 11, padding: "5px 14px" }}>Connect</button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Facebook/Instagram Connection Modal */}
      {modal === "facebook" && (
        <div style={S.modal} onClick={closeModal}>
          <div style={{ ...S.modalBox, maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#1877F2" }}>Connect Facebook / Instagram</div>
              <button onClick={closeModal} style={{ ...S.btn("ghost"), padding: "2px 8px" }}>x</button>
            </div>
            <div style={{ background: `${COLORS.cardBorder}22`, borderRadius: 6, padding: 12, marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Setup Steps:</div>
              <div style={stepStyle}>1. Go to <a href="https://developers.facebook.com" target="_blank" rel="noreferrer">developers.facebook.com</a> and create an app (type: Business).</div>
              <div style={stepStyle}>2. Add "Pages" and "Instagram" products to your app.</div>
              <div style={stepStyle}>3. In Graph API Explorer, select your app and request: pages_manage_posts, pages_read_engagement, instagram_basic, instagram_content_publish.</div>
              <div style={stepStyle}>4. Generate a Page Access Token (long-lived recommended).</div>
              <div style={stepStyle}>5. Find your Page ID from your Facebook Page's "About" section.</div>
              <div style={stepStyle}>6. For Instagram: use the Page's linked Instagram Business Account ID.</div>
            </div>
            <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase", display: "block", marginBottom: 4 }}>Page Access Token</label>
            <input value={fbToken} onChange={e => setFbToken(e.target.value)} placeholder="EAAxxxxxxx..." style={inputStyle} />
            <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase", display: "block", marginBottom: 4 }}>Page ID</label>
            <input value={fbPageId} onChange={e => setFbPageId(e.target.value)} placeholder="123456789012345" style={inputStyle} />
            <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase", display: "block", marginBottom: 4 }}>Instagram Business Account ID (optional)</label>
            <input value={igAccountId} onChange={e => setIgAccountId(e.target.value)} placeholder="17841400000000000" style={inputStyle} />

            <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
              <button onClick={testFbConnection} disabled={!fbToken || !fbPageId || fbTesting}
                style={{ ...S.btn("ghost"), fontSize: 11, opacity: (!fbToken || !fbPageId || fbTesting) ? 0.5 : 1 }}>
                {fbTesting ? "Testing..." : "Test Connection"}
              </button>
              {fbTestResult && (
                <span style={{ fontSize: 12 }}>
                  {fbTestResult.ok
                    ? <span style={{ color: COLORS.success }}>{"\u2705"} {fbTestResult.name}</span>
                    : <span style={{ color: COLORS.danger }}>{"\u274c"} {fbTestResult.error}</span>}
                </span>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16, borderTop: `1px solid ${COLORS.cardBorder}`, paddingTop: 12 }}>
              <button onClick={closeModal} style={{ ...S.btn("ghost"), fontSize: 12 }}>Cancel</button>
              <button onClick={saveFbConnection} disabled={!fbToken || !fbPageId || fbSaving}
                style={{ ...S.btn("primary"), fontSize: 12, opacity: (!fbToken || !fbPageId || fbSaving) ? 0.5 : 1 }}>
                {fbSaving ? "Saving..." : "Save Connection"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LinkedIn Connection Modal */}
      {modal === "linkedin" && (
        <div style={S.modal} onClick={closeModal}>
          <div style={{ ...S.modalBox, maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#0077B5" }}>Connect LinkedIn</div>
              <button onClick={closeModal} style={{ ...S.btn("ghost"), padding: "2px 8px" }}>x</button>
            </div>
            <div style={{ background: `${COLORS.cardBorder}22`, borderRadius: 6, padding: 12, marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Setup Steps:</div>
              <div style={stepStyle}>1. Go to <a href="https://www.linkedin.com/developers" target="_blank" rel="noreferrer">linkedin.com/developers</a> and create an app.</div>
              <div style={stepStyle}>2. Under Products, request "Share on LinkedIn" and "Sign In with LinkedIn using OpenID Connect".</div>
              <div style={stepStyle}>3. Add redirect URL: https://outpost-crm-production.up.railway.app/social/linkedin/callback</div>
              <div style={stepStyle}>4. Copy the Client ID and Client Secret from the Auth tab.</div>
            </div>
            <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase", display: "block", marginBottom: 4 }}>Client ID</label>
            <input value={liClientId} onChange={e => setLiClientId(e.target.value)} placeholder="xxxxxxxxxxxxxxxx" style={inputStyle} />
            <label style={{ fontSize: 10, color: COLORS.grayMid, textTransform: "uppercase", display: "block", marginBottom: 4 }}>Client Secret</label>
            <input value={liClientSecret} onChange={e => setLiClientSecret(e.target.value)} placeholder="xxxxxxxxxxxxxxxx" type="password" style={inputStyle} />

            {liPolling && (
              <div style={{ fontSize: 12, color: COLORS.warning, marginTop: 8, padding: 10, background: `${COLORS.warning}11`, borderRadius: 6 }}>
                Complete authorization in the popup window. This page will refresh automatically when done.
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16, borderTop: `1px solid ${COLORS.cardBorder}`, paddingTop: 12 }}>
              <button onClick={closeModal} style={{ ...S.btn("ghost"), fontSize: 12 }}>Cancel</button>
              <button onClick={saveLiConnection} disabled={!liClientId || !liClientSecret || liSaving || liPolling}
                style={{ ...S.btn("primary"), fontSize: 12, opacity: (!liClientId || !liClientSecret || liSaving || liPolling) ? 0.5 : 1 }}>
                {liSaving ? "Saving..." : liPolling ? "Waiting for auth..." : "Save & Authorize"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Google Business Connection Modal */}
      {modal === "google" && (
        <div style={S.modal} onClick={closeModal}>
          <div style={{ ...S.modalBox, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#4285F4" }}>Google Business Profile</div>
              <button onClick={closeModal} style={{ ...S.btn("ghost"), padding: "2px 8px" }}>x</button>
            </div>
            <div style={{ background: `${COLORS.cardBorder}22`, borderRadius: 6, padding: 16, textAlign: "center" }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>{"Coming soon"}</div>
              <div style={{ fontSize: 13, color: COLORS.grayMid }}>
                Google Business Profile posting will use your existing Google Calendar OAuth credentials.
                This integration is coming in a future update.
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <button onClick={closeModal} style={{ ...S.btn("ghost"), fontSize: 12 }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SettingsScreen() {
  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Settings</h2>
      <SignatureSettings />
    </div>
  );
}

function SignatureSettings() {
  const [data, setData] = useState(null);
  const [btb, setBtb] = useState("");
  const [btc, setBtc] = useState("");
  const [logoB64, setLogoB64] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [activeTab, setActiveTab] = useState("btb");
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api("/settings/signatures").then(r => {
      if (!r) return;
      setData(r);
      // Pre-populate textareas with stored custom HTML, or the rendered default so
      // the operator can edit a working starting point instead of an empty box.
      setBtb(r.btb_html || r.rendered_btb || "");
      setBtc(r.btc_html || r.rendered_btc || "");
      setLogoB64(r.logo_base64 || null);
    });
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!msg) return; const t = setTimeout(() => setMsg(null), 3000); return () => clearTimeout(t); }, [msg]);

  async function save() {
    setSaving(true);
    const r = await api("/settings/signatures", "POST", {
      btb_html: btb, btc_html: btc,
      logo_base64: logoB64 && logoB64.startsWith("data:") ? logoB64 : undefined,
    });
    setSaving(false);
    if (r) { setData(r); setMsg("Signatures saved \u2713"); }
    else { setMsg("Save failed"); }
  }

  function onLogoPick(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { setMsg("Logo must be under 2MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setLogoB64(reader.result);
    reader.readAsDataURL(f);
  }

  function revertToDefault(kind) {
    if (!window.confirm(`Clear the custom ${kind.toUpperCase()} signature and use the default template?`)) return;
    if (kind === "btb") setBtb("");
    else setBtc("");
  }

  if (!data) return <div style={{ fontSize: 12, color: COLORS.grayMid }}>Loading signatures\u2026</div>;

  const currentHtml = activeTab === "btb" ? btb : btc;
  const currentRendered = activeTab === "btb" ? data.rendered_btb : data.rendered_btc;

  return (
    <div style={S.card}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Email Signatures</div>
      <div style={{ fontSize: 11, color: COLORS.grayMid, marginBottom: 12 }}>
        Controls the block appended to every DrewOS-generated email. BTB = business outreach
        (RCFE/Hotel/Hospital/Campus/PropertyMgmt). BTC = consumer/personal deals.
        Use <code>{"{{LOGO_IMG}}"}</code> to embed the logo image inside your HTML.
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
        {["btb","btc"].map(k => (
          <button key={k} onClick={() => setActiveTab(k)}
            style={{ ...S.btn(activeTab === k ? "primary" : "ghost"), fontSize: 12 }}>
            {k.toUpperCase()} Signature
            {((k === "btb" && data.using_default_btb) || (k === "btc" && data.using_default_btc))
              && <span style={{ fontSize: 9, marginLeft: 6, opacity: 0.7 }}>(default)</span>}
          </button>
        ))}
      </div>

      <textarea value={currentHtml}
        onChange={e => activeTab === "btb" ? setBtb(e.target.value) : setBtc(e.target.value)}
        style={{ ...S.input, width: "100%", minHeight: 200, fontSize: 11, fontFamily: "monospace" }}
        placeholder="HTML markup. Use {{LOGO_IMG}} to embed the logo image." />
      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
        <button onClick={() => revertToDefault(activeTab)}
          style={{ ...S.btn("ghost"), fontSize: 11 }}>Clear \u2192 use default</button>
      </div>

      <div style={{ marginTop: 20, borderTop: `1px solid ${COLORS.cardBorder}`, paddingTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Signature Image (logo)</div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 6 }}>
          {logoB64 && (
            <img src={logoB64.startsWith("data:") ? logoB64 : `data:image/png;base64,${logoB64}`}
              alt="signature logo"
              style={{ width: 160, border: `1px solid ${COLORS.cardBorder}`, padding: 4, borderRadius: 4 }} />
          )}
          <div>
            <button onClick={() => fileRef.current?.click()}
              style={{ ...S.btn("ghost"), fontSize: 11 }}>
              {logoB64 ? "Replace logo" : "Upload logo"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onLogoPick} />
            <div style={{ fontSize: 10, color: COLORS.grayMid, marginTop: 4 }}>PNG or JPG, under 2MB.</div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 20, borderTop: `1px solid ${COLORS.cardBorder}`, paddingTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Live preview</div>
        <div style={{ border: `1px solid ${COLORS.cardBorder}`, padding: 16, borderRadius: 4, background: "#fff" }}
          dangerouslySetInnerHTML={{
            __html: (currentHtml || currentRendered || "").replace(
              /\{\{\s*LOGO(_IMG)?\s*\}\}/g,
              logoB64 ?
                `<img src="${logoB64.startsWith("data:") ? logoB64 : "data:image/png;base64," + logoB64}" alt="Outpost Preparedness" width="200" style="margin-top:12px;display:block"/>`
                : "",
            )
          }} />
      </div>

      <div style={{ marginTop: 16, display: "flex", gap: 8, alignItems: "center" }}>
        <button onClick={save} disabled={saving}
          style={{ ...S.btn("primary"), fontSize: 12, opacity: saving ? 0.5 : 1 }}>
          {saving ? "Saving\u2026" : "Save Signatures"}
        </button>
        {msg && <span style={{ fontSize: 11, color: msg.includes("\u2713") ? COLORS.success : COLORS.danger }}>{msg}</span>}
      </div>
    </div>
  );
}


// ─── KNOWLEDGE BASE ─────────────────────────────────────────────────────────
// Drew reads every active doc in this list on every generation call.
function KnowledgeBase() {
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api("/knowledge-base");
    if (r) {
      setDocs(r.documents || []);
      setCategories(r.categories || []);
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const onDelete = async (id) => {
    if (!window.confirm("Remove this document from DrewOS's knowledge base?")) return;
    await fetch(`${CRM_URL}/knowledge-base/${id}`, { method: "DELETE" });
    load();
  };

  const onView = async (id) => {
    const r = await api(`/knowledge-base/${id}`);
    if (!r) return;
    // If the doc has a stored binary (PDF/DOCX/etc.), open it directly in a new
    // tab so the browser renders the native file. Otherwise fall back to the
    // text preview modal for manual entries + markdown.
    const ext = (r.file_type || "").toLowerCase();
    const binaryKinds = ["pdf", "docx", "png", "jpg", "jpeg", "gif"];
    if (r.has_binary && binaryKinds.includes(ext)) {
      window.open(`${CRM_URL}/knowledge-base/${id}/file?inline=true`, "_blank", "noopener,noreferrer");
      return;
    }
    setViewDoc(r);
  };

  const onDownload = (d) => {
    // Always forces a download with the correct filename/extension.
    const url = `${CRM_URL}/knowledge-base/${d.id}/file?inline=false`;
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener noreferrer";
    a.download = d.original_filename || `${d.title || "document"}.${d.file_type || "txt"}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const filtered = filter === "All" ? docs : docs.filter(d => d.category === filter);
  const filterChips = ["All", ...categories];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: COLORS.textPrimary }}>
            DrewOS's Knowledge Base
          </div>
          <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 2 }}>
            {docs.length} {docs.length === 1 ? "document" : "documents"} feeding DrewOS
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={S.btn("primary")} onClick={() => setShowUpload(true)}>
            <Plus size={14} style={{ display: "inline", marginRight: 4, verticalAlign: "-2px" }} />
            Upload Document
          </button>
          <button style={S.btn()} onClick={() => setShowManual(true)}>
            <PenLine size={14} style={{ display: "inline", marginRight: 4, verticalAlign: "-2px" }} />
            Add Text
          </button>
        </div>
      </div>

      {docs.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
          {filterChips.map(c => (
            <button
              key={c}
              style={{ ...S.btn(filter === c ? "primary" : "ghost"), padding: "5px 12px", fontSize: 11 }}
              onClick={() => setFilter(c)}
            >{c}</button>
          ))}
        </div>
      )}

      {loading && <div style={{ color: COLORS.textSecondary, padding: 20 }}>Loading…</div>}

      {!loading && docs.length === 0 && (
        <div style={{ ...S.card, textAlign: "center", padding: 40 }}>
          <BookOpen size={36} style={{ color: COLORS.orange, marginBottom: 12 }} />
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>
            DrewOS's Knowledge Base is empty.
          </div>
          <div style={{ color: COLORS.textSecondary, fontSize: 13, marginBottom: 16, lineHeight: 1.6 }}>
            The more you feed DrewOS, the smarter he gets.<br />
            Start by adding:<br />
            → Customer testimonials<br />
            → Regulations for your verticals<br />
            → Your sales playbook notes<br />
            → Competitor research
          </div>
          <button style={S.btn("primary")} onClick={() => setShowUpload(true)}>
            <Plus size={14} style={{ display: "inline", marginRight: 4, verticalAlign: "-2px" }} />
            Upload Your First Document
          </button>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div style={{ display: "grid", gap: 10 }}>
          {filtered.map(d => (
            <div key={d.id} style={{ ...S.card, padding: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 2 }}>
                    <FileText size={13} style={{ display: "inline", marginRight: 6, color: COLORS.orange, verticalAlign: "-2px" }} />
                    {d.title}
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.textSecondary, marginBottom: 4 }}>
                    Category: {d.category || "Custom"}
                  </div>
                  {d.summary && (
                    <div style={{ fontSize: 12, color: COLORS.textPrimary, fontStyle: "italic", marginBottom: 6, lineHeight: 1.4 }}>
                      &ldquo;{d.summary}&rdquo;
                    </div>
                  )}
                  {d.tags && d.tags.length > 0 && (
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 4 }}>
                      {d.tags.map(t => (
                        <span key={t} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 3, background: "#EEEDE6", color: COLORS.textSecondary, border: `1px solid ${COLORS.cardBorder}` }}>{t}</span>
                      ))}
                    </div>
                  )}
                  <div style={{ fontSize: 10, color: COLORS.grayMid }}>
                    Added: {d.created_at ? d.created_at.slice(0,10) : "—"} · {(d.file_size_kb || 0)} KB · {d.file_type || "manual"}
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <button style={{ ...S.btn("ghost"), padding: "4px 10px", fontSize: 11 }} onClick={() => onView(d.id)}>View</button>
                  {d.has_binary && (
                    <button style={{ ...S.btn("ghost"), padding: "4px 10px", fontSize: 11 }} onClick={() => onDownload(d)}>Download</button>
                  )}
                  <button style={{ ...S.btn("ghost"), padding: "4px 10px", fontSize: 11, color: COLORS.danger }} onClick={() => onDelete(d.id)}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && docs.length > 0 && filtered.length === 0 && (
        <div style={{ color: COLORS.textSecondary, padding: 20, textAlign: "center" }}>
          No documents in &ldquo;{filter}&rdquo;.
        </div>
      )}

      {showUpload && <KbUploadModal categories={categories} onClose={() => setShowUpload(false)} onSaved={() => { setShowUpload(false); load(); }} />}
      {showManual && <KbManualModal categories={categories} onClose={() => setShowManual(false)} onSaved={() => { setShowManual(false); load(); }} />}
      {viewDoc && <KbViewModal doc={viewDoc} onClose={() => setViewDoc(null)} />}
    </div>
  );
}

function KbUploadModal({ categories, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Custom");
  const [tags, setTags] = useState("");
  const [posting, setPosting] = useState(false);
  const [err, setErr] = useState(null);

  const onSubmit = async () => {
    if (!file) { setErr("Pick a file first"); return; }
    setPosting(true); setErr(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    fd.append("category", category);
    fd.append("tags", tags);
    try {
      const r = await fetch(`${CRM_URL}/knowledge-base/upload`, { method: "POST", body: fd });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `${r.status}`);
      }
      onSaved();
    } catch (e) {
      setErr(e.message || "Upload failed");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ ...S.modalBox, maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>+ Upload Document</div>
        <div style={{ fontSize: 11, color: COLORS.textSecondary, marginBottom: 14 }}>
          PDF · TXT · MD · DOCX · max 10MB
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: COLORS.textSecondary, marginBottom: 4 }}>Title (optional)</div>
          <input style={S.input} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Title 22 RCFE Regulations" />
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: COLORS.textSecondary, marginBottom: 4 }}>Category</div>
          <select style={{ ...S.select, width: "100%" }} value={category} onChange={e => setCategory(e.target.value)}>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: COLORS.textSecondary, marginBottom: 4 }}>Tags (comma-separated, optional)</div>
          <input style={S.input} value={tags} onChange={e => setTags(e.target.value)} placeholder="RCFE, compliance, Title 22" />
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: COLORS.textSecondary, marginBottom: 4 }}>File</div>
          <input type="file" accept=".pdf,.txt,.md,.docx" onChange={e => setFile(e.target.files?.[0] || null)} />
          {file && <div style={{ fontSize: 11, color: COLORS.textSecondary, marginTop: 4 }}>{file.name} · {Math.round(file.size / 1024)} KB</div>}
        </div>

        {err && <div style={{ color: COLORS.danger, fontSize: 12, marginBottom: 10 }}>{err}</div>}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button style={S.btn("ghost")} onClick={onClose} disabled={posting}>Cancel</button>
          <button style={S.btn("primary")} onClick={onSubmit} disabled={posting || !file}>
            {posting ? "Saving…" : "Save to Knowledge Base"}
          </button>
        </div>
      </div>
    </div>
  );
}

function KbManualModal({ categories, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Custom");
  const [tags, setTags] = useState("");
  const [content, setContent] = useState("");
  const [posting, setPosting] = useState(false);
  const [err, setErr] = useState(null);

  const onSubmit = async () => {
    if (!title.trim() || !content.trim()) { setErr("Title and content are required"); return; }
    setPosting(true); setErr(null);
    const tagList = tags.split(",").map(t => t.trim()).filter(Boolean);
    const r = await api("/knowledge-base/manual", "POST", { title, category, content, tags: tagList });
    if (!r) {
      setErr("Save failed");
      setPosting(false);
      return;
    }
    onSaved();
  };

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ ...S.modalBox, maxWidth: 640 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 14 }}>+ Add Text</div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: COLORS.textSecondary, marginBottom: 4 }}>Title</div>
          <input style={S.input} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Marriott Install Testimonial" />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 11, color: COLORS.textSecondary, marginBottom: 4 }}>Category</div>
            <select style={{ ...S.select, width: "100%" }} value={category} onChange={e => setCategory(e.target.value)}>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: COLORS.textSecondary, marginBottom: 4 }}>Tags (comma-separated)</div>
            <input style={S.input} value={tags} onChange={e => setTags(e.target.value)} placeholder="hotel, testimonial" />
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: COLORS.textSecondary, marginBottom: 4 }}>Content</div>
          <textarea style={{ ...S.input, minHeight: 220, fontFamily: "inherit", resize: "vertical" }} value={content} onChange={e => setContent(e.target.value)} placeholder="Paste text, notes, research…" />
        </div>

        {err && <div style={{ color: COLORS.danger, fontSize: 12, marginBottom: 10 }}>{err}</div>}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button style={S.btn("ghost")} onClick={onClose} disabled={posting}>Cancel</button>
          <button style={S.btn("primary")} onClick={onSubmit} disabled={posting}>
            {posting ? "Saving…" : "Save to Knowledge Base"}
          </button>
        </div>
      </div>
    </div>
  );
}

function KbViewModal({ doc, onClose }) {
  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ ...S.modalBox, maxWidth: 780 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>{doc.title}</div>
            <div style={{ fontSize: 11, color: COLORS.textSecondary, marginTop: 2 }}>
              {doc.category} · {doc.file_type || "manual"} · {doc.file_size_kb} KB
            </div>
          </div>
          <button style={S.btn("ghost")} onClick={onClose}>Close</button>
        </div>
        {doc.summary && (
          <div style={{ fontStyle: "italic", color: COLORS.textPrimary, marginBottom: 10, padding: 10, background: "#FFF8F3", borderRadius: 6 }}>
            &ldquo;{doc.summary}&rdquo;
          </div>
        )}
        {doc.tags && doc.tags.length > 0 && (
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 12 }}>
            {doc.tags.map(t => (
              <span key={t} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 3, background: "#EEEDE6", color: COLORS.textSecondary, border: `1px solid ${COLORS.cardBorder}` }}>{t}</span>
            ))}
          </div>
        )}
        <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 12, color: COLORS.textPrimary, maxHeight: "55vh", overflow: "auto", background: COLORS.gray, padding: 12, borderRadius: 6, border: `1px solid ${COLORS.cardBorder}` }}>
          {doc.content || "(empty)"}
        </pre>
      </div>
    </div>
  );
}


// ─── TESTIMONIALS (non-public review page) ───────────────────────────────────
// Drew's spec: feedback/testimonial cadence runs server-side (testimonials.py
// on Railway). This page is the review surface — countdown ticker for what's
// coming up, sent emails awaiting response, collected responses with a
// publish toggle. Nothing here is public until Drew explicitly flips it.
function Testimonials() {
  const [data, setData] = useState({ testimonials: [] });
  const [upcoming, setUpcoming] = useState({ upcoming: [] });
  const [loading, setLoading] = useState(true);
  const [adminEmail] = useState("drew@outpostprep.com");
  const [toast, setToast] = useState(null);
  const [filter, setFilter] = useState("all");

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      api("/testimonials?limit=200"),
      api("/testimonials/upcoming?within_days=30"),
    ]).then(([list, up]) => {
      setData(list || { testimonials: [] });
      setUpcoming(up || { upcoming: [] });
      setLoading(false);
    });
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  async function togglePublish(id, makePublic) {
    const r = await api(
      `/testimonials/${id}/publish?admin_email=${encodeURIComponent(adminEmail)}`,
      "POST", { public: makePublic }
    );
    if (r && r.ok) {
      setToast({ type: "success", msg: makePublic ? "Made public" : "Unpublished" });
      fetchAll();
    } else {
      setToast({ type: "error", msg: "Publish toggle failed" });
    }
  }

  async function manualTick() {
    const r = await api(
      `/testimonials/tick?admin_email=${encodeURIComponent(adminEmail)}`,
      "POST"
    );
    if (r) {
      setToast({ type: "success",
        msg: `Tick ran — ${r.drafts?.drafted || 0} new drafts` });
      fetchAll();
    }
  }

  const tList = (data && data.testimonials) || [];
  const grouped = {
    pending: tList.filter(t => t.status === "pending"),
    drafted: tList.filter(t => t.status === "drafted"),
    sent: tList.filter(t => t.status === "sent"),
    collected: tList.filter(t => t.status === "collected"),
    approved: tList.filter(t => t.status === "approved"),
    public: tList.filter(t => t.status === "public"),
  };
  const visibleTestimonials = filter === "all" ? tList :
    (grouped[filter] || []);

  const upcomingList = (upcoming && upcoming.upcoming) || [];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 16,
                    marginBottom: 16, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, color: COLORS.orange, fontSize: 22,
                     fontWeight: 700, letterSpacing: 0.5 }}>
          Testimonials & Feedback
        </h2>
        <span style={{ fontSize: 11, color: COLORS.grayMid,
                       textTransform: "uppercase", letterSpacing: 1 }}>
          Non-public · Drew approves every publish
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button onClick={manualTick} style={S.btn("ghost")}
                  title="Schedule new milestones + draft any due request emails">
            <RefreshCw size={14} style={{ verticalAlign: "middle",
                                            marginRight: 6 }} />
            Run Cadence Now
          </button>
        </div>
      </div>

      {/* COUNTDOWN TICKER */}
      <div style={{ ...S.card, marginBottom: 18, padding: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.orange,
                       letterSpacing: 1.2, textTransform: "uppercase",
                       marginBottom: 10 }}>
          Countdown — next 30 days
        </div>
        {upcomingList.length === 0 ? (
          <div style={{ fontSize: 13, color: COLORS.grayMid }}>
            No milestones in the next 30 days.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {upcomingList.map(u => {
              const days = u.days_until_milestone;
              const tag = days === 0 ? "TODAY" :
                          (days > 0 ? `in ${days}d` : `${Math.abs(days)}d overdue`);
              const overdue = days < 0;
              return (
                <div key={u.id} style={{ display: "flex", alignItems: "center",
                                          gap: 12, padding: "6px 10px",
                                          borderLeft: `3px solid ${overdue ? COLORS.danger : COLORS.orange}`,
                                          background: overdue ? `${COLORS.danger}11` : `${COLORS.orange}08`,
                                          fontSize: 13 }}>
                  <span style={{ fontWeight: 700, minWidth: 110 }}>
                    {u.tenant_name}
                  </span>
                  <span style={{ color: COLORS.grayMid, flex: 1 }}>
                    Day {u.milestone_day} · {u.milestone_label}
                  </span>
                  <span style={{ fontFamily: "monospace", fontWeight: 700,
                                  color: overdue ? COLORS.danger : COLORS.orange }}>
                    {tag}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* STATUS FILTER */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        {[
          ["all", `All (${tList.length})`],
          ["pending", `Pending (${grouped.pending.length})`],
          ["drafted", `Drafted (${grouped.drafted.length})`],
          ["sent", `Sent (${grouped.sent.length})`],
          ["collected", `Collected (${grouped.collected.length})`],
          ["approved", `Approved (${grouped.approved.length})`],
          ["public", `Public (${grouped.public.length})`],
        ].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)}
                  style={S.btn(filter === k ? "primary" : "ghost")}>
            {label}
          </button>
        ))}
      </div>

      {/* LIST */}
      {loading ? <Spinner /> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {visibleTestimonials.length === 0 && (
            <div style={{ fontSize: 13, color: COLORS.grayMid, padding: 24,
                           textAlign: "center", border: `1px dashed ${COLORS.cardBorder}`,
                           borderRadius: 6 }}>
              No testimonials in this view.
            </div>
          )}
          {visibleTestimonials.map(t => (
            <TestimonialRow key={t.id} t={t} onPublish={togglePublish} />
          ))}
        </div>
      )}

      {toast && (
        <div style={{ position: "fixed", top: 20, left: "50%",
                       transform: "translateX(-50%)", zIndex: 9999,
                       background: toast.type === "success" ? COLORS.success : COLORS.danger,
                       color: "#FFFFFF", padding: "10px 24px", borderRadius: 8,
                       fontSize: 13, fontWeight: 600 }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

function TestimonialRow({ t, onPublish }) {
  const [expanded, setExpanded] = useState(false);
  const statusColor = {
    pending: COLORS.grayMid, drafted: COLORS.warning,
    sent: "#3B82F6", collected: COLORS.success,
    approved: "#8B5CF6", public: COLORS.orange, skipped: COLORS.grayDark,
  }[t.status] || COLORS.grayMid;
  const isCollectedOrApproved = ["collected", "approved"].includes(t.status);
  const isPublic = t.status === "public";

  return (
    <div style={{ ...S.card, padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={S.badge(statusColor)}>{t.status}</span>
        <div style={{ fontWeight: 700, fontSize: 14 }}>
          {t.tenant_name || `Tenant #${t.tenant_id}`}
        </div>
        <div style={{ fontSize: 12, color: COLORS.grayMid }}>
          Day {t.milestone_day} · {t.milestone_label}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {isCollectedOrApproved && (
            <button onClick={() => onPublish(t.id, true)}
                    style={{ ...S.btn("primary"), fontSize: 11 }}>
              <Star size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />
              Make Public
            </button>
          )}
          {isPublic && (
            <button onClick={() => onPublish(t.id, false)}
                    style={{ ...S.btn("ghost"), fontSize: 11 }}>
              Unpublish
            </button>
          )}
          <button onClick={() => setExpanded(e => !e)}
                  style={{ ...S.btn("ghost"), fontSize: 11 }}>
            {expanded ? "Hide" : "View"}
          </button>
        </div>
      </div>
      {expanded && (
        <div style={{ marginTop: 12, display: "grid",
                       gridTemplateColumns: "1fr 1fr", gap: 12, fontSize: 12 }}>
          <div>
            <div style={{ fontWeight: 700, color: COLORS.grayMid,
                           textTransform: "uppercase", letterSpacing: 1,
                           fontSize: 10, marginBottom: 4 }}>Request draft</div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {t.draft_subject || "(no subject)"}
            </div>
            <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit",
                           background: COLORS.gray, padding: 10, borderRadius: 4,
                           border: `1px solid ${COLORS.cardBorder}`,
                           maxHeight: 200, overflow: "auto",
                           color: COLORS.textPrimary }}>
              {t.draft_body || "(not drafted yet)"}
            </pre>
          </div>
          <div>
            <div style={{ fontWeight: 700, color: COLORS.grayMid,
                           textTransform: "uppercase", letterSpacing: 1,
                           fontSize: 10, marginBottom: 4 }}>Their response</div>
            {t.response_body ? (
              <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit",
                             background: "#fffbe6", padding: 10, borderRadius: 4,
                             border: `1px solid ${COLORS.warning}`,
                             maxHeight: 200, overflow: "auto",
                             color: COLORS.textPrimary }}>
                {t.response_body}
              </pre>
            ) : (
              <div style={{ fontSize: 12, color: COLORS.grayDark,
                             fontStyle: "italic" }}>
                Awaiting response…
              </div>
            )}
            <div style={{ marginTop: 8, fontSize: 11, color: COLORS.grayMid }}>
              Drafted: {t.drafted_at?.slice(0, 16) || "—"} ·
              Sent: {t.sent_at?.slice(0, 16) || "—"} ·
              Received: {t.response_received_at?.slice(0, 16) || "—"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


const NAV = [
  { id: "command",  label: "Command Center", icon: <Terminal size={18} /> },
  { id: "dashboard",label: "Dashboard",      icon: <LayoutDashboard size={18} /> },
  { id: "deals",    label: "Deals",          icon: <TrendingUp size={18} /> },
  { id: "leads",    label: "Leads",          icon: <Users size={18} /> },
  { id: "orgs",     label: "Organizations",  icon: <Building2 size={18} /> },
  { id: "contacts", label: "Contacts",       icon: <UserCircle size={18} /> },
  { id: "calendar", label: "Calendar",       icon: <CalendarDays size={18} /> },
  { id: "marketing",label: "Marketing",      icon: <Megaphone size={18} /> },
  { id: "inbox",    label: "Inbox",          icon: <Mail size={18} /> },
  { id: "testimonials", label: "Testimonials", icon: <Star size={18} /> },
  { id: "settings", label: "Settings",       icon: <Settings size={18} /> },
  { id: "knowledge",label: "Knowledge",      icon: <BookOpen size={18} /> },
  { id: "chairs",   label: "Chairs",         icon: <Accessibility size={18} /> },
  { id: "financials", label: "Financials",   icon: <Wallet size={18} /> },
];

const SCREENS = {
  command:   <CommandCenter />,
  dashboard: <Dashboard />,
  deals:     <DealsScreen />,
  leads:     <LeadsTable />,
  orgs:      <Organizations />,
  contacts:  <Contacts />,
  calendar:  <CalendarPage />,
  marketing: <MarketingHub />,
  inbox:     <InboxView />,
  testimonials: <Testimonials />,
  settings:  <SettingsScreen />,
  knowledge: <KnowledgeBase />,
  chairs:    <ChairsScreen />,
  financials: <FinancialsScreen />,
};

// ─── PUBLIC BOOKING HEADER ───────────────────────────────────────────────────
// Animated logo header for the public booking page. Fades in on mount with a
// subtle slide-up. Logo links to outpostprep.com in a new tab.
// Detect a DrewOS-flavored booking session. Matches any appointment type
// whose slug starts with "drewos-" (drewos-discovery, drewos-onboarding,
// etc.) via either path-style #booking/drewos-discovery or query-style
// #booking?type=drewos-discovery. Used to swap the booking page branding
// (logo, palette, voice) without forking the whole BookingPage component.
function isDrewOSBookingHash() {
  if (typeof window === "undefined") return false;
  const h = window.location.hash || "";
  return (
    /#booking\/drewos-/i.test(h) ||
    /[?&]type=drewos-/i.test(h)
  );
}

function BookingPageHeader() {
  const [show, setShow] = useState(false);
  const [isDrewos, setIsDrewos] = useState(isDrewOSBookingHash());
  useEffect(() => { requestAnimationFrame(() => setShow(true)); }, []);
  // Re-evaluate when the hash changes (e.g., user clicks a reschedule
  // link mid-session). Cheap listener — no perf concern.
  useEffect(() => {
    const onHash = () => setIsDrewos(isDrewOSBookingHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  if (isDrewos) {
    return (
      <div style={{
        background: "#0B0F1A",
        borderBottom: "1px solid #1a2030",
        padding: "28px 20px 22px",
        textAlign: "center",
        opacity: show ? 1 : 0,
        transform: show ? "translateY(0)" : "translateY(-8px)",
        transition: "opacity 0.5s ease, transform 0.5s ease",
      }}>
        <a href="https://drewos.ai" target="_blank" rel="noreferrer"
          style={{ display: "inline-block", textDecoration: "none" }}>
          <span style={{
            fontFamily: "'JetBrains Mono', ui-monospace, Menlo, Consolas, monospace",
            fontSize: 28,
            fontWeight: 700,
            color: "#F5F0E8",   // Bone
            letterSpacing: "-0.02em",
          }}>
            drewo<span style={{ color: "#FF6B1A" }}>s</span>
          </span>
        </a>
        <div style={{
          fontFamily: "'JetBrains Mono', ui-monospace, Menlo, Consolas, monospace",
          fontSize: 10,
          color: "#FF6B1A",   // Signal
          marginTop: 10,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          fontWeight: 600,
          opacity: show ? 1 : 0,
          transition: "opacity 0.8s ease 0.3s",
        }}>
          Book Your 20-Minute Call
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: "#FFFFFF",
      borderBottom: "1px solid #E5E5E0",
      padding: "24px 20px 16px",
      textAlign: "center",
      opacity: show ? 1 : 0,
      transform: show ? "translateY(0)" : "translateY(-8px)",
      transition: "opacity 0.5s ease, transform 0.5s ease",
    }}>
      <a href="https://outpostprep.com" target="_blank" rel="noreferrer"
        style={{ display: "inline-block" }}>
        <img
          src="/logo.png"
          alt="Outpost Preparedness"
          style={{
            maxWidth: 200,
            width: "100%",
            height: "auto",
          }}
        />
      </a>
      <div style={{
        fontSize: 11,
        color: "#9A9B96",
        marginTop: 8,
        letterSpacing: 0.8,
        textTransform: "uppercase",
        fontWeight: 600,
        opacity: show ? 1 : 0,
        transition: "opacity 0.8s ease 0.3s",
      }}>
        Schedule Your Readiness Review
      </div>
    </div>
  );
}

// ─── APP SHELL ───────────────────────────────────────────────────────────────
export default function App() {
  // All hooks must be called unconditionally (React rules of hooks).
  const [isBooking, setIsBooking] = useState(
    window.location.hash.startsWith("#booking")
  );
  const [page, setPage] = useState("command");
  const [collapsed, setCollapsed] = useState(false);
  const [mobile, setMobile] = useState(window.innerWidth < 768);
  const [inboxUnread, setInboxUnread] = useState(0);

  // Poll Gmail unread count every 60s for the Inbox nav badge.
  useEffect(() => {
    let cancelled = false;
    function fetchUnread() {
      api("/gmail/inbox/unread-count").then(r => {
        if (cancelled) return;
        if (r && typeof r.count === "number") setInboxUnread(r.count);
      });
    }
    fetchUnread();
    const iv = setInterval(fetchUnread, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  // Opening the Inbox page clears the badge optimistically; the real count
  // will be re-synced on the next poll.
  useEffect(() => {
    if (page === "inbox") setInboxUnread(0);
  }, [page]);

  // Notification panel navigation: switch to the right page, then re-dispatch as
  // crm:open-record so sub-views (CommandCenter / MarketingHub / Calendar) open
  // the specific record after their tab finishes mounting.
  useEffect(() => {
    function handler(e) {
      const d = e.detail || {};
      const t = d.entity_type;
      if (t === "comment") setPage("marketing");
      else if (t === "booking") setPage("calendar");
      else if (t === "lead" || t === "contact" || t === "deal") setPage("command");
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("crm:open-record", { detail: d }));
      }, 60);
    }
    window.addEventListener("crm:navigate", handler);
    return () => window.removeEventListener("crm:navigate", handler);
  }, []);

  useEffect(() => {
    function onHash() {
      setIsBooking(window.location.hash.startsWith("#booking"));
    }
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // ── PUBLIC BOOKING ROUTE — no CRM shell, no auth ─────────────
  // Any URL with #booking renders BookingPage directly so prospects
  // clicking email links land on the booking calendar immediately.
  if (isBooking) {
    return (
      <div style={{ background: "#F5F5F0", minHeight: "100vh", color: "#1E2019",
        fontFamily: "'Barlow Condensed', 'Arial Narrow', Arial, sans-serif" }}>
        <BookingPageHeader />
        <BookingPage isPublic />
      </div>
    );
  }

  // ── CRM SHELL (sidebar + topbar + screens) ───────────────────

  const sidebarStyle = {
    ...S.sidebar,
    ...(collapsed || mobile ? S.sidebarCollapsed : {}),
    ...(mobile ? { position: "fixed", left: 0, top: 0, bottom: 0, zIndex: 200 } : {}),
  };

  const currentLabel = NAV.find(n => n.id === page)?.label || "";

  return (
    <div style={S.app}>
      {/* Sidebar */}
      <div style={sidebarStyle}>
        <div style={{ ...S.logoArea, justifyContent: "center" }}>
          {/* DrewOS wordmark — inline so it scales cleanly with sidebar collapse */}
          <span
            aria-label="DrewOS"
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, Menlo, monospace",
              fontWeight: 600,
              letterSpacing: "-0.02em",
              fontSize: collapsed ? 22 : 30,
              color: "#F4F1EB",
              display: "inline-flex",
              alignItems: "baseline",
              lineHeight: 1,
              transition: "font-size 0.2s",
            }}
          >
            {!collapsed && "drewo"}
            <span
              style={{
                background: "#FF6B1A",
                color: "#0A0E1A",
                padding: "0 0.04em",
              }}
            >
              s
            </span>
          </span>
        </div>

        <nav style={S.nav}>
          {NAV.map(item => {
            const badge = item.id === "inbox" && inboxUnread > 0 ? inboxUnread : 0;
            return (
              <div
                key={item.id}
                style={{ ...S.navItem(page === item.id, collapsed || mobile), position: "relative" }}
                onClick={() => { setPage(item.id); if (mobile) setCollapsed(true); }}
              >
                <span style={{ ...S.navIcon, position: "relative" }}>
                  {item.icon}
                  {badge > 0 && (
                    <span style={{
                      position: "absolute", top: -4, right: -6,
                      background: "#EF4444", color: "#FFFFFF",
                      fontSize: 9, fontWeight: 800, borderRadius: 99,
                      minWidth: 14, height: 14, padding: "0 4px",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      border: "2px solid #FFFFFF",
                    }}>{badge > 99 ? "99+" : badge}</span>
                  )}
                </span>
                {!collapsed && !mobile && <span>{item.label}</span>}
                {badge > 0 && !collapsed && !mobile && (
                  <span style={{
                    marginLeft: "auto",
                    background: "#EF4444", color: "#FFFFFF",
                    fontSize: 10, fontWeight: 700, borderRadius: 99,
                    minWidth: 18, padding: "1px 6px",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                  }}>{badge > 99 ? "99+" : badge}</span>
                )}
              </div>
            );
          })}
        </nav>

        <div style={S.sidebarBottom}>
          {!collapsed && !mobile && (
            <>
              <span className="drewos-pip" aria-hidden />
              <span style={{ flex: 1 }}>system live</span>
            </>
          )}
          <div
            onClick={() => setCollapsed(c => !c)}
            style={{
              cursor: "pointer", display: "flex", alignItems: "center",
              justifyContent: "center", color: "#5a6478",
              transition: "color 0.12s",
              marginLeft: collapsed || mobile ? "auto" : 0,
              marginRight: collapsed || mobile ? "auto" : 0,
            }}
            title="Toggle sidebar">
            {collapsed || mobile ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </div>
        </div>
      </div>

      {/* Mobile overlay */}
      {mobile && !collapsed && (
        <div onClick={() => setCollapsed(true)}
          style={{ position: "fixed", inset: 0, background: "#00000066", zIndex: 199 }} />
      )}

      {/* Main */}
      <div style={{ ...S.main, marginLeft: mobile ? 0 : 0 }}>
        {/* Topbar */}
        <div style={S.topbar}>
          {mobile && (
            <button onClick={() => setCollapsed(c => !c)} style={{ ...S.btn("ghost"), padding: "4px 8px", marginRight: 12 }}>
              <Menu size={18} />
            </button>
          )}
          <div style={S.pageTitle}>{currentLabel}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ fontSize: 11, color: COLORS.grayMid }}>
              SB 435: <span style={{ color: COLORS.orange, fontWeight: 700 }}>{daysUntil(SB435_DEADLINE)}d</span>
            </div>
            <NotificationBell />
            <button onClick={() => setPage("calendar")}
              style={{ ...S.btn("primary"), fontSize: 12 }}>
              <CalendarPlus size={14} style={{marginRight:4}} /> Schedule
            </button>
          </div>
        </div>

        {/* Page content */}
        <div style={S.content}>
          {SCREENS[page]}
        </div>
      </div>
    </div>
  );
}
